<template>
  <div class="adm-root">
    <nav class="adm-nav"><div class="container-fluid d-flex align-items-center py-2 px-4">
      <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:28px;" class="me-3" />
      <h6 class="mb-0 adm-brand">Admin Console</h6>
      <div class="ms-auto d-flex align-items-center gap-3">
        <span class="adm-user"><i class="mdi mdi-shield-account me-1"></i>{{ adminName }}</span>
        <button class="btn btn-sm btn-outline-warning" @click="$router.push('/')"><i class="mdi mdi-arrow-left me-1"></i>Back to App</button>
      </div>
    </div></nav>

    <div class="container-fluid px-4" style="padding-top:70px;">
      <!-- TABS -->
      <div class="adm-tabs">
        <button v-for="t in tabs" :key="t.key" class="adm-tab" :class="{active:tab===t.key}" @click="switchTab(t.key)">
          <i :class="t.icon" class="me-1"></i>{{ t.label }}
          <span v-if="t.badge" class="adm-tab-badge">{{ t.badge }}</span>
        </button>
      </div>

      <!-- ===== OVERVIEW ===== -->
      <div v-if="tab==='overview'">
        <div class="adm-stats-grid">
          <div class="adm-stat" v-for="s in overviewStats" :key="s.label" @click="s.action ? s.action() : null" :style="{cursor: s.action ? 'pointer' : 'default'}">
            <div class="adm-stat-icon" :style="{background:s.bg}"><i :class="s.icon" :style="{color:s.color}"></i></div>
            <div><div class="adm-stat-val">{{ s.value }}</div><div class="adm-stat-lbl">{{ s.label }}</div></div>
          </div>
        </div>

        <!-- REVENUE + RECENT ORDERS SIDE BY SIDE -->
        <div class="row g-4 mt-2">
          <div class="col-lg-5">
            <div class="adm-card">
              <h6 class="adm-card-title"><i class="mdi mdi-chart-bar me-2"></i>Revenue Breakdown</h6>
              <div class="adm-rev-rows">
                <div class="adm-rev-row"><span>Today's Payments</span><strong style="color:#50C878;">KES {{ fN(flockrPayments.today?flockrPayments.today.amount:0) }}</strong></div>
                <div class="adm-rev-row"><span>This Month</span><strong style="color:#50C878;">KES {{ fN(flockrPayments.thisMonth?flockrPayments.thisMonth.amount:0) }}</strong></div>
                <div class="adm-rev-row" style="border-top:1px solid var(--fm-br);padding-top:8px;margin-top:4px;"><span>Total GMV (sold value)</span><strong style="color:#BFA164;">KES {{ fN(revenue.gmv) }}</strong></div>
                <div class="adm-rev-row"><span>Platform Fees Earned</span><strong style="color:#50C878;">KES {{ fN(revenue.platformFees) }}</strong></div>
                <div class="adm-rev-row"><span>Transport Revenue (Flockr)</span><strong style="color:#50C878;">KES {{ fN(revenue.transportRevenue) }}</strong></div>
                <div class="adm-rev-row"><span>Insurance Collected</span><strong style="color:#50C878;">KES {{ fN(revenue.insurance) }}</strong></div>
                <div class="adm-rev-row" style="border-top:1px solid var(--fm-br);padding-top:10px;margin-top:4px;"><span style="color:var(--fm-h,#F5F0E8);font-weight:600;">Total Platform Revenue</span><strong style="color:#50C878;font-size:16px;">KES {{ fN(revenue.totalRevenue) }}</strong></div>
                <div class="adm-rev-row"><span>Seller Payouts</span><strong style="color:var(--fm-m,#A09888);">KES {{ fN(revenue.sellerPayouts) }}</strong></div>
                <div class="adm-rev-row"><span>In Escrow (held)</span><strong style="color:#FFAB00;">KES {{ fN(revenue.inEscrow) }}</strong></div>
              </div>
            </div>
          </div>
          <div class="col-lg-7">
            <div class="adm-card">
              <h6 class="adm-card-title"><i class="mdi mdi-receipt me-2"></i>Recent Orders</h6>
              <div class="adm-table-wrap">
                <table class="adm-table"><thead><tr><th>Order</th><th>Buyer</th><th>Seller</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
                  <tbody>
                  <tr v-for="o in recentOrders" :key="o.id" @click="viewOrder(o.id)" style="cursor:pointer;">
                    <td class="adm-mono">{{ o.order_number }}</td>
                    <td>{{ o.buyer_name }}</td>
                    <td>{{ o.seller_name }}</td>
                    <td>KES {{ fN(o.total_amount) }}</td>
                    <td><span class="adm-badge" :class="'adm-b-'+o.status">{{ o.status }}</span></td>
                    <td class="adm-muted">{{ fD(o.created_at) }}</td>
                  </tr>
                  </tbody></table>
                <div v-if="!recentOrders.length" class="adm-empty">No orders yet</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ALERTS -->
        <div class="row g-4 mt-2">
          <div class="col-lg-4">
            <div class="adm-card">
              <h6 class="adm-card-title"><i class="mdi mdi-alert me-2" style="color:#FF6B6B;"></i>Open Disputes ({{ disputes.length }})</h6>
              <div v-for="d in disputes.slice(0,5)" :key="d.id" class="adm-alert-item" @click="tab='disputes';disputeFilter='open';openDisputeDetail(d)">
                <div style="font-size:13px;color:var(--fm-h,#F5F0E8);">{{ d.title }}</div>
                <div class="adm-muted">{{ d.dispute_number }} · {{ d.dispute_type }}</div>
              </div>
              <div v-if="!disputes.length" class="adm-empty">No open disputes</div>
            </div>
          </div>
          <div class="col-lg-4">
            <div class="adm-card">
              <h6 class="adm-card-title"><i class="mdi mdi-cash-clock me-2" style="color:#FFAB00;"></i>Pending Withdrawals ({{ pendingWithdrawals.length }})</h6>
              <div v-for="w in pendingWithdrawals.slice(0,5)" :key="w.id" class="adm-alert-item" @click="tab='finance';finSubTab='withdrawals'">
                <div style="font-size:13px;color:var(--fm-h,#F5F0E8);">KES {{ fN(w.amount) }} → {{ w.mpesa_phone }}</div>
                <div class="adm-muted">{{ w.display_name }} · {{ fD(w.created_at) }}</div>
              </div>
              <div v-if="!pendingWithdrawals.length" class="adm-empty">No pending withdrawals</div>
            </div>
          </div>
          <div class="col-lg-4">
            <div class="adm-card">
              <h6 class="adm-card-title"><i class="mdi mdi-account-group me-2" style="color:#BFA164;"></i>New Users (last 7 days)</h6>
              <div v-for="u in newUsers.slice(0,5)" :key="u.id" class="adm-alert-item" @click="tab='users';userFilter='';viewUserDetail(u)">
                <div style="font-size:13px;color:var(--fm-h,#F5F0E8);">{{ u.display_name }}</div>
                <div class="adm-muted">{{ u.phone }} · {{ u.role_type || 'buyer' }} · {{ fD(u.created_at) }}</div>
              </div>
              <div v-if="!newUsers.length" class="adm-empty">No new users</div>
            </div>
          </div>
        </div>

        <!-- USERS BY COUNTRY -->
        <div class="row g-4 mt-2">
          <div class="col-12">
            <div class="adm-card">
              <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap">
                <h6 class="adm-card-title mb-0"><i class="mdi mdi-earth me-2" style="color:#4FC3F7;"></i>Users by Country <span class="adm-muted" style="font-size:12px;font-weight:400;margin-left:6px;">{{ allUsers.length }} total · {{ usersByCountry.length }} countries</span></h6>
                <span class="adm-muted" style="font-size:11px;">Derived from phone prefix</span>
              </div>
              <div class="adm-table-wrap">
                <table class="adm-table">
                  <thead><tr><th>Country</th><th class="text-end">Users</th><th class="text-end">Share</th><th style="min-width:160px;">Distribution</th></tr></thead>
                  <tbody>
                    <tr v-for="c in usersByCountry" :key="c.key">
                      <td>
                        <span style="font-size:14px;margin-right:6px;">{{ c.flag }}</span>{{ c.country }}
                        <span v-if="c.prefix" class="adm-muted" style="font-size:11px;margin-left:6px;">{{ c.prefix }}</span>
                      </td>
                      <td class="text-end"><strong>{{ c.users }}</strong></td>
                      <td class="text-end">{{ c.share }}%</td>
                      <td>
                        <div style="background:rgba(255,255,255,.05);border-radius:4px;height:6px;overflow:hidden;">
                          <div :style="{width: c.share+'%', height:'100%', background: c.color, transition:'width .3s ease'}"></div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="!usersByCountry.length" class="adm-empty">No users yet</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== ANALYTICS (Alex 2026-07-21) ===== -->
      <div v-if="tab==='analytics'">
        <!-- Range selector + backfill -->
        <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div class="d-flex gap-2">
            <button v-for="r in analyticsRanges" :key="r.days"
                    class="adm-tab" :class="{active: analyticsDays === r.days}"
                    @click="analyticsDays = r.days; loadAnalytics()"
                    style="font-size:11px;padding:5px 10px;">
              {{ r.label }}
            </button>
          </div>
          <div class="d-flex gap-2">
            <button class="btn btn-sm btn-outline-warning" @click="backfillAnalytics" :disabled="analyticsBusy">
              <i class="mdi mdi-refresh me-1"></i>Backfill 90 days
            </button>
          </div>
        </div>

        <!-- KPI strip -->
        <div class="adm-stats-grid" style="grid-template-columns:repeat(auto-fit,minmax(150px,1fr));margin-bottom:14px;">
          <!-- Admin-portal #6: one Active card only — the range buttons above cover 7d/30d activity.
               Admin-portal #5: every card is clickable → drill modal with the underlying records. -->
          <div class="adm-stat" style="cursor:pointer;" @click="openAnalyticsDrill('active_today')"><div class="adm-stat-icon" style="background:rgba(80,200,120,.10);"><i class="mdi mdi-account-check" style="color:#50C878;"></i></div><div><div class="adm-stat-val">{{ fN(analytics.kpis.dau) }}</div><div class="adm-stat-lbl">Active today</div><div style="font-size:9px;color:var(--fm-s,#6B6356);">Click to view →</div></div></div>
          <div class="adm-stat" style="cursor:pointer;" @click="openAnalyticsDrill('new_signups')"><div class="adm-stat-icon" style="background:rgba(206,147,216,.10);"><i class="mdi mdi-account-plus" style="color:#CE93D8;"></i></div><div><div class="adm-stat-val">{{ fN(analytics.kpis.window.new_signups) }}</div><div class="adm-stat-lbl">New signups · {{ analyticsDays }}d</div><div style="font-size:9px;color:var(--fm-s,#6B6356);">Click to view →</div></div></div>
          <div class="adm-stat" style="cursor:pointer;" @click="openAnalyticsDrill('listings_created')"><div class="adm-stat-icon" style="background:rgba(255,171,0,.10);"><i class="mdi mdi-storefront" style="color:#FFAB00;"></i></div><div><div class="adm-stat-val">{{ fN(analytics.kpis.window.listings_created) }}</div><div class="adm-stat-lbl">New listings · {{ analyticsDays }}d</div><div style="font-size:9px;color:var(--fm-s,#6B6356);">Click to view →</div></div></div>
          <div class="adm-stat" style="cursor:pointer;" @click="openAnalyticsDrill('orders_placed')"><div class="adm-stat-icon" style="background:rgba(80,200,120,.10);"><i class="mdi mdi-cart" style="color:#50C878;"></i></div><div><div class="adm-stat-val">{{ fN(analytics.kpis.window.orders_placed) }}</div><div class="adm-stat-lbl">Orders · {{ analyticsDays }}d</div><div style="font-size:9px;color:var(--fm-s,#6B6356);">Click to view →</div></div></div>
          <div class="adm-stat" style="cursor:pointer;" @click="openAnalyticsDrill('gmv')"><div class="adm-stat-icon" style="background:rgba(191,161,100,.10);"><i class="mdi mdi-cash-multiple" style="color:#BFA164;"></i></div><div><div class="adm-stat-val">KES {{ fN(analytics.kpis.window.gmv_kes) }}</div><div class="adm-stat-lbl">GMV · {{ analyticsDays }}d</div><div style="font-size:9px;color:var(--fm-s,#6B6356);">Click to view →</div></div></div>
          <div class="adm-stat" style="cursor:pointer;" @click="openAnalyticsDrill('listing_views')"><div class="adm-stat-icon" style="background:rgba(255,107,107,.10);"><i class="mdi mdi-eye" style="color:#FF6B6B;"></i></div><div><div class="adm-stat-val">{{ fN(analytics.kpis.window.listing_views) }}</div><div class="adm-stat-lbl">Listing views · {{ analyticsDays }}d</div><div style="font-size:9px;color:var(--fm-s,#6B6356);">Click to view →</div></div></div>
        </div>

        <!-- Admin-portal #5: KPI drill-down modal -->
        <div v-if="analyticsDrill.open" class="adm-overlay" @click.self="analyticsDrill.open=false">
          <div class="adm-modal" style="max-width:900px;">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0">
                <i class="mdi mdi-chart-box-outline me-2"></i>{{ analyticsDrill.title }}
                <span style="color:var(--fm-s,#6B6356);font-weight:400;font-size:12px;margin-left:8px;">{{ analyticsDrill.rows.length }}</span>
              </h6>
              <div class="d-flex gap-2 align-items-center">
                <button v-if="analyticsDrill.rows.length" class="adm-tab" style="font-size:11px;padding:5px 10px;" title="Export these rows to CSV" @click="exportCsv('flockr_' + analyticsDrill.metric, analyticsDrill.cols, analyticsDrill.rows)"><i class="mdi mdi-download me-1"></i>Export CSV</button>
                <button class="btn-close btn-close-white" @click="analyticsDrill.open=false"></button>
              </div>
            </div>
            <div class="adm-table-wrap" style="max-height:60vh;overflow-y:auto;">
              <table class="adm-table">
                <thead><tr><th v-for="c in analyticsDrill.cols" :key="c.key" :class="{'text-end': c.fmt==='money'||c.fmt==='num'}">{{ c.label }}</th></tr></thead>
                <tbody>
                  <tr v-for="r in analyticsDrill.rows" :key="r.id">
                    <td v-for="c in analyticsDrill.cols" :key="c.key" :class="{'text-end': c.fmt==='money'||c.fmt==='num'}"
                        :style="c.fmt==='cap' ? 'font-size:11px;text-transform:capitalize;' : (c.fmt==='mono' ? '' : '')">
                      <span v-if="c.fmt==='date'" class="adm-muted">{{ fD(r[c.key]) }}</span>
                      <span v-else-if="c.fmt==='money'"><strong>KES {{ fN(r[c.key]) }}</strong></span>
                      <span v-else-if="c.fmt==='num'"><strong>{{ fN(r[c.key]) }}</strong></span>
                      <span v-else-if="c.fmt==='mono'" class="adm-mono">{{ r[c.key] || '—' }}</span>
                      <span v-else>{{ r[c.key] || '—' }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="!analyticsDrill.rows.length && !analyticsDrill.loading" class="adm-empty">None found in this window.</div>
              <div v-if="analyticsDrill.loading" class="adm-empty"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div>
            </div>
          </div>
        </div>

        <!-- 30-day line chart -->
        <div class="adm-card mb-3">
          <h6 class="adm-card-title"><i class="mdi mdi-chart-line me-2"></i>Last {{ analyticsDays }} days · engagement over time</h6>
          <div v-if="analyticsBusy" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!analytics.series.length" class="adm-empty">No rollup data yet — click Backfill above.</div>
          <!-- Admin-portal #7: labeled x/y axes, gridlines, shared scale + hover tooltip.
               Series share one y-scale — toggle off big series (GMV, views) via the
               legend to zoom into the smaller ones. -->
          <div v-else style="position:relative;">
            <div class="d-flex gap-3 mb-2 flex-wrap" style="font-size:11px;">
              <span v-for="s in chartLegend" :key="s.key" style="cursor:pointer;" @click="toggleSeries(s.key)" :style="{opacity: chartSeriesOn[s.key] ? 1 : .3}">
                <span :style="{display:'inline-block',width:'10px',height:'10px',background:s.color,borderRadius:'2px',marginRight:'4px'}"></span>{{ s.label }}
              </span>
            </div>
            <svg :viewBox="'0 0 '+chartW+' '+chartH" style="width:100%;height:240px;" @mousemove="chartMove($event)" @mouseleave="chartHover.show=false">
              <g v-for="t in chartYTicks" :key="'yt-'+t">
                <line :x1="chartPadL" :x2="chartW-chartPadR" :y1="yForVal(t)" :y2="yForVal(t)" stroke="rgba(138,130,122,.15)" stroke-width="1" />
                <text :x="chartPadL-6" :y="yForVal(t)+3" font-size="9" text-anchor="end" fill="#8A827A">{{ fN(t) }}</text>
              </g>
              <line :x1="chartPadL" :x2="chartPadL" :y1="chartPadT" :y2="chartH-chartPadB" stroke="#8A827A" stroke-width="1" />
              <line :x1="chartPadL" :x2="chartW-chartPadR" :y1="chartH-chartPadB" :y2="chartH-chartPadB" stroke="#8A827A" stroke-width="1" />
              <text :x="(chartPadL+chartW-chartPadR)/2" :y="chartH-2" font-size="10" text-anchor="middle" fill="#8A827A">Date</text>
              <text :x="12" :y="(chartPadT+chartH-chartPadB)/2" font-size="10" text-anchor="middle" fill="#8A827A" :transform="'rotate(-90 12 '+((chartPadT+chartH-chartPadB)/2)+')'">Count / KES</text>
              <polyline v-for="s in chartActiveSeries" :key="'line-'+s.key"
                        :points="polylinePoints(s.key)"
                        :stroke="s.color" fill="none" stroke-width="2" />
              <g v-for="(d, di) in analytics.series" :key="'xt-'+di">
                <text v-if="di % Math.ceil(analytics.series.length/8) === 0" :x="xForIndex(di)" :y="chartH-chartPadB+14" font-size="9" text-anchor="middle" fill="#8A827A">{{ shortDate(d.rollup_date) }}</text>
              </g>
              <g v-if="chartHover.show && chartHover.i > -1">
                <line :x1="xForIndex(chartHover.i)" :x2="xForIndex(chartHover.i)" :y1="chartPadT" :y2="chartH-chartPadB" stroke="#BFA164" stroke-width="1" stroke-dasharray="3,3" />
                <circle v-for="s in chartActiveSeries" :key="'hc-'+s.key" :cx="xForIndex(chartHover.i)" :cy="yForVal(Number(analytics.series[chartHover.i][s.key]||0))" r="3.5" :fill="s.color" stroke="#111" stroke-width="1" />
              </g>
            </svg>
            <div v-if="chartHover.show && chartHover.i > -1"
                 :style="{position:'absolute', pointerEvents:'none', left: chartHover.left+'px', top:'34px', background:'var(--fm-bg2,#141414)', border:'1px solid var(--fm-br,#2A2A2A)', borderRadius:'8px', padding:'8px 10px', fontSize:'11px', zIndex:5, minWidth:'170px', boxShadow:'0 4px 14px rgba(0,0,0,.45)'}">
              <div style="color:var(--fm-h,#F5F0E8);font-weight:600;margin-bottom:4px;">{{ shortDate(analytics.series[chartHover.i].rollup_date) }}</div>
              <div v-for="s in chartActiveSeries" :key="'tt-'+s.key" style="display:flex;justify-content:space-between;gap:14px;">
                <span :style="{color:s.color}">{{ s.label }}</span>
                <b style="color:var(--fm-h,#F5F0E8);">{{ fN(analytics.series[chartHover.i][s.key]||0) }}</b>
              </div>
            </div>
          </div>
        </div>

        <div class="row g-3">
          <!-- Funnel -->
          <div class="col-md-6">
            <div class="adm-card h-100">
              <h6 class="adm-card-title"><i class="mdi mdi-filter-variant me-2"></i>User funnel · {{ analyticsDays }} days</h6>
              <div v-if="!funnel.stages || !funnel.stages.length" class="adm-empty">Loading…</div>
              <div v-else>
                <div v-for="s in funnel.stages" :key="s.name" class="mb-2">
                  <div class="d-flex justify-content-between" style="font-size:12px;">
                    <span>{{ s.name }}</span>
                    <span><b>{{ fN(s.count) }}</b> <span style="color:var(--fm-s,#6B6356);">({{ s.pct }}%)</span></span>
                  </div>
                  <div style="background:rgba(191,161,100,.10);border-radius:6px;overflow:hidden;">
                    <div :style="{width:s.pct+'%',height:'8px',background:'#BFA164'}"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Top actions -->
          <div class="col-md-6">
            <div class="adm-card h-100">
              <h6 class="adm-card-title"><i class="mdi mdi-run-fast me-2"></i>Top actions · last 7 days</h6>
              <div v-if="!topActions.length" class="adm-empty">Loading…</div>
              <div v-else>
                <div v-for="a in topActions" :key="a.action" class="mb-2">
                  <div class="d-flex justify-content-between" style="font-size:12px;">
                    <span>{{ a.action.replace(/_/g,' ') }}</span>
                    <b>{{ fN(a.c) }}</b>
                  </div>
                  <div style="background:rgba(80,200,120,.10);border-radius:6px;overflow:hidden;">
                    <div :style="{width:actionBarPct(a.c)+'%',height:'6px',background:'#50C878'}"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Top listings -->
        <div class="adm-card mt-3">
          <h6 class="adm-card-title"><i class="mdi mdi-trophy-outline me-2"></i>Top listings · {{ analyticsDays }} days</h6>
          <div v-if="!topListings.length" class="adm-empty">Loading…</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Listing</th><th>Seller</th><th class="text-end">Views</th><th class="text-end">Saves</th><th class="text-end">Orders</th></tr></thead>
              <tbody>
                <tr v-for="l in topListings" :key="l.id">
                  <td><a :href="'/marketplace/listing/'+l.id" target="_blank" style="color:#BFA164;text-decoration:none;">{{ l.title }}</a></td>
                  <td style="font-size:11px;">{{ l.seller_name }}</td>
                  <td class="text-end"><b>{{ fN(l.views) }}</b></td>
                  <td class="text-end">{{ fN(l.saves) }}</td>
                  <td class="text-end">{{ fN(l.orders) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ===== APPROVALS (maker-checker) ===== -->
      <div v-if="tab==='approvals'">
        <div class="adm-card mb-3">
          <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-check-decagram me-2"></i>Maker-Checker Approvals</h6>
            <div class="d-flex gap-2 align-items-center">
              <select v-model="mcFilter" class="adm-input" style="width:auto;font-size:12px;" @change="loadPendingActions()">
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <button class="adm-tab" @click="loadPendingActions()" style="font-size:11px;padding:5px 10px;"><i class="mdi mdi-refresh me-1"></i>Refresh</button>
            </div>
          </div>
          <div style="background:rgba(191,161,100,.08);border:1px solid rgba(191,161,100,.3);border-radius:8px;padding:8px 12px;font-size:11px;color:var(--fm-s,#6B6356);margin-bottom:12px;">
            <i class="mdi mdi-shield-account me-1"></i>Sensitive admin actions need a second admin's approval before they take effect. You can't approve your own submissions.
          </div>
          <div v-if="mcLoading" class="text-center py-4"><span class="spinner-border spinner-border-sm"></span></div>
          <div v-else-if="!mcActions.length" class="text-center py-4" style="color:var(--fm-s,#6B6356);font-size:12px;">
            <i class="mdi mdi-check-all d-block mb-1" style="font-size:22px;"></i>No {{ mcFilter }} actions
          </div>
          <div v-else class="table-responsive">
            <table class="adm-table w-100">
              <thead><tr>
                <th>Submitted</th><th>Action</th><th>Details</th><th>Maker</th>
                <th v-if="mcFilter!=='pending'">Checked by</th>
                <th v-if="mcFilter!=='pending'">Notes</th>
                <th v-if="mcFilter==='pending'" class="text-end">Decision</th>
              </tr></thead>
              <tbody>
                <tr v-for="a in mcActions" :key="a.id">
                  <td style="white-space:nowrap;">{{ fD(a.created_at) }}</td>
                  <td><span class="adm-badge" style="background:rgba(191,161,100,0.12);color:#BFA164;">{{ mcLabel(a.function_name) }}</span></td>
                  <td style="max-width:320px;font-size:11px;">
                    <span>{{ a.summary || '—' }}</span>
                    <a href="#" @click.prevent="mcExpandedId = (mcExpandedId===a.id ? null : a.id)" style="font-size:10px;display:block;">{{ mcExpandedId===a.id ? 'hide payload' : 'view payload' }}</a>
                    <pre v-if="mcExpandedId===a.id" style="font-size:10px;background:rgba(0,0,0,.04);border-radius:6px;padding:6px;max-height:160px;overflow:auto;">{{ mcPrettyPayload(a) }}</pre>
                  </td>
                  <td>{{ a.maker_name || a.maker_id }}</td>
                  <td v-if="mcFilter!=='pending'">{{ a.checker_name || '—' }}<div style="font-size:10px;color:var(--fm-s,#6B6356);">{{ a.checked_at ? fD(a.checked_at) : '' }}</div></td>
                  <td v-if="mcFilter!=='pending'" style="max-width:220px;font-size:11px;">{{ a.checker_notes || '—' }}</td>
                  <td v-if="mcFilter==='pending'" class="text-end" style="white-space:nowrap;">
                    <span v-if="a.maker_id === myAdminId" class="adm-badge" style="background:rgba(150,150,150,.12);color:#888;">Yours — awaiting another admin</span>
                    <template v-else>
                      <button class="btn btn-sm btn-success me-1" @click="openMcModal(a,'approve')"><i class="mdi mdi-check me-1"></i>Approve</button>
                      <button class="btn btn-sm btn-outline-danger" @click="openMcModal(a,'reject')"><i class="mdi mdi-close me-1"></i>Reject</button>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Approve / Reject modal -->
        <div v-if="mcModal.show" class="adm-overlay" @click.self="closeMcModal">
          <div class="adm-modal">
            <div class="adm-modal-head d-flex justify-content-between align-items-center">
              <h6 class="m-0">
                <i :class="mcModal.mode==='approve' ? 'mdi mdi-check-decagram me-1' : 'mdi mdi-close-octagon me-1'" :style="{color: mcModal.mode==='approve' ? '#50C878' : '#FF6B6B'}"></i>
                {{ mcModal.mode==='approve' ? 'Approve' : 'Reject' }} · {{ mcLabel(mcModal.action && mcModal.action.function_name) }}
              </h6>
              <button class="btn-close-x" @click="closeMcModal">&times;</button>
            </div>
            <div class="adm-modal-body">
              <div class="adm-card" style="padding:10px;margin-bottom:12px;">
                <div style="font-size:11px;color:var(--fm-s,#6B6356);">Submitted by {{ mcModal.action && mcModal.action.maker_name }} · {{ mcModal.action ? fD(mcModal.action.created_at) : '' }}</div>
                <div style="font-weight:600;font-size:12px;margin-top:4px;">{{ mcModal.action && mcModal.action.summary || '—' }}</div>
                <pre style="font-size:10px;background:rgba(0,0,0,.04);border-radius:6px;padding:6px;max-height:180px;overflow:auto;margin-top:8px;margin-bottom:0;">{{ mcModal.action ? mcPrettyPayload(mcModal.action) : '' }}</pre>
              </div>
              <div v-if="mcModal.mode==='approve'" style="background:rgba(255,152,0,.08);border:1px solid rgba(255,152,0,.3);border-radius:8px;padding:8px 12px;font-size:11px;color:#FFB570;margin-bottom:12px;">
                <i class="mdi mdi-alert-outline me-1"></i>Approving executes this action immediately, exactly as the maker submitted it.
              </div>
              <label class="adm-lbl">{{ mcModal.mode==='approve' ? 'Notes (optional, recorded in audit log)' : 'Reason for rejection (recorded in audit log)' }}</label>
              <textarea v-model="mcModal.notes" class="adm-input w-100" rows="3" :placeholder="mcModal.mode==='approve' ? 'e.g. Verified with the seller by phone.' : 'e.g. Wrong order — resubmit with the correct one.'"></textarea>
            </div>
            <div class="adm-modal-foot d-flex gap-2">
              <button class="btn btn-sm btn-outline-secondary flex-fill" @click="closeMcModal" :disabled="mcModal.busy">Cancel</button>
              <button class="btn btn-sm flex-fill" :class="mcModal.mode==='approve' ? 'btn-success' : 'btn-danger'" @click="submitMcModal" :disabled="mcModal.busy">
                <span v-if="mcModal.busy"><span class="spinner-border spinner-border-sm me-1"></span>Working...</span>
                <span v-else><i :class="mcModal.mode==='approve' ? 'mdi mdi-check me-1' : 'mdi mdi-close me-1'"></i>{{ mcModal.mode==='approve' ? 'Approve & execute' : 'Reject' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== CREDIT PASSPORT ===== -->
      <div v-if="tab==='credit'">
        <div class="adm-card mb-3">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-shield-star me-2"></i>Credit Passport Distribution</h6>
            <button class="adm-tab" @click="loadCredit()" style="font-size:11px;padding:5px 10px;"><i class="mdi mdi-refresh me-1"></i>Refresh</button>
          </div>
          <div class="row g-2">
            <div v-for="d in creditDist" :key="d.tier" class="col-6 col-md-2">
              <div class="text-center" style="padding:12px;border:1px solid var(--fm-br,#2A2520);border-radius:10px;">
                <div style="font-size:22px;font-weight:800;color:#BFA164;font-family:monospace;">{{ d.c }}</div>
                <div style="font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:var(--fm-s,#6B6356);">{{ d.tier }}</div>
              </div>
            </div>
            <div v-if="!creditDist.length" class="col-12 text-center text-muted py-3">
              No snapshots yet. The nightly cron populates this at 03:00.
            </div>
          </div>
        </div>

        <div class="adm-card">
          <h6 class="adm-card-title"><i class="mdi mdi-trophy me-2"></i>Top-Scoring Users</h6>
          <div class="table-responsive">
            <table class="table adm-table">
              <thead><tr><th>Rank</th><th>User</th><th>Phone</th><th>County</th><th>Score</th><th>Tier</th><th>Last Snapshot</th></tr></thead>
              <tbody>
                <tr v-if="!creditUsers.length"><td colspan="7" class="text-center text-muted">No credit scores computed yet.</td></tr>
                <tr v-for="(u, i) in creditUsers" :key="u.user_id">
                  <td>{{ i+1 }}</td>
                  <td>{{ u.display_name || '—' }}</td>
                  <td>{{ u.phone }}</td>
                  <td>{{ u.county || '—' }}</td>
                  <td><strong>{{ u.score }}</strong></td>
                  <td><span class="badge" :style="{background: tierBadgeColour(u.tier), color:'#fff'}">{{ u.tier }}</span></td>
                  <td style="font-size:11px;color:var(--fm-s,#6B6356);">{{ u.snapshot_date }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ===== ORDERS ===== -->
      <div v-if="tab==='orders'">
        <div class="adm-card">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h6 class="adm-card-title mb-0">
              <i class="mdi mdi-receipt me-2"></i>All Orders ({{ allOrders.length }})
              <span class="adm-badge adm-b-info ms-2" style="font-size:11px;" title="Total order value (GMV) of the rows currently shown by the filters below">{{ filteredOrders.length }} shown &middot; GMV KES {{ fN(filteredOrdersGMV) }}</span>
            </h6>
            <div class="d-flex gap-2 flex-wrap">
              <select v-model="orderFilter" class="adm-select"><option value="">All Statuses</option><option v-for="s in orderStatuses" :key="s" :value="s">{{ s }}</option></select>
              <button class="adm-tab" :class="{active:orderTransportFilter==='flockr_arranges'}" @click="orderTransportFilter = orderTransportFilter==='flockr_arranges' ? '' : 'flockr_arranges'" style="font-size:11px;padding:5px 10px;" title="Show only orders that need admin to arrange transport">
                <i class="mdi mdi-truck-fast me-1"></i>Needs Transport
                <span v-if="needsTransportCount" style="background:#FF9800;color:#fff;border-radius:8px;padding:1px 6px;font-size:10px;margin-left:4px;">{{ needsTransportCount }}</span>
              </button>
              <input v-model="orderSearch" class="adm-input" placeholder="Search order #, buyer, seller..." style="width:220px;" />
              <button class="adm-tab" style="font-size:11px;padding:5px 10px;" title="Export the rows currently shown (filters applied)" @click="exportCsv('flockr_orders', [{key:'order_number',label:'Order #'},{key:'buyer_name',label:'Buyer'},{key:'seller_name',label:'Seller'},{key:'total_amount',label:'Amount (KES)'},{key:'status',label:'Status'},{key:'transport_type',label:'Transport'},{key:'created_at',label:'Created'}], filteredOrders)"><i class="mdi mdi-download me-1"></i>Export CSV</button>
            </div>
          </div>
          <div class="adm-table-wrap">
            <table class="adm-table"><thead><tr><th>Order #</th><th>Buyer</th><th>Seller</th><th>Item</th><th>Subtotal</th><th>Transport Fee</th><th>Total</th><th>Transport Type</th><th>Transport</th><th>Status</th><th>Escrow</th><th>Date</th><th title="Used when the buyer/seller hasn't confirmed in the system and Flockr has to complete the order">Admin Complete</th></tr></thead>
              <tbody>
              <tr v-for="o in filteredOrders" :key="o.id" :style="{cursor:'pointer', background: needsArrange(o) ? 'rgba(255,152,0,.04)' : ''}">
                <td class="adm-mono" @click="viewOrder(o.id)">{{ o.order_number }}</td>
                <td @click="viewOrder(o.id)">{{ o.buyer_name }}</td>
                <td @click="viewOrder(o.id)">{{ o.seller_name }}</td>
                <td @click="viewOrder(o.id)">{{ o.listing_title || '-' }}</td>
                <td @click="viewOrder(o.id)">{{ fN(o.subtotal) }}</td>
                <td @click="viewOrder(o.id)">{{ fN(o.transport_fee) }}</td>
                <td @click="viewOrder(o.id)"><strong>{{ fN(o.total_amount) }}</strong></td>
                <td @click="viewOrder(o.id)">
                  <span class="adm-badge" :class="o.transport_option==='flockr_arranges' ? 'adm-b-priority-urgent' : 'adm-b-info'">{{ (o.transport_option||'').replace(/_/g,' ') }}</span>
                </td>
                <td @click.stop>
                  <!-- Admin-portal #17: "Pending Confirmation" until the transporter accepts -->
                  <span v-if="o.transport_option==='flockr_arranges' && o.transport_booking_id && (o.transport_booking_status==='requested' || o.transport_booking_status==='quoted')" class="adm-badge adm-b-pending" style="font-size:9px;">Pending Confirmation</span>
                  <span v-else-if="o.transport_option==='flockr_arranges' && o.transport_booking_id && o.transport_booking_status!=='cancelled'" class="adm-badge adm-b-completed" style="font-size:9px;">arranged</span>
                  <template v-else-if="needsArrange(o)">
                    <!-- Admin-portal #19: when the last booking was cancelled, show who cancelled + why
                         above the re-arrange CTA so the admin knows the context before re-booking. -->
                    <div v-if="o.transport_booking_status==='cancelled' && o.transport_cancelled_by_name" style="font-size:10px;color:#FF6B6B;margin-bottom:4px;line-height:1.3;">
                      <i class="mdi mdi-close-circle-outline"></i>
                      Cancelled by {{ o.transport_cancelled_by_name }}<span v-if="o.transport_cancelled_by_role"> ({{ o.transport_cancelled_by_role }})</span>
                      <div v-if="o.transport_cancellation_reason" style="color:var(--fm-m,#A09888);font-weight:400;">"{{ o.transport_cancellation_reason }}"</div>
                    </div>
                    <button class="btn btn-sm btn-warning" @click.stop="arrangeForOrder(o)" title="Arrange transport for this order"><i class="mdi mdi-truck-plus me-1"></i>Arrange</button>
                  </template>
                  <span v-else class="adm-muted">-</span>
                </td>
                <td @click="viewOrder(o.id)">
                  <span class="adm-badge" :class="'adm-b-'+o.status">{{ o.status }}</span>
                  <div v-if="paymentIssue(o)" style="font-size:10px;color:#FF6B6B;margin-top:2px;max-width:160px;white-space:normal;">{{ paymentIssue(o) }}</div>
                </td>
                <td @click="viewOrder(o.id)"><span class="adm-badge" :class="escrowDisplay(o)==='Not Funded' ? 'adm-b-notfunded' : 'adm-b-'+(o.escrow_status||'none')">{{ escrowDisplay(o) }}</span></td>
                <td class="adm-muted" @click="viewOrder(o.id)">{{ fD(o.created_at) }}</td>
                <td @click.stop>
                  <button v-if="canAdminOverride(o.status)" class="btn btn-sm btn-outline-success" @click.stop="openAdminOverride(o)" title="Move this order forward — use when the seller is unresponsive">
                    <i class="mdi mdi-fast-forward me-1"></i>Override
                  </button>
                </td>
              </tr>
              </tbody></table>
            <div v-if="!filteredOrders.length" class="adm-empty">No orders match</div>
          </div>
        </div>
      </div>

      <!-- Admin order override modal -->
      <div v-if="overrideOrder" class="adm-overlay" @click.self="closeAdminOverride">
        <div class="adm-modal">
          <div class="adm-modal-head d-flex justify-content-between align-items-center">
            <h6 class="m-0"><i class="mdi mdi-shield-key me-1" style="color:#BFA164;"></i>Admin Override · {{ overrideOrder.order_number }}</h6>
            <button class="btn-close-x" @click="closeAdminOverride">&times;</button>
          </div>
          <div class="adm-modal-body">
            <div class="adm-card" style="padding:10px;margin-bottom:12px;">
              <div style="font-size:11px;color:var(--fm-s,#6B6356);">{{ overrideOrder.buyer_name }} → {{ overrideOrder.seller_name }}</div>
              <div style="font-weight:600;">{{ overrideOrder.listing_title }} — KES {{ fN(overrideOrder.total_amount) }}</div>
              <div style="margin-top:4px;"><span class="adm-badge" :class="'adm-b-'+overrideOrder.status">Current: {{ overrideOrder.status }}</span></div>
            </div>

            <div style="background:rgba(255,152,0,.08);border:1px solid rgba(255,152,0,.3);border-radius:8px;padding:8px 12px;font-size:11px;color:#FFB570;margin-bottom:12px;">
              <i class="mdi mdi-alert-outline me-1"></i>Use this when the seller hasn't completed the system steps but the animal has actually been delivered. Selecting <strong>Completed</strong> releases funds from escrow to the seller's wallet immediately.
            </div>

            <!-- Issue #19b — flag orders that never had buyer confirmation of receipt
                 so the admin can't unwittingly release funds on undelivered goods. -->
            <div v-if="overrideNewStatus==='completed' && !overrideOrder.buyer_confirmed_at && !overrideOrder.actual_delivery_at" style="background:rgba(255,107,107,.08);border:1px solid rgba(255,107,107,.4);border-radius:8px;padding:8px 12px;font-size:11px;color:#FF6B6B;margin-bottom:12px;">
              <i class="mdi mdi-alert-circle me-1"></i><strong>Buyer never confirmed receipt.</strong> This order has no delivery or confirmation timestamp on record. Verify with the buyer before releasing funds — write who you spoke to and when in the notes.
            </div>

            <label class="adm-lbl">New status</label>
            <select v-model="overrideNewStatus" class="adm-input w-100 mb-2">
              <option value="">— choose —</option>
              <option value="confirmed">Confirmed</option>
              <option value="preparing">Preparing</option>
              <option value="dispatched">Dispatched</option>
              <option value="in_transit">In Transit</option>
              <option value="delivered">Delivered</option>
              <option value="completed">Completed (releases funds)</option>
              <option value="cancelled">Cancelled (refund buyer)</option>
            </select>

            <label class="adm-lbl">Admin notes <span v-if="overrideNewStatus==='completed'||overrideNewStatus==='cancelled'" style="color:#FF6B6B;">(required — audit log)</span><span v-else>(recorded in audit log)</span></label>
            <textarea v-model="overrideNotes" class="adm-input w-100" rows="3" placeholder="Why are you overriding? e.g. 'Seller delivered but didn't tick Dispatched. Buyer confirmed receipt by phone on 22 May 2026.'"></textarea>
          </div>
          <div class="adm-modal-foot d-flex gap-2">
            <button class="btn btn-sm btn-outline-secondary flex-fill" @click="closeAdminOverride" :disabled="overriding">Cancel</button>
            <button class="btn btn-sm btn-success flex-fill" @click="submitAdminOverride" :disabled="!overrideNewStatus || overriding || ((overrideNewStatus==='completed'||overrideNewStatus==='cancelled') && !overrideNotes.trim())">
              <span v-if="overriding"><span class="spinner-border spinner-border-sm me-1"></span>Applying...</span>
              <span v-else><i class="mdi mdi-check me-1"></i>Apply override</span>
            </button>
          </div>
        </div>
      </div>

      <!-- ===== TRANSPORT ===== -->
      <div v-if="tab==='transports'">
        <!-- KPI strip -->
        <div class="row g-2 mb-3">
          <div class="col-6 col-md"><div class="adm-card" style="padding:12px;"><div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Total Bookings</div><div style="color:var(--fm-h,#F5F0E8);font-weight:700;font-size:18px;margin-top:4px;">{{ transportStats.total||0 }}</div></div></div>
          <div class="col-6 col-md"><div class="adm-card" style="padding:12px;"><div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Pending</div><div style="color:#FF9800;font-weight:700;font-size:18px;margin-top:4px;">{{ transportStats.pending||0 }}</div></div></div>
          <div class="col-6 col-md"><div class="adm-card" style="padding:12px;"><div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Active</div><div style="color:#BFA164;font-weight:700;font-size:18px;margin-top:4px;">{{ transportStats.active||0 }}</div></div></div>
          <div class="col-6 col-md"><div class="adm-card" style="padding:12px;"><div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Completed</div><div style="color:#50C878;font-weight:700;font-size:18px;margin-top:4px;">{{ transportStats.completed||0 }}</div></div></div>
          <div class="col-12 col-md"><div class="adm-card" style="padding:12px;"><div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Transport Revenue (Month)</div><div style="color:#BFA164;font-weight:700;font-size:18px;margin-top:4px;">KES {{ Number(transportStats.revenue||0).toLocaleString() }}</div></div></div>
        </div>

        <!-- Sub-tabs: Bookings / Transporters -->
        <div class="d-flex gap-2 mb-3 flex-wrap">
          <button class="adm-tab" :class="{active:transportSubTab==='bookings'}" @click="transportSubTab='bookings'" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-receipt me-1"></i>Bookings</button>
          <button class="adm-tab" :class="{active:transportSubTab==='transporters'}" @click="transportSubTab='transporters';loadAllTransporters()" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-truck-fast me-1"></i>Browse Transporters</button>
        </div>

        <!-- TRANSPORTERS BROWSE -->
        <div v-if="transportSubTab==='transporters'">
          <div class="adm-card mb-3">
            <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h6 class="adm-card-title mb-0"><i class="mdi mdi-truck-check me-2"></i>All Transporters ({{ allTransporters.length }})</h6>
              <div class="d-flex gap-2 flex-wrap">
                <input v-model="transporterSearch" class="adm-input" placeholder="Search company / name / phone / NTSA..." style="min-width:240px;" @input="loadAllTransporters" />
                <select v-model="transporterStatusFilter" class="adm-input" @change="loadAllTransporters">
                  <option value="">All statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
          </div>
          <div v-if="loadingTransporters" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!allTransporters.length" class="adm-card adm-empty">No transporters match</div>
          <div v-else class="row g-3">
            <div class="col-md-6 col-lg-4" v-for="t in allTransporters" :key="t.id">
              <div class="adm-card" style="padding:16px;">
                <div class="d-flex align-items-start gap-3 mb-3">
                  <div class="adm-user-avatar-lg" style="width:48px;height:48px;font-size:18px;flex-shrink:0;">
                    <img v-if="t.avatar_url" :src="t.avatar_url" style="width:100%;height:100%;border-radius:50%;object-fit:cover;" />
                    <span v-else>{{ (t.company_name||t.transporter_name||'T').charAt(0) }}</span>
                  </div>
                  <div class="flex-fill" style="min-width:0;">
                    <div style="color:var(--fm-h,#F5F0E8);font-weight:700;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">{{ t.company_name || t.transporter_name }}</div>
                    <div v-if="t.company_name && t.transporter_name" style="color:var(--fm-m,#A09888);font-size:11px;">{{ t.transporter_name }}</div>
                    <div style="margin-top:3px;">
                      <span class="adm-pill" :class="t.status==='active'?'pill-completed':'pill-cancelled'" style="font-size:10px;">{{ t.status }}</span>
                      <span v-if="String(t.ntsa_verified)==='1'" class="adm-pill pill-active ms-1" style="font-size:10px;"><i class="mdi mdi-check-decagram me-1"></i>NTSA</span>
                    </div>
                  </div>
                </div>
                <div class="d-flex flex-wrap gap-1 mb-3" style="font-size:10px;">
                  <span v-for="a in (t.animal_types_served||[])" :key="a" style="padding:2px 8px;border-radius:6px;background:rgba(191,161,100,.1);color:#BFA164;text-transform:capitalize;">{{ a.replace(/_/g,' ') }}</span>
                </div>
                <div style="font-size:11px;color:var(--fm-m,#A09888);margin-bottom:12px;">
                  <div><i class="mdi mdi-truck me-1"></i>{{ t.vehicle_count||0 }} vehicle{{ t.vehicle_count==1?'':'s' }} · <i class="mdi mdi-map-marker-radius me-1"></i>{{ t.service_radius_km||'?' }} km radius</div>
                  <div><i class="mdi mdi-check-all me-1"></i>{{ t.total_trips||0 }} trips · <i class="mdi mdi-star me-1" style="color:#FFD700;"></i>{{ t.avg_rating ? Number(t.avg_rating).toFixed(1) : '—' }} ({{ t.total_reviews||0 }})</div>
                  <div v-if="t.transporter_phone"><i class="mdi mdi-phone me-1"></i><a :href="'tel:'+t.transporter_phone" style="color:#BFA164;text-decoration:none;">{{ t.transporter_phone }}</a></div>
                </div>
                <div class="d-flex gap-1 flex-wrap">
                  <button class="btn btn-sm btn-outline-secondary" @click="$router.push('/transport/'+t.user_id)" title="Profile"><i class="mdi mdi-eye"></i></button>
                  <a v-if="t.transporter_phone" :href="'tel:'+t.transporter_phone" class="btn btn-sm btn-outline-success" title="Call"><i class="mdi mdi-phone"></i></a>
                  <button class="btn btn-sm btn-outline-warning" @click="messageTransporter(t)" title="Message"><i class="mdi mdi-message-text"></i></button>
                  <button v-if="String(t.ntsa_verified)!=='1'" class="btn btn-sm btn-outline-success" @click="verifyTransporter(t,true)" :disabled="verifyingId===t.id" title="Verify NTSA"><i class="mdi mdi-check-decagram"></i></button>
                  <button v-else class="btn btn-sm btn-outline-danger" @click="verifyTransporter(t,false)" :disabled="verifyingId===t.id" title="Revoke verification"><i class="mdi mdi-shield-off"></i></button>
                  <button class="btn btn-sm btn-warning flex-fill" @click="arrangeWith(t)"><i class="mdi mdi-truck-plus me-1"></i>Arrange</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- BOOKINGS LIST -->
        <div v-if="transportSubTab==='bookings'" class="adm-card">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-truck-fast me-2"></i>Transport Bookings ({{ transports.length }})</h6>
            <div class="d-flex gap-2 flex-wrap align-items-center">
              <input v-model="transportFilter.search" class="adm-input" placeholder="Search ref, address, name..." style="min-width:220px;" @keyup.enter="loadTransports" />
              <select v-model="transportFilter.status" class="adm-input" @change="loadTransports">
                <option value="">All statuses</option>
                <option value="requested">Requested</option>
                <option value="quoted">Quoted</option>
                <option value="confirmed">Confirmed</option>
                <option value="en_route_to_pickup">En route to pickup</option>
                <option value="at_pickup">At pickup</option>
                <option value="in_transit">In transit</option>
                <option value="at_delivery">At delivery</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select v-model="transportFilter.paymentStatus" class="adm-input" @change="loadTransports">
                <option value="">All payments</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="refunded">Refunded</option>
              </select>
              <input v-model="transportFilter.dateFrom" type="date" class="adm-input" @change="loadTransports" />
              <input v-model="transportFilter.dateTo" type="date" class="adm-input" @change="loadTransports" />
              <button class="btn btn-sm btn-warning" @click="openArrangeTransport"><i class="mdi mdi-plus me-1"></i>Arrange for Buyer</button>
            </div>
          </div>

          <div v-if="transportsLoading" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else class="table-responsive">
            <table class="adm-table">
              <thead>
                <tr>
                  <th>Ref</th><th>Requester</th><th>Transporter</th><th>Route</th><th>Pickup</th><th>Cargo</th><th>Price</th><th>Status</th><th>Payment</th><th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="b in transports" :key="b.id">
                  <td><span class="mt-ref" style="background:rgba(191,161,100,.08);color:#BFA164;padding:2px 6px;border-radius:4px;font-family:monospace;font-size:10px;">{{ b.booking_ref }}</span></td>
                  <td><div style="font-weight:600;">{{ b.requester_name || '—' }}</div><div style="color:var(--fm-s,#6B6356);font-size:10px;">{{ b.requester_phone }}</div></td>
                  <td><div style="font-weight:600;">{{ b.transporter_company || b.transporter_name || '—' }}</div><div style="color:var(--fm-s,#6B6356);font-size:10px;">{{ b.transporter_phone }}</div></td>
                  <td style="max-width:260px;min-width:160px;font-size:11px;color:var(--fm-m,#A09888);white-space:normal;word-break:break-word;">{{ b.pickup_address }} → {{ b.delivery_address }}</td>
                  <td style="font-size:11px;min-width:110px;white-space:nowrap;">{{ b.scheduled_pickup_date }}<span v-if="b.scheduled_pickup_time" style="color:var(--fm-s,#6B6356);">· {{ b.scheduled_pickup_time }}</span></td>
                  <td style="font-size:11px;min-width:120px;white-space:normal;word-break:break-word;">{{ b.animal_count }}<span v-if="b.animal_type_description" style="color:var(--fm-s,#6B6356);"> · {{ b.animal_type_description }}</span></td>
                  <td style="color:#BFA164;font-weight:600;">{{ (b.final_price||b.quoted_price) ? 'KES '+Number(b.final_price||b.quoted_price).toLocaleString() : '—' }}</td>
                  <td><span class="adm-pill" :class="'pill-'+transportStatusGroup(b.status)" style="font-size:10px;">{{ b.status.replace(/_/g,' ') }}</span></td>
                  <td><span style="color:var(--fm-m,#A09888);font-size:11px;text-transform:capitalize;">{{ b.payment_status }}</span></td>
                  <td><button class="btn btn-sm btn-link" style="padding:0;color:#BFA164;" @click="$router.push('/transport/trip/'+b.id)"><i class="mdi mdi-open-in-new"></i></button></td>
                </tr>
              </tbody>
            </table>
            <div v-if="!transports.length" class="adm-empty">No transport bookings match</div>
          </div>
        </div>
      </div>

      <!-- ===== ARRANGE TRANSPORT MODAL ===== -->
      <div v-if="showArrangeModal" class="adm-overlay" @click.self="showArrangeModal=false">
        <div class="adm-modal" style="max-width:700px;max-height:92vh;display:flex;flex-direction:column;">
          <div style="padding:18px 20px;border-bottom:1px solid var(--fm-br,#1E1E1E);display:flex;justify-content:space-between;align-items:flex-start;">
            <div>
              <div style="color:#BFA164;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;">Admin Action</div>
              <h5 style="color:var(--fm-h,#F5F0E8);margin:2px 0 0;font-size:17px;font-weight:700;">Arrange transport on behalf of a buyer</h5>
            </div>
            <button style="background:transparent;border:none;color:var(--fm-m,#A09888);font-size:26px;cursor:pointer;" @click="showArrangeModal=false">&times;</button>
          </div>

          <div style="padding:18px 20px;overflow-y:auto;flex:1;">
            <div v-if="arrangeError" style="background:rgba(255,107,107,.1);border:1px solid rgba(255,107,107,.3);color:#FF6B6B;padding:8px 12px;border-radius:6px;font-size:12px;margin-bottom:12px;">{{ arrangeError }}</div>

            <h6 class="adm-sect">Buyer</h6>
            <div style="position:relative;" class="mb-3">
              <input v-model="arrange.buyerSearch" class="adm-input w-100" placeholder="Search buyer by name, phone, or email..." @input="searchBuyer" />
              <div v-if="arrange.buyerMatches.length" class="adm-dropdown">
                <div v-for="u in arrange.buyerMatches" :key="u.id" class="adm-dropdown-item" @click="pickBuyer(u)">
                  <div style="font-weight:600;">{{ u.display_name }}</div>
                  <div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ u.phone }} · {{ u.email }}</div>
                </div>
              </div>
              <div v-if="arrange.onBehalfOfUserId" style="font-size:11px;color:#50C878;margin-top:4px;"><i class="mdi mdi-check-circle me-1"></i>Buyer locked in</div>
              <!-- Admin-portal #20: surface this buyer's orders that still need transport for one-click prefill -->
              <div v-if="arrangeBuyerOrders.length" class="mt-2" style="border:1px solid rgba(255,152,0,.35);border-radius:6px;padding:8px;">
                <div style="font-size:11px;color:#FFAB00;font-weight:600;margin-bottom:6px;"><i class="mdi mdi-truck-alert me-1"></i>{{ arrangeBuyerOrders.length }} active order{{ arrangeBuyerOrders.length>1?'s':'' }} awaiting transport — click to prefill</div>
                <div v-for="o in arrangeBuyerOrders" :key="'abo-'+o.id" @click="prefillArrangeFromOrder(o)" style="padding:6px 8px;border-radius:6px;cursor:pointer;" :style="{border: arrange.orderId===o.id ? '1px solid #50C878' : '1px solid transparent'}">
                  <div style="font-weight:600;font-size:12px;">{{ o.order_number }} · {{ o.listing_title || 'order' }} <i v-if="arrange.orderId===o.id" class="mdi mdi-check-circle" style="color:#50C878;"></i></div>
                  <div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ o.quantity }} animal(s) · KES {{ fN(o.total_amount) }} · {{ o.delivery_location || 'no delivery address on order' }}</div>
                </div>
              </div>
            </div>

            <h6 class="adm-sect mt-3">Transporter (optional — leave blank to broadcast)</h6>
            <div style="position:relative;" class="mb-3">
              <input v-model="arrange.transporterSearch" class="adm-input w-100" placeholder="Search transporter by company / name..." @input="searchArrangeTransporter" />
              <div v-if="arrange.transporterMatches.length" class="adm-dropdown">
                <div v-for="t in arrange.transporterMatches" :key="t.user_id" class="adm-dropdown-item" @click="pickArrangeTransporter(t)">
                  <div style="font-weight:600;">{{ t.company_name || t.transporter_name }}</div>
                  <div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ t.total_trips||0 }} trips · {{ t.phone }}</div>
                </div>
              </div>
            </div>

            <h6 class="adm-sect mt-3">Cargo</h6>
            <div class="row g-2 mb-3">
              <div class="col-md-4"><label class="adm-lbl">Animal count *</label><input v-model.number="arrange.animalCount" type="number" min="1" class="adm-input w-100" /></div>
              <div class="col-md-8"><label class="adm-lbl">Description</label><input v-model="arrange.animalTypeDescription" class="adm-input w-100" placeholder="e.g. 10 Friesian cows" /></div>
            </div>

            <h6 class="adm-sect">Pickup</h6>
            <div style="position:relative;" class="mb-2">
              <input v-model="arrange.pickupAddress" class="adm-input w-100" placeholder="Search pickup location..." @input="searchArrangeLocation('pickup')" />
              <div v-if="arrange.pickupMatches && arrange.pickupMatches.length" class="adm-dropdown">
                <div v-for="(loc,i) in arrange.pickupMatches" :key="'p'+i" class="adm-dropdown-item" @click="pickArrangeLocation('pickup',loc)">{{ loc.display_name }}</div>
              </div>
            </div>
            <div class="mb-2">
              <input v-model="arrange.pickupLandmark" class="adm-input w-100" placeholder="Landmark / extra directions (e.g. next to Kwa Mutua shops, blue gate)" />
            </div>
            <div class="row g-2 mb-3">
              <div class="col-md-6"><input v-model="arrange.pickupContactName" class="adm-input w-100" placeholder="Pickup contact name" /></div>
              <div class="col-md-6"><input v-model="arrange.pickupContactPhone" class="adm-input w-100" placeholder="Pickup contact phone" /></div>
            </div>

            <h6 class="adm-sect">Delivery</h6>
            <div style="position:relative;" class="mb-2">
              <input v-model="arrange.deliveryAddress" class="adm-input w-100" placeholder="Search delivery location..." @input="searchArrangeLocation('delivery')" />
              <div v-if="arrange.deliveryMatches && arrange.deliveryMatches.length" class="adm-dropdown">
                <div v-for="(loc,i) in arrange.deliveryMatches" :key="'d'+i" class="adm-dropdown-item" @click="pickArrangeLocation('delivery',loc)">{{ loc.display_name }}</div>
              </div>
            </div>
            <div class="row g-2 mb-3">
              <div class="col-md-6"><input v-model="arrange.deliveryContactName" class="adm-input w-100" placeholder="Delivery contact name" /></div>
              <div class="col-md-6"><input v-model="arrange.deliveryContactPhone" class="adm-input w-100" placeholder="Delivery contact phone" /></div>
            </div>

            <h6 class="adm-sect">Schedule &amp; Price</h6>
            <div class="row g-2 mb-3">
              <div class="col-md-4"><label class="adm-lbl">Pickup date *</label><input v-model="arrange.scheduledPickupDate" type="date" class="adm-input w-100" /></div>
              <div class="col-md-4"><label class="adm-lbl">Pickup time</label><input v-model="arrange.scheduledPickupTime" type="time" class="adm-input w-100" /></div>
              <div class="col-md-4"><label class="adm-lbl">Quoted price (KES)</label><input v-model.number="arrange.quotedPrice" type="number" min="0" class="adm-input w-100" /></div>
            </div>

            <label class="adm-lbl">Special instructions</label>
            <textarea v-model="arrange.specialInstructions" rows="2" class="adm-input w-100 mb-2"></textarea>
            <label class="adm-lbl">Admin notes (internal)</label>
            <textarea v-model="arrange.adminNotes" rows="2" class="adm-input w-100" placeholder="Why we're arranging this, anything the transporter should know"></textarea>
          </div>

          <div style="padding:14px 20px;border-top:1px solid var(--fm-br,#1E1E1E);display:flex;justify-content:flex-end;gap:8px;">
            <button class="btn btn-sm btn-outline-secondary" @click="showArrangeModal=false" :disabled="arranging">Cancel</button>
            <button class="btn btn-sm btn-warning" @click="submitArrangeTransport" :disabled="arranging">
              <span v-if="arranging" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="mdi mdi-truck-check me-1"></i>
              {{ arranging ? 'Creating...' : 'Create Transport Booking' }}
            </button>
          </div>
        </div>
      </div>

      <!-- ===== USERS ===== -->
      <div v-if="tab==='users'">
        <!-- Sub-tabs: All Users / Field Agents / Referrals (Dev-Requests #9-11) -->
        <div class="d-flex gap-2 mb-3 flex-wrap">
          <button class="adm-tab" :class="{active:userSubTab==='all'}" @click="userSubTab='all'" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-account-group me-1"></i>All Users</button>
          <button class="adm-tab" :class="{active:userSubTab==='agents'}" @click="userSubTab='agents';loadAgents()" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-account-tie me-1"></i>Field Agents <span v-if="agents.length" style="background:#BFA164;color:#fff;border-radius:8px;padding:1px 6px;font-size:10px;margin-left:4px;">{{ agents.length }}</span></button>
          <button class="adm-tab" :class="{active:userSubTab==='referrals'}" @click="userSubTab='referrals';loadReferralMetrics()" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-account-group-outline me-1"></i>Referrals</button>
        </div>

        <!-- Referrals sub-tab (Dev-Requests #9/#10/#11) -->
        <div v-if="userSubTab==='referrals'">
          <!-- Clickable metrics tiles (#9) — each opens a drill-down modal with the
               underlying users so admin can inspect who's behind the number. -->
          <div class="adm-stats-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:12px;">
            <div class="adm-stat" style="cursor:pointer;" @click="openReferralDrill('total')">
              <div class="adm-stat-icon" style="background:rgba(191,161,100,0.1);"><i class="mdi mdi-account-group" style="color:#BFA164;"></i></div>
              <div><div class="adm-stat-val">{{ referralMetrics.totalReferrals }}</div><div class="adm-stat-lbl">Total Referrals</div><div style="font-size:9px;color:var(--fm-s,#6B6356);">Click to view →</div></div>
            </div>
            <div class="adm-stat" style="cursor:pointer;" @click="openReferralDrill('active')">
              <div class="adm-stat-icon" style="background:rgba(80,200,120,0.1);"><i class="mdi mdi-account-check" style="color:#50C878;"></i></div>
              <div><div class="adm-stat-val">{{ referralMetrics.activeUsers }}</div><div class="adm-stat-lbl">Active Users</div><div style="font-size:9px;color:var(--fm-s,#6B6356);">Last 30 days</div></div>
            </div>
            <div class="adm-stat" style="cursor:pointer;" @click="openReferralDrill('listed')">
              <div class="adm-stat-icon" style="background:rgba(79,195,247,0.1);"><i class="mdi mdi-storefront-outline" style="color:#4FC3F7;"></i></div>
              <div><div class="adm-stat-val">{{ referralMetrics.usersWhoListed }}</div><div class="adm-stat-lbl">Users Who Listed</div><div style="font-size:9px;color:var(--fm-s,#6B6356);">≥1 active listing</div></div>
            </div>
            <div class="adm-stat" style="cursor:pointer;" @click="openReferralDrill('transacted')">
              <div class="adm-stat-icon" style="background:rgba(255,171,0,0.1);"><i class="mdi mdi-receipt" style="color:#FFAB00;"></i></div>
              <div><div class="adm-stat-val">{{ referralMetrics.usersWhoTransacted }}</div><div class="adm-stat-lbl">Users Who Transacted</div><div style="font-size:9px;color:var(--fm-s,#6B6356);">≥1 order</div></div>
            </div>
          </div>

          <!-- Referral codes usage (#10) with date filtering -->
          <div class="adm-card mb-3">
            <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <h6 class="adm-card-title mb-0"><i class="mdi mdi-ticket-percent me-2"></i>Referral Codes Used ({{ referralCodes.length }})</h6>
              <div class="d-flex gap-2 align-items-center">
                <label style="font-size:11px;color:var(--fm-s,#6B6356);">From</label>
                <input type="date" v-model="referralFilter.dateFrom" class="adm-input" style="width:150px;" @change="loadReferralCodes" />
                <label style="font-size:11px;color:var(--fm-s,#6B6356);">To</label>
                <input type="date" v-model="referralFilter.dateTo" class="adm-input" style="width:150px;" @change="loadReferralCodes" />
                <button class="btn btn-sm btn-outline-warning" @click="referralFilter.dateFrom='';referralFilter.dateTo='';loadReferralCodes()">Reset</button>
              </div>
            </div>
            <div class="adm-table-wrap">
              <table class="adm-table">
                <thead><tr><th>Code</th><th>Owner</th><th>Owner Type</th><th class="text-end">Uses</th><th class="text-end">Active</th><th>Last Used</th></tr></thead>
                <tbody>
                  <tr v-for="c in referralCodes" :key="c.owner_id">
                    <td><span class="adm-mono" style="background:rgba(191,161,100,.12);padding:2px 8px;border-radius:6px;color:#BFA164;font-weight:700;">{{ c.referral_code }}</span></td>
                    <td><strong>{{ c.owner_name }}</strong><div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ c.owner_phone }}</div></td>
                    <td style="font-size:11px;text-transform:capitalize;">{{ c.owner_type }}</td>
                    <td class="text-end"><strong style="color:#50C878;">{{ c.uses }}</strong></td>
                    <td class="text-end"><span style="color:#4FC3F7;">{{ c.active_uses }}</span></td>
                    <td class="adm-muted">{{ fD(c.last_used_at) }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-if="!referralCodes.length && !referralLoading" class="adm-empty">No referral code usage in this period.</div>
            </div>
          </div>

          <!-- Cross-user referral summary (#11) -->
          <div class="adm-card">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h6 class="adm-card-title mb-0"><i class="mdi mdi-source-branch me-2"></i>Who Referred Who</h6>
            </div>
            <div class="adm-table-wrap">
              <table class="adm-table">
                <thead><tr><th>Referrer</th><th>Code</th><th class="text-end">Referred</th><th class="text-end">Active</th><th class="text-end">Transactions</th><th class="text-end">GMV (KES)</th></tr></thead>
                <tbody>
                  <tr v-for="r in referralSummary" :key="r.referrer_id">
                    <td><strong>{{ r.referrer_name }}</strong><div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ r.referrer_phone }} · {{ r.referrer_type }}</div></td>
                    <td><span class="adm-mono" style="background:rgba(191,161,100,.12);padding:2px 8px;border-radius:6px;color:#BFA164;">{{ r.referral_code }}</span></td>
                    <td class="text-end"><strong style="color:#50C878;">{{ r.referred_count }}</strong></td>
                    <td class="text-end">{{ r.active_referred }}</td>
                    <td class="text-end">{{ r.referred_transaction_count }}</td>
                    <td class="text-end">{{ fN(r.referred_gmv) }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-if="!referralSummary.length && !referralLoading" class="adm-empty">No referrers with signups yet.</div>
            </div>
          </div>
        </div>

        <!-- Referral drill-down modal (#9) -->
        <div v-if="referralDrill.open" class="adm-overlay" @click.self="referralDrill.open=false">
          <div class="adm-modal" style="max-width:900px;">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0">
                <i class="mdi mdi-account-group me-2"></i>
                <span v-if="referralDrill.bucket==='total'">All Referred Users</span>
                <span v-else-if="referralDrill.bucket==='active'">Active Referred Users (last 30 days)</span>
                <span v-else-if="referralDrill.bucket==='listed'">Referred Users Who Have Listed</span>
                <span v-else-if="referralDrill.bucket==='transacted'">Referred Users Who Have Transacted</span>
                <span style="color:var(--fm-s,#6B6356);font-weight:400;font-size:12px;margin-left:8px;">{{ referralDrill.users.length }}</span>
              </h6>
              <button class="btn-close btn-close-white" @click="referralDrill.open=false"></button>
            </div>
            <div class="adm-table-wrap" style="max-height:60vh;overflow-y:auto;">
              <table class="adm-table">
                <thead><tr><th>Name</th><th>Phone</th><th>Type</th><th>Referred By</th><th>Joined</th><th v-if="referralDrill.bucket==='listed'" class="text-end">Listings</th><th v-if="referralDrill.bucket==='transacted'" class="text-end">Orders</th></tr></thead>
                <tbody>
                  <tr v-for="u in referralDrill.users" :key="u.id">
                    <td><strong>{{ u.display_name }}</strong></td>
                    <td class="adm-mono">{{ u.phone }}</td>
                    <td style="font-size:11px;text-transform:capitalize;">{{ u.user_type }}</td>
                    <td style="font-size:11px;">{{ u.referrer_name || '—' }}</td>
                    <td class="adm-muted">{{ fD(u.created_at) }}</td>
                    <td v-if="referralDrill.bucket==='listed'" class="text-end"><strong>{{ u.listing_count }}</strong></td>
                    <td v-if="referralDrill.bucket==='transacted'" class="text-end"><strong>{{ u.order_count }}</strong></td>
                  </tr>
                </tbody>
              </table>
              <div v-if="!referralDrill.users.length && !referralDrill.loading" class="adm-empty">None found.</div>
              <div v-if="referralDrill.loading" class="adm-empty"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div>
            </div>
          </div>
        </div>

        <!-- All Users sub-tab -->
        <div v-if="userSubTab==='all'" class="adm-card">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-account-group me-2"></i>Users ({{ userTypeCounts.total }})</h6>
            <div class="d-flex gap-2 flex-wrap">
              <input v-model="userSearch" class="adm-input" placeholder="Search name, phone..." style="width:220px;" />
              <button class="adm-tab" style="font-size:11px;padding:5px 10px;" title="Export the rows currently shown (filters applied)" @click="exportCsv('flockr_users', [{key:'display_name',label:'Name'},{key:'phone',label:'Phone'},{key:'user_type',label:'Type'},{key:'roles',label:'Roles'},{key:'farm_count',label:'Farms'},{key:'status',label:'Status'},{key:'wallet_balance',label:'Wallet'},{key:'order_count',label:'Orders'},{key:'created_at',label:'Joined'}], filteredUsers)"><i class="mdi mdi-download me-1"></i>Export CSV</button>
            </div>
          </div>

          <!-- Dev-Requests #8 — user-type tab strip. Filters allUsers[] client-side by
               user_type. Counts update live so admin can see at a glance who's who. -->
          <div class="d-flex gap-2 mb-3 flex-wrap">
            <button class="adm-tab" :class="{active:userTypeTab===''}"           @click="userTypeTab=''"           style="font-size:11px;padding:4px 10px;">All <span class="adm-tab-count">{{ userTypeCounts.total }}</span></button>
            <button class="adm-tab" :class="{active:userTypeTab==='farmer'}"     @click="userTypeTab='farmer'"     style="font-size:11px;padding:4px 10px;"><i class="mdi mdi-tractor me-1"></i>Farmers <span class="adm-tab-count">{{ userTypeCounts.farmer }}</span></button>
            <button class="adm-tab" :class="{active:userTypeTab==='buyer'}"      @click="userTypeTab='buyer'"      style="font-size:11px;padding:4px 10px;"><i class="mdi mdi-cart-outline me-1"></i>Buyers <span class="adm-tab-count">{{ userTypeCounts.buyer }}</span></button>
            <button class="adm-tab" :class="{active:userTypeTab==='seller'}"     @click="userTypeTab='seller'"     style="font-size:11px;padding:4px 10px;"><i class="mdi mdi-storefront me-1"></i>Sellers <span class="adm-tab-count">{{ userTypeCounts.seller }}</span></button>
            <button class="adm-tab" :class="{active:userTypeTab==='aggregator'}" @click="userTypeTab='aggregator'" style="font-size:11px;padding:4px 10px;"><i class="mdi mdi-truck-cargo-container me-1"></i>Aggregators <span class="adm-tab-count">{{ userTypeCounts.aggregator }}</span></button>
            <button class="adm-tab" :class="{active:userTypeTab==='vet'}"        @click="userTypeTab='vet'"        style="font-size:11px;padding:4px 10px;"><i class="mdi mdi-stethoscope me-1"></i>Vets <span class="adm-tab-count">{{ userTypeCounts.vet }}</span></button>
            <button class="adm-tab" :class="{active:userTypeTab==='transporter'}" @click="userTypeTab='transporter'" style="font-size:11px;padding:4px 10px;"><i class="mdi mdi-truck-fast me-1"></i>Transporters <span class="adm-tab-count">{{ userTypeCounts.transporter }}</span></button>
            <button class="adm-tab" :class="{active:userTypeTab==='product_seller'}" @click="userTypeTab='product_seller'" style="font-size:11px;padding:4px 10px;"><i class="mdi mdi-basket-outline me-1"></i>Agro-input <span class="adm-tab-count">{{ userTypeCounts.product_seller }}</span></button>
            <button class="adm-tab" :class="{active:userTypeTab==='corporate'}"  @click="userTypeTab='corporate'"  style="font-size:11px;padding:4px 10px;"><i class="mdi mdi-domain me-1"></i>Corporates <span class="adm-tab-count">{{ userTypeCounts.corporate }}</span></button>
            <button class="adm-tab" :class="{active:userTypeTab==='agent'}"      @click="userTypeTab='agent'"      style="font-size:11px;padding:4px 10px;"><i class="mdi mdi-account-tie me-1"></i>Agents <span class="adm-tab-count">{{ userTypeCounts.agent }}</span></button>
            <button class="adm-tab" :class="{active:userTypeTab==='admin'}"      @click="userTypeTab='admin'"      style="font-size:11px;padding:4px 10px;"><i class="mdi mdi-shield-crown me-1"></i>Admins <span class="adm-tab-count">{{ userTypeCounts.admin }}</span></button>
          </div>

          <div class="adm-table-wrap">
            <!-- Admin-portal #28: Type column removed (users never pick a type at
                 signup and can hold several roles) — replaced with the one
                 distinction the client wants: who has registered a farm. -->
            <table class="adm-table"><thead><tr><th>Name</th><th>Phone</th><th>Farm</th><th>Status</th><th>Wallet</th><th>Orders</th><th>Joined</th><th>Actions</th></tr></thead>
              <tbody>
              <tr v-for="u in filteredUsers" :key="u.id">
                <td><strong style="color:var(--fm-h,#F5F0E8);">{{ u.display_name }}</strong></td>
                <td class="adm-mono">{{ u.phone }}</td>
                <td><span v-if="Number(u.farm_count)>0" class="adm-badge adm-b-completed" title="Registered a farm (farm management)"><i class="mdi mdi-tractor me-1"></i>Farm</span><span v-else class="adm-muted">—</span></td>
                <td><span class="adm-badge" :class="u.status==='active'?'adm-b-completed':'adm-b-cancelled'">{{ u.status }}</span></td>
                <td>KES {{ fN(u.wallet_balance) }}</td>
                <td>{{ u.order_count||0 }}</td>
                <td class="adm-muted">{{ fD(u.created_at) }}</td>
                <td>
                  <button class="btn btn-sm btn-outline-warning me-1" @click="viewUserDetail(u)" title="View"><i class="mdi mdi-eye"></i></button>
                  <button v-if="u.status==='active'" class="btn btn-sm btn-outline-danger" @click="toggleUserStatus(u,'suspended')" title="Suspend"><i class="mdi mdi-block-helper"></i></button>
                  <button v-else class="btn btn-sm btn-outline-success" @click="toggleUserStatus(u,'active')" title="Activate"><i class="mdi mdi-check"></i></button>
                </td>
              </tr>
              </tbody></table>
          </div>
        </div>

        <!-- Field Agents sub-tab — referral-driven onboarding (Governor program) -->
        <div v-if="userSubTab==='agents'">
          <!-- Summary strip -->
          <div class="adm-stats-grid" style="grid-template-columns:repeat(auto-fit,minmax(160px,1fr));margin-bottom:12px;">
            <div class="adm-stat"><div class="adm-stat-icon" style="background:rgba(191,161,100,0.1);"><i class="mdi mdi-account-tie" style="color:#BFA164;"></i></div><div><div class="adm-stat-val">{{ agents.length }}</div><div class="adm-stat-lbl">Total Agents</div></div></div>
            <div class="adm-stat"><div class="adm-stat-icon" style="background:rgba(80,200,120,0.1);"><i class="mdi mdi-account-plus" style="color:#50C878;"></i></div><div><div class="adm-stat-val">{{ agentsTotalReferred }}</div><div class="adm-stat-lbl">Users Brought In</div></div></div>
            <div class="adm-stat"><div class="adm-stat-icon" style="background:rgba(79,195,247,0.1);"><i class="mdi mdi-trophy" style="color:#4FC3F7;"></i></div><div><div class="adm-stat-val">{{ topAgentName }}</div><div class="adm-stat-lbl">Top Agent</div></div></div>
            <div class="adm-stat"><div class="adm-stat-icon" style="background:rgba(255,171,0,0.1);"><i class="mdi mdi-clock-outline" style="color:#FFAB00;"></i></div><div><div class="adm-stat-val">{{ agentsActiveLast30 }}</div><div class="adm-stat-lbl">Active Last 30d</div></div></div>
          </div>

          <div class="adm-card">
            <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <h6 class="adm-card-title mb-0"><i class="mdi mdi-account-tie me-2"></i>Field Agent Leaderboard</h6>
              <div class="d-flex gap-2">
                <input v-model="agentSearch" class="adm-input" placeholder="Search name, phone, code..." style="width:220px;" />
                <button class="btn btn-warning btn-sm" @click="openCreateAgentModal"><i class="mdi mdi-account-plus me-1"></i>Register New Agent</button>
              </div>
            </div>
            <div class="adm-table-wrap">
              <table class="adm-table">
                <thead><tr><th>#</th><th>Agent</th><th>Phone</th><th>Referral Code</th><th class="text-end"># Brought In</th><th>Last Referral</th><th>Region</th><th>Joined</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  <tr v-for="(a, ai) in filteredAgents" :key="a.id">
                    <td><span v-if="ai < 3" :style="{fontSize:'16px'}">{{ ['🥇','🥈','🥉'][ai] }}</span><span v-else>{{ ai+1 }}</span></td>
                    <td><strong style="color:var(--fm-h,#F5F0E8);">{{ a.display_name }}</strong></td>
                    <td class="adm-mono">{{ a.phone }}</td>
                    <td><span class="adm-mono" style="background:rgba(191,161,100,.12);padding:2px 8px;border-radius:6px;color:#BFA164;font-weight:700;">{{ a.referral_code }}</span></td>
                    <td class="text-end"><strong style="color:#50C878;">{{ a.referred_count || 0 }}</strong></td>
                    <td class="adm-muted">{{ a.last_referral_at ? fD(a.last_referral_at) : '—' }}</td>
                    <td>{{ a.county_name || '—' }}</td>
                    <td class="adm-muted">{{ fD(a.created_at) }}</td>
                    <td><span class="adm-badge" :class="a.account_status==='active'?'adm-b-completed':'adm-b-cancelled'">{{ a.account_status }}</span></td>
                    <td>
                      <button class="btn btn-sm btn-outline-warning me-1" @click="copyAgentCode(a)" title="Copy referral code"><i class="mdi mdi-content-copy"></i></button>
                      <button class="btn btn-sm btn-outline-info me-1" @click="resendAgentWelcome(a)" title="Resend welcome SMS+Email (rotates password)"><i class="mdi mdi-email-send"></i></button>
                      <button class="btn btn-sm btn-outline-success me-1" @click="viewAgentReferrals(a)" title="View their referrals"><i class="mdi mdi-eye"></i></button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="!filteredAgents.length && !agentsLoading" class="adm-empty">No agents yet — click "Register New Agent" to add one.</div>
              <div v-if="agentsLoading" class="adm-empty"><span class="spinner-border spinner-border-sm me-2"></span>Loading agents…</div>
            </div>
          </div>
        </div>
      </div>

      <!-- CREATE AGENT MODAL -->
      <div v-if="showCreateAgent" class="adm-overlay" @click.self="showCreateAgent=false">
        <div class="adm-modal">
          <div class="adm-modal-head d-flex justify-content-between align-items-center">
            <h6 class="m-0"><i class="mdi mdi-account-plus me-1" style="color:#BFA164;"></i>Register Field Agent</h6>
            <button class="btn-close-x" @click="showCreateAgent=false">&times;</button>
          </div>
          <div class="adm-modal-body">
            <div style="background:rgba(191,161,100,.08);border:1px solid rgba(191,161,100,.3);border-radius:8px;padding:10px 14px;font-size:12px;color:#BFA164;margin-bottom:14px;">
              <i class="mdi mdi-information-outline me-1"></i>The agent will receive a welcome SMS + email with their referral code and a temporary password. They share the code with farmers; signups using their code are credited to them.
            </div>
            <div class="row g-2 mb-2">
              <div class="col-md-6"><label class="adm-lbl">First name *</label><input v-model="newAgent.firstName" class="adm-input w-100" placeholder="Jane" /></div>
              <div class="col-md-6"><label class="adm-lbl">Last name *</label><input v-model="newAgent.lastName" class="adm-input w-100" placeholder="Wanjiku" /></div>
            </div>
            <div class="row g-2 mb-2">
              <div class="col-md-6"><label class="adm-lbl">Phone (incl. country code) *</label><input v-model="newAgent.phone" class="adm-input w-100" placeholder="2547XXXXXXXX" /></div>
              <div class="col-md-6"><label class="adm-lbl">Email (recommended)</label><input v-model="newAgent.email" type="email" class="adm-input w-100" placeholder="jane@example.com" /></div>
            </div>
            <div class="row g-2 mb-2">
              <div class="col-md-6"><label class="adm-lbl">Region / county (optional)</label><input v-model="newAgent.region" class="adm-input w-100" placeholder="Meru, Eastern" /></div>
            </div>
          </div>
          <div class="adm-modal-foot d-flex gap-2">
            <button class="btn btn-sm btn-outline-secondary flex-fill" @click="showCreateAgent=false" :disabled="creatingAgent">Cancel</button>
            <button class="btn btn-sm btn-warning flex-fill" @click="submitCreateAgent" :disabled="creatingAgent || !newAgent.firstName || !newAgent.lastName || !newAgent.phone">
              <span v-if="creatingAgent" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="mdi mdi-account-plus me-1"></i>
              {{ creatingAgent ? 'Creating…' : 'Create &amp; send credentials' }}
            </button>
          </div>
        </div>
      </div>

      <!-- AGENT-CREATED CONFIRMATION MODAL — surfaces the code/password if the user wants to also copy them manually -->
      <div v-if="agentCreated" class="adm-overlay" @click.self="agentCreated=null">
        <div class="adm-modal">
          <div class="adm-modal-head d-flex justify-content-between align-items-center">
            <h6 class="m-0"><i class="mdi mdi-check-circle me-1" style="color:#50C878;"></i>Agent created</h6>
            <button class="btn-close-x" @click="agentCreated=null">&times;</button>
          </div>
          <div class="adm-modal-body">
            <p style="font-size:13px;color:var(--fm-m,#A09888);">Welcome SMS sent: <strong :style="{color: agentCreated.smsSent ? '#50C878' : '#FF6B6B'}">{{ agentCreated.smsSent ? '✓ delivered' : '✗ failed' }}</strong> · Email sent: <strong :style="{color: agentCreated.emailSent ? '#50C878' : '#A09888'}">{{ agentCreated.emailSent ? '✓ delivered' : (agentCreated.agent.email ? '✗ failed' : '— no email') }}</strong></p>
            <div style="background:rgba(191,161,100,.08);border:2px solid #BFA164;border-radius:10px;padding:14px;text-align:center;margin:12px 0;">
              <div style="font-size:11px;color:var(--fm-m,#A09888);text-transform:uppercase;letter-spacing:.5px;font-weight:600;">{{ agentCreated.agent.display_name }}'s referral code</div>
              <div style="font-size:30px;font-weight:800;color:#BFA164;letter-spacing:6px;font-family:'Courier New',monospace;margin:6px 0;">{{ agentCreated.agent.referral_code }}</div>
              <button class="btn btn-sm btn-outline-warning" @click="copyToClipboard(agentCreated.agent.referral_code)"><i class="mdi mdi-content-copy me-1"></i>Copy code</button>
              <a v-if="agentCreated.waLink" :href="agentCreated.waLink" target="_blank" class="btn btn-sm btn-success ms-1"><i class="mdi mdi-whatsapp me-1"></i>WhatsApp share</a>
            </div>
            <div style="font-size:12px;color:var(--fm-m,#A09888);"><i class="mdi mdi-information-outline me-1"></i>The agent's temporary password was sent only in the SMS/email. If they lose it, click "Resend welcome" — that rotates the password and re-sends both.</div>
          </div>
          <div class="adm-modal-foot d-flex gap-2">
            <button class="btn btn-sm btn-outline-secondary flex-fill" @click="agentCreated=null">Close</button>
          </div>
        </div>
      </div>

      <!-- ===== FINANCE ===== -->
      <div v-if="tab==='finance'">
        <!-- TOP STATS -->
        <div class="adm-stats-grid" style="grid-template-columns:repeat(auto-fit,minmax(160px,1fr));">
          <div class="adm-stat"><div class="adm-stat-icon" style="background:rgba(80,200,120,0.1);"><i class="mdi mdi-cash" style="color:#50C878;"></i></div><div><div class="adm-stat-val">KES {{ fN(flockrPayments.today?flockrPayments.today.amount:0) }}</div><div class="adm-stat-lbl">Today ({{ flockrPayments.today?flockrPayments.today.count:0 }})</div></div></div>
          <div class="adm-stat"><div class="adm-stat-icon" style="background:rgba(79,195,247,0.1);"><i class="mdi mdi-calendar" style="color:#4FC3F7;"></i></div><div><div class="adm-stat-val">KES {{ fN(flockrPayments.thisMonth?flockrPayments.thisMonth.amount:0) }}</div><div class="adm-stat-lbl">This Month ({{ flockrPayments.thisMonth?flockrPayments.thisMonth.count:0 }})</div></div></div>
          <div class="adm-stat"><div class="adm-stat-icon" style="background:rgba(191,161,100,0.1);"><i class="mdi mdi-chart-line" style="color:#BFA164;"></i></div><div><div class="adm-stat-val">KES {{ fN(flockrPayments.allTime?flockrPayments.allTime.amount:0) }}</div><div class="adm-stat-lbl">All Time ({{ flockrPayments.allTime?flockrPayments.allTime.count:0 }})</div></div></div>
          <div class="adm-stat"><div class="adm-stat-icon" style="background:rgba(206,147,216,0.1);"><i class="mdi mdi-message-text" style="color:#CE93D8;"></i></div><div><div class="adm-stat-val">KES {{ fN(flockrPayments.smsTopupRevenue||0) }}</div><div class="adm-stat-lbl">SMS Top-ups</div></div></div>
          <div class="adm-stat"><div class="adm-stat-icon" style="background:rgba(255,171,0,0.1);"><i class="mdi mdi-lock" style="color:#FFAB00;"></i></div><div><div class="adm-stat-val">KES {{ fN(revenue.inEscrow) }}</div><div class="adm-stat-lbl">In Escrow</div></div></div>
          <div class="adm-stat"><div class="adm-stat-icon" style="background:rgba(255,107,107,0.1);"><i class="mdi mdi-cash-refund" style="color:#FF6B6B;"></i></div><div><div class="adm-stat-val">KES {{ fN(revenue.totalWithdrawn) }}</div><div class="adm-stat-lbl">Total Withdrawn</div></div></div>
        </div>

        <!-- FINANCE SUB-TABS -->
        <div class="d-flex gap-2 mt-3 mb-3 flex-wrap">
          <button class="adm-tab" :class="{active:finSubTab==='payments'}" @click="finSubTab='payments'" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-cash-multiple me-1"></i>All Payments</button>
          <button class="adm-tab" :class="{active:finSubTab==='withdrawals'}" @click="finSubTab='withdrawals'" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-bank-transfer me-1"></i>Withdrawals <span v-if="pendingWithdrawals.length" style="background:#FF6B6B;color:#fff;border-radius:8px;padding:1px 6px;font-size:10px;margin-left:4px;">{{ pendingWithdrawals.length }}</span></button>
          <button class="adm-tab" :class="{active:finSubTab==='escrow'}" @click="finSubTab='escrow'" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-shield-lock me-1"></i>Escrow</button>
        </div>

        <!-- ===== ALL PAYMENTS SUB-TAB ===== -->
        <div v-if="finSubTab==='payments'">
          <div class="adm-card">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="adm-card-title" style="margin:0;"><i class="mdi mdi-cash-multiple me-2" style="color:#50C878;"></i>All Payments</h6>
              <button class="btn btn-sm btn-outline-warning" @click="loadFlockrPayments"><i class="mdi mdi-refresh me-1"></i>Refresh</button>
            </div>

            <!-- Filters -->
            <div class="d-flex gap-2 flex-wrap mb-3">
              <select v-model="payFilterType" class="form-select form-select-sm" style="width:auto;background:var(--fm-bg1,#141414);border-color:#2A2520;color:var(--fm-m,#A09888);font-size:11px;">
                <option value="">All Types</option>
                <option value="marketplace_order">Marketplace</option>
                <option value="farm_c2b">Farm C2B</option>
                <option value="farm_stk">Farm STK</option>
                <option value="sms_topup">SMS Top-up</option>
                <option value="pos_sale">POS Sale</option>
                <option value="withdrawal">Withdrawal</option>
                <option value="admin_test">Admin Test</option>
                <option value="manual">Manual</option>
                <option value="other">Other</option>
              </select>
              <select v-model="payFilterMethod" class="form-select form-select-sm" style="width:auto;background:var(--fm-bg1,#141414);border-color:#2A2520;color:var(--fm-m,#A09888);font-size:11px;">
                <option value="">All Methods</option>
                <option value="mpesa_stk">M-Pesa STK</option>
                <option value="mpesa_c2b">M-Pesa C2B</option>
                <option value="mpesa_b2c">M-Pesa B2C</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
              <select v-model="payFilterStatus" class="form-select form-select-sm" style="width:auto;background:var(--fm-bg1,#141414);border-color:#2A2520;color:var(--fm-m,#A09888);font-size:11px;">
                <option value="">All Status</option>
                <option value="completed">Completed</option>
                <option value="stk_sent">Pending</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <span style="color:var(--fm-s,#6B6356);font-size:11px;align-self:center;">{{ filteredPayments.length }} payments</span>
              <button class="adm-tab" style="font-size:11px;padding:5px 10px;" title="Export the rows currently shown (filters applied)" @click="exportCsv('flockr_payments', [{key:'created_at',label:'Date'},{key:'mpesa_receipt_number',label:'M-Pesa Receipt'},{key:'payment_type',label:'Type'},{key:'payment_method',label:'Method'},{key:'amount',label:'Amount (KES)'},{key:'payer_name',label:'Payer'},{key:'payer_phone',label:'Phone'},{key:'account_ref',label:'Account Ref'},{key:'status',label:'Status'}], filteredPayments)"><i class="mdi mdi-download me-1"></i>Export CSV</button>
            </div>

            <!-- By Type & Method breakdown -->
            <div class="row g-3 mb-3">
              <div class="col-md-4" v-if="flockrPayments.byType&&flockrPayments.byType.length">
                <div style="font-size:11px;color:var(--fm-s,#6B6356);margin-bottom:4px;font-weight:600;">By Type</div>
                <div v-for="t in flockrPayments.byType" :key="t.payment_type" class="d-flex justify-content-between" style="padding:2px 0;font-size:11px;border-bottom:1px solid var(--fm-br);">
                  <span style="color:var(--fm-m,#A09888);text-transform:capitalize;">{{ (t.payment_type||'other').replace(/_/g,' ') }} <span style="color:var(--fm-s,#6B6356);">({{ t.count }})</span></span>
                  <span style="color:var(--fm-h,#F5F0E8);">KES {{ fN(t.amount) }}</span>
                </div>
              </div>
              <div class="col-md-4" v-if="flockrPayments.byMethod&&flockrPayments.byMethod.length">
                <div style="font-size:11px;color:var(--fm-s,#6B6356);margin-bottom:4px;font-weight:600;">By Method</div>
                <div v-for="m in flockrPayments.byMethod" :key="m.payment_method" class="d-flex justify-content-between" style="padding:2px 0;font-size:11px;border-bottom:1px solid var(--fm-br);">
                  <span style="color:var(--fm-m,#A09888);text-transform:capitalize;">{{ (m.payment_method||'').replace(/_/g,' ') }} <span style="color:var(--fm-s,#6B6356);">({{ m.count }})</span></span>
                  <span style="color:var(--fm-h,#F5F0E8);">KES {{ fN(m.amount) }}</span>
                </div>
              </div>
              <div class="col-md-4" v-if="flockrPayments.byShortcode&&flockrPayments.byShortcode.length">
                <div style="font-size:11px;color:var(--fm-s,#6B6356);margin-bottom:4px;font-weight:600;">By Paybill</div>
                <div v-for="s in flockrPayments.byShortcode" :key="s.receiver_shortcode" class="d-flex justify-content-between" style="padding:2px 0;font-size:11px;border-bottom:1px solid var(--fm-br);">
                  <span><strong style="color:#4FC3F7;">{{ s.receiver_shortcode }}</strong> <span style="color:var(--fm-s,#6B6356);">{{ s.receiver_name }} ({{ s.count }})</span></span>
                  <span style="color:var(--fm-h,#F5F0E8);">KES {{ fN(s.amount) }}</span>
                </div>
              </div>
            </div>

            <!-- Payments table — for marketplace_order rows we add a "Split" column
                 that shows where the buyer's money goes: → seller / → Flockr / → insurance.
                 Lets ops trace any M-Pesa receipt to its components without
                 chasing through the orders table. -->
            <div class="adm-table-wrap">
              <table class="adm-table"><thead><tr><th>Date</th><th>Payer</th><th>Amount</th><th>Split (Seller / Flockr / Insurance)</th><th>Method</th><th>Type</th><th>Status</th><th>Paybill</th><th>Account Ref</th><th>Farm</th><th>Receipt</th></tr></thead>
                <tbody>
                <tr v-for="p in filteredPayments" :key="p.id" @click="selectedPayment=p;paymentModal=true" style="cursor:pointer;">
                  <td class="adm-muted" style="white-space:nowrap;">{{ fDT(p.completed_at||p.created_at) }}</td>
                  <td><span style="color:var(--fm-h,#F5F0E8);">{{ p.payer_name||'—' }}</span><br/><span class="adm-mono" style="font-size:10px;">{{ formatPhone(p.payer_phone) }}</span></td>
                  <td><strong :style="{color:p.payment_direction==='outbound'?'#FF6B6B':'#50C878'}">{{ p.payment_direction==='outbound'?'-':'' }}KES {{ fN(p.amount) }}</strong></td>
                  <!-- Split breakdown: only meaningful for marketplace_order rows where
                       orders exist. Other payment_types just show "—" since the buyer's
                       money goes to a single destination. -->
                  <td style="font-size:11px;">
                    <template v-if="p.payment_type === 'marketplace_order' && Number(p.order_count) > 0">
                      <span style="color:#50C878;" title="Seller payout">→ KES {{ fN(p.seller_subtotal) }}</span>
                      <span style="color:var(--fm-s,#6B6356);"> / </span>
                      <span style="color:#BFA164;" title="Flockr commission (3%)">KES {{ fN(p.platform_fee_total) }}</span>
                      <span style="color:var(--fm-s,#6B6356);"> / </span>
                      <span style="color:#4FC3F7;" title="Insurance premium">KES {{ fN(p.insurance_fee_total) }}</span>
                      <div v-if="Number(p.order_count) > 1" style="font-size:9px;color:var(--fm-s,#6B6356);margin-top:2px;">across {{ p.order_count }} orders</div>
                    </template>
                    <span v-else style="color:var(--fm-s,#3A3530);">—</span>
                  </td>
                  <td style="text-transform:capitalize;font-size:11px;">{{ (p.payment_method||'').replace(/_/g,' ') }}</td>
                  <td><span style="font-size:10px;text-transform:capitalize;padding:2px 6px;border-radius:4px;" :style="payTypeBadge(p.payment_type)">{{ (p.payment_type||'other').replace(/_/g,' ') }}</span></td>
                  <td><span class="adm-badge" :class="'adm-b-'+p.status">{{ p.status }}</span></td>
                  <td class="adm-mono" style="font-size:10px;">{{ p.receiver_shortcode||'—' }}</td>
                  <td style="font-size:10px;color:var(--fm-m,#A09888);max-width:100px;overflow:hidden;text-overflow:ellipsis;">{{ p.account_ref||'—' }}</td>
                  <td style="font-size:11px;">{{ p.farm_name||'—' }}</td>
                  <td class="adm-mono" style="color:#4FC3F7;font-size:10px;">{{ p.mpesa_receipt_number||p.payment_ref||'—' }}</td>
                </tr>
                </tbody></table>
              <div v-if="!filteredPayments.length" class="adm-empty">No payments match filters</div>
            </div>
          </div>
        </div>

        <!-- ===== WITHDRAWALS SUB-TAB ===== -->
        <div v-if="finSubTab==='withdrawals'">
          <div class="adm-card">
            <h6 class="adm-card-title"><i class="mdi mdi-bank-transfer me-2"></i>Withdrawal Requests</h6>
            <div class="adm-table-wrap">
              <table class="adm-table"><thead><tr><th>User</th><th>Phone</th><th>Amount</th><th>Method</th><th>M-Pesa #</th><th>Receipt</th><th>Status</th><th>Requested</th><th>Actions</th></tr></thead>
                <tbody>
                <tr v-for="w in allWithdrawals" :key="w.id">
                  <td>{{ w.display_name }}</td>
                  <td class="adm-mono">{{ w.phone }}</td>
                  <td><strong style="color:#BFA164;">KES {{ fN(w.amount) }}</strong></td>
                  <td>{{ w.withdrawal_method }}</td>
                  <td class="adm-mono">{{ w.mpesa_phone }}</td>
                  <td class="adm-mono" style="color:#4FC3F7;font-size:10px;">{{ w.mpesa_receipt || '—' }}</td>
                  <td><span class="adm-badge" :class="'adm-b-'+w.status">{{ w.status }}</span></td>
                  <td class="adm-muted">{{ fD(w.created_at) }}</td>
                  <td>
                  <span v-if="w.status==='pending'">
                    <button class="btn btn-sm btn-success me-1" @click="processWithdrawal(w.id,'approved',true)" title="Approve — admin will send M-Pesa by hand"><i class="mdi mdi-check"></i> Approve (manual)</button>
                    <button class="btn btn-sm btn-outline-danger" @click="rejectWithdrawal(w)"><i class="mdi mdi-close"></i></button>
                  </span>
                  <span v-else-if="w.status==='approved'">
                    <button class="btn btn-sm btn-warning text-dark fw-bold" @click="openDisburse(w)" title="Record the M-Pesa receipt after sending the money manually">
                      <i class="mdi mdi-cash-check"></i> Mark Disbursed
                    </button>
                  </span>
                    <span v-else class="adm-muted">{{ w.status }}</span>
                  </td>
                </tr>
                </tbody></table>
              <div v-if="!allWithdrawals.length" class="adm-empty">No withdrawal requests</div>
            </div>
          </div>
        </div>

        <!-- ===== MARK DISBURSED MODAL — manual M-Pesa reconciliation ===== -->
        <div v-if="disburseModal" class="adm-overlay" @click.self="!disbursing && (disburseModal=false)">
          <div class="adm-modal" style="max-width:480px;">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 style="color:var(--fm-h,#F5F0E8);font-weight:700;margin:0;"><i class="mdi mdi-cash-check me-2" style="color:#BFA164;"></i>Mark Withdrawal Disbursed</h6>
              <button class="btn btn-sm btn-outline-secondary" @click="disburseModal=false" :disabled="disbursing"><i class="mdi mdi-close"></i></button>
            </div>
            <div v-if="disburseWd" style="font-size:12px;">
              <div style="background:rgba(191,161,100,0.08);border:1px solid rgba(191,161,100,0.25);border-radius:8px;padding:10px;margin-bottom:14px;">
                <div class="d-flex justify-content-between" style="padding:2px 0;">
                  <span style="color:var(--fm-s,#6B6356);">Recipient</span>
                  <strong style="color:var(--fm-h,#F5F0E8);">{{ disburseWd.display_name }}</strong>
                </div>
                <div class="d-flex justify-content-between" style="padding:2px 0;">
                  <span style="color:var(--fm-s,#6B6356);">M-Pesa phone</span>
                  <strong class="adm-mono" style="color:#4FC3F7;">{{ disburseWd.mpesa_phone }}</strong>
                </div>
                <div class="d-flex justify-content-between" style="padding:2px 0;">
                  <span style="color:var(--fm-s,#6B6356);">Requested amount</span>
                  <strong style="color:#50C878;">KES {{ fN(disburseWd.amount) }}</strong>
                </div>
              </div>

              <label style="color:var(--fm-s,#6B6356);font-size:11px;text-transform:uppercase;letter-spacing:0.5px;display:block;margin-bottom:4px;">M-Pesa transaction code *</label>
              <input v-model="disburseForm.mpesaReceipt" type="text" class="adm-input" placeholder="e.g. UFB9V73AJ3" style="text-transform:uppercase;" />

              <label style="color:var(--fm-s,#6B6356);font-size:11px;text-transform:uppercase;letter-spacing:0.5px;display:block;margin-top:10px;margin-bottom:4px;">Amount actually sent (KES) *</label>
              <input v-model.number="disburseForm.amountSent" type="number" class="adm-input" :placeholder="String(disburseWd.amount)" />
              <div v-if="disburseForm.amountSent && Number(disburseForm.amountSent) !== Number(disburseWd.amount)" style="font-size:10px;color:#FF9800;margin-top:2px;">
                <i class="mdi mdi-alert-circle me-1"></i>Variance from request: KES {{ fN(Number(disburseForm.amountSent) - Number(disburseWd.amount)) }}
              </div>

              <label style="color:var(--fm-s,#6B6356);font-size:11px;text-transform:uppercase;letter-spacing:0.5px;display:block;margin-top:10px;margin-bottom:4px;">Notes (optional)</label>
              <input v-model="disburseForm.notes" type="text" class="adm-input" placeholder="Any extra context for the recipient" />

              <div v-if="disburseError" style="color:#FF6B6B;font-size:12px;margin-top:10px;"><i class="mdi mdi-alert-circle me-1"></i>{{ disburseError }}</div>

              <div class="d-flex gap-2 mt-3">
                <button class="btn btn-outline-secondary flex-fill" @click="disburseModal=false" :disabled="disbursing">Cancel</button>
                <button class="btn btn-warning text-dark fw-bold flex-fill" @click="submitDisburse" :disabled="disbursing || !disburseForm.mpesaReceipt || !(Number(disburseForm.amountSent) > 0)">
                  <span v-if="disbursing"><span class="spinner-border spinner-border-sm me-1"></span>Recording...</span>
                  <span v-else><i class="mdi mdi-check me-1"></i>Record Disbursement</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== ESCROW SUB-TAB ===== -->
        <div v-if="finSubTab==='escrow'">
          <div class="adm-card">
            <h6 class="adm-card-title"><i class="mdi mdi-shield-lock me-2"></i>Escrow Transactions</h6>
            <div class="adm-table-wrap">
              <table class="adm-table"><thead><tr><th>Order</th><th>Buyer</th><th>Seller</th><th>Amount</th><th>Status</th><th>Created</th><th>Released</th></tr></thead>
                <tbody>
                <tr v-for="e in escrowTxns" :key="e.id">
                  <td class="adm-mono">{{ e.order_number }}</td>
                  <td>{{ e.buyer_name }}</td>
                  <td>{{ e.seller_name }}</td>
                  <td>KES {{ fN(e.amount) }}</td>
                  <td><span class="adm-badge" :class="'adm-b-'+e.status">{{ e.status }}</span></td>
                  <td class="adm-muted">{{ fD(e.created_at) }}</td>
                  <td class="adm-muted">{{ e.released_at ? fD(e.released_at) : '-' }}</td>
                </tr>
                </tbody></table>
              <div v-if="!escrowTxns.length" class="adm-empty">No escrow transactions</div>
            </div>
          </div>
        </div>

        <!-- PAYMENT DETAIL MODAL -->
        <div v-if="paymentModal" class="adm-overlay" @click.self="paymentModal=false">
          <div class="adm-modal" style="max-width:600px;">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 style="margin:0;color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-receipt me-2"></i>Payment Detail</h6>
              <button class="btn btn-sm btn-outline-secondary" @click="paymentModal=false"><i class="mdi mdi-close"></i></button>
            </div>
            <div v-if="selectedPayment" style="font-size:12px;">
              <div class="row g-2">
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Receipt</div><div class="adm-mono" style="color:#4FC3F7;">{{ selectedPayment.mpesa_receipt_number||'—' }}</div></div>
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Payment Ref</div><div class="adm-mono">{{ selectedPayment.payment_ref||'—' }}</div></div>
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Amount</div><div><strong :style="{color:selectedPayment.payment_direction==='outbound'?'#FF6B6B':'#50C878'}">KES {{ fN(selectedPayment.amount) }}</strong></div></div>
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Status</div><div><span class="adm-badge" :class="'adm-b-'+selectedPayment.status">{{ selectedPayment.status }}</span></div></div>
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Method</div><div style="text-transform:capitalize;">{{ (selectedPayment.payment_method||'').replace(/_/g,' ') }}</div></div>
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Type</div><div style="text-transform:capitalize;">{{ (selectedPayment.payment_type||'').replace(/_/g,' ') }}</div></div>
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Payer</div><div>{{ selectedPayment.payer_name||'—' }}</div></div>
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Phone</div><div class="adm-mono">{{ formatPhone(selectedPayment.payer_phone) }}</div></div>
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Paybill</div><div>{{ selectedPayment.receiver_shortcode||'—' }} <span style="color:var(--fm-s,#6B6356);">{{ selectedPayment.receiver_name }}</span></div></div>
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Account Ref</div><div>{{ selectedPayment.account_ref||'—' }}</div></div>
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Farm</div><div>{{ selectedPayment.farm_name||'—' }}</div></div>
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Source</div><div style="text-transform:capitalize;">{{ (selectedPayment.source||'').replace(/_/g,' ') }}</div></div>
                <div class="col-12"><div style="color:var(--fm-s,#6B6356);">Description</div><div>{{ selectedPayment.description||'—' }}</div></div>
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Completed</div><div class="adm-muted">{{ selectedPayment.completed_at ? fDT(selectedPayment.completed_at) : '—' }}</div></div>
                <div class="col-6"><div style="color:var(--fm-s,#6B6356);">Created</div><div class="adm-muted">{{ fDT(selectedPayment.created_at) }}</div></div>
                <div v-if="selectedPayment.failure_reason" class="col-12"><div style="color:#FF6B6B;">Failure</div><div style="color:#FF6B6B;">{{ selectedPayment.failure_reason }}</div></div>

                <!-- Where the money went — only for marketplace_order rows. Mirrors
                     the breakdown ops use when explaining a transaction:
                     "buyer paid X → seller Y → Flockr Z → insurance W". -->
                <div v-if="selectedPayment.payment_type === 'marketplace_order' && Number(selectedPayment.order_count) > 0" class="col-12" style="margin-top:6px;">
                  <div style="color:var(--fm-s,#6B6356);font-weight:600;margin-bottom:6px;">Where this money went</div>
                  <div style="background:rgba(80,200,120,0.06);border:1px solid rgba(80,200,120,0.2);border-radius:8px;padding:10px;">
                    <div class="d-flex justify-content-between" style="padding:3px 0;border-bottom:1px solid rgba(255,255,255,0.06);">
                      <span style="color:var(--fm-m,#A09888);">Buyer paid (gross)</span>
                      <strong style="color:#50C878;">KES {{ fN(selectedPayment.amount) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between" style="padding:3px 0;border-bottom:1px solid rgba(255,255,255,0.06);">
                      <span style="color:var(--fm-m,#A09888);">→ Seller payout</span>
                      <strong style="color:#50C878;">KES {{ fN(selectedPayment.seller_subtotal) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between" style="padding:3px 0;border-bottom:1px solid rgba(255,255,255,0.06);">
                      <span style="color:var(--fm-m,#A09888);">→ Flockr commission (3%)</span>
                      <strong style="color:#BFA164;">KES {{ fN(selectedPayment.platform_fee_total) }}</strong>
                    </div>
                    <div v-if="Number(selectedPayment.insurance_fee_total) > 0" class="d-flex justify-content-between" style="padding:3px 0;">
                      <span style="color:var(--fm-m,#A09888);">→ Insurance premium</span>
                      <strong style="color:#4FC3F7;">KES {{ fN(selectedPayment.insurance_fee_total) }}</strong>
                    </div>
                    <div v-if="Number(selectedPayment.order_count) > 1" style="margin-top:6px;font-size:10px;color:var(--fm-s,#6B6356);">
                      <i class="mdi mdi-information-outline me-1"></i>Spread across {{ selectedPayment.order_count }} orders in this cart.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== DISPUTES ===== -->
      <div v-if="tab==='disputes'">
        <!-- DISPUTE LIST -->
        <div v-if="!disputeDetail" class="adm-card">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-gavel me-2"></i>Disputes ({{ disputes.length }})</h6>
            <div class="d-flex gap-2">
              <select v-model="disputeFilter" class="adm-select"><option value="">All</option><option value="open">Open</option><option value="under_review">Under Review</option><option value="resolved">Resolved</option><option value="rejected">Rejected</option></select>
            </div>
          </div>
          <div class="adm-table-wrap">
            <table class="adm-table"><thead><tr><th>Dispute #</th><th>Order</th><th>Raised By</th><th>Against</th><th>Type</th><th>Title</th><th>Desired</th><th>Priority</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
              <tr v-for="d in filteredDisputes" :key="d.id" @click="openDisputeDetail(d)" style="cursor:pointer;">
                <td class="adm-mono">{{ d.dispute_number }}</td>
                <td class="adm-mono">{{ d.order_number }}</td>
                <td>{{ d.raiser_name }}</td>
                <td>{{ d.against_name }}</td>
                <td>{{ (d.dispute_type||'').replace(/_/g,' ') }}</td>
                <td style="max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ d.title }}</td>
                <td>{{ (d.desired_resolution||'').replace(/_/g,' ') }}</td>
                <td><span class="adm-badge" :class="'adm-b-priority-'+(d.priority||'medium')">{{ d.priority }}</span></td>
                <td><span class="adm-badge" :class="'adm-b-'+d.status">{{ d.status }}</span></td>
                <td class="adm-muted">{{ fD(d.created_at) }}</td>
              </tr>
              </tbody></table>
            <div v-if="!filteredDisputes.length" class="adm-empty">No disputes match</div>
          </div>
        </div>

        <!-- DISPUTE DETAIL VIEW -->
        <div v-if="disputeDetail">
          <button class="btn btn-sm btn-outline-secondary mb-3" @click="disputeDetail=null"><i class="mdi mdi-arrow-left me-1"></i>Back to Disputes</button>
          <div class="row g-4">
            <!-- LEFT: Info + Evidence -->
            <div class="col-lg-7">
              <!-- Header -->
              <div class="adm-card mb-4">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h6 style="color:var(--fm-h,#F5F0E8);font-weight:700;margin:0;">{{ disputeDetail.title }}</h6>
                    <div class="adm-muted mt-1">{{ disputeDetail.dispute_number }} · {{ fD(disputeDetail.created_at) }}</div>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <span class="adm-badge" :class="'adm-b-priority-'+(disputeDetail.priority||'medium')" style="font-size:11px;">{{ disputeDetail.priority }}</span>
                    <span class="adm-badge" :class="'adm-b-'+disputeDetail.status" style="font-size:11px;">{{ disputeDetail.status }}</span>
                  </div>
                </div>
                <div class="adm-disp-grid">
                  <div class="adm-disp-field"><span class="adm-lbl">Order</span><a href="javascript:void(0)" @click="viewOrder(disputeDetail.order_id)" style="color:#BFA164;">{{ disputeDetail.order_number }}</a> — KES {{ fN(disputeDetail.total_amount) }}</div>
                  <div class="adm-disp-field"><span class="adm-lbl">Type</span><span>{{ (disputeDetail.dispute_type||'').replace(/_/g,' ') }}</span></div>
                  <div class="adm-disp-field"><span class="adm-lbl">Raised By</span><span style="color:#FF6B6B;">{{ disputeDetail.raised_by_name }}</span></div>
                  <div class="adm-disp-field"><span class="adm-lbl">Against</span><span>{{ disputeDetail.against_name }}</span></div>
                  <div class="adm-disp-field"><span class="adm-lbl">Desired Resolution</span><span>{{ (disputeDetail.desired_resolution||'').replace(/_/g,' ') }}</span></div>
                  <div v-if="disputeDetail.desired_amount" class="adm-disp-field"><span class="adm-lbl">Desired Amount</span><span>KES {{ fN(disputeDetail.desired_amount) }}</span></div>
                </div>
              </div>

              <!-- Description -->
              <div class="adm-card mb-4">
                <h6 class="adm-card-title"><i class="mdi mdi-text me-2"></i>Complaint Description</h6>
                <div style="color:var(--fm-m,#A09888);font-size:13px;line-height:1.6;white-space:pre-wrap;">{{ disputeDetail.description }}</div>
              </div>

              <!-- Evidence -->
              <div class="adm-card mb-4">
                <h6 class="adm-card-title"><i class="mdi mdi-file-document me-2"></i>Evidence ({{ (disputeDetail.evidence||[]).length }})</h6>
                <div v-if="disputeDetail.evidence && disputeDetail.evidence.length" class="adm-evidence-list">
                  <div v-for="ev in disputeDetail.evidence" :key="ev.id" class="adm-evidence-item">
                    <div class="adm-evidence-header">
                      <span style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:12px;">{{ ev.submitted_by_name }}</span>
                      <span class="adm-muted">{{ fDT(ev.created_at) }}</span>
                    </div>
                    <div v-if="ev.content" style="color:var(--fm-m,#A09888);font-size:12px;margin:6px 0;">{{ ev.content }}</div>
                    <!-- Photo evidence -->
                    <div v-if="ev.file_url && isImage(ev.file_url)" class="adm-evidence-photo" @click="previewImage(ev.file_url)">
                      <img :src="photoUrl(ev.file_url)" alt="Evidence" />
                      <div class="adm-evidence-zoom"><i class="mdi mdi-magnify-plus"></i></div>
                    </div>
                    <!-- Document evidence -->
                    <a v-else-if="ev.file_url" :href="photoUrl(ev.file_url)" target="_blank" class="adm-evidence-doc">
                      <i class="mdi mdi-file-document" style="font-size:20px;color:#BFA164;"></i>
                      <span>{{ ev.evidence_type }} — {{ getFileName(ev.file_url) }}</span>
                      <i class="mdi mdi-download" style="color:var(--fm-s,#6B6356);"></i>
                    </a>
                    <span class="adm-badge adm-b-info" style="font-size:9px;margin-top:4px;">{{ ev.evidence_type }}</span>
                  </div>
                </div>
                <div v-else class="adm-empty">No evidence uploaded</div>
              </div>

              <!-- Admin Notes / Communication -->
              <div class="adm-card">
                <h6 class="adm-card-title"><i class="mdi mdi-message-reply-text me-2"></i>Admin Notes & Communication</h6>
                <div v-if="disputeDetail.adminNotes && disputeDetail.adminNotes.length" class="adm-notes-list">
                  <div v-for="n in disputeDetail.adminNotes" :key="n.id" class="adm-note-item">
                    <div class="adm-note-header">
                      <span style="color:#BFA164;font-weight:600;font-size:12px;"><i class="mdi mdi-shield-account me-1"></i>{{ n.submitted_by_name || 'Admin' }}</span>
                      <span class="adm-muted">{{ fDT(n.created_at) }}</span>
                    </div>
                    <div style="color:var(--fm-m,#A09888);font-size:12px;margin-top:4px;">{{ n.content }}</div>
                  </div>
                </div>
                <div v-else style="color:var(--fm-s,#3A3530);font-size:12px;margin-bottom:12px;">No admin notes yet</div>
                <!-- Add note -->
                <div class="mt-3" style="border-top:1px solid #1E1E1E;padding-top:12px;">
                  <textarea v-model="adminNoteText" class="adm-input w-100" rows="2" placeholder="Add a note or message to the parties..."></textarea>
                  <div class="d-flex gap-2 mt-2">
                    <button class="btn btn-sm btn-outline-warning flex-fill" @click="addAdminNote(false)" :disabled="!adminNoteText.trim()||addingNote"><i class="mdi mdi-send me-1"></i>Add Internal Note</button>
                    <button class="btn btn-sm btn-warning text-dark flex-fill" @click="addAdminNote(true)" :disabled="!adminNoteText.trim()||addingNote"><i class="mdi mdi-message-text me-1"></i>Send to Parties</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- RIGHT: Actions + Order Summary -->
            <div class="col-lg-5">
              <!-- Order Summary -->
              <div class="adm-card mb-4">
                <h6 class="adm-card-title"><i class="mdi mdi-receipt me-2"></i>Order Summary</h6>
                <div class="adm-rev-rows">
                  <div class="adm-rev-row"><span>Order</span><strong style="color:#BFA164;">{{ disputeDetail.order_number }}</strong></div>
                  <div class="adm-rev-row"><span>Status</span><span class="adm-badge" :class="'adm-b-'+(disputeDetail.order_status||'')">{{ disputeDetail.order_status }}</span></div>
                  <div class="adm-rev-row"><span>Subtotal</span><strong>KES {{ fN(disputeDetail.subtotal) }}</strong></div>
                  <div class="adm-rev-row"><span>Total Paid</span><strong style="color:var(--fm-h,#F5F0E8);">KES {{ fN(disputeDetail.total_amount) }}</strong></div>
                </div>
                <button class="btn btn-sm btn-outline-warning w-100 mt-3" @click="viewOrder(disputeDetail.order_id)"><i class="mdi mdi-eye me-1"></i>View Full Order</button>
              </div>

              <!-- Resolution Actions -->
              <div class="adm-card mb-4" v-if="disputeDetail.status==='open'||disputeDetail.status==='under_review'||disputeDetail.status==='awaiting_evidence'||disputeDetail.status==='mediation'">
                <h6 class="adm-card-title"><i class="mdi mdi-gavel me-2"></i>Resolve Dispute</h6>

                <!-- Change status -->
                <div class="mb-3" v-if="disputeDetail.status==='open'">
                  <button class="btn btn-sm btn-outline-warning w-100 mb-2" @click="updateDisputeStatus('under_review')"><i class="mdi mdi-eye me-1"></i>Mark as Under Review</button>
                </div>

                <div class="mb-3"><label class="adm-lbl">Resolution Type</label><select v-model="resolveType" class="adm-select w-100"><option value="refund">Full Refund to Buyer</option><option value="partial_refund">Partial Refund</option><option value="release_to_seller">Release to Seller</option><option value="rejected">Reject Dispute</option></select></div>
                <div v-if="resolveType==='partial_refund'" class="mb-3"><label class="adm-lbl">Refund Amount (KES)</label><input v-model="resolveAmount" type="number" class="adm-input w-100" :placeholder="'Max: '+fN(disputeDetail.total_amount)" /></div>
                <div class="mb-3"><label class="adm-lbl">Resolution Notes</label><textarea v-model="resolveNotes" class="adm-input w-100" rows="3" placeholder="Explain your decision..."></textarea></div>
                <button class="btn btn-warning text-dark fw-bold w-100" @click="submitResolve" :disabled="resolving"><span v-if="resolving"><span class="spinner-border spinner-border-sm me-1"></span></span><span v-else><i class="mdi mdi-gavel me-1"></i>Resolve Dispute</span></button>
              </div>

              <!-- Already resolved -->
              <div class="adm-card mb-4" v-else>
                <h6 class="adm-card-title"><i class="mdi mdi-check-circle me-2" style="color:#50C878;"></i>Resolution</h6>
                <div class="adm-rev-rows">
                  <div class="adm-rev-row"><span>Status</span><span class="adm-badge" :class="'adm-b-'+disputeDetail.status">{{ disputeDetail.status }}</span></div>
                  <div class="adm-rev-row"><span>Type</span><strong>{{ (disputeDetail.resolution_type||'').replace(/_/g,' ') }}</strong></div>
                  <div v-if="disputeDetail.resolution_amount > 0" class="adm-rev-row"><span>Amount</span><strong style="color:#50C878;">KES {{ fN(disputeDetail.resolution_amount) }}</strong></div>
                  <div v-if="disputeDetail.resolved_at" class="adm-rev-row"><span>Resolved</span><strong>{{ fDT(disputeDetail.resolved_at) }}</strong></div>
                </div>
                <div v-if="disputeDetail.resolution_notes" style="color:var(--fm-m,#A09888);font-size:12px;margin-top:10px;padding:10px;background:var(--fm-bg2,#0F0F0F);border-radius:8px;">{{ disputeDetail.resolution_notes }}</div>
              </div>

              <!-- Parties -->
              <div class="adm-card">
                <h6 class="adm-card-title"><i class="mdi mdi-account-group me-2"></i>Parties</h6>
                <div class="adm-party-card mb-2">
                  <div class="adm-party-label" style="color:#FF6B6B;">Complainant (Raised By)</div>
                  <div style="color:var(--fm-h,#F5F0E8);font-weight:600;">{{ disputeDetail.raised_by_name }}</div>
                </div>
                <div class="adm-party-card">
                  <div class="adm-party-label">Respondent (Against)</div>
                  <div style="color:var(--fm-h,#F5F0E8);font-weight:600;">{{ disputeDetail.against_name }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== VERIFICATIONS / KYC ===== -->
      <div v-if="tab==='verifications'">
        <!-- DETAIL VIEW -->
        <div v-if="verifDetail">
          <button class="btn btn-sm btn-outline-secondary mb-3" @click="verifDetail=null"><i class="mdi mdi-arrow-left me-1"></i>Back to Verifications</button>
          <div class="row g-4">
            <div class="col-lg-7">
              <!-- User Info -->
              <div class="adm-card mb-4">
                <h6 class="adm-card-title"><i class="mdi mdi-account me-2"></i>{{ verifDetail.display_name }}</h6>
                <div class="adm-rev-rows">
                  <div class="adm-rev-row"><span>Phone</span><strong>{{ verifDetail.phone }}</strong></div>
                  <div class="adm-rev-row"><span>Type</span><strong>{{ verifLabel(verifDetail.verification_type) }}</strong></div>
                  <div class="adm-rev-row"><span>Document #</span><strong style="color:#BFA164;">{{ verifDetail.document_number || 'N/A' }}</strong></div>
                  <div class="adm-rev-row"><span>Status</span><span class="adm-badge" :class="'adm-b-'+verifDetail.verification_status">{{ verifDetail.verification_status }}</span></div>
                  <div class="adm-rev-row"><span>Submitted</span><strong>{{ fDT(verifDetail.created_at) }}</strong></div>
                </div>
              </div>
              <!-- Documents -->
              <div class="adm-card mb-4">
                <h6 class="adm-card-title"><i class="mdi mdi-file-image me-2"></i>Documents</h6>
                <div class="adm-kyc-docs">
                  <!-- Front -->
                  <div v-if="verifDetail.document_front_url && isPdf(verifDetail.document_front_url)" class="adm-kyc-doc adm-kyc-pdf" @click="openDoc(verifDetail.document_front_url)">
                    <div class="adm-kyc-pdf-inner"><i class="mdi mdi-file-pdf-box"></i><div class="adm-kyc-pdf-name">{{ getFileName(verifDetail.document_front_url) }}</div></div>
                    <div class="adm-kyc-doc-label">Front — <i class="mdi mdi-open-in-new"></i> View PDF</div>
                  </div>
                  <div v-else-if="verifDetail.document_front_url" class="adm-kyc-doc" @click="previewImage(verifDetail.document_front_url)">
                    <img :src="photoUrl(verifDetail.document_front_url)" />
                    <div class="adm-kyc-doc-label">Front</div>
                  </div>
                  <!-- Back -->
                  <div v-if="verifDetail.document_back_url && isPdf(verifDetail.document_back_url)" class="adm-kyc-doc adm-kyc-pdf" @click="openDoc(verifDetail.document_back_url)">
                    <div class="adm-kyc-pdf-inner"><i class="mdi mdi-file-pdf-box"></i><div class="adm-kyc-pdf-name">{{ getFileName(verifDetail.document_back_url) }}</div></div>
                    <div class="adm-kyc-doc-label">Back — <i class="mdi mdi-open-in-new"></i> View PDF</div>
                  </div>
                  <div v-else-if="verifDetail.document_back_url" class="adm-kyc-doc" @click="previewImage(verifDetail.document_back_url)">
                    <img :src="photoUrl(verifDetail.document_back_url)" />
                    <div class="adm-kyc-doc-label">Back</div>
                  </div>
                  <!-- Selfie -->
                  <div v-if="verifDetail.selfie_url" class="adm-kyc-doc" @click="previewImage(verifDetail.selfie_url)">
                    <img :src="photoUrl(verifDetail.selfie_url)" />
                    <div class="adm-kyc-doc-label">Selfie</div>
                  </div>
                  <div v-if="!verifDetail.document_front_url && !verifDetail.document_back_url && !verifDetail.selfie_url" class="adm-empty">No documents uploaded</div>
                </div>
              </div>
            </div>
            <div class="col-lg-5">
              <!-- AI Review (Claude vision) — shown when AI has scored the submission -->
              <div v-if="verifDetail.ai_score !== null && verifDetail.ai_score !== undefined" class="adm-card mb-4" style="border-left:4px solid #BFA164;">
                <h6 class="adm-card-title"><i class="mdi mdi-brain me-2" style="color:#BFA164;"></i>AI Review
                  <span class="badge ms-2" :class="verifDetail.ai_score >= 85 ? 'bg-success' : (verifDetail.ai_score < 40 ? 'bg-danger' : 'bg-warning text-dark')">
                    Score: {{ verifDetail.ai_score }}/100
                  </span>
                </h6>
                <div v-if="aiReport" class="adm-rev-rows">
                  <div class="adm-rev-row"><span>Name match</span><strong :style="{color: aiBarColor(aiReport.name_match)}">{{ aiReport.name_match }}/100</strong></div>
                  <div class="adm-rev-row"><span>ID number match</span><strong :style="{color: aiBarColor(aiReport.id_number_match)}">{{ aiReport.id_number_match }}/100</strong></div>
                  <div class="adm-rev-row"><span>Face match</span><strong :style="{color: aiBarColor(aiReport.face_match)}">{{ aiReport.face_match }}/100</strong></div>
                  <div class="adm-rev-row"><span>Authenticity</span><strong :style="{color: aiBarColor(aiReport.authenticity)}">{{ aiReport.authenticity }}/100</strong></div>
                  <div v-if="aiReport.detected_name" class="adm-rev-row"><span>Detected name</span><strong style="color:#BFA164;">{{ aiReport.detected_name }}</strong></div>
                  <div v-if="aiReport.detected_id_number" class="adm-rev-row"><span>Detected ID #</span><strong style="color:#BFA164;">{{ aiReport.detected_id_number }}</strong></div>
                </div>
                <div v-if="aiReport && aiReport.reasoning" class="mt-2 p-2" style="background:rgba(191,161,100,0.08);border-radius:6px;font-size:12px;color:var(--fm-m,#A09888);">
                  <strong style="color:#BFA164;">Reasoning:</strong> {{ aiReport.reasoning }}
                </div>
                <div v-if="aiReport && aiReport.red_flags && aiReport.red_flags.length" class="mt-2 p-2" style="background:rgba(255,107,107,0.08);border-radius:6px;font-size:12px;color:#FF6B6B;">
                  <strong>🚩 Red flags:</strong>
                  <ul class="mb-0 mt-1" style="padding-left:18px;">
                    <li v-for="(rf, ri) in aiReport.red_flags" :key="'rf-'+ri">{{ rf }}</li>
                  </ul>
                </div>
                <div v-if="verifDetail.ai_reviewed_at" class="adm-muted mt-2" style="font-size:11px;">AI reviewed {{ fDT(verifDetail.ai_reviewed_at) }}</div>
              </div>

              <!-- Actions -->
              <div class="adm-card mb-4" v-if="verifDetail.verification_status==='pending'">
                <h6 class="adm-card-title"><i class="mdi mdi-gavel me-2"></i>Review</h6>
                <button class="btn btn-success fw-bold w-100 mb-3" @click="processVerif('approve')" :disabled="verifProcessing"><i class="mdi mdi-check-circle me-1"></i>Approve Verification</button>
                <div class="mb-2"><label class="adm-lbl">Rejection Reason (if rejecting)</label><textarea v-model="rejectReason" class="adm-input w-100" rows="2" placeholder="Reason for rejection..."></textarea></div>
                <button class="btn btn-outline-danger fw-bold w-100" @click="processVerif('reject')" :disabled="verifProcessing || !rejectReason.trim()"><i class="mdi mdi-close-circle me-1"></i>Reject</button>
              </div>
              <div class="adm-card mb-4" v-else>
                <h6 class="adm-card-title"><i class="mdi mdi-check-circle me-2" style="color:#50C878;"></i>Decision</h6>
                <div class="adm-rev-rows">
                  <div class="adm-rev-row"><span>Status</span><span class="adm-badge" :class="'adm-b-'+verifDetail.verification_status">{{ verifDetail.verification_status }}</span></div>
                  <div v-if="verifDetail.verified_at" class="adm-rev-row"><span>Verified</span><strong>{{ fDT(verifDetail.verified_at) }}</strong></div>
                  <div v-if="verifDetail.rejection_reason" class="adm-rev-row"><span>Reason</span><strong style="color:#FF6B6B;">{{ verifDetail.rejection_reason }}</strong></div>
                </div>
              </div>

              <!-- Send Notification to Seller -->
              <div class="adm-card">
                <h6 class="adm-card-title"><i class="mdi mdi-bell-ring me-2" style="color:#FFAB00;"></i>Message Seller</h6>
                <div class="mb-2"><label class="adm-lbl">Quick Templates</label>
                  <div class="d-flex flex-wrap gap-1 mb-2">
                    <button class="btn btn-sm adm-notif-chip" @click="setVerifNotif('Please upload a clearer photo of your document — the current one is blurry.')">Blurry photo</button>
                    <button class="btn btn-sm adm-notif-chip" @click="setVerifNotif('Please upload the back of your ID as well.')">Missing back</button>
                    <button class="btn btn-sm adm-notif-chip" @click="setVerifNotif('The document appears to be expired. Please upload a valid document.')">Expired doc</button>
                    <button class="btn btn-sm adm-notif-chip" @click="setVerifNotif('Please upload a selfie holding your ID next to your face so we can verify your identity.')">Need selfie</button>
                    <button class="btn btn-sm adm-notif-chip" @click="setVerifNotif('The name on your document doesn\'t match your profile. Please update your profile name or upload a matching document.')">Name mismatch</button>
                  </div>
                </div>
                <div class="mb-2"><label class="adm-lbl">Notification Title</label><input v-model="verifNotifTitle" class="adm-input w-100" placeholder="e.g. Verification Update" /></div>
                <div class="mb-2"><label class="adm-lbl">Message</label><textarea v-model="verifNotifBody" class="adm-input w-100" rows="3" placeholder="Write a message to the seller..."></textarea></div>
                <button class="btn btn-warning text-dark fw-bold w-100" @click="sendVerifNotification" :disabled="sendingNotif || !verifNotifBody.trim()">
                  <span v-if="sendingNotif"><span class="spinner-border spinner-border-sm me-1"></span></span>
                  <span v-else><i class="mdi mdi-send me-1"></i>Send Notification</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- LIST VIEW — Grouped by user -->
        <div v-else class="adm-card">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-shield-check me-2"></i>Verifications ({{ groupedVerifications.length }} users)</h6>
            <select v-model="verifFilter" class="adm-select"><option value="">All</option><option value="pending">Has Pending</option><option value="verified">All Verified</option><option value="rejected">Has Rejected</option></select>
          </div>

          <div v-if="filteredGroupedVerifications.length" class="adm-verif-groups">
            <div v-for="g in filteredGroupedVerifications" :key="g.user_id" class="adm-verif-group">
              <!-- User header — click to expand -->
              <div class="adm-verif-group-header" @click="expandedVerifUser = expandedVerifUser === g.user_id ? null : g.user_id">
                <div class="d-flex align-items-center gap-3">
                  <div class="adm-user-avatar-sm">{{ getInitials(g.display_name) }}</div>
                  <div>
                    <div style="color:var(--fm-h,#F5F0E8);font-size:14px;font-weight:600;">{{ g.display_name }}</div>
                    <div class="adm-muted">{{ g.phone }}</div>
                  </div>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <span v-if="g.pendingCount > 0" class="adm-badge adm-b-pending">{{ g.pendingCount }} pending</span>
                  <span v-if="g.verifiedCount > 0" class="adm-badge adm-b-completed">{{ g.verifiedCount }} verified</span>
                  <span v-if="g.rejectedCount > 0" class="adm-badge adm-b-cancelled">{{ g.rejectedCount }} rejected</span>
                  <i class="mdi" :class="expandedVerifUser === g.user_id ? 'mdi-chevron-up' : 'mdi-chevron-down'" style="color:var(--fm-s,#6B6356);font-size:20px;"></i>
                </div>
              </div>

              <!-- Expanded: individual verifications. Dev-Requests #13 — inline
                   ID + selfie thumbnails so admin can eyeball validity without
                   drilling into detail. Click thumbnail → detail view. -->
              <div v-if="expandedVerifUser === g.user_id" class="adm-verif-group-body">
                <div v-for="v in g.items" :key="v.id" class="adm-verif-item-row" @click="verifDetail=v;rejectReason=''">
                  <div class="d-flex align-items-center gap-3" style="min-width:0;flex:1;">
                    <i :class="'mdi '+verifTypeIcon(v.verification_type)" style="font-size:22px;color:#BFA164;"></i>
                    <div style="min-width:0;">
                      <div style="color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:600;">{{ verifLabel(v.verification_type) }}</div>
                      <div class="adm-muted">{{ v.document_number || 'No document number' }} · {{ fD(v.created_at) }}</div>
                    </div>
                    <!-- Inline thumbnails: ID front + back + selfie -->
                    <div class="adm-verif-thumb-strip">
                      <div v-if="v.document_front_url" class="adm-verif-thumb" :title="'ID Front'" @click.stop="previewImage(v.document_front_url)">
                        <i v-if="isPdf(v.document_front_url)" class="mdi mdi-file-pdf-box"></i>
                        <img v-else :src="photoUrl(v.document_front_url)" alt="ID front" />
                      </div>
                      <div v-if="v.document_back_url" class="adm-verif-thumb" :title="'ID Back'" @click.stop="previewImage(v.document_back_url)">
                        <img :src="photoUrl(v.document_back_url)" alt="ID back" />
                      </div>
                      <div v-if="v.selfie_url" class="adm-verif-thumb adm-verif-thumb-selfie" :title="'Selfie'" @click.stop="previewImage(v.selfie_url)">
                        <img :src="photoUrl(v.selfie_url)" alt="Selfie" />
                      </div>
                      <span v-if="!v.document_front_url && !v.selfie_url" class="adm-muted" style="font-size:11px;">No docs</span>
                    </div>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <!-- Dev-Requests #14 — AI score badge for at-a-glance triage -->
                    <span v-if="v.ai_score !== null && v.ai_score !== undefined" class="adm-badge" :style="{background: aiScoreBg(v.ai_score), color: aiScoreFg(v.ai_score), fontFamily:'monospace'}" :title="'AI verification score'">
                      <i class="mdi mdi-brain me-1"></i>{{ v.ai_score }}
                    </span>
                    <span class="adm-badge" :class="'adm-b-'+v.verification_status">{{ v.verification_status }}</span>
                    <i class="mdi mdi-chevron-right" style="color:var(--fm-s,#3A3530);font-size:18px;"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="adm-empty">No verifications match</div>
        </div>
      </div>

      <!-- ===== INSURANCE ===== -->
      <div v-if="tab==='insurance'">

        <!-- Stat strip -->
        <div class="row g-3 mb-3">
          <div class="col-6 col-md"><div class="adm-card" style="padding:12px;"><div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Policies</div><div style="color:#BFA164;font-weight:700;font-size:18px;margin-top:4px;">{{ insuranceStats.total || 0 }}</div></div></div>
          <div class="col-6 col-md"><div class="adm-card" style="padding:12px;"><div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Pending</div><div style="color:#f1b44c;font-weight:700;font-size:18px;margin-top:4px;">{{ insuranceStats.pending || 0 }}</div></div></div>
          <div class="col-6 col-md"><div class="adm-card" style="padding:12px;"><div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Active</div><div style="color:#50C878;font-weight:700;font-size:18px;margin-top:4px;">{{ insuranceStats.active || 0 }}</div></div></div>
          <div class="col-6 col-md"><div class="adm-card" style="padding:12px;"><div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Claims open</div><div style="color:#FF9800;font-weight:700;font-size:18px;margin-top:4px;">{{ (claimStats.submitted||0)+(claimStats.under_review||0) }}</div></div></div>
          <div class="col-6 col-md"><div class="adm-card" style="padding:12px;"><div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Premium GMV</div><div style="color:#BFA164;font-weight:700;font-size:18px;margin-top:4px;">KES {{ fN(insuranceStats.premiumGmv||0) }}</div></div></div>
          <div class="col-6 col-md"><div class="adm-card" style="padding:12px;"><div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Cover GMV</div><div style="color:#50C878;font-weight:700;font-size:18px;margin-top:4px;">KES {{ fN(insuranceStats.coverGmv||0) }}</div></div></div>
        </div>

        <!-- Sub-tabs: Policies / Claims / Products / Providers -->
        <div class="d-flex gap-2 mb-3">
          <button class="adm-tab" :class="{active:insSubTab==='policies'}"  @click="insSubTab='policies';loadInsurancePolicies()" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-shield-check me-1"></i>Policies</button>
          <button class="adm-tab" :class="{active:insSubTab==='claims'}"    @click="insSubTab='claims';loadInsuranceClaims()" style="font-size:12px;padding:5px 12px;">
            <i class="mdi mdi-file-document-edit me-1"></i>Claims
            <span v-if="(claimStats.submitted||0)+(claimStats.under_review||0)" style="background:#FF9800;color:#fff;border-radius:8px;padding:1px 6px;font-size:10px;margin-left:4px;">{{ (claimStats.submitted||0)+(claimStats.under_review||0) }}</span>
          </button>
          <button class="adm-tab" :class="{active:insSubTab==='products'}"  @click="insSubTab='products';loadInsuranceProducts()" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-tag-multiple me-1"></i>Products</button>
          <button class="adm-tab" :class="{active:insSubTab==='providers'}" @click="insSubTab='providers';loadInsuranceProviders()" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-domain me-1"></i>Providers</button>
        </div>

        <!-- ===== POLICIES sub-tab ===== -->
        <div v-if="insSubTab==='policies'" class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap" style="gap:8px;">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-shield-check me-2"></i>All Policies ({{ insurancePolicies.length }})</h6>
            <div class="d-flex gap-2 flex-wrap">
              <input v-model="polFilter.search" class="adm-input" placeholder="Search ref, user, product..." style="min-width:220px;" @keyup.enter="loadInsurancePolicies" />
              <select v-model="polFilter.status" class="adm-input" @change="loadInsurancePolicies">
                <option value="">All statuses</option>
                <option value="pending">Pending</option><option value="active">Active</option>
                <option value="claimed">Claimed</option><option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option><option value="lapsed">Lapsed</option>
              </select>
              <select v-model="polFilter.paymentStatus" class="adm-input" @change="loadInsurancePolicies">
                <option value="">Any payment</option>
                <option value="pending">Payment pending</option>
                <option value="paid">Paid</option>
                <option value="refunded">Refunded</option>
              </select>
              <select v-model="polFilter.providerId" class="adm-input" @change="loadInsurancePolicies">
                <option value="">Any provider</option>
                <option v-for="p in insuranceProviders" :key="p.id" :value="p.id">{{ p.short_name || p.name }}</option>
              </select>
              <button class="btn btn-sm btn-outline-warning" @click="loadInsurancePolicies"><i class="mdi mdi-refresh"></i></button>
            </div>
          </div>

          <div v-if="loadingInsurance" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!insurancePolicies.length" class="adm-empty">No policies match</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Policy #</th><th>User</th><th>Product</th><th>Provider</th><th>Cover</th><th>Premium</th><th>Period</th><th>Status</th><th>Payment</th><th></th></tr></thead>
              <tbody>
                <tr v-for="p in insurancePolicies" :key="p.id">
                  <td style="font-family:monospace;font-size:11px;">{{ p.policy_number }}</td>
                  <td>{{ p.user_name }}<div style="font-size:10px;color:var(--fm-s,#6B6356);">{{ p.user_phone }}</div></td>
                  <td>{{ p.product_name }}<div style="font-size:10px;color:var(--fm-s,#6B6356);text-transform:capitalize;">{{ (p.product_type||'').replace('_',' ') }}</div></td>
                  <td>{{ p.provider_short || p.provider_name }}</td>
                  <td>KES {{ fN(p.covered_value) }}</td>
                  <td style="color:#BFA164;font-weight:600;">KES {{ fN(p.premium_amount) }}</td>
                  <td style="font-size:11px;">{{ p.start_date }}<div style="color:var(--fm-s,#6B6356);">→ {{ p.end_date }}</div></td>
                  <td><span class="ins-st" :class="'ins-st-'+p.status">{{ p.status }}</span></td>
                  <td><span class="ins-st" :class="'ins-st-'+p.payment_status">{{ p.payment_status }}</span></td>
                  <td>
                    <button v-if="p.status==='pending'" class="btn btn-sm btn-success me-1" @click="openActivatePolicy(p)" title="Activate (premium received)"><i class="mdi mdi-check-decagram"></i></button>
                    <button v-if="p.status!=='cancelled' && p.status!=='expired'" class="btn btn-sm btn-outline-danger" @click="openCancelPolicy(p)" title="Cancel policy"><i class="mdi mdi-close-octagon"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ===== CLAIMS sub-tab ===== -->
        <div v-if="insSubTab==='claims'" class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap" style="gap:8px;">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-file-document-edit me-2"></i>All Claims ({{ insuranceClaims.length }})</h6>
            <div class="d-flex gap-2 flex-wrap">
              <input v-model="claimFilter.search" class="adm-input" placeholder="Search claim, policy, user..." style="min-width:220px;" @keyup.enter="loadInsuranceClaims" />
              <select v-model="claimFilter.status" class="adm-input" @change="loadInsuranceClaims">
                <option value="">All statuses</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under review</option>
                <option value="approved">Approved (awaiting payout)</option>
                <option value="paid">Paid</option>
                <option value="rejected">Rejected</option>
                <option value="withdrawn">Withdrawn</option>
              </select>
              <select v-model="claimFilter.claimType" class="adm-input" @change="loadInsuranceClaims">
                <option value="">Any type</option>
                <option value="death">Death</option><option value="disease">Disease</option>
                <option value="accident">Accident</option><option value="theft">Theft</option>
                <option value="drought">Drought</option><option value="transport_damage">Transport damage</option>
                <option value="other">Other</option>
              </select>
              <button class="btn btn-sm btn-outline-warning" @click="loadInsuranceClaims"><i class="mdi mdi-refresh"></i></button>
            </div>
          </div>

          <div v-if="loadingInsurance" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!insuranceClaims.length" class="adm-empty">No claims match</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Claim #</th><th>Policy #</th><th>User</th><th>Type</th><th>Incident</th><th>Claimed</th><th>Approved</th><th>Status</th><th></th></tr></thead>
              <tbody>
                <tr v-for="c in insuranceClaims" :key="c.id" style="cursor:pointer;" @click="openClaimDetail(c)">
                  <td style="font-family:monospace;font-size:11px;">{{ c.claim_number }}</td>
                  <td style="font-family:monospace;font-size:11px;">{{ c.policy_number }}</td>
                  <td>{{ c.user_name }}<div style="font-size:10px;color:var(--fm-s,#6B6356);">{{ c.user_phone }}</div></td>
                  <td style="text-transform:capitalize;">{{ (c.claim_type||'').replace('_',' ') }}</td>
                  <td>{{ c.incident_date }}</td>
                  <td>KES {{ fN(c.claimed_amount) }}</td>
                  <td>{{ c.approved_amount ? 'KES '+fN(c.approved_amount) : '—' }}</td>
                  <td><span class="ins-st" :class="'ins-st-'+c.status">{{ (c.status||'').replace('_',' ') }}</span></td>
                  <td><i class="mdi mdi-chevron-right" style="color:var(--fm-s,#6B6356);"></i></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ===== PRODUCTS sub-tab ===== -->
        <div v-if="insSubTab==='products'" class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-tag-multiple me-2"></i>Insurance Products ({{ insuranceProducts.length }})</h6>
            <button class="btn btn-sm btn-warning" @click="openProductModal(null)"><i class="mdi mdi-plus me-1"></i>New Product</button>
          </div>

          <div v-if="loadingInsurance" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!insuranceProducts.length" class="adm-empty">No products yet</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Product</th><th>Provider</th><th>Type</th><th>Premium</th><th>Range</th><th>Duration</th><th>Active</th><th></th></tr></thead>
              <tbody>
                <tr v-for="p in insuranceProducts" :key="p.id">
                  <td>{{ p.name }}<div style="font-size:10px;color:var(--fm-s,#6B6356);">{{ p.description }}</div></td>
                  <td>{{ p.provider_name }}</td>
                  <td style="text-transform:capitalize;">{{ (p.insurance_type||'').replace('_',' ') }}</td>
                  <td>{{ p.flat_premium ? 'KES '+fN(p.flat_premium) : Number(p.premium_rate_pct||0).toFixed(2)+'%' }}</td>
                  <td style="font-size:11px;">{{ p.min_animal_value ? 'KES '+fN(p.min_animal_value) : '—' }}<div style="color:var(--fm-s,#6B6356);">{{ p.max_animal_value ? '→ '+fN(p.max_animal_value) : '' }}</div></td>
                  <td>{{ p.duration_months }}m</td>
                  <td><span class="ins-st" :class="String(p.is_active)==='1'?'ins-st-active':'ins-st-cancelled'">{{ String(p.is_active)==='1' ? 'active' : 'inactive' }}</span></td>
                  <td><button class="btn btn-sm btn-outline-warning" @click="openProductModal(p)"><i class="mdi mdi-pencil"></i></button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ===== PROVIDERS sub-tab ===== -->
        <div v-if="insSubTab==='providers'" class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-domain me-2"></i>Insurance Providers ({{ insuranceProviders.length }})</h6>
            <button class="btn btn-sm btn-warning" @click="openProviderModal(null)"><i class="mdi mdi-plus me-1"></i>New Provider</button>
          </div>

          <div v-if="loadingInsurance" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!insuranceProviders.length" class="adm-empty">No providers yet</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Name</th><th>Short</th><th>Contact</th><th>Types</th><th>Active</th><th></th></tr></thead>
              <tbody>
                <tr v-for="pr in insuranceProviders" :key="pr.id">
                  <td>{{ pr.name }}<div style="font-size:10px;color:var(--fm-s,#6B6356);">{{ pr.website_url }}</div></td>
                  <td>{{ pr.short_name }}</td>
                  <td style="font-size:11px;">{{ pr.contact_email }}<div style="color:var(--fm-s,#6B6356);">{{ pr.contact_phone }}</div></td>
                  <td style="font-size:11px;">{{ humanizeTypes(pr.insurance_types) }}</td>
                  <td><span class="ins-st" :class="String(pr.is_active)==='1'?'ins-st-active':'ins-st-cancelled'">{{ String(pr.is_active)==='1' ? 'active' : 'inactive' }}</span></td>
                  <td><button class="btn btn-sm btn-outline-warning" @click="openProviderModal(pr)"><i class="mdi mdi-pencil"></i></button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <!-- ===== MARKET INTELLIGENCE ===== -->
      <div v-if="tab==='market'">
        <!-- Sub-tabs -->
        <div class="d-flex gap-2 mb-3 flex-wrap">
          <button class="adm-tab" :class="{active:marketSubTab==='days'}" @click="marketSubTab='days'" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-cow me-1"></i>Market Days</button>
          <button class="adm-tab" :class="{active:marketSubTab==='insights'}" @click="marketSubTab='insights';loadExpertInsights()" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-account-star me-1"></i>Expert Insights</button>
          <button class="adm-tab" :class="{active:marketSubTab==='prices'}" @click="marketSubTab='prices'" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-tag-multiple me-1"></i>Prices <small class="text-muted">(archived)</small></button>
          <button class="adm-tab" :class="{active:marketSubTab==='reports'}" @click="marketSubTab='reports'" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-file-document-edit me-1"></i>Reports</button>
        </div>

        <!-- ===== MARKET DAYS (weekly gate figures from physical livestock markets) ===== -->
        <div v-if="marketSubTab==='days'" class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap" style="gap:8px;">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-cow me-2"></i>Market Day Reports ({{ marketDays.length }})</h6>
            <div class="d-flex gap-2 flex-wrap">
              <button class="btn btn-sm btn-outline-warning" @click="mdDownloadTemplate"><i class="mdi mdi-file-excel me-1"></i>Download Excel Template</button>
              <button class="btn btn-sm btn-warning" @click="openMarketDayModal(null)"><i class="mdi mdi-plus me-1"></i>New Market Day</button>
            </div>
          </div>
          <div style="color:var(--fm-s,#6B6356);font-size:11px;margin-bottom:12px;">
            Weekly animal movement figures + trending gate prices from physical livestock markets (e.g. Rumuruti).
            Key figures in manually, or fill the Excel template and upload it inside the editor.
          </div>
          <div v-if="marketLoading" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!marketDays.length" class="adm-empty">No market day reports yet</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Date</th><th>Market</th><th>County</th><th>Animals presented</th><th>Movements</th><th>Prices</th><th>Status</th><th></th></tr></thead>
              <tbody>
                <tr v-for="r in marketDays" :key="r.id">
                  <td style="font-weight:600;">{{ r.report_date }}</td>
                  <td>{{ r.market_name }}</td>
                  <td style="font-size:11px;">{{ r.county_name || '—' }}</td>
                  <td style="font-size:11px;">
                    <span v-for="(t,i) in r.totals" :key="t.species">
                      <span v-if="i>0"> · </span>{{ t.species }} <b style="color:#BFA164;">{{ fN(t.total) }}</b>
                    </span>
                    <span v-if="!r.totals || !r.totals.length">—</span>
                  </td>
                  <td style="font-size:11px;">{{ (r.movements||[]).length }} rows</td>
                  <td style="font-size:11px;">{{ (r.prices||[]).length }} rows</td>
                  <td><span class="ins-st" :class="r.status==='published'?'ins-st-active':'ins-st-pending'">{{ r.status }}</span></td>
                  <td style="white-space:nowrap;">
                    <button class="btn btn-sm btn-outline-warning me-1" @click="openMarketDayModal(r)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                    <button class="btn btn-sm btn-outline-danger" @click="marketDayDeleteTarget=r" title="Delete"><i class="mdi mdi-delete"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Market Day editor modal -->
        <div v-if="marketDayModal" class="adm-overlay" @click.self="marketDayModal=false">
          <div class="adm-modal" style="max-width:960px;width:96%;max-height:92vh;overflow-y:auto;">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0"><i class="mdi mdi-cow me-2" style="color:#BFA164;"></i>{{ mdForm.id ? 'Edit Market Day' : 'New Market Day' }}</h6>
              <button class="btn-close btn-close-white" @click="marketDayModal=false"></button>
            </div>

            <!-- Excel upload -->
            <div class="d-flex align-items-center gap-2 flex-wrap mb-3" style="background:rgba(191,161,100,.07);border:1px dashed #BFA164;border-radius:8px;padding:10px 12px;">
              <i class="mdi mdi-file-excel" style="color:#50C878;font-size:20px;"></i>
              <div style="flex:1;min-width:200px;">
                <div style="font-size:12px;font-weight:600;">Fill from Excel</div>
                <div style="font-size:10px;color:var(--fm-s,#6B6356);">Upload the filled template — everything lands in the form below for review before saving.</div>
              </div>
              <button class="btn btn-sm btn-outline-warning" @click="mdDownloadTemplate"><i class="mdi mdi-download me-1"></i>Template</button>
              <label class="btn btn-sm btn-warning" style="margin:0;cursor:pointer;">
                <i class="mdi mdi-upload me-1"></i>Upload Excel
                <input type="file" accept=".xlsx,.xls,.csv" style="display:none;" @change="mdOnExcelFile" />
              </label>
            </div>
            <div v-if="mdExcelInfo" style="font-size:11px;color:#50C878;margin:-6px 0 10px;"><i class="mdi mdi-check-circle me-1"></i>{{ mdExcelInfo }}</div>

            <!-- Header -->
            <div class="row g-2 mb-3">
              <div class="col-md-3">
                <label class="adm-lbl">Market name *</label>
                <input v-model="mdForm.marketName" class="form-control adm-input" placeholder="e.g. Rumuruti" />
              </div>
              <div class="col-md-3">
                <label class="adm-lbl">County</label>
                <select v-model="mdForm.countyId" class="form-select adm-input">
                  <option value="">— none —</option>
                  <option v-for="c in marketCounties" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="adm-lbl">Market date *</label>
                <input v-model="mdForm.reportDate" type="date" class="form-control adm-input" />
              </div>
              <div class="col-md-3">
                <label class="adm-lbl">Status</label>
                <select v-model="mdForm.status" class="form-select adm-input">
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              <div class="col-12">
                <label class="adm-lbl">Notes</label>
                <input v-model="mdForm.notes" class="form-control adm-input" placeholder="e.g. Lambs and kids not presented to the market" />
              </div>
            </div>

            <!-- Movements -->
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label class="adm-lbl" style="margin:0;">Animal movements ({{ mdForm.movements.length }})</label>
              <button class="btn btn-sm btn-outline-warning" @click="mdAddMovement"><i class="mdi mdi-plus"></i> Add row</button>
            </div>
            <div class="adm-table-wrap mb-3" style="max-height:280px;overflow-y:auto;">
              <table class="adm-table">
                <thead><tr><th style="width:150px;">Scope</th><th>Destination</th><th style="width:130px;">Species</th><th style="width:90px;">Slaughter</th><th style="width:90px;">Breeding</th><th style="width:40px;"></th></tr></thead>
                <tbody>
                  <tr v-if="!mdForm.movements.length"><td colspan="6" style="text-align:center;color:var(--fm-s,#6B6356);font-size:11px;">No movement rows — add manually or upload Excel</td></tr>
                  <tr v-for="(m,i) in mdForm.movements" :key="'mv'+i">
                    <td><select v-model="m.scope" class="form-select adm-input" style="font-size:11px;padding:3px 6px;"><option value="outside_county">Outside county</option><option value="within_county">Within county</option></select></td>
                    <td><input v-model="m.destination" class="form-control adm-input" style="font-size:11px;padding:3px 6px;" placeholder="e.g. Nakuru" /></td>
                    <td><input v-model="m.species" class="form-control adm-input" style="font-size:11px;padding:3px 6px;" list="mdSpeciesList" placeholder="Cattle" /></td>
                    <td><input v-model.number="m.slaughter" type="number" min="0" class="form-control adm-input" style="font-size:11px;padding:3px 6px;" /></td>
                    <td><input v-model.number="m.breeding" type="number" min="0" class="form-control adm-input" style="font-size:11px;padding:3px 6px;" /></td>
                    <td><button class="btn btn-sm btn-outline-danger" style="padding:2px 6px;" @click="mdForm.movements.splice(i,1)"><i class="mdi mdi-close"></i></button></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <datalist id="mdSpeciesList"><option>Cattle</option><option>Sheep</option><option>Goats</option><option>Camels</option><option>Donkeys</option><option>Pigs</option><option>Chickens</option></datalist>

            <!-- Totals -->
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label class="adm-lbl" style="margin:0;">Species totals</label>
              <label style="font-size:11px;color:var(--fm-s,#6B6356);cursor:pointer;">
                <input type="checkbox" v-model="mdForm.totalsOverride" @change="mdOnTotalsOverrideToggle" /> Override (use figures as stated on the source report)
              </label>
            </div>
            <div class="adm-table-wrap mb-3">
              <table class="adm-table">
                <thead><tr><th>Species</th><th>Slaughter</th><th>Breeding</th><th>Total</th><th v-if="mdForm.totalsOverride" style="width:40px;"></th></tr></thead>
                <tbody>
                  <template v-if="!mdForm.totalsOverride">
                    <tr v-if="!mdComputedTotals.length"><td colspan="4" style="text-align:center;color:var(--fm-s,#6B6356);font-size:11px;">Auto-computed from movement rows</td></tr>
                    <tr v-for="t in mdComputedTotals" :key="'ct'+t.species">
                      <td>{{ t.species }}</td><td>{{ fN(t.slaughter) }}</td><td>{{ fN(t.breeding) }}</td><td style="color:#BFA164;font-weight:600;">{{ fN(t.total) }}</td>
                    </tr>
                  </template>
                  <template v-else>
                    <tr v-for="(t,i) in mdForm.totals" :key="'ot'+i">
                      <td><input v-model="t.species" class="form-control adm-input" style="font-size:11px;padding:3px 6px;" list="mdSpeciesList" /></td>
                      <td><input v-model.number="t.slaughter" type="number" min="0" class="form-control adm-input" style="font-size:11px;padding:3px 6px;" /></td>
                      <td><input v-model.number="t.breeding" type="number" min="0" class="form-control adm-input" style="font-size:11px;padding:3px 6px;" /></td>
                      <td><input v-model.number="t.total" type="number" min="0" class="form-control adm-input" style="font-size:11px;padding:3px 6px;" /></td>
                      <td><button class="btn btn-sm btn-outline-danger" style="padding:2px 6px;" @click="mdForm.totals.splice(i,1)"><i class="mdi mdi-close"></i></button></td>
                    </tr>
                    <tr><td colspan="5"><button class="btn btn-sm btn-outline-warning" @click="mdForm.totals.push({species:'',slaughter:0,breeding:0,total:0})"><i class="mdi mdi-plus"></i> Add species total</button></td></tr>
                  </template>
                </tbody>
              </table>
            </div>

            <!-- Prices -->
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label class="adm-lbl" style="margin:0;">Trending market prices ({{ mdForm.prices.length }})</label>
              <button class="btn btn-sm btn-outline-warning" @click="mdAddPrice"><i class="mdi mdi-plus"></i> Add row</button>
            </div>
            <div class="adm-table-wrap mb-3" style="max-height:240px;overflow-y:auto;">
              <table class="adm-table">
                <thead><tr><th style="width:130px;">Species</th><th style="width:110px;">Sex</th><th style="width:120px;">Age</th><th>Price (KES)</th><th style="width:40px;"></th></tr></thead>
                <tbody>
                  <tr v-if="!mdForm.prices.length"><td colspan="5" style="text-align:center;color:var(--fm-s,#6B6356);font-size:11px;">No price rows — add manually or upload Excel</td></tr>
                  <tr v-for="(p,i) in mdForm.prices" :key="'pr'+i">
                    <td><input v-model="p.species" class="form-control adm-input" style="font-size:11px;padding:3px 6px;" list="mdSpeciesList" placeholder="Cattle" /></td>
                    <td><select v-model="p.sex" class="form-select adm-input" style="font-size:11px;padding:3px 6px;"><option value="male">Male</option><option value="female">Female</option></select></td>
                    <td><select v-model="p.ageGroup" class="form-select adm-input" style="font-size:11px;padding:3px 6px;"><option value="calf">Calf</option><option value="weaner">Weaner</option><option value="matured">Matured</option><option value="kid">Kid</option><option value="lamb">Lamb</option></select></td>
                    <td><input v-model.number="p.price" type="number" min="0" class="form-control adm-input" style="font-size:11px;padding:3px 6px;" placeholder="e.g. 80000" /></td>
                    <td><button class="btn btn-sm btn-outline-danger" style="padding:2px 6px;" @click="mdForm.prices.splice(i,1)"><i class="mdi mdi-close"></i></button></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="marketDayError" style="color:#FF6B6B;font-size:12px;margin-bottom:8px;"><i class="mdi mdi-alert-circle me-1"></i>{{ marketDayError }}</div>
            <div class="d-flex justify-content-end gap-2">
              <button class="btn btn-outline-secondary btn-sm" @click="marketDayModal=false" :disabled="marketDayBusy">Cancel</button>
              <button class="btn btn-warning btn-sm" @click="submitMarketDay" :disabled="marketDayBusy || !mdForm.marketName || !mdForm.reportDate">
                <span v-if="marketDayBusy" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="mdi mdi-content-save me-1"></i>Save Market Day
              </button>
            </div>
          </div>
        </div>

        <!-- Market Day delete confirm -->
        <div v-if="marketDayDeleteTarget" class="adm-overlay" @click.self="marketDayDeleteTarget=null">
          <div class="adm-modal" style="max-width:420px;">
            <h6 class="mb-2"><i class="mdi mdi-alert me-2" style="color:#FF6B6B;"></i>Delete market day report?</h6>
            <div style="font-size:13px;color:var(--fm-m,#A09888);margin-bottom:14px;">
              {{ marketDayDeleteTarget.market_name }} — {{ marketDayDeleteTarget.report_date }}<br/>
              All movement figures and prices for this day will be permanently removed.
            </div>
            <div class="d-flex justify-content-end gap-2">
              <button class="btn btn-outline-secondary btn-sm" @click="marketDayDeleteTarget=null">Cancel</button>
              <button class="btn btn-danger btn-sm" @click="doDeleteMarketDay" :disabled="marketDayBusy"><i class="mdi mdi-delete me-1"></i>Delete</button>
            </div>
          </div>
        </div>

        <!-- ===== EXPERT INSIGHTS (Dev-Requests #3) ===== -->
        <div v-if="marketSubTab==='insights'" class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap" style="gap:8px;">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-account-star me-2"></i>Expert Insights ({{ expertInsights.length }})</h6>
            <button class="btn btn-sm btn-warning" @click="openInsightModal(null)"><i class="mdi mdi-plus me-1"></i>New Insight</button>
          </div>
          <div v-if="loadingExpertInsights" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!expertInsights.length" class="adm-empty">No expert insights yet</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Title</th><th>Author</th><th>Scope</th><th>Status</th><th>Updated</th><th></th></tr></thead>
              <tbody>
                <tr v-for="ins in expertInsights" :key="ins.id">
                  <td>
                    <div style="font-weight:600;">{{ ins.title }}</div>
                    <div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ (ins.body||'').slice(0,80) }}<span v-if="(ins.body||'').length>80">…</span></div>
                  </td>
                  <td style="font-size:11px;">{{ ins.author_name || ins.display_author || '—' }}<div style="color:var(--fm-s,#6B6356);">{{ ins.author_role }}</div></td>
                  <td style="font-size:11px;">
                    <span v-if="ins.species_name">{{ ins.species_name }}</span>
                    <span v-if="ins.county_name"> · {{ ins.county_name }}</span>
                    <span v-if="!ins.species_name && !ins.county_name">All</span>
                  </td>
                  <td>
                    <span class="ins-st" :class="ins.is_published==1?'ins-st-active':'ins-st-pending'">{{ ins.is_published==1?'Published':'Draft' }}</span>
                    <span v-if="ins.is_pinned==1" class="ins-st ins-st-active" style="margin-left:4px;">Pinned</span>
                  </td>
                  <td style="font-size:11px;">{{ ins.updated_at }}</td>
                  <td>
                    <button class="btn btn-sm btn-outline-warning me-1" @click="openInsightModal(ins)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                    <button class="btn btn-sm btn-outline-danger" @click="deleteInsight(ins)" title="Archive"><i class="mdi mdi-delete"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Expert insight modal -->
        <div v-if="insightModal.open" class="adm-overlay" @click.self="insightModal.open=false">
          <div class="adm-modal">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0">{{ insightModal.editing ? 'Edit Insight' : 'New Insight' }}</h6>
              <button class="btn-close btn-close-white" @click="insightModal.open=false"></button>
            </div>
            <div class="mb-2">
              <label class="adm-lbl">Title</label>
              <input v-model="insightModal.title" class="form-control adm-input" placeholder="e.g. Boran cattle prices holding steady in Laikipia" />
            </div>
            <div class="mb-2">
              <label class="adm-lbl">Body</label>
              <textarea v-model="insightModal.body" class="form-control adm-input" rows="6" placeholder="What buyers and sellers need to know this week…"></textarea>
            </div>
            <div class="row g-2">
              <div class="col-md-6">
                <label class="adm-lbl">Species (optional)</label>
                <select v-model="insightModal.speciesId" class="form-select adm-input">
                  <option value="">All species</option>
                  <option v-for="s in speciesList" :key="s.id" :value="s.id">{{ s.name }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="adm-lbl">County (optional)</label>
                <select v-model="insightModal.countyId" class="form-select adm-input">
                  <option value="">All counties</option>
                  <option v-for="c in countiesList" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="adm-lbl">Author name</label>
                <input v-model="insightModal.authorName" class="form-control adm-input" placeholder="Dr. Jane Doe" />
              </div>
              <div class="col-md-6">
                <label class="adm-lbl">Author role</label>
                <input v-model="insightModal.authorRole" class="form-control adm-input" placeholder="Extension Officer, Laikipia County" />
              </div>
              <div class="col-md-6">
                <label class="adm-lbl">
                  <input type="checkbox" v-model="insightModal.isPinned" /> Pin to top
                </label>
              </div>
              <div class="col-md-6">
                <label class="adm-lbl">
                  <input type="checkbox" v-model="insightModal.isPublished" /> Published
                </label>
              </div>
            </div>
            <div class="d-flex justify-content-end mt-3">
              <button class="btn btn-outline-secondary btn-sm me-2" @click="insightModal.open=false">Cancel</button>
              <button class="btn btn-warning btn-sm" @click="saveInsight" :disabled="!insightModal.title || !insightModal.body">
                <i class="mdi mdi-content-save me-1"></i>Save
              </button>
            </div>
          </div>
        </div>

        <!-- ===== PRICES ===== -->
        <div v-if="marketSubTab==='prices'" class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap" style="gap:8px;">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-tag-multiple me-2"></i>Market Prices ({{ marketPrices.length }})</h6>
            <div class="d-flex gap-2 flex-wrap">
              <select v-model="marketPriceFilter.speciesId" class="adm-input" @change="loadMarketAdmin">
                <option value="">All species</option>
                <option v-for="s in marketSpecies" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
              <select v-model="marketPriceFilter.countyId" class="adm-input" @change="loadMarketAdmin">
                <option value="">All counties</option>
                <option v-for="c in marketCounties" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
              <button class="btn btn-sm btn-outline-warning" @click="openBulkPriceModal"><i class="mdi mdi-table-large me-1"></i>Bulk Import</button>
              <button class="btn btn-sm btn-warning" @click="openPriceModal(null)"><i class="mdi mdi-plus me-1"></i>Add Price</button>
            </div>
          </div>
          <div v-if="marketLoading" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!marketPrices.length" class="adm-empty">No prices yet</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Date</th><th>Species</th><th>Breed</th><th>County</th><th>Avg</th><th>Min</th><th>Max</th><th>Unit</th><th>Sample</th><th>Source</th><th></th></tr></thead>
              <tbody>
                <tr v-for="p in marketPrices" :key="p.id">
                  <td style="font-size:11px;">{{ p.record_date }}<div style="color:var(--fm-s,#6B6356);font-size:10px;">{{ p.period_type }}</div></td>
                  <td>{{ p.species_name }}</td>
                  <td>{{ p.breed_name || '—' }}</td>
                  <td>{{ p.county_name }}</td>
                  <td style="color:#BFA164;font-weight:600;">KES {{ fN(p.avg_price) }}</td>
                  <td style="font-size:11px;color:#FF6B6B;">{{ p.min_price ? fN(p.min_price) : '—' }}</td>
                  <td style="font-size:11px;color:#50C878;">{{ p.max_price ? fN(p.max_price) : '—' }}</td>
                  <td style="font-size:11px;">{{ p.price_unit === 'per_kg' ? '/kg' : '/animal' }}</td>
                  <td style="font-size:11px;">{{ p.sample_size }}</td>
                  <td style="font-size:10px;text-transform:capitalize;">{{ String(p.source).replace(/_/g,' ') }}</td>
                  <td>
                    <button class="btn btn-sm btn-outline-warning me-1" @click="openPriceModal(p)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                    <button class="btn btn-sm btn-outline-danger" @click="deletePrice(p)" title="Delete"><i class="mdi mdi-delete"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ===== REPORTS ===== -->
        <div v-if="marketSubTab==='reports'" class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap" style="gap:8px;">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-file-document-edit me-2"></i>Market Reports ({{ marketReports.length }})</h6>
            <button class="btn btn-sm btn-warning" @click="openReportModal(null)"><i class="mdi mdi-plus me-1"></i>New Report</button>
          </div>
          <div v-if="marketLoading" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!marketReports.length" class="adm-empty">No reports yet</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Title</th><th>Type</th><th>Date</th><th>Scope</th><th>Status</th><th>Views</th><th>Downloads</th><th></th></tr></thead>
              <tbody>
                <tr v-for="r in marketReports" :key="r.id">
                  <td>{{ r.title }}<div style="color:var(--fm-s,#6B6356);font-size:10px;font-family:monospace;">{{ r.slug }}</div></td>
                  <td style="text-transform:capitalize;">{{ r.report_type }}</td>
                  <td>{{ r.report_date }}</td>
                  <td style="font-size:11px;">{{ r.county_name || 'National' }}<span v-if="r.species_name"> · {{ r.species_name }}</span></td>
                  <td><span class="ins-st" :class="r.status==='published'?'ins-st-active':'ins-st-pending'">{{ r.status }}</span></td>
                  <td style="font-size:11px;">{{ r.view_count }}</td>
                  <td style="font-size:11px;">{{ r.download_count }}</td>
                  <td>
                    <button class="btn btn-sm btn-outline-warning me-1" @click="openReportModal(r)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                    <button class="btn btn-sm btn-outline-danger" @click="deleteReport(r)" title="Delete"><i class="mdi mdi-delete"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ===== COMMUNITY ===== -->
      <div v-if="tab==='community'">
        <!-- Sub-tabs -->
        <div class="d-flex gap-2 mb-3">
          <button class="adm-tab" :class="{active:communitySubTab==='articles'}" @click="communitySubTab='articles'" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-book-multiple me-1"></i>Articles</button>
          <button class="adm-tab" :class="{active:communitySubTab==='forum'}" @click="communitySubTab='forum'" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-forum me-1"></i>Forum Moderation</button>
          <button class="adm-tab" :class="{active:communitySubTab==='categories'}" @click="communitySubTab='categories'" style="font-size:12px;padding:5px 12px;"><i class="mdi mdi-tag-multiple me-1"></i>Categories</button>
        </div>

        <!-- ARTICLES -->
        <div v-if="communitySubTab==='articles'" class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-book-multiple me-2"></i>Articles ({{ communityArticles.length }})</h6>
            <button class="btn btn-sm btn-warning" @click="openArticleModal(null)"><i class="mdi mdi-plus me-1"></i>New Article</button>
          </div>
          <div v-if="communityLoading" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!communityArticles.length" class="adm-empty">No articles yet</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Title</th><th>Category</th><th>Audience</th><th>Status</th><th>Featured</th><th>Views</th><th>Likes</th><th></th></tr></thead>
              <tbody>
                <tr v-for="a in communityArticles" :key="a.id">
                  <td>{{ a.title }}<div style="color:var(--fm-s,#6B6356);font-size:10px;font-family:monospace;">{{ a.slug }}</div></td>
                  <td>{{ a.category_name || '—' }}</td>
                  <td style="text-transform:capitalize;font-size:11px;">{{ a.target_audience }}</td>
                  <td><span class="ins-st" :class="a.status==='published'?'ins-st-active':a.status==='archived'?'ins-st-cancelled':'ins-st-pending'">{{ a.status }}</span></td>
                  <td><i v-if="String(a.is_featured)==='1'" class="mdi mdi-star" style="color:#FFD700;"></i><span v-else style="color:var(--fm-s,#6B6356);">—</span></td>
                  <td style="font-size:11px;">{{ a.view_count }}</td>
                  <td style="font-size:11px;">{{ a.like_count }}</td>
                  <td>
                    <button class="btn btn-sm btn-outline-warning me-1" @click="openArticleModal(a)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                    <button class="btn btn-sm btn-outline-danger" @click="deleteArticle(a)" title="Delete"><i class="mdi mdi-delete"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- FORUM MODERATION -->
        <div v-if="communitySubTab==='forum'" class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-forum me-2"></i>Forum Topics ({{ communityForumTopics.length }})</h6>
          </div>
          <div v-if="communityLoading" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!communityForumTopics.length" class="adm-empty">No topics yet</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Title</th><th>Author</th><th>Category</th><th>Replies</th><th>Views</th><th>Status</th><th></th></tr></thead>
              <tbody>
                <tr v-for="t in communityForumTopics" :key="t.id">
                  <td><a :href="'/forum/'+t.id" target="_blank" style="color:var(--fm-h,#F5F0E8);">{{ t.title }}</a><div v-if="t.is_pinned == 1" style="color:#BFA164;font-size:10px;"><i class="mdi mdi-pin"></i> pinned</div></td>
                  <td>{{ t.author_name && t.author_name.trim() ? t.author_name : '—' }}<div style="font-size:10px;color:var(--fm-s,#6B6356);">{{ t.author_phone }}</div></td>
                  <td style="text-transform:capitalize;">{{ t.category }}</td>
                  <td style="font-size:11px;">{{ t.reply_count }}</td>
                  <td style="font-size:11px;">{{ t.view_count }}</td>
                  <td><span class="ins-st" :class="t.status==='active'?'ins-st-active':t.status==='hidden'?'ins-st-cancelled':'ins-st-pending'">{{ t.status }}</span></td>
                  <td>
                    <button class="btn btn-sm" :class="t.is_pinned == 1 ? 'btn-warning' : 'btn-outline-warning'" @click="pinTopic(t)" :title="t.is_pinned == 1 ? 'Unpin' : 'Pin'"><i class="mdi mdi-pin"></i></button>
                    <button class="btn btn-sm" :class="t.status === 'closed' ? 'btn-secondary' : 'btn-outline-secondary'" @click="lockTopic(t)" :title="t.status === 'closed' ? 'Unlock' : 'Lock'" style="margin-left:4px;"><i class="mdi mdi-lock"></i></button>
                    <button v-if="t.status !== 'hidden'" class="btn btn-sm btn-outline-danger" @click="hideTopic(t)" title="Hide" style="margin-left:4px;"><i class="mdi mdi-eye-off"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- CATEGORIES -->
        <div v-if="communitySubTab==='categories'" class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-tag-multiple me-2"></i>Article Categories ({{ communityCategories.length }})</h6>
            <button class="btn btn-sm btn-warning" @click="openCategoryModal(null)"><i class="mdi mdi-plus me-1"></i>New Category</button>
          </div>
          <div v-if="communityLoading" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!communityCategories.length" class="adm-empty">No categories</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Name</th><th>Slug</th><th>Order</th><th>Active</th><th></th></tr></thead>
              <tbody>
                <tr v-for="c in communityCategories" :key="c.id">
                  <td>{{ c.name }}<div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ c.description }}</div></td>
                  <td style="font-family:monospace;font-size:11px;">{{ c.slug }}</td>
                  <td style="font-size:11px;">{{ c.sort_order }}</td>
                  <td><span class="ins-st" :class="String(c.is_active)==='1'?'ins-st-active':'ins-st-cancelled'">{{ String(c.is_active)==='1' ? 'active' : 'inactive' }}</span></td>
                  <td><button class="btn btn-sm btn-outline-warning" @click="openCategoryModal(c)"><i class="mdi mdi-pencil"></i></button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ===== TEAM (admin RBAC at-a-glance) ===== -->
      <div v-if="tab==='team'">
        <div class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap" style="gap:8px;">
            <div>
              <h6 class="adm-card-title mb-0"><i class="mdi mdi-account-tie-hat me-2"></i>Admin Team ({{ team.length }})</h6>
              <div style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:2px;">Every operator with admin or super-admin access. {{ iAmSuperAdmin ? 'Click a module chip to toggle access.' : 'Read-only — only super-admins can change module access.' }}</div>
            </div>
            <div class="d-flex gap-2 align-items-center">
              <input v-model="teamSearch" class="adm-input" placeholder="Search name, phone, email..." style="min-width:240px;" @input="loadTeam" />
              <button class="btn btn-sm btn-outline-warning" @click="loadTeam" :disabled="teamLoading"><i class="mdi mdi-refresh"></i></button>
            </div>
          </div>

          <div v-if="teamLoading" class="text-center py-4"><div class="spinner-border text-warning"></div></div>
          <div v-else-if="!team.length" class="adm-empty">No admins found</div>
          <div v-else>
            <div v-for="t in team" :key="t.id" class="team-row">
              <!-- Identity -->
              <div class="team-id" @click="viewUserDetail({ id: t.id, display_name: t.display_name, phone: t.phone, email: t.email, avatar_url: t.avatar_url })" :title="'Open ' + t.display_name + ' profile'">
                <div class="team-avatar">
                  <img v-if="t.avatar_url" :src="t.avatar_url" />
                  <span v-else>{{ (t.display_name||'?').charAt(0).toUpperCase() }}</span>
                </div>
                <div style="min-width:0;flex:1;">
                  <div class="team-name">
                    {{ t.display_name || 'Unnamed' }}
                    <span v-if="t.is_super" class="team-role-pill team-role-super">Super-Admin</span>
                    <span v-else class="team-role-pill team-role-admin">Admin</span>
                    <span v-if="t.account_status && t.account_status!=='active'" class="team-role-pill" style="background:rgba(255,107,107,.15);color:#FF6B6B;">{{ t.account_status }}</span>
                  </div>
                  <div class="team-meta">
                    <span v-if="t.phone"><i class="mdi mdi-phone me-1"></i>{{ t.phone }}</span>
                    <span v-if="t.email"><i class="mdi mdi-email-outline me-1"></i>{{ t.email }}</span>
                    <span v-if="t.last_active_at" style="color:var(--fm-s,#6B6356);"><i class="mdi mdi-clock-outline me-1"></i>active {{ t.last_active_at }}</span>
                  </div>
                </div>
              </div>

              <!-- Module chips -->
              <div class="team-chips">
                <span v-if="t.is_super" class="team-chip team-chip-implicit" title="Super-admins implicitly have every module">
                  <i class="mdi mdi-infinity me-1"></i>All modules
                </span>
                <button v-else
                        v-for="m in teamCatalogue" :key="m.key"
                        class="team-chip"
                        :class="{
                          'team-chip-on': teamHasModule(t, m.key),
                          'team-chip-off': !teamHasModule(t, m.key),
                          'team-chip-static': !iAmSuperAdmin || m.key === 'overview'
                        }"
                        :disabled="!iAmSuperAdmin || m.key === 'overview' || teamTogglingKey === t.id + ':' + m.key"
                        @click="toggleTeamModule(t, m.key)"
                        :title="m.desc + (iAmSuperAdmin && m.key !== 'overview' ? ' (click to ' + (teamHasModule(t, m.key) ? 'revoke' : 'grant') + ')' : '')">
                  <i :class="teamHasModule(t, m.key) ? 'mdi mdi-check-circle' : 'mdi mdi-circle-outline'" class="me-1"></i>{{ m.label }}
                  <span v-if="teamTogglingKey === t.id + ':' + m.key" class="spinner-border spinner-border-sm ms-1"></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== COMPLIANCE (T&C ledger) ===== -->
      <div v-if="tab==='compliance'">
        <div class="adm-card mb-4">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 style="color:#BFA164;margin:0;"><i class="mdi mdi-file-document-check me-2"></i>Terms &amp; Conditions Acceptance</h6>
            <button class="btn btn-sm btn-outline-warning" @click="loadCompliance" :disabled="compliance.loading">
              <span v-if="compliance.loading"><span class="spinner-border spinner-border-sm me-1"></span></span>
              <span v-else><i class="mdi mdi-refresh me-1"></i>Refresh</span>
            </button>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-md-3"><div class="adm-cap-row" style="text-align:center;"><div style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;letter-spacing:0.5px;">Current version</div><div style="font-size:24px;font-weight:700;color:#BFA164;margin-top:4px;">v{{ compliance.currentVersion || '—' }}</div></div></div>
            <div class="col-md-3"><div class="adm-cap-row" style="text-align:center;"><div style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;letter-spacing:0.5px;">Active users</div><div style="font-size:24px;font-weight:700;color:var(--fm-h,#F5F0E8);margin-top:4px;">{{ compliance.totalUsers }}</div></div></div>
            <div class="col-md-3"><div class="adm-cap-row" style="text-align:center;border-color:rgba(80,200,120,.4);"><div style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;letter-spacing:0.5px;">Accepted current</div><div style="font-size:24px;font-weight:700;color:#50C878;margin-top:4px;">{{ compliance.acceptedCurrent }}</div></div></div>
            <div class="col-md-3"><div class="adm-cap-row" style="text-align:center;border-color:rgba(255,152,0,.4);"><div style="font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;letter-spacing:0.5px;">Yet to accept</div><div style="font-size:24px;font-weight:700;color:#FF9800;margin-top:4px;">{{ compliance.pendingCurrent }}</div></div></div>
          </div>

          <div v-if="compliance.byVersion.length" class="mb-3">
            <div style="color:#BFA164;font-size:11px;font-weight:600;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.5px;">By version</div>
            <div v-for="bv in compliance.byVersion" :key="bv.version" class="adm-top-consumers-row">
              <div><span class="adm-cap-code">v{{ bv.version }}</span></div>
              <div><strong style="color:var(--fm-h,#F5F0E8);">{{ bv.users }}</strong> users · {{ bv.c }} acceptances</div>
            </div>
          </div>

          <div style="color:#BFA164;font-size:11px;font-weight:600;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.5px;">Recent acceptances</div>
          <div class="adm-top-consumers">
            <div v-if="!compliance.rows.length && !compliance.loading" style="text-align:center;padding:20px;color:var(--fm-s,#6B6356);font-size:12px;">No acceptances yet.</div>
            <div v-for="r in compliance.rows" :key="r.id" class="adm-top-consumers-row" style="padding:8px 12px;">
              <div style="flex:1;min-width:0;">
                <div class="adm-tc-name">{{ r.display_name || 'Unknown user' }}</div>
                <div class="adm-tc-meta">{{ r.phone || r.email || r.user_id }} · v{{ r.version }} · {{ r.dwell_seconds }}s read · {{ r.ip_address }}</div>
              </div>
              <div style="color:#50C878;font-size:10px;">{{ r.accepted_at }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== WATCHLIST ===== -->
      <div v-if="tab==='watchlist'">
        <div class="adm-card mb-4">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 style="color:#BFA164;margin:0;"><i class="mdi mdi-account-alert me-2"></i>Watchlist</h6>
            <button class="btn btn-sm btn-outline-warning" @click="loadWatchlist" :disabled="watchlist.loading">
              <span v-if="watchlist.loading"><span class="spinner-border spinner-border-sm me-1"></span></span>
              <span v-else><i class="mdi mdi-refresh me-1"></i>Refresh</span>
            </button>
          </div>
          <div style="font-size:11px;color:var(--fm-m,#A09888);margin-bottom:14px;">
            Soft signals only. Review before suspending — many of these are legitimate users who need a nudge, not bad actors.
          </div>

          <!-- Pending verifications -->
          <div class="mb-4">
            <div style="color:#FF9800;font-size:11px;font-weight:600;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.5px;"><i class="mdi mdi-clock-outline me-1"></i>Pending verifications ({{ watchlist.pendingVerifs.length }})</div>
            <div class="adm-top-consumers">
              <div v-if="!watchlist.pendingVerifs.length" style="text-align:center;padding:14px;color:var(--fm-s,#6B6356);font-size:12px;">All caught up.</div>
              <div v-for="r in watchlist.pendingVerifs" :key="'pv-'+r.id" class="adm-top-consumers-row" style="padding:8px 12px;cursor:pointer;" @click="tab='verifications'">
                <div style="flex:1;min-width:0;">
                  <div class="adm-tc-name">{{ r.display_name || 'Unknown' }}</div>
                  <div class="adm-tc-meta">{{ r.phone || r.email }} · {{ r.verification_type }} · submitted {{ r.created_at }}</div>
                </div>
                <i class="mdi mdi-chevron-right" style="color:#BFA164;"></i>
              </div>
            </div>
          </div>

          <!-- Google sign-ups missing real phone -->
          <div class="mb-4">
            <div style="color:#4FC3F7;font-size:11px;font-weight:600;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.5px;"><i class="mdi mdi-google me-1"></i>Google sign-ups missing phone ({{ watchlist.needsPhone.length }})</div>
            <div class="adm-top-consumers">
              <div v-if="!watchlist.needsPhone.length" style="text-align:center;padding:14px;color:var(--fm-s,#6B6356);font-size:12px;">None.</div>
              <div v-for="u in watchlist.needsPhone" :key="'np-'+u.id" class="adm-top-consumers-row" style="padding:8px 12px;">
                <div style="flex:1;min-width:0;">
                  <div class="adm-tc-name">{{ u.display_name || 'Unknown' }}</div>
                  <div class="adm-tc-meta">{{ u.email }} · created {{ u.created_at }}</div>
                </div>
                <span class="adm-cap-code">{{ u.phone }}</span>
              </div>
            </div>
          </div>

          <!-- Heavy users (hit caps) -->
          <div class="mb-4">
            <div style="color:#FF6B6B;font-size:11px;font-weight:600;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.5px;"><i class="mdi mdi-fire me-1"></i>Heavy users — hit caps in last 24h ({{ watchlist.heavyUsers.length }})</div>
            <div class="adm-top-consumers">
              <div v-if="!watchlist.heavyUsers.length" style="text-align:center;padding:14px;color:var(--fm-s,#6B6356);font-size:12px;">No abuse signals.</div>
              <div v-for="u in watchlist.heavyUsers" :key="'hu-'+u.id+'-'+u.action" class="adm-top-consumers-row" style="padding:8px 12px;">
                <div style="flex:1;min-width:0;">
                  <div class="adm-tc-name">{{ u.display_name || 'Unknown' }}</div>
                  <div class="adm-tc-meta">{{ u.phone || u.email }} · {{ u.action }}</div>
                </div>
                <div style="color:#FF6B6B;font-weight:700;">{{ u.calls }}</div>
              </div>
            </div>
          </div>

          <!-- Multiple rejected verifications -->
          <div>
            <div style="color:#FF6B6B;font-size:11px;font-weight:600;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.5px;"><i class="mdi mdi-shield-alert-outline me-1"></i>Repeat-rejected KYC ({{ watchlist.multipleRejects.length }})</div>
            <div class="adm-top-consumers">
              <div v-if="!watchlist.multipleRejects.length" style="text-align:center;padding:14px;color:var(--fm-s,#6B6356);font-size:12px;">None.</div>
              <div v-for="u in watchlist.multipleRejects" :key="'mr-'+u.id" class="adm-top-consumers-row" style="padding:8px 12px;">
                <div style="flex:1;min-width:0;">
                  <div class="adm-tc-name">{{ u.display_name || 'Unknown' }}</div>
                  <div class="adm-tc-meta">{{ u.phone || u.email }}</div>
                </div>
                <div style="color:#FF6B6B;font-weight:700;">{{ u.rejected_count }}× rejected</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== PRICING & PLANS ===== -->
      <div v-if="tab==='pricing'">
        <div class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="adm-card-title m-0"><i class="mdi mdi-tag-multiple me-2" style="color:#BFA164;"></i>Subscription Plans</h6>
            <button class="btn btn-sm btn-warning" @click="openPlanEditor(null)"><i class="mdi mdi-plus me-1"></i>New plan</button>
          </div>
          <p class="adm-muted mb-3" style="font-size:12px;">These plans are shown on the public landing page (<a href="javascript:void(0)" @click="$router.push('/')" style="color:#BFA164;">/</a>). Edit any field and changes go live instantly — no code deploy needed.</p>

          <div v-if="plansLoading" class="adm-empty">Loading plans…</div>
          <div v-else-if="!adminPlans.length" class="adm-empty">No plans defined yet</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Order</th><th>Name</th><th>Audience</th><th>Monthly</th><th>Annual</th><th>Listings</th><th>Commission</th><th>Active</th><th>Popular</th><th></th></tr></thead>
              <tbody>
                <tr v-for="p in adminPlans" :key="p.id">
                  <td class="adm-mono">{{ p.sort_order }}</td>
                  <td><strong>{{ p.name }}</strong><div class="adm-muted" style="font-size:10px;">{{ p.slug }}</div></td>
                  <td><span class="adm-badge adm-b-info">{{ p.target_user_type }}</span></td>
                  <td>{{ p.currency }} {{ Number(p.priceMonthly).toLocaleString() }}</td>
                  <td>{{ p.priceAnnually !== null ? p.currency + ' ' + Number(p.priceAnnually).toLocaleString() : '—' }}</td>
                  <td>{{ p.max_listings !== null ? p.max_listings : 'Unlimited' }}</td>
                  <td>{{ p.commission_rate_pct !== null ? Number(p.commission_rate_pct) + '%' : '—' }}</td>
                  <td>
                    <label class="adm-switch"><input type="checkbox" :checked="p.isActive" @change="togglePlan(p, 'is_active', $event.target.checked)" /><span></span></label>
                  </td>
                  <td>
                    <label class="adm-switch"><input type="checkbox" :checked="p.isPopular" @change="togglePlan(p, 'is_popular', $event.target.checked)" /><span></span></label>
                  </td>
                  <td><button class="btn btn-sm btn-outline-warning" @click="openPlanEditor(p)"><i class="mdi mdi-pencil"></i></button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Plan editor modal -->
        <div v-if="planEditor.open" class="adm-overlay" @click.self="closePlanEditor">
          <div class="adm-modal" style="max-width:640px;">
            <div class="adm-modal-head d-flex justify-content-between align-items-center">
              <h6 class="m-0"><i class="mdi mdi-tag-edit-outline me-1" style="color:#BFA164;"></i>{{ planEditor.form.id ? 'Edit plan' : 'New plan' }}</h6>
              <button class="btn-close-x" @click="closePlanEditor">&times;</button>
            </div>
            <div class="adm-modal-body" style="max-height:60vh;overflow-y:auto;">
              <div class="row g-2">
                <div class="col-md-7"><label class="adm-lbl">Display name *</label><input v-model="planEditor.form.name" class="adm-input w-100" placeholder="e.g. Premium" /></div>
                <div class="col-md-5"><label class="adm-lbl">Slug * (lowercase, dashes)</label><input v-model="planEditor.form.slug" class="adm-input w-100" placeholder="premium" /></div>
                <div class="col-12"><label class="adm-lbl">Short description</label><input v-model="planEditor.form.description" class="adm-input w-100" placeholder="What does this plan unlock?" /></div>
                <div class="col-md-4"><label class="adm-lbl">Audience</label>
                  <select v-model="planEditor.form.targetUserType" class="adm-input w-100">
                    <option value="any">Any</option><option value="farmer">Farmer</option><option value="aggregator">Aggregator</option><option value="vet">Vet</option><option value="transporter">Transporter</option><option value="product_seller">Product seller</option>
                  </select>
                </div>
                <div class="col-md-4"><label class="adm-lbl">Currency</label><input v-model="planEditor.form.currency" class="adm-input w-100" placeholder="KES" /></div>
                <div class="col-md-4"><label class="adm-lbl">Display order</label><input v-model.number="planEditor.form.sortOrder" type="number" class="adm-input w-100" /></div>
                <div class="col-md-6"><label class="adm-lbl">Monthly price</label><input v-model.number="planEditor.form.priceMonthly" type="number" step="0.01" class="adm-input w-100" placeholder="0" /></div>
                <div class="col-md-6"><label class="adm-lbl">Annual price (optional)</label><input v-model="planEditor.form.priceAnnually" type="number" step="0.01" class="adm-input w-100" placeholder="leave blank for none" /></div>
                <div class="col-md-4"><label class="adm-lbl">Max listings (blank = unlimited)</label><input v-model="planEditor.form.maxListings" type="number" class="adm-input w-100" /></div>
                <div class="col-md-4"><label class="adm-lbl">Max photos / listing</label><input v-model="planEditor.form.maxPhotosPerListing" type="number" class="adm-input w-100" placeholder="5" /></div>
                <div class="col-md-4"><label class="adm-lbl">Commission % (blank = default)</label><input v-model="planEditor.form.commissionRatePct" type="number" step="0.01" class="adm-input w-100" /></div>
                <div class="col-12">
                  <label class="adm-lbl">Features (JSON — leave the default to auto-generate bullets)</label>
                  <textarea v-model="planEditor.form.featuresJson" class="adm-input w-100" rows="5" style="font-family:monospace;font-size:11px;"></textarea>
                  <div class="adm-muted" style="font-size:10px;margin-top:4px;">Keys like <code>analytics</code>, <code>ai_features</code>, <code>boost</code>, <code>health_records</code>, <code>support</code> render as bullets automatically. Or write a <code>"bullets"</code> array directly with <code>{icon, label}</code> objects for full control.</div>
                </div>
                <div class="col-md-6 d-flex align-items-center" style="gap:8px;"><label class="adm-switch"><input type="checkbox" v-model="planEditor.form.isActive" /><span></span></label><span class="adm-muted">Visible on landing page</span></div>
                <div class="col-md-6 d-flex align-items-center" style="gap:8px;"><label class="adm-switch"><input type="checkbox" v-model="planEditor.form.isPopular" /><span></span></label><span class="adm-muted">Mark as "Most popular"</span></div>
              </div>
            </div>
            <div class="adm-modal-foot d-flex gap-2">
              <button class="btn btn-sm btn-outline-secondary flex-fill" @click="closePlanEditor" :disabled="planEditor.saving">Cancel</button>
              <button class="btn btn-sm btn-warning flex-fill" @click="savePlan" :disabled="planEditor.saving || !planEditor.form.name || !planEditor.form.slug">
                <span v-if="planEditor.saving"><span class="spinner-border spinner-border-sm me-1"></span>Saving…</span>
                <span v-else><i class="mdi mdi-content-save me-1"></i>{{ planEditor.form.id ? 'Save changes' : 'Create plan' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== SUPPORT INBOX ===== -->
      <div v-if="tab==='support'">
        <div class="adm-card">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="adm-card-title m-0"><i class="mdi mdi-lifebuoy me-2" style="color:#BFA164;"></i>Support Inbox <span v-if="supportConvos.length" class="adm-muted" style="font-weight:400;font-size:12px;margin-left:6px;">· {{ supportConvos.length }} thread(s)</span></h6>
            <button class="adm-tab" @click="loadSupportConvos" :disabled="supportLoading" style="font-size:11px;padding:5px 10px;"><i class="mdi mdi-refresh me-1"></i>{{ supportLoading ? 'Loading…' : 'Refresh' }}</button>
          </div>
          <p style="color:var(--fm-s,#6B6356);font-size:12px;margin-bottom:12px;">All conversations users have opened with the Flockr Support account (via the "Chat with Support" CTA on order pages). Click a row to open the thread and reply as support.</p>
          <div v-if="supportLoading" class="text-center py-4"><div class="spinner-border spinner-border-sm text-warning"></div></div>
          <div v-else-if="!supportConvos.length" class="adm-empty">No support conversations yet.</div>
          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>User</th><th>Context</th><th>Last message</th><th>Sent by</th><th>Received</th><th>Unread</th></tr></thead>
              <tbody>
                <tr v-for="c in supportConvos" :key="c.id" @click="openSupportConvo(c)" style="cursor:pointer;">
                  <td><strong>{{ c.user_name || '—' }}</strong><div class="adm-muted" style="font-size:10px;">{{ c.user_phone || '' }}</div></td>
                  <td><span class="adm-badge" :class="'adm-b-info'" style="font-size:9px;text-transform:capitalize;">{{ (c.context_type||'general').replace(/_/g,' ') }}</span><div v-if="c.context_id" class="adm-muted" style="font-size:10px;">{{ c.context_id.slice(0,8) }}…</div></td>
                  <td style="max-width:340px;"><span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;display:inline-block;max-width:340px;">{{ c.last_message || '(no messages yet)' }}</span></td>
                  <td><span v-if="c.last_sender===supportUserId" style="color:#BFA164;font-size:11px;">Support</span><span v-else-if="c.last_sender" style="color:#4FC3F7;font-size:11px;">User</span><span v-else class="adm-muted">—</span></td>
                  <td class="adm-muted">{{ c.last_at ? fD(c.last_at) : fD(c.created_at) }}</td>
                  <td><span v-if="Number(c.user_unread)>0" class="adm-badge adm-b-priority-urgent" style="font-size:9px;">{{ c.user_unread }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ===== SETTINGS ===== -->
      <div v-if="tab==='settings'">
        <!-- Landing page toggles (Alex 2026-07-21) — hidden by default so we don't
             overpromise integrations that aren't wired end-to-end yet. -->
        <div class="adm-card mb-3">
          <h6 class="adm-card-title"><i class="mdi mdi-web me-2"></i>Landing Page</h6>
          <label class="d-flex align-items-center gap-2 mb-3" style="cursor:pointer;">
            <input type="checkbox" v-model="trustStripEnabled" @change="settings.landing_trust_strip_enabled = trustStripEnabled ? '1' : '0'" style="width:18px;height:18px;" />
            <div>
              <div style="font-weight:600;">Show "Integrated with platforms you already use" strip</div>
              <div style="font-size:11px;color:var(--fm-s,#6B6356);">M-Pesa · Kenya Veterinary Board · NTSA · Insurance Partners · Market Data. Off by default until integrations are live.</div>
            </div>
          </label>
          <label class="d-flex align-items-center gap-2" style="cursor:pointer;">
            <input type="checkbox" v-model="anitracSectionEnabled" @change="settings.landing_anitrac_section_enabled = anitracSectionEnabled ? '1' : '0'" style="width:18px;height:18px;" />
            <div>
              <div style="font-weight:600;">Show "Flockr is integrated with ANITRAC" section</div>
              <div style="font-size:11px;color:var(--fm-s,#6B6356);">The National Livestock Traceability section on the landing page. Off by default — re-enable when the DVS partnership is formal enough to publicise.</div>
            </div>
          </label>
        </div>

        <!-- SEO & Analytics (Alex 2026-07-23) -->
        <div class="adm-card mb-3">
          <h6 class="adm-card-title"><i class="mdi mdi-chart-line me-2"></i>SEO &amp; Analytics</h6>
          <div class="mb-3">
            <label class="adm-lbl">Google Analytics 4 Measurement ID</label>
            <input v-model="settings.ga4_measurement_id" class="adm-input w-100" placeholder="G-XXXXXXXXXX" />
            <div style="font-size:11px;color:var(--fm-s,#6B6356);margin-top:4px;">
              Paste the Measurement ID from GA4 → Admin → Data Streams → your web stream. Leave blank to disable analytics.
              <a href="https://analytics.google.com/analytics/web/" target="_blank" rel="noopener" style="color:#BFA164;">Open GA4 →</a>
            </div>
          </div>

          <div class="d-flex flex-wrap gap-2">
            <a class="btn btn-sm btn-outline-warning"
               href="https://search.google.com/search-console/performance/insights?resource_id=https%3A%2F%2Fflockr.africa%2F"
               target="_blank" rel="noopener">
              <i class="mdi mdi-magnify me-1"></i>Open Google Search Console
            </a>
            <a class="btn btn-sm btn-outline-warning"
               href="https://analytics.google.com/analytics/web/#/home"
               target="_blank" rel="noopener">
              <i class="mdi mdi-google-analytics me-1"></i>Open Google Analytics 4
            </a>
            <a class="btn btn-sm btn-outline-secondary"
               href="https://flockr.africa/sitemap.xml"
               target="_blank" rel="noopener">
              <i class="mdi mdi-sitemap me-1"></i>View live sitemap
            </a>
          </div>
        </div>

        <div class="row g-4">
          <div class="col-lg-6">
            <!-- SMS Settings -->
            <div class="adm-card mb-4">
              <h6 class="adm-card-title"><i class="mdi mdi-cellphone-message me-2"></i>SMS Configuration</h6>

              <!-- Provider balance banner (live AT credit check) -->
              <div v-if="smsBalance" :class="['mb-3','p-2','rounded']" :style="smsBalanceBannerStyle">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div style="font-size:11px;font-weight:600;color:rgba(0,0,0,.55);text-transform:uppercase;letter-spacing:.5px;">Provider balance · {{ smsBalance.provider }}</div>
                    <div style="font-size:18px;font-weight:700;margin-top:2px;">{{ smsBalance.rawBalance || (smsBalance.balance !== null ? ('KES ' + smsBalance.balance) : 'Unknown') }}</div>
                    <div style="font-size:11px;margin-top:2px;">{{ smsBalance.message }}</div>
                  </div>
                  <button class="btn btn-sm btn-outline-secondary" @click="loadSmsBalance" :disabled="smsBalanceLoading">
                    <span v-if="smsBalanceLoading"><span class="spinner-border spinner-border-sm"></span></span>
                    <span v-else><i class="mdi mdi-refresh"></i></span>
                  </button>
                </div>
              </div>

              <div class="mb-2"><label class="adm-lbl">SMS Provider</label><select v-model="settings.sms_provider" class="adm-select w-100"><option value="CRAFT">Craft Silicon</option><option value="AFRICASTALKING">Africa's Talking</option><option value="SAFARICOM">Safaricom</option><option value="MOBILESASA">MobileSasa</option></select></div>
              <div class="mb-2"><label class="adm-lbl">SMS Enabled</label><select v-model="settings.sms_enabled" class="adm-select w-100"><option value="true">Yes</option><option value="false">No</option></select></div>
              <!-- Craft fields -->
              <div v-if="settings.sms_provider==='CRAFT'">
                <div class="mb-2"><label class="adm-lbl">Craft UserID</label><input v-model="settings.sms_craft_userid" class="adm-input w-100" /></div>
                <div class="mb-2"><label class="adm-lbl">Craft Password</label><input v-model="settings.sms_craft_password" class="adm-input w-100" type="password" /></div>
              </div>
              <!-- AT fields -->
              <div v-if="settings.sms_provider==='AFRICASTALKING'">
                <div class="mb-2"><label class="adm-lbl">AT Username</label><input v-model="settings.sms_at_username" class="adm-input w-100" /></div>
                <div class="mb-2"><label class="adm-lbl">AT API Key</label><input v-model="settings.sms_at_apikey" class="adm-input w-100" type="password" /></div>
                <div class="mb-2"><label class="adm-lbl">Sender ID</label><input v-model="settings.sms_at_sender" class="adm-input w-100" placeholder="FLOCKR" /></div>
              </div>
              <div class="mb-2"><label class="adm-lbl">Use RabbitMQ Queue</label><select v-model="settings.sms_rabbitmq" class="adm-select w-100"><option value="true">Yes</option><option value="false">No</option></select></div>
            </div>

            <!-- Email Settings -->
            <div class="adm-card">
              <h6 class="adm-card-title"><i class="mdi mdi-email me-2"></i>Email Configuration</h6>
              <div class="mb-2"><label class="adm-lbl">Email Enabled</label><select v-model="settings.email_enabled" class="adm-select w-100"><option value="true">Yes</option><option value="false">No</option></select></div>
              <div class="mb-2"><label class="adm-lbl">Provider</label><select v-model="settings.email_provider" class="adm-select w-100"><option value="BREVO">Brevo (Sendinblue)</option><option value="GMAIL">Gmail SMTP</option></select></div>
              <div class="mb-2"><label class="adm-lbl">SMTP Host</label><input v-model="settings.email_smtp_host" class="adm-input w-100" :placeholder="settings.email_provider==='BREVO'?'smtp-relay.brevo.com':'smtp.gmail.com'" /></div>
              <div class="mb-2"><label class="adm-lbl">SMTP Port</label><input v-model="settings.email_smtp_port" class="adm-input w-100" :placeholder="settings.email_provider==='BREVO'?'2525':'587'" /></div>
              <div class="mb-2"><label class="adm-lbl">SMTP Username</label><input v-model="settings.email_smtp_username" class="adm-input w-100" /></div>
              <div class="mb-2"><label class="adm-lbl">SMTP Password</label><input v-model="settings.email_smtp_password" class="adm-input w-100" type="password" /></div>
              <div class="mb-2"><label class="adm-lbl">From Email</label><input v-model="settings.email_from_address" class="adm-input w-100" placeholder="noreply@flockr.africa" /></div>
              <div class="mb-2"><label class="adm-lbl">From Name</label><input v-model="settings.email_from_name" class="adm-input w-100" placeholder="Flockr" /></div>
            </div>

            <!-- Marketplace -->
            <div class="adm-card mb-4">
              <h6 class="adm-card-title"><i class="mdi mdi-storefront me-2" style="color:#BFA164;"></i>Marketplace</h6>
              <div style="background:#1A1F2A;border:1px solid #2A3A4A;border-radius:8px;padding:10px;margin-bottom:12px;">
                <div style="color:#90CAF9;font-size:11px;">After an animal sells, it stays visible in the marketplace with a SOLD watermark as social proof. This setting caps how many sold listings show per seller.</div>
              </div>
              <div class="mb-2">
                <label class="adm-lbl">Max sold listings shown per seller (showcase cap)</label>
                <input v-model="settings.max_sold_per_seller_visible" class="adm-input w-100" type="number" min="0" placeholder="10" />
                <div style="color:var(--fm-s,#6B6356);font-size:10px;margin-top:4px;">Set to 0 to disable the sold showcase entirely.</div>
              </div>
            </div>
          </div>

          <div class="col-lg-6">
            <!-- SMS Pricing (Farm Billing) -->
            <div class="adm-card mb-4">
              <h6 class="adm-card-title"><i class="mdi mdi-currency-usd me-2" style="color:#50C878;"></i>SMS Pricing (Farm Billing)</h6>
              <div style="background:#0F200F;border:1px solid #1b5e20;border-radius:8px;padding:10px;margin-bottom:12px;">
                <div style="color:#a5d6a7;font-size:11px;margin-bottom:4px;">Farms pay Flockr for SMS credits. This sets the rate charged to ALL farms.</div>
                <div style="color:var(--fm-s,#6B6356);font-size:10px;">Current: <strong style="color:var(--fm-h,#F5F0E8);">KES {{ smsPricing.rate_per_unit }} per {{ smsPricing.chars_per_unit }} characters</strong></div>
              </div>
              <div class="row g-2 mb-2">
                <div class="col-6"><label class="adm-lbl">Rate per SMS Unit (KES)</label><input v-model.number="smsPricing.rate_per_unit" class="adm-input w-100" type="number" step="0.1" min="0.1" /></div>
                <div class="col-6"><label class="adm-lbl">Characters per Unit</label><input v-model.number="smsPricing.chars_per_unit" class="adm-input w-100" type="number" min="1" /></div>
              </div>
              <div class="row g-2 mb-2">
                <div class="col-6"><label class="adm-lbl">Min Top-up (KES)</label><input v-model.number="smsPricing.min_topup" class="adm-input w-100" type="number" /></div>
                <div class="col-6"><label class="adm-lbl">Max Top-up (KES)</label><input v-model.number="smsPricing.max_topup" class="adm-input w-100" type="number" /></div>
              </div>
              <div v-if="smsPricing.rate_per_unit" style="color:var(--fm-s,#6B6356);font-size:11px;margin-bottom:8px;">
                <i class="mdi mdi-calculator me-1"></i>Example: KES 100 top-up = <strong style="color:#50C878;">{{ Math.floor(100/smsPricing.rate_per_unit) }} SMS</strong> · KES 1000 = <strong style="color:#50C878;">{{ Math.floor(1000/smsPricing.rate_per_unit) }} SMS</strong>
              </div>
              <button class="btn btn-sm btn-warning w-100" @click="saveSmsPricing" :disabled="savingSmsPricing">
                <span v-if="savingSmsPricing"><span class="spinner-border spinner-border-sm me-1"></span></span>
                <span v-else><i class="mdi mdi-content-save me-1"></i>Save SMS Pricing</span>
              </button>
              <div v-if="smsPricingSaved" style="color:#50C878;font-size:11px;margin-top:6px;text-align:center;"><i class="mdi mdi-check-circle me-1"></i>Pricing updated</div>
            </div>

            <!-- Flockr M-Pesa Paybill Config -->
            <div class="adm-card mb-4">
              <h6 class="adm-card-title"><i class="mdi mdi-cellphone-key me-2" style="color:#50C878;"></i>Flockr M-Pesa Paybill</h6>
              <div style="background:#0F150F;border:1px solid #1b5e20;border-radius:8px;padding:10px;margin-bottom:12px;">
                <div style="color:#a5d6a7;font-size:11px;">This paybill receives payments from farms for SMS top-ups and marketplace transactions.</div>
              </div>
              <div class="row g-2 mb-2">
                <div class="col-6"><label class="adm-lbl">Shortcode (Paybill)</label><input v-model="flockrMpesa.shortcode" class="adm-input w-100" placeholder="e.g. 4160303" /></div>
                <div class="col-6"><label class="adm-lbl">Type</label><select v-model="flockrMpesa.shortcode_type" class="adm-select w-100"><option value="paybill">Paybill</option><option value="till">Till (Buy Goods)</option></select></div>
              </div>
              <div class="mb-2"><label class="adm-lbl">Consumer Key</label><input v-model="flockrMpesa.consumer_key" class="adm-input w-100" placeholder="From Daraja portal" /></div>
              <div class="mb-2"><label class="adm-lbl">Consumer Secret</label><input v-model="flockrMpesa.consumer_secret" class="adm-input w-100" :type="showFlockrSecret?'text':'password'" />
                <button class="btn btn-sm btn-link text-muted" style="font-size:10px;" @click="showFlockrSecret=!showFlockrSecret">{{ showFlockrSecret?'Hide':'Show' }}</button>
              </div>
              <div class="mb-2"><label class="adm-lbl">Passkey</label><input v-model="flockrMpesa.passkey" class="adm-input w-100" :type="showFlockrSecret?'text':'password'" /></div>
              <div class="mb-2"><label class="adm-lbl">Environment</label><select v-model="flockrMpesa.environment" class="adm-select w-100"><option value="sandbox">Sandbox</option><option value="production">Production</option></select></div>
              <div v-if="flockrMpesa.environment==='production'" style="background:#200F0F;border:1px solid #c62828;border-radius:6px;padding:6px 10px;margin-bottom:8px;"><span style="color:#FF6B6B;font-size:10px;"><i class="mdi mdi-alert me-1"></i>Production — real money</span></div>
              <div class="d-flex gap-2">
                <button class="btn btn-sm btn-warning flex-fill" @click="saveFlockrMpesa" :disabled="savingFlockrMpesa">
                  <span v-if="savingFlockrMpesa"><span class="spinner-border spinner-border-sm me-1"></span></span>
                  <span v-else><i class="mdi mdi-content-save me-1"></i>Save</span>
                </button>
                <button class="btn btn-sm btn-outline-info flex-fill" @click="testFlockrMpesa" :disabled="testingFlockrMpesa">
                  <span v-if="testingFlockrMpesa"><span class="spinner-border spinner-border-sm me-1"></span></span>
                  <span v-else><i class="mdi mdi-lan-check me-1"></i>Test Connection</span>
                </button>
              </div>
              <div class="d-flex gap-2 mt-2">
                <button class="btn btn-sm btn-success flex-fill" @click="activateFlockrMpesa" :disabled="activatingFlockrMpesa||!flockrMpesa.shortcode||!flockrMpesa.consumer_key">
                  <span v-if="activatingFlockrMpesa"><span class="spinner-border spinner-border-sm me-1"></span>Registering...</span>
                  <span v-else>
                    <i class="mdi mdi-power-plug me-1"></i>
                    {{ flockrMpesa.callback_registered ? 'Re-register C2B Callbacks' : 'Activate Paybill (Register C2B Callbacks)' }}
                  </span>
                </button>
              </div>
              <div v-if="flockrMpesa.callback_registered" style="color:#a5d6a7;font-size:10px;margin-top:4px;text-align:center;">
                <i class="mdi mdi-check-decagram me-1"></i>Callbacks registered with Safaricom<span v-if="flockrMpesa.callback_registered_at"> · {{ flockrMpesa.callback_registered_at }}</span>
              </div>
              <div v-if="flockrMpesaSaved" style="color:#50C878;font-size:11px;margin-top:6px;text-align:center;"><i class="mdi mdi-check-circle me-1"></i>M-Pesa config saved</div>
              <div v-if="flockrMpesaTestResult" :style="{color:flockrMpesaTestResult.ok?'#50C878':'#FF6B6B',fontSize:'11px',marginTop:'6px',textAlign:'center'}"><i :class="'mdi '+(flockrMpesaTestResult.ok?'mdi-check-circle':'mdi-alert-circle')+' me-1'"></i>{{ flockrMpesaTestResult.msg }}</div>
              <div v-if="flockrMpesaActivateResult" :style="{color:flockrMpesaActivateResult.ok?'#50C878':'#FF6B6B',fontSize:'11px',marginTop:'6px',textAlign:'center'}">
                <i :class="'mdi '+(flockrMpesaActivateResult.ok?'mdi-check-decagram':'mdi-alert-circle')+' me-1'"></i>{{ flockrMpesaActivateResult.msg }}
              </div>

              <!-- Test STK Push -->
              <div style="border-top:1px solid #1E1E1E;margin-top:12px;padding-top:12px;">
                <div style="color:var(--fm-h,#F5F0E8);font-size:12px;font-weight:600;margin-bottom:6px;"><i class="mdi mdi-cellphone-arrow-down me-1" style="color:#4FC3F7;"></i>Test STK Push</div>
                <div v-if="!adminStkSent" class="d-flex gap-2">
                  <input v-model="flockrStkTestPhone" class="adm-input flex-fill" placeholder="07XX XXX XXX" />
                  <input v-model.number="flockrStkTestAmount" class="adm-input" style="width:80px;" type="number" placeholder="10" />
                  <button class="btn btn-sm btn-success" @click="testFlockrStkPush" :disabled="!flockrStkTestPhone||!flockrStkTestAmount||testingFlockrStk">
                    <span v-if="testingFlockrStk"><span class="spinner-border spinner-border-sm me-1"></span></span>
                    <span v-else><i class="mdi mdi-send me-1"></i>Send</span>
                  </button>
                </div>
                <!-- Pending -->
                <div v-if="adminStkSent&&adminStkStatus==='pending'" style="text-align:center;background:#0F200F;border:1px solid #1b5e20;border-radius:8px;padding:10px;margin-top:6px;">
                  <div v-if="adminStkPolling" class="spinner-border spinner-border-sm text-success" style="width:16px;height:16px;"></div>
                  <div v-else><i class="mdi mdi-pause-circle" style="color:#FF9800;font-size:16px;"></i></div>
                  <div style="color:#50C878;font-size:12px;margin-top:4px;">{{ adminStkPolling?'Waiting for '+flockrStkTestPhone+' to enter PIN...':'Polling paused' }}</div>
                  <div class="d-flex justify-content-center gap-1 mt-2">
                    <button class="btn btn-sm btn-outline-info" style="font-size:10px;" @click="pollAdminStkStatus" :disabled="adminStkChecking"><span v-if="adminStkChecking"><span class="spinner-border spinner-border-sm" style="width:10px;height:10px;"></span></span><i v-else class="mdi mdi-refresh me-1"></i>Check</button>
                    <button v-if="adminStkPolling" class="btn btn-sm btn-outline-warning" style="font-size:10px;" @click="stopAdminStkPolling()"><i class="mdi mdi-stop me-1"></i>Stop</button>
                    <button v-else class="btn btn-sm btn-outline-success" style="font-size:10px;" @click="startAdminStkPolling()"><i class="mdi mdi-play me-1"></i>Resume</button>
                    <button class="btn btn-sm btn-outline-secondary" style="font-size:10px;" @click="resetAdminStk()"><i class="mdi mdi-cellphone-arrow-down me-1"></i>Resend</button>
                  </div>
                </div>
                <!-- Success -->
                <div v-if="adminStkSent&&adminStkStatus==='success'" style="text-align:center;background:#0F200F;border:1px solid #1b5e20;border-radius:8px;padding:10px;margin-top:6px;">
                  <i class="mdi mdi-check-circle" style="color:#50C878;font-size:24px;"></i>
                  <div style="color:#50C878;font-size:13px;font-weight:700;margin-top:4px;">Payment Received!</div>
                  <div v-if="adminStkReceipt" style="color:#4FC3F7;font-size:12px;">Ref: {{ adminStkReceipt }}</div>
                  <button class="btn btn-sm btn-outline-secondary mt-2" style="font-size:10px;" @click="resetAdminStk()">Done</button>
                </div>
                <!-- Failed/Cancelled/Timeout -->
                <div v-if="adminStkSent&&(adminStkStatus==='cancelled'||adminStkStatus==='failed'||adminStkStatus==='timeout')" style="text-align:center;background:#200F0F;border:1px solid #c62828;border-radius:8px;padding:10px;margin-top:6px;">
                  <i class="mdi mdi-alert-circle" style="color:#FF6B6B;font-size:24px;"></i>
                  <div style="color:#FF6B6B;font-size:12px;font-weight:600;margin-top:4px;">{{ adminStkStatus==='cancelled'?'Cancelled':adminStkStatus==='timeout'?'No Response':'Failed' }}</div>
                  <div style="color:var(--fm-m,#A09888);font-size:10px;">{{ adminStkMessage }}</div>
                  <button class="btn btn-sm btn-outline-success mt-2" style="font-size:10px;" @click="resetAdminStk()"><i class="mdi mdi-refresh me-1"></i>Try Again</button>
                </div>
              </div>
            </div>

            <!-- Farm SMS Balances -->
            <div class="adm-card mb-4">
              <h6 class="adm-card-title"><i class="mdi mdi-wallet me-2" style="color:#BFA164;"></i>Farm SMS Balances</h6>
              <div class="row g-2 mb-3">
                <div class="col-4"><div style="background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:8px;padding:10px;text-align:center;"><div style="color:#50C878;font-size:18px;font-weight:700;">KES {{ fN(smsPlatformStats.total_balance) }}</div><div style="color:var(--fm-s,#6B6356);font-size:10px;">Total Balance</div></div></div>
                <div class="col-4"><div style="background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:8px;padding:10px;text-align:center;"><div style="color:#BFA164;font-size:18px;font-weight:700;">KES {{ fN(smsPlatformStats.total_topped_up) }}</div><div style="color:var(--fm-s,#6B6356);font-size:10px;">Total Top-ups</div></div></div>
                <div class="col-4"><div style="background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:8px;padding:10px;text-align:center;"><div style="color:#FF6B6B;font-size:18px;font-weight:700;">KES {{ fN(smsPlatformStats.total_spent) }}</div><div style="color:var(--fm-s,#6B6356);font-size:10px;">Total Spent</div></div></div>
              </div>
              <div class="d-flex justify-content-between" style="font-size:12px;color:var(--fm-s,#6B6356);">
                <span>{{ smsPlatformStats.farm_count||0 }} farms with SMS</span>
                <span>{{ smsPlatformStats.total_sms||0 }} SMS sent platform-wide</span>
              </div>
            </div>

            <!-- Usage & Limits -->
            <div class="adm-card mb-4">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <h6 style="color:#BFA164;margin:0;"><i class="mdi mdi-shield-clock me-2"></i>Usage & Limits</h6>
                <button class="btn btn-sm btn-outline-warning" @click="loadUsageLimits" :disabled="usageLoading">
                  <span v-if="usageLoading"><span class="spinner-border spinner-border-sm me-1"></span></span>
                  <span v-else><i class="mdi mdi-refresh me-1"></i>Refresh</span>
                </button>
              </div>
              <div style="font-size:11px;color:var(--fm-m,#A09888);margin-bottom:12px;">
                Daily caps prevent abuse without paywalls. Set 0 to disable a cap (unlimited). When users hit a limit, the UI directs them to talk to a real vet.
              </div>

              <div v-if="!usageCaps.length && !usageLoading" style="text-align:center;padding:20px;color:var(--fm-s,#6B6356);font-size:12px;">
                Click Refresh to load usage data.
              </div>

              <div v-for="cap in usageCaps" :key="cap.action" class="adm-cap-row">
                <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                  <div>
                    <div class="adm-cap-label">{{ cap.label }}</div>
                    <div class="adm-cap-meta">action: <code class="adm-cap-code">{{ cap.action }}</code> · window: {{ cap.window }} · default: {{ cap.default }}</div>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <input type="number" v-model.number="cap.value" min="0" class="form-control form-control-sm adm-cap-input" />
                    <span class="adm-cap-unit">/day</span>
                    <button class="btn btn-sm btn-warning" @click="saveUsageLimit(cap)" :disabled="cap._saving">
                      <span v-if="cap._saving"><span class="spinner-border spinner-border-sm me-1"></span></span>
                      <span v-else><i class="mdi mdi-content-save"></i></span>
                    </button>
                  </div>
                </div>
                <div v-if="usageStats[cap.action]" class="adm-cap-stats">
                  <span><i class="mdi mdi-calendar-today me-1" style="color:#BFA164;"></i><strong>{{ usageStats[cap.action].today }}</strong> today</span>
                  <span><i class="mdi mdi-calendar-week me-1" style="color:#BFA164;"></i><strong>{{ usageStats[cap.action].week }}</strong> this week</span>
                  <span><i class="mdi mdi-calendar-month me-1" style="color:#BFA164;"></i><strong>{{ usageStats[cap.action].month }}</strong> this month</span>
                  <span><i class="mdi mdi-account-multiple me-1" style="color:#BFA164;"></i><strong>{{ usageStats[cap.action].unique_users }}</strong> unique users</span>
                </div>
              </div>

              <!-- Top consumers -->
              <div v-if="topConsumers.length" class="mt-3">
                <div style="color:#BFA164;font-size:11px;font-weight:600;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.5px;">Top consumers (last 7 days)</div>
                <div class="adm-top-consumers">
                  <div v-for="(c,ci) in topConsumers" :key="ci" class="adm-top-consumers-row">
                    <div style="flex:1;min-width:0;">
                      <div class="adm-tc-name">{{ c.display_name || 'Unknown user' }}</div>
                      <div class="adm-tc-meta">{{ c.phone || c.id }} · {{ c.action }}</div>
                    </div>
                    <div style="color:#BFA164;font-weight:700;">{{ c.calls }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Save Button -->
            <div class="adm-card mb-4">
              <button class="btn btn-warning text-dark fw-bold w-100" @click="saveSettings" :disabled="savingSettings">
                <span v-if="savingSettings"><span class="spinner-border spinner-border-sm me-1"></span></span>
                <span v-else><i class="mdi mdi-content-save me-1"></i>Save All Settings</span>
              </button>
              <div v-if="settingsSaved" style="color:#50C878;font-size:12px;margin-top:8px;text-align:center;"><i class="mdi mdi-check-circle me-1"></i>Settings saved</div>

              <!-- Test buttons -->
              <div class="d-flex gap-2 mt-3">
                <button class="btn btn-sm btn-outline-warning flex-fill" @click="testSMS" :disabled="testingSMS"><i class="mdi mdi-cellphone me-1"></i>{{ testingSMS ? 'Sending...' : 'Test SMS' }}</button>
                <button class="btn btn-sm btn-outline-warning flex-fill" @click="testEmail" :disabled="testingEmail"><i class="mdi mdi-email me-1"></i>{{ testingEmail ? 'Sending...' : 'Test Email' }}</button>
              </div>
              <div v-if="testResult" :style="{color:testResult.ok?'#50C878':'#FF6B6B',fontSize:'12px',marginTop:'8px'}"><i :class="'mdi '+(testResult.ok?'mdi-check-circle':'mdi-alert')+ ' me-1'"></i>{{ testResult.msg }}</div>
            </div>

            <!-- SMS Log -->
            <div class="adm-card mb-4">
              <h6 class="adm-card-title"><i class="mdi mdi-message-text-clock me-2"></i>Recent SMS ({{ smsLogs.length }})</h6>
              <div v-if="smsLogs.length" style="max-height:300px;overflow-y:auto;">
                <div v-for="s in smsLogs" :key="s.id" style="padding:8px 0;border-bottom:1px solid var(--fm-br);font-size:12px;">
                  <div class="d-flex justify-content-between"><span class="adm-mono">{{ s.phone }}</span><span :style="{color:s.status==='sent'?'#50C878':'#FF6B6B'}">{{ s.status }}</span></div>
                  <div style="color:var(--fm-m,#A09888);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">{{ s.message }}</div>
                  <div class="adm-muted">{{ fDT(s.created_at) }} · {{ s.provider }}</div>
                </div>
              </div>
              <div v-else class="adm-empty">No SMS sent yet</div>
            </div>

            <!-- Email Log -->
            <div class="adm-card">
              <h6 class="adm-card-title"><i class="mdi mdi-email-outline me-2"></i>Recent Emails ({{ emailLogs.length }})</h6>
              <div v-if="emailLogs.length" style="max-height:300px;overflow-y:auto;">
                <div v-for="e in emailLogs" :key="e.id" style="padding:8px 0;border-bottom:1px solid var(--fm-br);font-size:12px;">
                  <div class="d-flex justify-content-between"><span>{{ e.to_email }}</span><span :style="{color:e.status==='sent'?'#50C878':'#FF6B6B'}">{{ e.status }}</span></div>
                  <div style="color:var(--fm-h,#F5F0E8);">{{ e.subject }}</div>
                  <div v-if="e.error" style="color:#FF6B6B;">{{ e.error }}</div>
                  <div class="adm-muted">{{ fDT(e.created_at) }}</div>
                </div>
              </div>
              <div v-else class="adm-empty">No emails sent yet</div>
            </div>
          </div>
        </div>

        <!-- Support contacts editor — landing-page shows these; updates are live -->
        <div class="row g-4 mt-2">
          <div class="col-12">
            <div class="adm-card">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="adm-card-title mb-0"><i class="mdi mdi-headset me-2" style="color:#BFA164;"></i>Support Contacts (landing page)</h6>
                <button class="btn btn-sm btn-outline-warning" @click="addSupportContactRow"><i class="mdi mdi-plus me-1"></i>Add contact</button>
              </div>
              <div style="background:#1A1F2A;border:1px solid #2A3A4A;border-radius:8px;padding:10px;margin-bottom:12px;">
                <div style="color:#90CAF9;font-size:11px;">These appear in the "Seller Support" block on the landing-page footer. Active rows are listed in `display_order` ascending. Saves apply instantly — no redeploy.</div>
              </div>
              <div v-if="!supportContactsAdmin.length" class="adm-empty">No contacts yet — click "Add contact" to seed one.</div>
              <div v-for="(c, ci) in supportContactsAdmin" :key="c.id || 'new-'+ci" class="row g-2 align-items-end mb-2" style="padding:10px;border:1px solid var(--fm-br);border-radius:8px;">
                <div class="col-md-3"><label class="adm-lbl">Name *</label><input v-model="c.name" class="adm-input w-100" placeholder="June Kawira" /></div>
                <div class="col-md-2"><label class="adm-lbl">Role</label><input v-model="c.role" class="adm-input w-100" placeholder="Customer Success" /></div>
                <div class="col-md-2"><label class="adm-lbl">Phone</label><input v-model="c.phone" class="adm-input w-100" placeholder="0720883182" /></div>
                <div class="col-md-3"><label class="adm-lbl">Email</label><input v-model="c.email" class="adm-input w-100" placeholder="june@ranchexperts.co.ke" /></div>
                <div class="col-md-1"><label class="adm-lbl">Order</label><input v-model.number="c.display_order" class="adm-input w-100" type="number" min="0" /></div>
                <div class="col-md-1 d-flex flex-column">
                  <label class="adm-lbl">Active</label>
                  <select v-model="c.is_active" class="adm-select w-100"><option :value="1">Yes</option><option :value="0">No</option></select>
                </div>
                <div class="col-12 d-flex gap-2">
                  <button class="btn btn-sm btn-warning" @click="saveSupportContact(c, ci)" :disabled="c._saving"><i class="mdi mdi-content-save me-1"></i>{{ c.id ? 'Update' : 'Create' }}</button>
                  <button v-if="c.id" class="btn btn-sm btn-outline-danger ms-auto" @click="deleteSupportContact(c, ci)"><i class="mdi mdi-delete me-1"></i>Delete</button>
                  <button v-else class="btn btn-sm btn-outline-secondary ms-auto" @click="supportContactsAdmin.splice(ci,1)">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- IMAGE PREVIEW MODAL -->
      <div v-if="previewImg" class="adm-overlay" @click="previewImg=null" style="cursor:zoom-out;">
        <img :src="previewImg" style="max-width:90%;max-height:90%;border-radius:8px;object-fit:contain;" />
      </div>

      <!-- ===== LISTINGS ===== -->
      <div v-if="tab==='listings'">
        <!-- Sub-tab pills: All Listings | Image Duplicates | Unverified Sellers -->
        <div class="d-flex gap-2 mb-3 flex-wrap">
          <button class="btn btn-sm" :class="listingsSub==='all' ? 'btn-warning' : 'btn-outline-warning'" @click="listingsSub='all'">
            <i class="mdi mdi-storefront me-1"></i>All Listings
          </button>
          <button class="btn btn-sm" :class="listingsSub==='duplicates' ? 'btn-warning' : 'btn-outline-warning'" @click="switchToDuplicates()">
            <i class="mdi mdi-image-multiple me-1"></i>Image Duplicates
            <span v-if="imageClusters.totalClusters > 0" class="badge bg-danger ms-1">{{ imageClusters.totalClusters }}</span>
          </button>
          <button class="btn btn-sm" :class="listingsSub==='unverified' ? 'btn-warning' : 'btn-outline-warning'" @click="switchToUnverified()">
            <i class="mdi mdi-shield-alert me-1"></i>Unverified Sellers
            <span v-if="unverifiedSellers.count > 0" class="badge bg-danger ms-1">{{ unverifiedSellers.count }}</span>
          </button>
        </div>

      <div v-if="listingsSub === 'all'">
        <div class="adm-card">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-storefront me-2"></i>Listings ({{ filteredListings.length }})</h6>
            <div class="d-flex gap-2 align-items-center flex-wrap">
              <select v-model="listingFilter" class="adm-select">
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="pending_review">Pending review</option>
                <option value="paused">Paused</option>
                <option value="sold">Sold</option>
                <option value="expired">Expired</option>
                <option value="removed">Removed (unlisted)</option>
                <option value="draft">Draft</option>
              </select>
              <input v-model="listingSearch" class="adm-input" placeholder="Search title, seller..." style="width:200px;" />
              <button class="adm-tab" style="font-size:11px;padding:5px 10px;" title="Export the rows currently shown (filters applied)" @click="exportCsv('flockr_listings', [{key:'title',label:'Title'},{key:'seller_name',label:'Seller'},{key:'species_name',label:'Species'},{key:'price',label:'Price (KES)'},{key:'county_name',label:'Location'},{key:'view_count',label:'Views'},{key:'offer_count',label:'Offers'},{key:'status',label:'Status'},{key:'available_quantity',label:'Available'},{key:'created_at',label:'Created'}], filteredListings)"><i class="mdi mdi-download me-1"></i>Export CSV</button>
              <div class="adm-view-toggle">
                <button :class="{active:listingView==='cards'}" @click="listingView='cards'" title="Card view"><i class="mdi mdi-view-grid"></i></button>
                <button :class="{active:listingView==='table'}" @click="listingView='table'" title="Table view"><i class="mdi mdi-view-list"></i></button>
              </div>
            </div>
          </div>

          <!-- CARD VIEW -->
          <div v-if="listingView==='cards'" class="adm-listing-grid">
            <div v-for="l in filteredListings" :key="l.id" class="adm-lcard" @click="$router.push('/marketplace/listing/'+l.id)">
              <div class="adm-lcard-img">
                <img v-if="l.primary_photo" :src="photoUrl(l.primary_photo)" alt="" />
                <div v-else class="adm-lcard-noimg"><i class="mdi mdi-cow"></i></div>
                <span class="adm-lcard-status" :class="'adm-b-'+l.status">{{ l.status }}</span>
                <span v-if="l.listing_type==='batch'" class="adm-lcard-batch">Batch</span>
              </div>
              <div class="adm-lcard-body">
                <div class="adm-lcard-title">{{ l.title }}</div>
                <div class="adm-lcard-meta">
                  <span>{{ l.species_name }}<span v-if="l.breed_name"> · {{ l.breed_name }}</span></span>
                </div>
                <div class="adm-lcard-price">KES {{ fN(l.price) }} <span v-if="l.price_type && l.price_type!=='fixed'" class="adm-lcard-ptype">{{ l.price_type }}</span></div>
                <div class="adm-lcard-details">
                  <span v-if="l.sex"><i class="mdi mdi-gender-male-female"></i> {{ l.sex }}</span>
                  <span v-if="l.age_months"><i class="mdi mdi-calendar"></i> {{ l.age_months }}mo</span>
                  <span v-if="l.weight_kg"><i class="mdi mdi-weight"></i> {{ l.weight_kg }}kg</span>
                  <span v-if="l.total_quantity > 1"><i class="mdi mdi-group"></i> Qty: {{ l.available_quantity }}/{{ l.total_quantity }}</span>
                </div>
                <div class="adm-lcard-seller">
                  <i class="mdi mdi-account" style="color:#BFA164;"></i>
                  <span>{{ l.seller_name }}</span>
                  <span v-if="l.seller_phone" class="adm-muted">· {{ l.seller_phone }}</span>
                </div>
                <div class="adm-lcard-loc" v-if="l.county_name">
                  <i class="mdi mdi-map-marker" style="color:#BFA164;"></i> {{ l.county_name }}<span v-if="l.location_description"> · {{ l.location_description }}</span>
                </div>
                <div v-if="l.offers_delivery==1" class="adm-lcard-delivery">
                  <i class="mdi mdi-truck" style="color:#50C878;"></i> Delivers: KES {{ fN(l.delivery_base_fee||0) }} + {{ fN(l.delivery_per_km||0) }}/km
                  <span v-if="l.delivery_max_km > 0"> · Max {{ l.delivery_max_km }}km</span>
                </div>
                <div class="adm-lcard-footer">
                  <span><i class="mdi mdi-eye"></i> {{ l.view_count||0 }}</span>
                  <span><i class="mdi mdi-message-text"></i> {{ l.offer_count||0 }}</span>
                  <span><i class="mdi mdi-heart"></i> {{ l.save_count||0 }}</span>
                  <span class="adm-muted">{{ fD(l.created_at) }}</span>
                </div>
                <!-- Admin moderation -->
                <div class="d-flex gap-1 mt-2 pt-2" style="border-top:1px solid var(--fm-br,#1E1E1E);" @click.stop>
                  <button v-if="l.status==='active'" class="btn btn-sm btn-outline-warning" @click.stop="moderateListing(l,'paused')" :disabled="moderatingId===l.id" title="Pause"><i class="mdi mdi-pause"></i></button>
                  <button v-if="l.status==='paused'" class="btn btn-sm btn-outline-success" @click.stop="moderateListing(l,'active')" :disabled="moderatingId===l.id" title="Reactivate"><i class="mdi mdi-play"></i></button>
                  <button v-if="l.status!=='removed'" class="btn btn-sm btn-outline-danger flex-fill" @click.stop="moderateListing(l,'removed')" :disabled="moderatingId===l.id"><i class="mdi mdi-block-helper me-1"></i>Unlist</button>
                  <button v-if="l.status==='removed'" class="btn btn-sm btn-outline-success flex-fill" @click.stop="moderateListing(l,'active')" :disabled="moderatingId===l.id"><i class="mdi mdi-restore me-1"></i>Restore</button>
                </div>
              </div>
            </div>
            <div v-if="!filteredListings.length" class="adm-empty" style="grid-column:1/-1;">No listings match</div>
          </div>

          <!-- TABLE VIEW -->
          <div v-if="listingView==='table'" class="adm-table-wrap">
            <table class="adm-table"><thead><tr><th></th><th>Title</th><th>Seller</th><th>Species</th><th>Price</th><th>Delivery</th><th>Location</th><th>Views</th><th>Offers</th><th>Status</th><th>Created</th></tr></thead>
              <tbody>
              <tr v-for="l in filteredListings" :key="l.id" @click="$router.push('/marketplace/listing/'+l.id)" style="cursor:pointer;" title="View listing">
                <td><img v-if="l.primary_photo" :src="photoUrl(l.primary_photo)" style="width:40px;height:40px;border-radius:6px;object-fit:cover;" /><div v-else style="width:40px;height:40px;border-radius:6px;background:var(--fm-bg3,#1E1E1E);display:flex;align-items:center;justify-content:center;"><i class="mdi mdi-cow" style="color:#3A3530;"></i></div></td>
                <td><strong style="color:var(--fm-h,#F5F0E8);">{{ l.title }}</strong></td>
                <td>{{ l.seller_name }}</td>
                <td>{{ l.species_name }}</td>
                <td>KES {{ fN(l.price) }}</td>
                <td><span v-if="l.offers_delivery==1" style="color:#50C878;font-size:11px;"><i class="mdi mdi-truck me-1"></i>KES {{ fN(l.delivery_base_fee) }}+{{ fN(l.delivery_per_km) }}/km</span><span v-else class="adm-muted">No</span></td>
                <td class="adm-muted">{{ l.county_name || '-' }}</td>
                <td>{{ l.view_count||0 }}</td>
                <td>{{ l.offer_count||0 }}</td>
                <td><span class="adm-badge" :class="'adm-b-'+l.status">{{ l.status }}</span></td>
                <td class="adm-muted">{{ fD(l.created_at) }}</td>
              </tr>
              </tbody></table>
            <div v-if="!filteredListings.length" class="adm-empty">No listings match</div>
          </div>
        </div>
      </div><!-- /listingsSub === 'all' -->

      <!-- ===== IMAGE DUPLICATES (cross-seller photo reuse) ===== -->
      <div v-if="listingsSub === 'duplicates'">
        <div class="adm-card">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <h6 class="adm-card-title mb-0"><i class="mdi mdi-image-multiple me-2"></i>Image Duplicates</h6>
              <p class="adm-muted mb-0" style="font-size:12px;">Listings sharing the same photo across different sellers. The oldest upload is treated as the original.</p>
            </div>
            <div class="d-flex gap-2">
              <button class="btn btn-sm btn-outline-warning" @click="loadImageClusters()" :disabled="imageClustersLoading"><i class="mdi mdi-refresh me-1"></i>Refresh</button>
              <button class="btn btn-sm btn-danger" @click="bulkUnlistDuplicates()" :disabled="imageClustersLoading || !imageClusters.totalClusters" title="Unlist every non-original duplicate and notify the owners">
                <i class="mdi mdi-broom me-1"></i>Bulk Unlist + Notify All
              </button>
            </div>
          </div>

          <div v-if="imageClustersLoading" class="adm-empty"><i class="mdi mdi-loading mdi-spin"></i> Scanning photos…</div>

          <div v-else-if="!imageClusters.totalClusters" class="adm-empty">
            <i class="mdi mdi-check-circle" style="color:#50C878;font-size:32px;"></i>
            <p class="mt-2 mb-0">No duplicate clusters found. All listings have original photos.</p>
          </div>

          <div v-else>
            <div v-for="(c, ci) in allClusters" :key="'c-'+ci" class="adm-dupe-cluster">
              <div class="adm-dupe-cluster-hdr">
                <span class="badge" :class="c.kind==='exact' ? 'bg-danger' : 'bg-warning text-dark'">
                  {{ c.kind==='exact' ? 'EXACT MATCH' : 'PERCEPTUAL MATCH' }}
                </span>
                <span class="adm-muted ms-2">{{ c.photos.length }} photos · {{ c.sellers }} sellers</span>
              </div>
              <div class="adm-dupe-photos">
                <div v-for="p in c.photos" :key="p.photo_id"
                     class="adm-dupe-photo"
                     :class="{ 'is-original': p.photo_id === originalOf(c).photo_id }">
                  <img :src="photoUrl(p.photo_url)" :alt="p.title" />
                  <div class="adm-dupe-photo-meta">
                    <div v-if="p.photo_id === originalOf(c).photo_id" class="adm-dupe-tag-orig">ORIGINAL</div>
                    <div class="adm-dupe-photo-title">{{ p.title }}</div>
                    <div class="adm-muted" style="font-size:11px;">{{ p.seller_name }}</div>
                    <div class="adm-muted" style="font-size:10px;">{{ fD(p.created_at) }} · {{ p.status }}</div>
                    <div class="d-flex gap-1 mt-2" v-if="p.photo_id !== originalOf(c).photo_id">
                      <button class="btn btn-xs btn-outline-danger flex-fill" @click="unlistDuplicate(p, originalOf(c))" :disabled="unlistingPhotoId===p.photo_id">
                        <i class="mdi mdi-block-helper me-1"></i>Unlist + Notify
                      </button>
                      <button class="btn btn-xs btn-outline-secondary" @click="forceAllowPhoto(p)" :disabled="unlistingPhotoId===p.photo_id" title="Legit case — allow this photo to coexist">
                        <i class="mdi mdi-shield-check"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div><!-- /listingsSub === 'duplicates' -->

      <!-- ===== UNVERIFIED SELLERS (KYC grace period) ===== -->
      <div v-if="listingsSub === 'unverified'">
        <div class="adm-card">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <h6 class="adm-card-title mb-0"><i class="mdi mdi-shield-alert me-2"></i>Unverified Sellers (&gt;{{ unverifiedSellers.graceDays || 30 }} days old)</h6>
              <p class="adm-muted mb-0" style="font-size:12px;">Accounts that registered more than {{ unverifiedSellers.graceDays || 30 }} days ago, still have live listings, and never completed identity verification.</p>
            </div>
            <div class="d-flex gap-2">
              <button class="btn btn-sm btn-outline-warning" @click="loadUnverifiedSellers()" :disabled="unverifiedLoading"><i class="mdi mdi-refresh me-1"></i>Refresh</button>
              <button class="btn btn-sm btn-danger" @click="bulkUnlistUnverified()" :disabled="unverifiedLoading || !unverifiedSellers.count" title="Unlist every active listing from these sellers and email/SMS each one to verify">
                <i class="mdi mdi-broom me-1"></i>Bulk Unlist + Notify ({{ unverifiedSellers.totalListingsToUnlist || 0 }} listings)
              </button>
            </div>
          </div>

          <div v-if="unverifiedLoading" class="adm-empty"><i class="mdi mdi-loading mdi-spin"></i> Loading…</div>

          <div v-else-if="!unverifiedSellers.count" class="adm-empty">
            <i class="mdi mdi-check-circle" style="color:#50C878;font-size:32px;"></i>
            <p class="mt-2 mb-0">All active sellers are verified or still within their grace period.</p>
          </div>

          <div v-else class="adm-table-wrap">
            <table class="adm-table">
              <thead><tr><th>Seller</th><th>Phone</th><th>Email</th><th>Registered</th><th>Days Old</th><th>Active Listings</th></tr></thead>
              <tbody>
                <tr v-for="s in unverifiedSellers.sellers" :key="s.id">
                  <td><strong style="color:var(--fm-h,#F5F0E8);">{{ s.display_name || (s.first_name + ' ' + s.last_name) }}</strong></td>
                  <td>{{ s.phone || '-' }}</td>
                  <td>{{ s.email || '-' }}</td>
                  <td>{{ fD(s.created_at) }}</td>
                  <td><span class="adm-badge" :class="s.days_old > 60 ? 'adm-b-removed' : 'adm-b-paused'">{{ s.days_old }} days</span></td>
                  <td><strong style="color:#FFAB00;">{{ s.active_listings }}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div><!-- /listingsSub === 'unverified' -->

      </div><!-- /tab === 'listings' -->

      <!-- ===== AGRO SHOPS (KYC review) ===== -->
      <div v-if="tab==='agroshops'">
        <div class="adm-card">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-storefront-outline me-2"></i>Agro Shops ({{ agroShops.length }})</h6>
            <select v-model="agroShopFilter" class="adm-select" @change="loadAgroShops">
              <option value="">All statuses</option>
              <option value="pending">Pending review</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div v-if="agroShopLoading" class="text-center py-4"><div class="spinner-border spinner-border-sm text-warning"></div></div>
          <div v-else class="table-responsive">
            <table class="adm-table"><thead><tr>
              <th>Shop</th><th>Owner</th><th>Type</th><th>County</th><th>Products</th><th>KYC</th><th>Created</th><th style="text-align:right;">Action</th>
            </tr></thead><tbody>
              <tr v-for="s in agroShops" :key="s.id">
                <td>
                  <strong style="color:var(--fm-h,#F5F0E8);">{{ s.shop_name }}</strong>
                  <div class="adm-muted" v-if="s.license_number">Lic: {{ s.license_number }}</div>
                  <div class="adm-muted" v-if="s.physical_address">{{ s.physical_address }}</div>
                </td>
                <td>{{ s.owner_name || '—' }}<div class="adm-muted">{{ s.owner_phone }}</div></td>
                <td style="text-transform:capitalize;">{{ (s.shop_type||'').replace(/_/g,' ') }}</td>
                <td>{{ s.county_name || '—' }}</td>
                <td>{{ s.product_count || 0 }}</td>
                <td><span class="adm-badge" :class="'adm-b-'+s.kyc_status">{{ s.kyc_status }}</span></td>
                <td class="adm-muted">{{ fD(s.created_at) }}</td>
                <td style="text-align:right;white-space:nowrap;">
                  <button v-if="s.kyc_status!=='verified'" class="btn btn-sm btn-outline-success" :disabled="agroShopActing===s.id" @click="verifyAgroShop(s,'verified')"><i class="mdi mdi-check-decagram me-1"></i>Verify</button>
                  <button v-if="s.kyc_status!=='rejected'" class="btn btn-sm btn-outline-danger ms-1" :disabled="agroShopActing===s.id" @click="verifyAgroShop(s,'rejected')"><i class="mdi mdi-close-octagon me-1"></i>Reject</button>
                  <button v-if="s.kyc_status!=='pending'" class="btn btn-sm btn-outline-secondary ms-1" :disabled="agroShopActing===s.id" @click="verifyAgroShop(s,'pending')" title="Reset to pending"><i class="mdi mdi-restore"></i></button>
                </td>
              </tr>
            </tbody></table>
            <div v-if="!agroShops.length" class="adm-empty">No agro shops{{ agroShopFilter ? ' with this status' : ' yet' }}</div>
          </div>
        </div>
      </div>

      <!-- ===== INSURANCE PROVIDERS (KYC review) ===== -->
      <div v-if="tab==='insproviders'">
        <div class="adm-card">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-shield-account me-2"></i>Insurance Companies ({{ insProviders.length }})</h6>
            <select v-model="insProviderFilter" class="adm-select" @change="loadInsProviders">
              <option value="">All statuses</option>
              <option value="pending">Pending review</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div v-if="insProviderLoading" class="text-center py-4"><div class="spinner-border spinner-border-sm text-warning"></div></div>
          <div v-else class="table-responsive">
            <table class="adm-table"><thead><tr>
              <th>Company</th><th>Owner</th><th>Contact</th><th>Products</th><th>KYC</th><th>Created</th><th style="text-align:right;">Action</th>
            </tr></thead><tbody>
              <tr v-for="p in insProviders" :key="p.id">
                <td><strong style="color:var(--fm-h,#F5F0E8);">{{ p.name }}</strong><div class="adm-muted" v-if="p.website_url">{{ p.website_url }}</div></td>
                <td>{{ p.owner_name || '—' }}<div class="adm-muted">{{ p.owner_phone }}</div></td>
                <td class="adm-muted">{{ p.contact_phone || p.contact_email || '—' }}</td>
                <td>{{ p.product_count || 0 }}</td>
                <td><span class="adm-badge" :class="'adm-b-'+p.kyc_status">{{ p.kyc_status }}</span></td>
                <td class="adm-muted">{{ fD(p.created_at) }}</td>
                <td style="text-align:right;white-space:nowrap;">
                  <button v-if="p.kyc_status!=='verified'" class="btn btn-sm btn-outline-success" :disabled="insProviderActing===p.id" @click="verifyInsProvider(p,'verified')"><i class="mdi mdi-check-decagram me-1"></i>Verify</button>
                  <button v-if="p.kyc_status!=='rejected'" class="btn btn-sm btn-outline-danger ms-1" :disabled="insProviderActing===p.id" @click="verifyInsProvider(p,'rejected')"><i class="mdi mdi-close-octagon me-1"></i>Reject</button>
                  <button v-if="p.kyc_status!=='pending'" class="btn btn-sm btn-outline-secondary ms-1" :disabled="insProviderActing===p.id" @click="verifyInsProvider(p,'pending')" title="Reset to pending"><i class="mdi mdi-restore"></i></button>
                </td>
              </tr>
            </tbody></table>
            <div v-if="!insProviders.length" class="adm-empty">No insurance companies{{ insProviderFilter ? ' with this status' : ' registered yet' }}</div>
          </div>
        </div>
      </div>

      <!-- ===== FINANCE PROVIDERS (KYC review) ===== -->
      <div v-if="tab==='finproviders'">
        <div class="adm-card">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-bank-outline me-2"></i>Finance Institutions ({{ finProviders.length }})</h6>
            <select v-model="finProviderFilter" class="adm-select" @change="loadFinProviders">
              <option value="">All statuses</option>
              <option value="pending">Pending review</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div v-if="finProviderLoading" class="text-center py-4"><div class="spinner-border spinner-border-sm text-warning"></div></div>
          <div v-else class="table-responsive">
            <table class="adm-table"><thead><tr>
              <th>Institution</th><th>Type</th><th>Owner</th><th>Contact</th><th>Products</th><th>Finance types</th><th>KYC</th><th>Created</th><th style="text-align:right;">Action</th>
            </tr></thead><tbody>
              <tr v-for="p in finProviders" :key="p.id">
                <td>
                  <strong style="color:var(--fm-h,#F5F0E8);">{{ p.name }}</strong>
                  <div class="adm-muted" v-if="p.short_name">{{ p.short_name }}</div>
                  <div class="adm-muted" v-if="p.website_url"><a :href="p.website_url" target="_blank" rel="noopener" style="color:#BFA164;">{{ p.website_url }}</a></div>
                  <div class="adm-muted" v-if="p.description" style="margin-top:4px;font-style:italic;">{{ p.description }}</div>
                </td>
                <td class="adm-muted">{{ finInstLabel(p.institution_type) }}</td>
                <td>{{ p.owner_name || '—' }}<div class="adm-muted">{{ p.owner_phone }}</div></td>
                <td class="adm-muted">
                  <div v-if="p.contact_phone">{{ p.contact_phone }}</div>
                  <div v-if="p.contact_email">{{ p.contact_email }}</div>
                </td>
                <td>{{ p.product_count || 0 }}</td>
                <td class="adm-muted">
                  <span v-for="t in parseFinTypes(p.finance_types)" :key="'ft-'+p.id+'-'+t" class="adm-badge adm-b-pending" style="margin-right:4px;">{{ finProductTypeLabel(t) }}</span>
                </td>
                <td>
                  <span class="adm-badge" :class="'adm-b-'+p.kyc_status">{{ p.kyc_status }}</span>
                  <div v-if="p.kyc_notes" class="adm-muted" style="margin-top:4px;max-width:200px;white-space:normal;font-size:11px;">{{ p.kyc_notes }}</div>
                </td>
                <td class="adm-muted">{{ fD(p.created_at) }}</td>
                <td style="text-align:right;white-space:nowrap;">
                  <button v-if="p.kyc_status!=='verified'" class="btn btn-sm btn-outline-success" :disabled="finProviderActing===p.id" @click="verifyFinProvider(p,'verified')"><i class="mdi mdi-check-decagram me-1"></i>Verify</button>
                  <button v-if="p.kyc_status!=='rejected'" class="btn btn-sm btn-outline-danger ms-1" :disabled="finProviderActing===p.id" @click="verifyFinProvider(p,'rejected')"><i class="mdi mdi-close-octagon me-1"></i>Reject</button>
                  <button v-if="p.kyc_status!=='pending'" class="btn btn-sm btn-outline-secondary ms-1" :disabled="finProviderActing===p.id" @click="verifyFinProvider(p,'pending')" title="Reset to pending"><i class="mdi mdi-restore"></i></button>
                </td>
              </tr>
            </tbody></table>
            <div v-if="!finProviders.length" class="adm-empty">No finance institutions{{ finProviderFilter ? ' with this status' : ' registered yet' }}</div>
          </div>
        </div>
      </div>

      <!-- ===== CORPORATE BUYERS (KYC review) ===== -->
      <div v-if="tab==='corporates'">
        <div class="adm-card">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h6 class="adm-card-title mb-0"><i class="mdi mdi-domain me-2"></i>Corporate Buyers ({{ corpAccounts.length }})</h6>
            <select v-model="corpFilter" class="adm-select" @change="loadCorporates">
              <option value="">All statuses</option>
              <option value="pending">Pending review</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div v-if="corpLoading" class="text-center py-4"><div class="spinner-border spinner-border-sm text-warning"></div></div>
          <div v-else class="table-responsive">
            <table class="adm-table"><thead><tr>
              <th>Company</th><th>Business type</th><th>Owner</th><th>Contact</th><th>Terms</th><th>KYC</th><th>Created</th><th style="text-align:right;">Action</th>
            </tr></thead><tbody>
              <tr v-for="c in corpAccounts" :key="c.id">
                <td>
                  <strong style="color:var(--fm-h,#F5F0E8);">{{ c.company_name }}</strong>
                  <div class="adm-muted" v-if="c.short_name">{{ c.short_name }}</div>
                  <div class="adm-muted" v-if="c.kra_pin">KRA: {{ c.kra_pin }}</div>
                </td>
                <td class="adm-muted" style="text-transform:capitalize;">{{ (c.business_type||'').replace(/_/g,' ') }}</td>
                <td>{{ c.owner_name || '—' }}<div class="adm-muted">{{ c.owner_phone }}</div></td>
                <td class="adm-muted">
                  <div v-if="c.contact_phone">{{ c.contact_phone }}</div>
                  <div v-if="c.contact_email">{{ c.contact_email }}</div>
                </td>
                <td class="adm-muted">
                  {{ c.default_payment_terms_days === 0 ? 'Cash' : (c.default_payment_terms_days + '-day') }}
                  <div v-if="c.default_payment_method" style="font-size:10px;">{{ c.default_payment_method }}</div>
                </td>
                <td>
                  <span class="adm-badge" :class="'adm-b-'+c.kyc_status">{{ c.kyc_status }}</span>
                  <div v-if="c.kyc_notes" class="adm-muted" style="margin-top:4px;max-width:200px;white-space:normal;font-size:11px;">{{ c.kyc_notes }}</div>
                </td>
                <td class="adm-muted">{{ fD(c.created_at) }}</td>
                <td style="text-align:right;white-space:nowrap;">
                  <button v-if="c.kyc_status!=='verified'" class="btn btn-sm btn-outline-success" :disabled="corpActing===c.id" @click="verifyCorp(c,'verified')"><i class="mdi mdi-check-decagram me-1"></i>Verify</button>
                  <button v-if="c.kyc_status!=='rejected'" class="btn btn-sm btn-outline-danger ms-1" :disabled="corpActing===c.id" @click="verifyCorp(c,'rejected')"><i class="mdi mdi-close-octagon me-1"></i>Reject</button>
                  <button v-if="c.kyc_status!=='pending'" class="btn btn-sm btn-outline-secondary ms-1" :disabled="corpActing===c.id" @click="verifyCorp(c,'pending')" title="Reset to pending"><i class="mdi mdi-restore"></i></button>
                </td>
              </tr>
            </tbody></table>
            <div v-if="!corpAccounts.length" class="adm-empty">No corporate buyers{{ corpFilter ? ' with this status' : ' registered yet' }}</div>
          </div>
        </div>
      </div>

      <!-- USER DETAIL MODAL (full view) -->
      <div v-if="userModal" class="adm-overlay" @click.self="userModal=false" style="overflow-y:auto;align-items:flex-start;padding:40px 20px;">
        <div style="width:100%;max-width:900px;margin:0 auto;">
          <button class="btn btn-sm btn-outline-secondary mb-3" @click="userModal=false"><i class="mdi mdi-close me-1"></i>Close</button>
          <div class="row g-4">
            <!-- LEFT: Profile + Stats -->
            <div class="col-lg-5">
              <!-- Profile Card -->
              <div class="adm-card mb-4">
                <div class="text-center mb-3">
                  <div class="adm-user-avatar-lg">{{ getInitials(selectedUser.display_name) }}</div>
                  <h5 style="color:var(--fm-h,#F5F0E8);font-weight:700;margin-top:10px;margin-bottom:2px;">{{ selectedUser.display_name }}</h5>
                  <div class="adm-muted">{{ selectedUser.phone }}</div>
                  <div class="d-flex justify-content-center gap-2 mt-2">
                    <span class="adm-badge" :class="selectedUser.role_type==='seller'?'adm-b-confirmed':selectedUser.role_type==='admin'?'adm-b-priority-urgent':'adm-b-info'">{{ selectedUser.role_type||'buyer' }}</span>
                    <span class="adm-badge" :class="selectedUser.status==='active'?'adm-b-completed':'adm-b-cancelled'">{{ selectedUser.status }}</span>
                  </div>
                </div>
                <div class="adm-rev-rows">
                  <div class="adm-rev-row"><span>Wallet Balance</span><strong style="color:#BFA164;">KES {{ fN(selectedUser.wallet_balance) }}</strong></div>
                  <div class="adm-rev-row"><span>Total Earned</span><strong style="color:#50C878;">KES {{ fN(selectedUser.total_earned) }}</strong></div>
                  <div class="adm-rev-row"><span>Total Withdrawn</span><strong>KES {{ fN(selectedUser.total_withdrawn) }}</strong></div>
                  <div class="adm-rev-row"><span>Orders</span><strong>{{ selectedUser.order_count||0 }}</strong></div>
                  <div class="adm-rev-row"><span>Joined</span><strong>{{ fD(selectedUser.created_at) }}</strong></div>
                  <div class="adm-rev-row"><span>Last Active</span><strong>{{ fD(selectedUser.updated_at) }}</strong></div>
                </div>
                <!-- Actions -->
                <div class="d-flex gap-2 mt-3 flex-wrap">
                  <button v-if="selectedUser.status==='active'" class="btn btn-sm btn-outline-danger flex-fill" @click="toggleUserStatus(selectedUser,'suspended')"><i class="mdi mdi-block-helper me-1"></i>Suspend</button>
                  <button v-else class="btn btn-sm btn-outline-success flex-fill" @click="toggleUserStatus(selectedUser,'active')"><i class="mdi mdi-check me-1"></i>Activate</button>
                  <button v-if="selectedUser.role_type==='seller'" class="btn btn-sm btn-outline-warning flex-fill" @click="$router.push('/marketplace/seller/'+selectedUser.id);userModal=false"><i class="mdi mdi-eye me-1"></i>Public Profile</button>
                </div>
              </div>

              <!-- Account Actions -->
              <div class="adm-card mb-4">
                <h6 class="adm-card-title"><i class="mdi mdi-cog me-2" style="color:#BFA164;"></i>Account Actions</h6>
                <div class="d-grid gap-2">
                  <button class="btn btn-sm btn-outline-warning" @click="resetUserPassword" :disabled="userActionBusy"><i class="mdi mdi-lock-reset me-1"></i>Reset Password &amp; notify</button>
                  <button class="btn btn-sm btn-outline-warning" @click="openSendMessage"><i class="mdi mdi-message-text me-1"></i>Send In-App Message</button>
                  <button class="btn btn-sm btn-outline-warning" @click="openSendEmail" :disabled="!selectedUser.email"><i class="mdi mdi-email-fast me-1"></i>Send Email</button>
                  <button class="btn btn-sm btn-outline-secondary" @click="openChatThread"><i class="mdi mdi-chat-processing me-1"></i>Open Chat Thread</button>
                </div>
              </div>

              <!-- Subscription (grant / view / end a comp trial) -->
              <div class="adm-card mb-4">
                <h6 class="adm-card-title d-flex justify-content-between align-items-center">
                  <span><i class="mdi mdi-star-circle me-2" style="color:#BFA164;"></i>Subscription</span>
                  <button class="btn btn-sm btn-outline-warning" @click="openGrantTrial" :disabled="userSubBusy" style="font-size:11px;padding:3px 10px;"><i class="mdi mdi-gift-outline me-1"></i>Grant trial</button>
                </h6>
                <div v-if="userSubLoading" class="text-center py-2"><div class="spinner-border spinner-border-sm text-warning"></div></div>
                <template v-else>
                  <div v-if="userSub && userSub.status==='active'" class="adm-rev-rows">
                    <div class="adm-rev-row"><span>Plan</span><strong style="color:#BFA164;">{{ userSub.plan_name }}</strong></div>
                    <div class="adm-rev-row"><span>Status</span><strong style="color:#50C878;text-transform:capitalize;">{{ userSub.status }}</strong></div>
                    <div class="adm-rev-row"><span>Amount</span><strong>{{ Number(userSub.amount)===0 ? 'FREE (comp)' : ('KES ' + fN(userSub.amount) + ' / ' + userSub.billing_cycle) }}</strong></div>
                    <div class="adm-rev-row"><span>Ends</span><strong>{{ fD(userSub.current_period_end) }}</strong></div>
                    <div v-if="userSub.cancellation_reason" style="font-size:10px;color:var(--fm-s,#6B6356);line-height:1.4;margin-top:6px;padding:6px 8px;background:var(--fm-bg2,#0F0F0F);border-radius:6px;">{{ userSub.cancellation_reason }}</div>
                    <button class="btn btn-sm btn-outline-danger w-100 mt-2" @click="endUserSub" :disabled="userSubBusy" style="font-size:11px;"><i class="mdi mdi-close-circle me-1"></i>End early</button>
                  </div>
                  <div v-else-if="userSub" class="adm-muted" style="font-size:12px;">
                    Last plan: <strong>{{ userSub.plan_name }}</strong> · <span style="text-transform:capitalize;">{{ userSub.status }}</span> · ended {{ fD(userSub.current_period_end) }}
                  </div>
                  <div v-else class="adm-muted" style="font-size:12px;">On the Free plan — no subscription history. Use "Grant trial" to comp them a higher tier for testing.</div>
                </template>
              </div>

              <!-- Roles & Permissions -->
              <div class="adm-card mb-4">
                <h6 class="adm-card-title"><i class="mdi mdi-shield-account me-2" style="color:#BFA164;"></i>Roles &amp; Permissions</h6>
                <div v-if="userRolesLoading" class="text-center py-3"><div class="spinner-border spinner-border-sm text-warning"></div></div>
                <div v-else>
                  <div v-for="r in roleCatalog" :key="r.v" class="d-flex align-items-center justify-content-between py-2" style="border-bottom:1px solid var(--fm-br,#1E1E1E);">
                    <div>
                      <div style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;">{{ r.l }}</div>
                      <div style="color:var(--fm-s,#6B6356);font-size:10px;">{{ r.desc }}</div>
                    </div>
                    <button v-if="hasRole(r.v)" class="btn btn-sm btn-outline-danger" @click="revokeRole(r.v)" :disabled="userActionBusy">
                      <i class="mdi mdi-minus me-1"></i>Revoke
                    </button>
                    <button v-else class="btn btn-sm btn-outline-success" @click="grantRole(r.v)" :disabled="userActionBusy">
                      <i class="mdi mdi-plus me-1"></i>Grant
                    </button>
                  </div>
                </div>
              </div>

              <!-- Module Access (admin RBAC) — visible only when target user holds admin or super_admin role -->
              <div v-if="hasRole('admin') || hasRole('super_admin')" class="adm-card mb-4">
                <h6 class="adm-card-title"><i class="mdi mdi-key-chain-variant me-2" style="color:#BFA164;"></i>Module Access</h6>
                <p style="color:var(--fm-s,#6B6356);font-size:11px;margin-bottom:12px;">
                  Pick which modules this admin can use. Super-admins implicitly have access to every module — these toggles only matter for plain admins.
                </p>
                <div v-if="!iAmSuperAdmin" class="text-warning" style="font-size:11px;background:rgba(255,171,0,.08);border:1px solid rgba(255,171,0,.25);border-radius:6px;padding:6px 10px;margin-bottom:10px;">
                  <i class="mdi mdi-lock me-1"></i>Read-only — only super-admins can change module access.
                </div>
                <div v-if="userPermissionsLoading" class="text-center py-3"><div class="spinner-border spinner-border-sm text-warning"></div></div>
                <div v-else>
                  <div v-for="m in moduleCatalogue" :key="m.key" class="d-flex align-items-center justify-content-between py-2" style="border-bottom:1px solid var(--fm-br,#1E1E1E);">
                    <div style="flex:1;min-width:0;">
                      <div style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;text-transform:capitalize;">{{ m.label }}</div>
                      <div style="color:var(--fm-s,#6B6356);font-size:10px;">{{ m.desc }}</div>
                    </div>
                    <span v-if="hasRole('super_admin')" class="ins-st ins-st-active" style="margin-left:8px;" title="Super-admin has every module by default">implicit</span>
                    <button v-else-if="hasModule(m.key)" class="btn btn-sm btn-outline-danger" :disabled="userActionBusy||!iAmSuperAdmin||m.key==='overview'" @click="revokeModulePermission(m.key)" :title="m.key==='overview' ? 'Overview is implicit for all admins' : ''">
                      <i class="mdi mdi-minus me-1"></i>Revoke
                    </button>
                    <button v-else class="btn btn-sm btn-outline-success" :disabled="userActionBusy||!iAmSuperAdmin" @click="grantModulePermission(m.key)">
                      <i class="mdi mdi-plus me-1"></i>Grant
                    </button>
                  </div>
                </div>
              </div>

              <!-- Seller Rating -->
              <div v-if="selectedUser.role_type==='seller' && userRatingStats" class="adm-card mb-4">
                <h6 class="adm-card-title"><i class="mdi mdi-star me-2" style="color:#FFD700;"></i>Seller Rating</h6>
                <div class="text-center mb-3">
                  <div style="font-size:36px;font-weight:700;color:#FFD700;">{{ Number(userRatingStats.average_rating||0).toFixed(1) }}</div>
                  <div class="adm-user-stars">
                    <i v-for="s in 5" :key="s" class="mdi" :class="s <= Math.round(userRatingStats.average_rating||0) ? 'mdi-star' : 'mdi-star-outline'" :style="{color: s <= Math.round(userRatingStats.average_rating||0) ? '#FFD700' : 'var(--fm-s,#3A3530)'}"></i>
                  </div>
                  <div class="adm-muted mt-1">{{ userRatingStats.total_reviews||0 }} reviews</div>
                </div>
              </div>

              <!-- Verifications -->
              <div v-if="selectedUser.role_type==='seller' && userVerifications.length" class="adm-card mb-4">
                <h6 class="adm-card-title"><i class="mdi mdi-shield-check me-2" style="color:#50C878;"></i>Verifications</h6>
                <div v-for="v in userVerifications" :key="v.id" class="adm-verif-item">
                  <i :class="'mdi '+verifIcon(v.verification_status)" :style="{color: verifColor(v.verification_status)}"></i>
                  <div>
                    <div style="color:var(--fm-h,#F5F0E8);font-size:12px;font-weight:600;">{{ (v.verification_type||'').replace(/_/g,' ') }}</div>
                    <div class="adm-muted">{{ v.verification_status }} · {{ fD(v.created_at) }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- RIGHT: Listings + Reviews + Orders -->
            <div class="col-lg-7">
              <!-- Seller Listings -->
              <div v-if="selectedUser.role_type==='seller'" class="adm-card mb-4">
                <h6 class="adm-card-title"><i class="mdi mdi-tag-multiple me-2"></i>Listings ({{ userListings.length }})</h6>
                <div v-if="userListingsLoading" class="text-center py-3"><div class="spinner-border spinner-border-sm text-warning"></div></div>
                <div v-else-if="userListings.length" class="adm-user-listing-grid">
                  <div v-for="l in userListings" :key="l.id" class="adm-user-lcard" @click="$router.push('/marketplace/listing/'+l.id);userModal=false">
                    <div class="adm-user-lcard-img">
                      <img v-if="l.primary_photo" :src="photoUrl(l.primary_photo)" />
                      <div v-else class="adm-user-lcard-noimg"><i class="mdi mdi-cow"></i></div>
                      <span class="adm-lcard-status" :class="'adm-b-'+l.status">{{ l.status }}</span>
                    </div>
                    <div class="adm-user-lcard-body">
                      <div style="color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">{{ l.title }}</div>
                      <div style="color:#BFA164;font-size:14px;font-weight:700;">KES {{ fN(l.price) }}</div>
                      <div class="adm-muted">{{ l.species_name }} · <i class="mdi mdi-eye" style="font-size:11px;"></i> {{ l.view_count||0 }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="adm-empty">No listings</div>
              </div>

              <!-- Reviews -->
              <div v-if="selectedUser.role_type==='seller'" class="adm-card mb-4">
                <h6 class="adm-card-title"><i class="mdi mdi-message-star me-2" style="color:#FFD700;"></i>Reviews ({{ userReviews.length }})</h6>
                <div v-if="userReviewsLoading" class="text-center py-3"><div class="spinner-border spinner-border-sm text-warning"></div></div>
                <div v-else-if="userReviews.length">
                  <div v-for="rev in userReviews" :key="rev.id" class="adm-review-item">
                    <div class="d-flex align-items-center justify-content-between mb-1">
                      <strong style="color:var(--fm-h,#F5F0E8);font-size:13px;">{{ rev.reviewer_name }}</strong>
                      <div><i v-for="s in 5" :key="s" class="mdi" :class="s <= rev.overall_rating ? 'mdi-star' : 'mdi-star-outline'" style="font-size:12px;" :style="{color: s <= rev.overall_rating ? '#FFD700' : 'var(--fm-s,#3A3530)'}"></i></div>
                    </div>
                    <div v-if="rev.review_title" style="color:#D4C9B8;font-size:12px;font-weight:600;">{{ rev.review_title }}</div>
                    <div style="color:var(--fm-m,#A09888);font-size:12px;line-height:1.5;">{{ rev.review_text }}</div>
                    <div class="adm-muted mt-1">{{ fD(rev.created_at) }}</div>
                  </div>
                </div>
                <div v-else class="adm-empty">No reviews yet</div>
              </div>

              <!-- Recent Orders -->
              <div class="adm-card">
                <h6 class="adm-card-title"><i class="mdi mdi-receipt me-2"></i>Recent Orders</h6>
                <div v-if="userOrders.length">
                  <div v-for="o in userOrders" :key="o.id" class="adm-user-order" @click="viewOrder(o.id);userModal=false">
                    <div>
                      <div class="adm-mono" style="font-size:11px;">{{ o.order_number }}</div>
                      <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">{{ o.listing_title || 'Order' }}</div>
                      <div class="adm-muted">{{ o.id === o.buyer_id ? 'Bought' : 'Sold' }} · {{ fD(o.created_at) }}</div>
                    </div>
                    <div class="text-end">
                      <div style="color:#BFA164;font-weight:700;">KES {{ fN(o.total_amount) }}</div>
                      <span class="adm-badge" :class="'adm-b-'+o.status">{{ o.status }}</span>
                    </div>
                  </div>
                </div>
                <div v-else class="adm-empty">No orders</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== SEND MESSAGE MODAL (in-app chat) ===== -->
      <div v-if="showMessageModal" class="adm-overlay" @click.self="showMessageModal=false">
        <div class="adm-modal" style="max-width:500px;">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;">
            <div>
              <div style="color:#BFA164;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;">Admin Action</div>
              <h5 style="color:var(--fm-h,#F5F0E8);margin:2px 0 0;font-size:17px;font-weight:700;">Message {{ selectedUser.display_name }}</h5>
            </div>
            <button style="background:transparent;border:none;color:var(--fm-m,#A09888);font-size:24px;cursor:pointer;" @click="showMessageModal=false">&times;</button>
          </div>
          <textarea v-model="messageDraft" rows="6" class="adm-input w-100" placeholder="Type your message... It will appear in the user's in-app inbox and trigger a push notification." />
          <div style="font-size:11px;color:var(--fm-s,#6B6356);margin-top:6px;">After sending, the conversation will appear in your /messages so you can keep chatting.</div>
          <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
            <button class="btn btn-sm btn-outline-secondary" @click="showMessageModal=false" :disabled="userActionBusy">Cancel</button>
            <button class="btn btn-sm btn-warning" @click="sendUserMessage" :disabled="userActionBusy||!messageDraft.trim()">
              <span v-if="userActionBusy" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="mdi mdi-send me-1"></i>Send
            </button>
          </div>
        </div>
      </div>

      <!-- ===== GRANT TRIAL SUBSCRIPTION MODAL ===== -->
      <div v-if="showGrantTrialModal" class="adm-overlay" @click.self="showGrantTrialModal=false">
        <div class="adm-modal" style="max-width:520px;">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;">
            <div>
              <div style="color:#BFA164;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;">Admin Action</div>
              <h5 style="color:var(--fm-h,#F5F0E8);margin:2px 0 0;font-size:17px;font-weight:700;">Grant trial to {{ selectedUser.display_name }}</h5>
              <div style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:2px;">Comps a paid plan (amount = 0) that expires on its own.</div>
            </div>
            <button style="background:transparent;border:none;color:var(--fm-m,#A09888);font-size:24px;cursor:pointer;" @click="showGrantTrialModal=false">&times;</button>
          </div>

          <label class="adm-lbl">Plan</label>
          <select v-model="grantTrialForm.planId" class="adm-input w-100">
            <option value="">— pick a plan —</option>
            <option v-for="p in grantTrialPlans" :key="p.id" :value="p.id">{{ p.name }} · KES {{ fN(p.price_monthly) }}/mo · {{ p.target_user_type }}</option>
          </select>

          <label class="adm-lbl mt-2">Duration (days)</label>
          <div class="d-flex gap-2 mb-1 flex-wrap">
            <button v-for="d in [7,14,30,60,90]" :key="d" class="btn btn-sm" :class="grantTrialForm.days==d?'btn-warning':'btn-outline-secondary'" style="font-size:12px;padding:3px 10px;" @click="grantTrialForm.days=d">{{ d }}d</button>
          </div>
          <input v-model.number="grantTrialForm.days" type="number" min="1" max="365" class="adm-input w-100" placeholder="Custom: 1 to 365 days" />

          <label class="adm-lbl mt-2">Reason <span style="color:#FF6B6B;">(required — audit log)</span></label>
          <textarea v-model="grantTrialForm.reason" class="adm-input w-100" rows="3" placeholder="e.g. Choice Meats requested more headroom while piloting the platform (Sept 2026)." />

          <div v-if="grantTrialForm.planId && grantTrialForm.days" style="background:rgba(80,200,120,.08);border:1px solid rgba(80,200,120,.3);border-radius:8px;padding:8px 12px;font-size:12px;color:#50C878;margin-top:12px;">
            <i class="mdi mdi-information-outline me-1"></i>
            Will activate <strong>{{ (grantTrialPlans.find(function(p){return p.id==grantTrialForm.planId})||{}).name }}</strong>, amount KES 0, ends <strong>{{ grantTrialEndsAt }}</strong>. Any current active sub gets cancelled and superseded.
          </div>

          <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
            <button class="btn btn-sm btn-outline-secondary" @click="showGrantTrialModal=false" :disabled="userSubBusy">Cancel</button>
            <button class="btn btn-sm btn-warning" @click="submitGrantTrial" :disabled="userSubBusy || !grantTrialForm.planId || !grantTrialForm.days || !grantTrialForm.reason.trim()">
              <span v-if="userSubBusy" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="mdi mdi-gift me-1"></i>Grant trial
            </button>
          </div>
        </div>
      </div>

      <!-- ===== SEND EMAIL MODAL ===== -->
      <div v-if="showEmailModal" class="adm-overlay" @click.self="showEmailModal=false">
        <div class="adm-modal" style="max-width:560px;">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;">
            <div>
              <div style="color:#BFA164;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;">Admin Action</div>
              <h5 style="color:var(--fm-h,#F5F0E8);margin:2px 0 0;font-size:17px;font-weight:700;">Email {{ selectedUser.display_name }}</h5>
              <div style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:2px;">{{ selectedUser.email }}</div>
            </div>
            <button style="background:transparent;border:none;color:var(--fm-m,#A09888);font-size:24px;cursor:pointer;" @click="showEmailModal=false">&times;</button>
          </div>
          <label class="adm-lbl">Subject</label>
          <input v-model="emailDraft.subject" class="adm-input w-100" placeholder="Subject line" />
          <label class="adm-lbl mt-2">Message</label>
          <textarea v-model="emailDraft.body" rows="8" class="adm-input w-100" placeholder="Compose your email. Plain text — line breaks are preserved. The Flockr branded shell + footer is added automatically." />
          <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
            <button class="btn btn-sm btn-outline-secondary" @click="showEmailModal=false" :disabled="userActionBusy">Cancel</button>
            <button class="btn btn-sm btn-warning" @click="sendUserEmail" :disabled="userActionBusy||!emailDraft.subject.trim()||!emailDraft.body.trim()">
              <span v-if="userActionBusy" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="mdi mdi-email-fast me-1"></i>Send Email
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== INSURANCE: Activate policy modal ===== -->
    <div v-if="activateModal" class="ins-mod-overlay" @click.self="activateModal=false">
      <div class="ins-mod-card" :style="themeCardStyle">
        <h6 class="adm-card-title"><i class="mdi mdi-check-decagram me-2" style="color:#50C878;"></i>Activate Policy</h6>
        <p style="color:var(--fm-s,#6B6356);font-size:12px;">Confirm the premium has been settled and the provider has issued cover. The user will be notified by SMS, email and in-app.</p>
        <div v-if="activatePolicy" class="ins-mod-summary">
          <div><span>Policy</span><strong style="font-family:monospace;">{{ activatePolicy.policy_number }}</strong></div>
          <div><span>User</span><strong>{{ activatePolicy.user_name }}</strong></div>
          <div><span>Provider</span><strong>{{ activatePolicy.provider_name }}</strong></div>
          <div><span>Cover</span><strong>KES {{ fN(activatePolicy.covered_value) }}</strong></div>
          <div><span>Premium</span><strong style="color:#BFA164;">KES {{ fN(activatePolicy.premium_amount) }}</strong></div>
        </div>
        <label class="adm-label">Provider's external policy number (optional)</label>
        <input v-model="activateForm.externalPolicyNumber" class="adm-input w-100" placeholder="e.g., APA-2026-12345" />
        <label class="adm-label">Admin note (optional)</label>
        <textarea v-model="activateForm.adminNote" class="adm-input w-100" rows="2" placeholder="e.g., Premium received via paybill ref ABC123"></textarea>
        <div v-if="activateError" class="text-danger" style="font-size:12px;">{{ activateError }}</div>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
          <button class="btn btn-sm btn-outline-secondary" @click="activateModal=false" :disabled="activateBusy">Cancel</button>
          <button class="btn btn-sm btn-success" @click="submitActivatePolicy" :disabled="activateBusy">
            <span v-if="activateBusy" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-shield-check me-1"></i>Activate Policy
          </button>
        </div>
      </div>
    </div>

    <!-- ===== INSURANCE: Cancel policy modal ===== -->
    <div v-if="cancelPolicyModal" class="ins-mod-overlay" @click.self="cancelPolicyModal=false">
      <div class="ins-mod-card" :style="themeCardStyle">
        <h6 class="adm-card-title"><i class="mdi mdi-close-octagon me-2" style="color:#FF6B6B;"></i>Cancel Policy</h6>
        <p style="color:var(--fm-s,#6B6356);font-size:12px;">User will be notified with the reason. Premiums already received are not auto-refunded — handle that on the wallet flow.</p>
        <div v-if="cancelPolicyTarget" class="ins-mod-summary">
          <div><span>Policy</span><strong style="font-family:monospace;">{{ cancelPolicyTarget.policy_number }}</strong></div>
          <div><span>User</span><strong>{{ cancelPolicyTarget.user_name }}</strong></div>
          <div><span>Cover</span><strong>KES {{ fN(cancelPolicyTarget.covered_value) }}</strong></div>
        </div>
        <label class="adm-label">Reason <span style="color:#FF6B6B;">*</span></label>
        <textarea v-model="cancelPolicyReason" class="adm-input w-100" rows="3" placeholder="Why is this policy being cancelled?"></textarea>
        <div v-if="cancelPolicyError" class="text-danger" style="font-size:12px;">{{ cancelPolicyError }}</div>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
          <button class="btn btn-sm btn-outline-secondary" @click="cancelPolicyModal=false" :disabled="cancelPolicyBusy">Back</button>
          <button class="btn btn-sm btn-danger" @click="submitCancelPolicy" :disabled="cancelPolicyBusy||!cancelPolicyReason.trim()">
            <span v-if="cancelPolicyBusy" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-close-octagon me-1"></i>Cancel Policy
          </button>
        </div>
      </div>
    </div>

    <!-- ===== INSURANCE: Claim review modal ===== -->
    <div v-if="claimDetailModal" class="ins-mod-overlay" @click.self="closeClaimDetail">
      <div class="ins-mod-card ins-mod-wide" :style="themeCardStyle">
        <h6 class="adm-card-title"><i class="mdi mdi-file-document-edit me-2" style="color:#BFA164;"></i>Claim Review</h6>

        <div v-if="claimDetailTarget" class="ins-mod-summary">
          <div><span>Claim</span><strong style="font-family:monospace;">{{ claimDetailTarget.claim_number }}</strong></div>
          <div><span>Policy</span><strong style="font-family:monospace;">{{ claimDetailTarget.policy_number }}</strong></div>
          <div><span>User</span><strong>{{ claimDetailTarget.user_name }} <span style="color:var(--fm-s,#6B6356);font-weight:400;">{{ claimDetailTarget.user_phone }}</span></strong></div>
          <div><span>Provider</span><strong>{{ claimDetailTarget.provider_name }}</strong></div>
          <div><span>Type</span><strong style="text-transform:capitalize;">{{ (claimDetailTarget.claim_type||'').replace('_',' ') }}</strong></div>
          <div><span>Incident</span><strong>{{ claimDetailTarget.incident_date }}</strong></div>
          <div><span>Cover</span><strong>KES {{ fN(claimDetailTarget.policy_cover) }}</strong></div>
          <div><span>Claimed</span><strong style="color:#BFA164;">KES {{ fN(claimDetailTarget.claimed_amount) }}</strong></div>
          <div><span>Status</span><strong style="text-transform:capitalize;">{{ (claimDetailTarget.status||'').replace('_',' ') }}</strong></div>
          <div v-if="claimDetailTarget.approved_amount"><span>Approved</span><strong style="color:#50C878;">KES {{ fN(claimDetailTarget.approved_amount) }}</strong></div>
          <div v-if="claimDetailTarget.payout_amount"><span>Paid</span><strong style="color:#50C878;">KES {{ fN(claimDetailTarget.payout_amount) }} ({{ claimDetailTarget.payout_method }})</strong></div>
        </div>

        <div v-if="claimDetailTarget" class="ins-mod-section">
          <strong>Incident description</strong>
          <p style="color:var(--fm-m,#A09888);font-size:13px;">{{ claimDetailTarget.incident_description }}</p>
        </div>

        <div v-if="claimDetailTarget && claimDetailTarget.review_notes" class="ins-mod-section" style="border-left:3px solid #BFA164;">
          <strong>Reviewer notes</strong>
          <p style="color:var(--fm-m,#A09888);font-size:13px;">{{ claimDetailTarget.review_notes }}</p>
        </div>
        <div v-if="claimDetailTarget && claimDetailTarget.rejection_reason" class="ins-mod-section" style="border-left:3px solid #FF6B6B;">
          <strong>Rejection reason</strong>
          <p style="color:var(--fm-m,#A09888);font-size:13px;">{{ claimDetailTarget.rejection_reason }}</p>
        </div>

        <!-- Action picker -->
        <div v-if="claimDetailTarget && claimDetailTarget.status!=='paid' && claimDetailTarget.status!=='withdrawn'" class="d-flex gap-2 flex-wrap mt-3 mb-2">
          <button class="btn btn-sm" :class="claimAction==='under_review'?'btn-warning':'btn-outline-warning'" @click="claimAction='under_review'" v-if="claimDetailTarget.status==='submitted'"><i class="mdi mdi-magnify me-1"></i>Mark Under Review</button>
          <button class="btn btn-sm" :class="claimAction==='approved'?'btn-success':'btn-outline-success'" @click="claimAction='approved'" v-if="['submitted','under_review'].indexOf(claimDetailTarget.status)>-1"><i class="mdi mdi-check-decagram me-1"></i>Approve</button>
          <button class="btn btn-sm" :class="claimAction==='rejected'?'btn-danger':'btn-outline-danger'" @click="claimAction='rejected'" v-if="['submitted','under_review'].indexOf(claimDetailTarget.status)>-1"><i class="mdi mdi-close-octagon me-1"></i>Reject</button>
          <button class="btn btn-sm" :class="claimAction==='paid'?'btn-success':'btn-outline-success'" @click="claimAction='paid'" v-if="claimDetailTarget.status==='approved'"><i class="mdi mdi-cash-fast me-1"></i>Mark Paid</button>
        </div>

        <div v-if="claimAction==='under_review'" class="ins-mod-action">
          <label class="adm-label">Reviewer notes (sent to user)</label>
          <textarea v-model="claimActionForm.reviewNotes" class="adm-input w-100" rows="2" placeholder="e.g., Awaiting vet report from KVB-licensed vet"></textarea>
        </div>

        <div v-if="claimAction==='approved'" class="ins-mod-action">
          <label class="adm-label">Approved amount (KES) <span style="color:#FF6B6B;">*</span></label>
          <input type="number" v-model.number="claimActionForm.approvedAmount" class="adm-input w-100" :placeholder="'Up to ' + fN(claimDetailTarget && claimDetailTarget.claimed_amount)" />
          <label class="adm-label mt-2">Notes (optional)</label>
          <textarea v-model="claimActionForm.reviewNotes" class="adm-input w-100" rows="2"></textarea>
        </div>

        <div v-if="claimAction==='rejected'" class="ins-mod-action">
          <label class="adm-label">Rejection reason <span style="color:#FF6B6B;">*</span></label>
          <textarea v-model="claimActionForm.rejectionReason" class="adm-input w-100" rows="3" placeholder="Required — user will see this exact reason"></textarea>
        </div>

        <div v-if="claimAction==='paid'" class="ins-mod-action">
          <label class="adm-label">Payout amount (KES) <span style="color:#FF6B6B;">*</span></label>
          <input type="number" v-model.number="claimActionForm.payoutAmount" class="adm-input w-100" :placeholder="'Suggested: ' + fN(claimDetailTarget && claimDetailTarget.approved_amount)" />
          <label class="adm-label mt-2">Method <span style="color:#FF6B6B;">*</span></label>
          <select v-model="claimActionForm.payoutMethod" class="adm-input w-100">
            <option value="mpesa">M-Pesa</option>
            <option value="bank_transfer">Bank transfer</option>
          </select>
          <label class="adm-label mt-2">Reference (M-Pesa code / bank ref)</label>
          <input v-model="claimActionForm.payoutReference" class="adm-input w-100" placeholder="e.g., QGT3K8L9MN" />
        </div>

        <div v-if="claimActionError" class="text-danger" style="font-size:12px;margin-top:8px;">{{ claimActionError }}</div>

        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
          <button class="btn btn-sm btn-outline-secondary" @click="closeClaimDetail" :disabled="claimActionBusy">Close</button>
          <button v-if="claimAction" class="btn btn-sm btn-warning" @click="submitClaimAction" :disabled="claimActionBusy">
            <span v-if="claimActionBusy" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-send me-1"></i>Submit &amp; Notify User
          </button>
        </div>
      </div>
    </div>

    <!-- ===== INSURANCE: Product modal ===== -->
    <div v-if="productModal" class="ins-mod-overlay" @click.self="productModal=false">
      <div class="ins-mod-card ins-mod-wide" :style="themeCardStyle">
        <h6 class="adm-card-title"><i class="mdi mdi-tag-multiple me-2"></i>{{ productForm.id ? 'Edit Product' : 'New Product' }}</h6>
        <div class="row g-2">
          <div class="col-md-6">
            <label class="adm-label">Provider <span style="color:#FF6B6B;">*</span></label>
            <select v-model="productForm.providerId" class="adm-input w-100">
              <option value="">— Select provider —</option>
              <option v-for="p in insuranceProviders" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="adm-label">Insurance type <span style="color:#FF6B6B;">*</span></label>
            <select v-model="productForm.insuranceType" class="adm-input w-100">
              <option value="livestock">Livestock</option>
              <option value="comprehensive">Comprehensive</option>
              <option value="transit">Transit</option>
              <option value="theft">Theft</option>
              <option value="pet">Pet</option>
              <option value="index_based">Index-based</option>
            </select>
          </div>
          <div class="col-12">
            <label class="adm-label">Product name <span style="color:#FF6B6B;">*</span></label>
            <input v-model="productForm.name" class="adm-input w-100" placeholder="e.g., APA Livestock Shield Plus" />
          </div>
          <div class="col-12">
            <label class="adm-label">Description</label>
            <textarea v-model="productForm.description" class="adm-input w-100" rows="2"></textarea>
          </div>
          <div class="col-md-6">
            <label class="adm-label">Min animal value (KES)</label>
            <input type="number" v-model.number="productForm.minAnimalValue" class="adm-input w-100" />
          </div>
          <div class="col-md-6">
            <label class="adm-label">Max animal value (KES)</label>
            <input type="number" v-model.number="productForm.maxAnimalValue" class="adm-input w-100" />
          </div>
          <div class="col-md-6">
            <label class="adm-label">Premium rate %</label>
            <input type="number" step="0.01" v-model.number="productForm.premiumRatePct" class="adm-input w-100" placeholder="e.g., 3.50" />
          </div>
          <div class="col-md-6">
            <label class="adm-label">Flat premium (overrides rate)</label>
            <input type="number" v-model.number="productForm.flatPremium" class="adm-input w-100" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Duration (months)</label>
            <input type="number" v-model.number="productForm.durationMonths" class="adm-input w-100" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Waiting period (days)</label>
            <input type="number" v-model.number="productForm.waitingPeriodDays" class="adm-input w-100" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Status</label>
            <select v-model.number="productForm.isActive" class="adm-input w-100">
              <option :value="1">Active</option>
              <option :value="0">Inactive (hidden from buyers)</option>
            </select>
          </div>
          <div class="col-12">
            <label class="adm-label">Coverage details JSON</label>
            <textarea v-model="productForm.coverageDetailsRaw" class="adm-input w-100" rows="3" style="font-family:monospace;font-size:12px;" placeholder='{"covers":["death","disease"],"excludes":["theft"],"max_payout_pct":100}'></textarea>
          </div>
          <div class="col-12">
            <label class="adm-label">Eligible species (JSON array of species IDs)</label>
            <input v-model="productForm.eligibleSpeciesRaw" class="adm-input w-100" style="font-family:monospace;" placeholder="[1,2,3,5]" />
          </div>
        </div>
        <div v-if="productError" class="text-danger" style="font-size:12px;margin-top:8px;">{{ productError }}</div>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
          <button class="btn btn-sm btn-outline-secondary" @click="productModal=false" :disabled="productBusy">Cancel</button>
          <button class="btn btn-sm btn-warning" @click="submitProduct" :disabled="productBusy||!productForm.providerId||!productForm.name">
            <span v-if="productBusy" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-content-save me-1"></i>Save Product
          </button>
        </div>
      </div>
    </div>

    <!-- ===== MARKET: Price modal ===== -->
    <div v-if="priceModal" class="ins-mod-overlay" @click.self="priceModal=false">
      <div class="ins-mod-card ins-mod-wide" :style="themeCardStyle">
        <h6 class="adm-card-title"><i class="mdi mdi-tag-multiple me-2"></i>{{ priceForm.id ? 'Edit Price' : 'New Price' }}</h6>
        <div class="row g-2">
          <div class="col-md-4">
            <label class="adm-label">Species <span style="color:#FF6B6B;">*</span></label>
            <select v-model="priceForm.speciesId" class="adm-input w-100">
              <option value="">Select</option>
              <option v-for="s in marketSpecies" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="adm-label">Breed (optional)</label>
            <input v-model="priceForm.breedId" class="adm-input w-100" placeholder="Breed ID" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">County <span style="color:#FF6B6B;">*</span></label>
            <select v-model="priceForm.countyId" class="adm-input w-100">
              <option value="">Select</option>
              <option v-for="c in marketCounties" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="adm-label">Avg price (KES) <span style="color:#FF6B6B;">*</span></label>
            <input type="number" v-model.number="priceForm.avgPrice" class="adm-input w-100" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Min price</label>
            <input type="number" v-model.number="priceForm.minPrice" class="adm-input w-100" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Max price</label>
            <input type="number" v-model.number="priceForm.maxPrice" class="adm-input w-100" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Unit</label>
            <select v-model="priceForm.priceUnit" class="adm-input w-100">
              <option value="per_animal">per animal</option>
              <option value="per_kg">per kg</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="adm-label">Sample size (txns)</label>
            <input type="number" v-model.number="priceForm.sampleSize" class="adm-input w-100" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Total volume</label>
            <input type="number" v-model.number="priceForm.totalVolume" class="adm-input w-100" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Source</label>
            <select v-model="priceForm.source" class="adm-input w-100">
              <option value="platform_transactions">Platform transactions</option>
              <option value="market_survey">Market survey</option>
              <option value="government_data">Government data</option>
              <option value="aggregated">Aggregated</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="adm-label">Record date <span style="color:#FF6B6B;">*</span></label>
            <input type="date" v-model="priceForm.recordDate" class="adm-input w-100" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Period</label>
            <select v-model="priceForm.periodType" class="adm-input w-100">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>
        <div v-if="priceError" class="text-danger" style="font-size:12px;margin-top:8px;">{{ priceError }}</div>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
          <button class="btn btn-sm btn-outline-secondary" @click="priceModal=false" :disabled="priceBusy">Cancel</button>
          <button class="btn btn-sm btn-warning" @click="submitPrice" :disabled="priceBusy||!priceForm.speciesId||!priceForm.countyId||!priceForm.avgPrice">
            <span v-if="priceBusy" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-content-save me-1"></i>Save Price
          </button>
        </div>
      </div>
    </div>

    <!-- ===== MARKET: Bulk import modal ===== -->
    <div v-if="bulkPriceModal" class="ins-mod-overlay" @click.self="bulkPriceModal=false">
      <div class="ins-mod-card ins-mod-wide" :style="themeCardStyle">
        <h6 class="adm-card-title"><i class="mdi mdi-table-large me-2"></i>Bulk Import Market Prices</h6>
        <p style="color:var(--fm-s,#6B6356);font-size:12px;">
          Paste tab-separated rows (copied from a spreadsheet). Columns in this order:
          <code>speciesId, countyId, avgPrice, breedId, minPrice, maxPrice, priceUnit, sampleSize, totalVolume, source, periodType, recordDate</code>.
          Required: speciesId, countyId, avgPrice. Leave optional cells empty (just \t\t).
        </p>
        <textarea v-model="bulkPriceText" class="adm-input w-100" rows="10" style="font-family:monospace;font-size:12px;" placeholder="1\t1\t65000\t\t40000\t120000\tper_animal\t45\t52\tmarket_survey\tweekly\t2026-04-25"></textarea>
        <div v-if="bulkPriceResult" :style="{color: bulkPriceResult.success ? '#50C878' : '#FF6B6B', fontSize:'12px', marginTop:'6px'}">
          <i :class="bulkPriceResult.success?'mdi mdi-check-circle':'mdi mdi-alert-circle'"></i>
          {{ bulkPriceResult.message }}
        </div>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
          <button class="btn btn-sm btn-outline-secondary" @click="bulkPriceModal=false" :disabled="bulkPriceBusy">Close</button>
          <button class="btn btn-sm btn-warning" @click="submitBulkPrices" :disabled="bulkPriceBusy||!bulkPriceText.trim()">
            <span v-if="bulkPriceBusy" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-upload me-1"></i>Import Rows
          </button>
        </div>
      </div>
    </div>

    <!-- ===== MARKET: Report modal ===== -->
    <div v-if="reportModal" class="ins-mod-overlay" @click.self="reportModal=false">
      <div class="ins-mod-card ins-mod-wide" :style="themeCardStyle" style="max-width:800px;">
        <h6 class="adm-card-title"><i class="mdi mdi-file-document-edit me-2"></i>{{ reportForm.id ? 'Edit Report' : 'New Report' }}</h6>
        <div class="row g-2">
          <div class="col-md-8">
            <label class="adm-label">Title <span style="color:#FF6B6B;">*</span></label>
            <input v-model="reportForm.title" class="adm-input w-100" placeholder="e.g., April 2026 livestock market round-up" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Type</label>
            <select v-model="reportForm.reportType" class="adm-input w-100">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
              <option value="special">Special</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="adm-label">Slug (auto if blank)</label>
            <input v-model="reportForm.slug" class="adm-input w-100" placeholder="april-2026-roundup" style="font-family:monospace;" />
          </div>
          <div class="col-md-6">
            <label class="adm-label">Report date <span style="color:#FF6B6B;">*</span></label>
            <input type="date" v-model="reportForm.reportDate" class="adm-input w-100" />
          </div>
          <div class="col-md-6">
            <label class="adm-label">County (blank = national)</label>
            <select v-model="reportForm.countyId" class="adm-input w-100">
              <option value="">National</option>
              <option v-for="c in marketCounties" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="adm-label">Species (blank = all)</label>
            <select v-model="reportForm.speciesId" class="adm-input w-100">
              <option value="">All species</option>
              <option v-for="s in marketSpecies" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="adm-label">Cover image URL</label>
            <input v-model="reportForm.coverImageUrl" class="adm-input w-100" placeholder="https:// or uploads/path" />
          </div>
          <div class="col-md-6">
            <label class="adm-label">PDF URL (optional)</label>
            <input v-model="reportForm.pdfUrl" class="adm-input w-100" placeholder="https:// or uploads/path" />
          </div>
          <div class="col-12">
            <label class="adm-label">Content (HTML supported)</label>
            <textarea v-model="reportForm.content" class="adm-input w-100" rows="10" style="font-family:monospace;font-size:12px;" placeholder="<p>Cattle prices in Nairobi rose 5% this week...</p>"></textarea>
            <div style="font-size:10px;color:var(--fm-s,#6B6356);margin-top:2px;">Supports inline HTML — use h2/h3, p, ul/li, blockquote, table, img.</div>
          </div>
          <div class="col-md-4">
            <label class="adm-label">Status</label>
            <select v-model="reportForm.status" class="adm-input w-100">
              <option value="draft">Draft (hidden)</option>
              <option value="published">Published (notifies all users)</option>
            </select>
          </div>
        </div>
        <div v-if="reportError" class="text-danger" style="font-size:12px;margin-top:8px;">{{ reportError }}</div>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
          <button class="btn btn-sm btn-outline-secondary" @click="reportModal=false" :disabled="reportBusy">Cancel</button>
          <button class="btn btn-sm btn-warning" @click="submitReport" :disabled="reportBusy||!reportForm.title||!reportForm.reportDate">
            <span v-if="reportBusy" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-content-save me-1"></i>{{ reportForm.status === 'published' ? 'Save & Publish' : 'Save Draft' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ===== COMMUNITY: Article modal ===== -->
    <div v-if="articleModal" class="ins-mod-overlay" @click.self="articleModal=false">
      <div class="ins-mod-card ins-mod-wide" :style="themeCardStyle" style="max-width:800px;">
        <h6 class="adm-card-title"><i class="mdi mdi-book-edit me-2"></i>{{ articleForm.id ? 'Edit Article' : 'New Article' }}</h6>
        <div class="row g-2">
          <div class="col-md-8">
            <label class="adm-label">Title <span style="color:#FF6B6B;">*</span></label>
            <input v-model="articleForm.title" class="adm-input w-100" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Slug (auto if blank)</label>
            <input v-model="articleForm.slug" class="adm-input w-100" style="font-family:monospace;" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Category</label>
            <select v-model="articleForm.categoryId" class="adm-input w-100">
              <option value="">— None —</option>
              <option v-for="c in communityCategories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="adm-label">Audience</label>
            <select v-model="articleForm.targetAudience" class="adm-input w-100">
              <option value="all">All</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="adm-label">Language</label>
            <select v-model="articleForm.language" class="adm-input w-100">
              <option value="en">English</option>
              <option value="sw">Kiswahili</option>
            </select>
          </div>
          <div class="col-12">
            <label class="adm-label">Excerpt</label>
            <input v-model="articleForm.excerpt" class="adm-input w-100" placeholder="One-line teaser shown on the card" />
          </div>
          <div class="col-md-8">
            <label class="adm-label">Cover image URL</label>
            <input v-model="articleForm.coverImageUrl" class="adm-input w-100" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Featured?</label>
            <select v-model.number="articleForm.isFeatured" class="adm-input w-100">
              <option :value="0">No</option>
              <option :value="1">Yes (top of Learning Hub)</option>
            </select>
          </div>
          <div class="col-12">
            <label class="adm-label">Content (HTML or markdown-ish)</label>
            <textarea v-model="articleForm.content" class="adm-input w-100" rows="12" style="font-family:monospace;font-size:12px;" placeholder="Use ## for headings, blank lines between paragraphs, or full HTML."></textarea>
          </div>
          <div class="col-md-4">
            <label class="adm-label">Status</label>
            <select v-model="articleForm.status" class="adm-input w-100">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="adm-label">Tags (JSON)</label>
            <input v-model="articleForm.tags" class="adm-input w-100" style="font-family:monospace;" placeholder='["dairy","feeding"]' />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Target species (JSON)</label>
            <input v-model="articleForm.targetSpecies" class="adm-input w-100" style="font-family:monospace;" placeholder="[1,7]" />
          </div>
        </div>
        <div v-if="articleError" class="text-danger" style="font-size:12px;margin-top:8px;">{{ articleError }}</div>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
          <button class="btn btn-sm btn-outline-secondary" @click="articleModal=false" :disabled="articleBusy">Cancel</button>
          <button class="btn btn-sm btn-warning" @click="submitArticle" :disabled="articleBusy||!articleForm.title||!articleForm.content">
            <span v-if="articleBusy" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-content-save me-1"></i>Save Article
          </button>
        </div>
      </div>
    </div>

    <!-- ===== COMMUNITY: Category modal ===== -->
    <div v-if="categoryModal" class="ins-mod-overlay" @click.self="categoryModal=false">
      <div class="ins-mod-card" :style="themeCardStyle">
        <h6 class="adm-card-title"><i class="mdi mdi-tag-edit me-2"></i>{{ categoryForm.id ? 'Edit Category' : 'New Category' }}</h6>
        <div class="row g-2">
          <div class="col-md-8">
            <label class="adm-label">Name <span style="color:#FF6B6B;">*</span></label>
            <input v-model="categoryForm.name" class="adm-input w-100" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Sort</label>
            <input type="number" v-model.number="categoryForm.sortOrder" class="adm-input w-100" />
          </div>
          <div class="col-md-6">
            <label class="adm-label">Slug (auto if blank)</label>
            <input v-model="categoryForm.slug" class="adm-input w-100" style="font-family:monospace;" />
          </div>
          <div class="col-md-6">
            <label class="adm-label">Icon (mdi-* class)</label>
            <input v-model="categoryForm.icon" class="adm-input w-100" placeholder="mdi-cow" />
          </div>
          <div class="col-12">
            <label class="adm-label">Description</label>
            <textarea v-model="categoryForm.description" class="adm-input w-100" rows="2"></textarea>
          </div>
          <div class="col-12">
            <label class="adm-label">Status</label>
            <select v-model.number="categoryForm.isActive" class="adm-input w-100">
              <option :value="1">Active</option>
              <option :value="0">Hidden</option>
            </select>
          </div>
        </div>
        <div v-if="categoryError" class="text-danger" style="font-size:12px;margin-top:8px;">{{ categoryError }}</div>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
          <button class="btn btn-sm btn-outline-secondary" @click="categoryModal=false" :disabled="categoryBusy">Cancel</button>
          <button class="btn btn-sm btn-warning" @click="submitCategory" :disabled="categoryBusy||!categoryForm.name">
            <span v-if="categoryBusy" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-content-save me-1"></i>Save Category
          </button>
        </div>
      </div>
    </div>

    <!-- ===== INSURANCE: Provider modal ===== -->
    <div v-if="providerModal" class="ins-mod-overlay" @click.self="providerModal=false">
      <div class="ins-mod-card" :style="themeCardStyle">
        <h6 class="adm-card-title"><i class="mdi mdi-domain me-2"></i>{{ providerForm.id ? 'Edit Provider' : 'New Provider' }}</h6>
        <div class="row g-2">
          <div class="col-md-8">
            <label class="adm-label">Name <span style="color:#FF6B6B;">*</span></label>
            <input v-model="providerForm.name" class="adm-input w-100" placeholder="e.g., APA Insurance" />
          </div>
          <div class="col-md-4">
            <label class="adm-label">Short name</label>
            <input v-model="providerForm.shortName" class="adm-input w-100" placeholder="APA" />
          </div>
          <div class="col-md-6">
            <label class="adm-label">Contact email</label>
            <input v-model="providerForm.contactEmail" class="adm-input w-100" />
          </div>
          <div class="col-md-6">
            <label class="adm-label">Contact phone</label>
            <input v-model="providerForm.contactPhone" class="adm-input w-100" />
          </div>
          <div class="col-12">
            <label class="adm-label">Website</label>
            <input v-model="providerForm.websiteUrl" class="adm-input w-100" placeholder="https://..." />
          </div>
          <div class="col-12">
            <label class="adm-label">Logo URL</label>
            <input v-model="providerForm.logoUrl" class="adm-input w-100" />
          </div>
          <div class="col-12">
            <label class="adm-label">Insurance types (JSON array)</label>
            <input v-model="providerForm.insuranceTypesRaw" class="adm-input w-100" style="font-family:monospace;" placeholder='["livestock","transit","comprehensive"]' />
          </div>
          <div class="col-12">
            <label class="adm-label">Status</label>
            <select v-model.number="providerForm.isActive" class="adm-input w-100">
              <option :value="1">Active</option>
              <option :value="0">Inactive</option>
            </select>
          </div>
        </div>

        <!-- ===== M-Pesa configuration ===== -->
        <div class="ins-mod-section" style="margin-top:16px;border-left:3px solid #50C878;">
          <strong style="display:flex;align-items:center;justify-content:space-between;">
            <span><i class="mdi mdi-cellphone-message me-1" style="color:#50C878;"></i>M-Pesa Premium Collection</span>
          </strong>
          <p style="color:var(--fm-s,#6B6356);font-size:11px;margin:6px 0 12px;">
            Choose how the buyer's premium reaches this provider: ride the Flockr platform paybill (and Flockr remits), or use the provider's own Daraja credentials so funds land directly with them.
          </p>

          <label class="adm-label">Settlement</label>
          <select v-model.number="providerForm.usePlatformMpesa" class="adm-input w-100">
            <option :value="1">Use Flockr platform M-Pesa (Flockr remits)</option>
            <option :value="0">Use this provider's own M-Pesa credentials (direct settlement)</option>
          </select>

          <div v-if="providerForm.usePlatformMpesa===0" class="row g-2 mt-2">
            <div class="col-md-6">
              <label class="adm-label">Shortcode (paybill / till) <span style="color:#FF6B6B;">*</span></label>
              <input v-model="providerForm.mpesaShortcode" class="adm-input w-100" placeholder="e.g., 247247" />
            </div>
            <div class="col-md-6">
              <label class="adm-label">Type</label>
              <select v-model="providerForm.mpesaShortcodeType" class="adm-input w-100">
                <option value="paybill">Paybill</option>
                <option value="till">Till (Buy Goods)</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="adm-label">Environment</label>
              <select v-model="providerForm.mpesaEnvironment" class="adm-input w-100">
                <option value="production">Production</option>
                <option value="sandbox">Sandbox</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="adm-label">Account ref template</label>
              <input v-model="providerForm.accountRefTemplate" class="adm-input w-100" placeholder="{policyNumber}" />
              <div style="font-size:10px;color:var(--fm-s,#6B6356);margin-top:2px;">{policyNumber} is replaced at STK time. Capped at 12 chars.</div>
            </div>
            <div class="col-12 d-flex align-items-center gap-2 mt-2">
              <label class="adm-label mb-0">Daraja credentials</label>
              <button type="button" class="btn btn-sm btn-link p-0" style="font-size:11px;text-decoration:none;" @click="providerSecretsRevealed=!providerSecretsRevealed">
                <i :class="providerSecretsRevealed?'mdi mdi-eye-off':'mdi mdi-eye'"></i>
                {{ providerSecretsRevealed ? 'Hide' : 'Show' }}
              </button>
              <span v-if="providerForm.id" style="font-size:10px;color:var(--fm-s,#6B6356);">Leave a field blank to keep the existing value.</span>
            </div>
            <div class="col-md-6">
              <label class="adm-label">Consumer key</label>
              <input :type="providerSecretsRevealed?'text':'password'" v-model="providerForm.mpesaConsumerKey" class="adm-input w-100" autocomplete="off" />
            </div>
            <div class="col-md-6">
              <label class="adm-label">Consumer secret</label>
              <input :type="providerSecretsRevealed?'text':'password'" v-model="providerForm.mpesaConsumerSecret" class="adm-input w-100" autocomplete="off" />
            </div>
            <div class="col-12">
              <label class="adm-label">STK passkey (LipaNaMpesaOnline)</label>
              <input :type="providerSecretsRevealed?'text':'password'" v-model="providerForm.mpesaPasskey" class="adm-input w-100" autocomplete="off" />
            </div>
          </div>

          <!-- Test STK -->
          <div v-if="providerForm.id" class="ins-mod-action">
            <label class="adm-label">Test the configuration</label>
            <div class="d-flex gap-2 align-items-center flex-wrap">
              <input v-model="providerTestPhone" class="adm-input" :placeholder="'Phone (e.g., 07XX XXX XXX)'" style="min-width:200px;" />
              <button class="btn btn-sm btn-success" @click="testProviderMpesa" :disabled="providerTestBusy||!providerTestPhone">
                <span v-if="providerTestBusy" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="mdi mdi-cellphone-arrow-down me-1"></i>Send 1 KES Test STK
              </button>
            </div>
            <div v-if="providerTestResult" :style="{color: providerTestResult.success ? '#50C878' : '#FF6B6B', fontSize:'12px', marginTop:'6px'}">
              <i :class="providerTestResult.success?'mdi mdi-check-circle':'mdi mdi-alert-circle'"></i>
              {{ providerTestResult.message }}
            </div>
            <div style="font-size:10px;color:var(--fm-s,#6B6356);margin-top:4px;">Sends a 1 KES STK push using the credentials currently saved on the server. Save first if you've changed any field.</div>
          </div>
        </div>

        <div v-if="providerError" class="text-danger" style="font-size:12px;margin-top:8px;">{{ providerError }}</div>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
          <button class="btn btn-sm btn-outline-secondary" @click="providerModal=false" :disabled="providerBusy">Cancel</button>
          <button class="btn btn-sm btn-warning" @click="submitProvider" :disabled="providerBusy||!providerForm.name">
            <span v-if="providerBusy" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-content-save me-1"></i>Save Provider
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import { execute, executeJson, flocker, uploads } from "@/api";
export default {
  name: "AdminDashboard",
  data() {
    return {
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',

      tab: 'overview', loading: false,
      adminName: sessionStorage.getItem('sessionNames') || 'Admin',
      myAdminId: sessionStorage.getItem('userId') || '',

      // Maker-checker approvals (Issue 4)
      mcActions: [], mcLoading: false, mcFilter: 'pending', mcPendingCount: 0, mcExpandedId: null,
      mcModal: { show:false, mode:'approve', action:null, notes:'', busy:false },

      // User management
      userRoles: [], userRolesLoading: false, userActionBusy: false,
      // Admin module permissions (RBAC) — for the user currently open in the modal
      userModules: [], userPermissionsLoading: false, moduleCatalogue: [],
      // Team tab — bird's-eye view of every admin and their module coverage
      team: [], teamLoading: false, teamSearch: '', teamCatalogue: [], teamTogglingKey: '',
      // Market admin
      marketSubTab: 'days',
      marketDays: [], marketDayModal: false, marketDayBusy: false, marketDayError: '', marketDayDeleteTarget: null, mdExcelInfo: '',
      mdForm: { id:'', marketName:'', countyId:'', reportDate:'', notes:'', status:'published', totalsOverride:false, movements:[], totals:[], prices:[] },
      expertInsights: [], loadingExpertInsights: false,
      speciesList: [], countiesList: [],
      insightModal: { open:false, editing:false, id:'', title:'', body:'', speciesId:'', countyId:'', authorName:'', authorRole:'', isPinned:false, isPublished:true },
      marketPrices: [], marketReports: [], marketLoading: false,
      marketSpecies: [], marketCounties: [],
      marketPriceFilter: { speciesId:'', countyId:'' },
      priceModal: false, priceForm: { id:'', speciesId:'', breedId:'', countyId:'', avgPrice:null, minPrice:null, maxPrice:null, priceUnit:'per_animal', sampleSize:0, totalVolume:null, source:'platform_transactions', recordDate:'', periodType:'weekly' }, priceBusy: false, priceError: '',
      bulkPriceModal: false, bulkPriceText: '', bulkPriceBusy: false, bulkPriceResult: null,
      reportModal: false, reportForm: { id:'', title:'', slug:'', reportType:'weekly', content:'', coverImageUrl:'', reportDate:'', countyId:'', speciesId:'', pdfUrl:'', status:'draft' }, reportBusy: false, reportError: '',
      // Community admin
      communitySubTab: 'articles',
      communityArticles: [], communityForumTopics: [], communityCategories: [],
      communityLoading: false,
      articleModal: false, articleForm: { id:'', categoryId:'', title:'', slug:'', excerpt:'', content:'', coverImageUrl:'', language:'en', targetAudience:'all', status:'draft', isFeatured:0, tags:'[]', targetSpecies:'[]' }, articleBusy: false, articleError: '',
      categoryModal: false, categoryForm: { id:'', name:'', slug:'', description:'', icon:'', sortOrder:0, isActive:1 }, categoryBusy: false, categoryError: '',
      showMessageModal: false, showEmailModal: false,
      // Admin trial-grant module
      userSub: null, userSubLoading: false, userSubBusy: false,
      showGrantTrialModal: false, grantTrialPlans: [],
      grantTrialForm: { planId: '', days: 30, reason: '' },
      messageDraft: '', emailDraft: { subject:'', body:'' },
      roleCatalog: [
        { v:'farmer',         l:'Farmer',          desc:'Run a farm, log animals, request consultations & transport' },
        { v:'buyer',          l:'Buyer',           desc:'Browse marketplace, place orders, raise buy requests' },
        { v:'seller',         l:'Seller',          desc:'List animals on marketplace, receive offers' },
        { v:'aggregator',     l:'Aggregator',      desc:'Manage supplier networks + post bulk buy requests' },
        { v:'vet',            l:'Vet',             desc:'Receive consultation bookings, manage prescriptions' },
        { v:'transporter',    l:'Transporter',     desc:'Receive transport jobs, manage fleet' },
        { v:'product_seller', l:'Product Seller',  desc:'Sell agro-inputs, feeds, drugs in product marketplace' },
        { v:'admin',          l:'Admin',           desc:'Platform-wide moderation. Super admins only.' },
        { v:'super_admin',    l:'Super Admin',     desc:'Top-level access. Can grant/revoke other admins.' }
      ],

      // Transport admin
      transports: [], transportStats: {}, transportsLoading: false,
      transportSubTab: 'bookings',
      allTransporters: [], loadingTransporters: false, transporterSearch: '', transporterStatusFilter: '', verifyingId: null,
      transportFilter: { status:'', paymentStatus:'', dateFrom:'', dateTo:'', search:'' },
      showArrangeModal: false, arranging: false, arrangeError: '',
      arrange: { onBehalfOfUserId:'', buyerSearch:'', buyerMatches:[], transporterUserId:'', transporterSearch:'', transporterMatches:[], orderId:'', animalCount:1, animalTypeDescription:'', estimatedTotalWeightKg:null, pickupAddress:'', pickupLatitude:null, pickupLongitude:null, pickupMatches:[], pickupLandmark:'', pickupContactName:'', pickupContactPhone:'', deliveryAddress:'', deliveryLatitude:null, deliveryLongitude:null, deliveryMatches:[], deliveryContactName:'', deliveryContactPhone:'', scheduledPickupDate:'', scheduledPickupTime:'', quotedPrice:null, specialInstructions:'', adminNotes:'' },
      // Overview
      stats: {}, revenue: { gmv:0, platformFees:0, transportRevenue:0, insurance:0, totalRevenue:0, sellerPayouts:0, inEscrow:0, totalWalletBalance:0, totalWithdrawn:0 },
      recentOrders: [], newUsers: [],
      // Orders
      allOrders: [], orderFilter: '', orderSearch: '', orderTransportFilter: '',
      overrideOrder: null, overrideNewStatus: '', overrideNotes: '', overriding: false,
      adminPlans: [], plansLoading: false,
      planEditor: { open: false, saving: false, form: { id:null, name:'', slug:'', description:'', targetUserType:'any', priceMonthly:0, priceAnnually:'', currency:'KES', maxListings:'', maxPhotosPerListing:5, commissionRatePct:'', isActive:true, isPopular:false, sortOrder:99, featuresJson:'{}' } },
      orderStatuses: ['pending_payment','payment_initiated','stk_sent','paid','confirmed','pending_seller_confirmation','in_transit','delivered','inspection','completed','cancelled'],
      // Users
      allUsers: [], userFilter: '', userSearch: '',
      // Dev-Requests #8 — user-type tab filter (farmer/buyer/seller/vet/…)
      userTypeTab: '',
      // Dev-Requests #9/#10/#11 — referrals sub-tab state
      referralMetrics: { totalReferrals:0, activeUsers:0, usersWhoListed:0, usersWhoTransacted:0 },
      referralCodes: [], referralSummary: [], referralLoading: false,
      referralFilter: { dateFrom:'', dateTo:'' },
      referralDrill: { open:false, bucket:'total', users:[], loading:false },
      analyticsDrill: { open:false, metric:'', title:'', cols:[], rows:[], loading:false },
      // Credit Passport tab (Alex 2026-07-23)
      creditUsers: [], creditDist: [],
      // Analytics tab (Alex 2026-07-21)
      analyticsDays: 30,
      analyticsBusy: false,
      analytics: { kpis: { dau:0, wau:0, mau:0, total_users:0, active_listings:0, window: { new_signups:0, listings_created:0, listing_views:0, orders_placed:0, gmv_kes:0 } }, series: [] },
      analyticsRanges: [ {days:7,label:'7d'}, {days:30,label:'30d'}, {days:90,label:'90d'}, {days:180,label:'180d'} ],
      chartW: 800, chartH: 220,
      chartPadL: 48, chartPadR: 16, chartPadT: 10, chartPadB: 34,
      chartHover: { show:false, i:-1, left:0 },
      chartSeriesOn: { active_users:true, new_signups:true, listings_created:true, orders_placed:true, listing_views:false, gmv_kes:false },
      funnel: { stages: [] },
      topListings: [],
      topActions: [],
      // Field Agents — referral-driven onboarding (Governor program 2026-06-28)
      userSubTab: 'all',
      agents: [], agentsLoading: false, agentSearch: '',
      showCreateAgent: false, creatingAgent: false,
      newAgent: { firstName:'', lastName:'', phone:'', email:'', region:'' },
      agentCreated: null,
      // Finance
      allWithdrawals: [], pendingWithdrawals: [], escrowTxns: [],
      // Manual withdrawal disbursement (Daraja B2C not yet configured, so ops sends
      // M-Pesa by hand and records the receipt here for reconciliation).
      disburseModal: false, disburseWd: null, disbursing: false, disburseError: '',
      disburseForm: { mpesaReceipt: '', amountSent: '', notes: '' },
      // Disputes
      disputes: [], disputeFilter: '', disputeDetail: null,
      resolveModal: false, resolveDispute: {}, resolveType: 'refund', resolveAmount: '', resolveNotes: '', resolving: false,
      adminNoteText: '', addingNote: false, previewImg: null,
      // Listings
      allListings: [], listingFilter: '', listingSearch: '', listingView: 'cards', moderatingId: null,
      // Listings sub-tabs + image-duplicate fraud guard + unverified seller cleanup
      listingsSub: 'all',
      imageClusters: { exactClusters: [], perceptualClusters: [], totalClusters: 0, threshold: 8 },
      imageClustersLoading: false, unlistingPhotoId: null,
      unverifiedSellers: { sellers: [], count: 0, totalListingsToUnlist: 0, graceDays: 30 },
      unverifiedLoading: false,
      agroShops: [], agroShopLoading: false, agroShopFilter: '', agroShopActing: null,
      insProviders: [], insProviderLoading: false, insProviderFilter: '', insProviderActing: null,
      finProviders: [], finProviderLoading: false, finProviderFilter: '', finProviderActing: null,
      corpAccounts: [], corpLoading: false, corpFilter: '', corpActing: null,
      // Verifications
      allVerifications: [], pendingVerifications: [], verifFilter: '', verifDetail: null, rejectReason: '', verifProcessing: false, expandedVerifUser: null,
      verifNotifTitle: 'Verification Update', verifNotifBody: '', sendingNotif: false,
      // Settings
      settings: {}, savingSettings: false, settingsSaved: false, trustStripEnabled: false, anitracSectionEnabled: false,
      usageCaps: [], usageStats: {}, topConsumers: [], usageLoading: false,
      compliance: { currentVersion:'', totalUsers:0, acceptedCurrent:0, pendingCurrent:0, rows:[], byVersion:[], loading:false },
      watchlist: { needsPhone:[], heavyUsers:[], multipleRejects:[], pendingVerifs:[], loading:false },
      supportConvos: [], supportLoading: false, supportUserId: '00000000-0000-0000-0000-000000000001',
      testingSMS: false, testingEmail: false, testResult: null,
      smsPricing: {rate_per_unit:1, chars_per_unit:160, min_topup:50, max_topup:50000},
      supportContactsAdmin: [],
      smsPlatformStats: {total_balance:0, total_topped_up:0, total_spent:0, farm_count:0, total_sms:0},
      savingSmsPricing: false, smsPricingSaved: false,
      flockrMpesa: {shortcode:'',shortcode_type:'paybill',consumer_key:'',consumer_secret:'',passkey:'',environment:'production'},
      showFlockrSecret: false, savingFlockrMpesa: false, flockrMpesaSaved: false,
      testingFlockrMpesa: false, flockrMpesaTestResult: null,
      activatingFlockrMpesa: false, flockrMpesaActivateResult: null,
      flockrStkTestPhone: '', flockrStkTestAmount: 10, testingFlockrStk: false,
      adminStkSent: false, adminStkRequestId: null, adminStkStatus: '', adminStkMessage: '', adminStkReceipt: '',
      adminStkPolling: false, adminStkChecking: false, adminStkPollTimer: null,
      flockrPayments: {payments:[], byType:[], byMethod:[], byShortcode:[], today:{amount:0,count:0}, thisMonth:{amount:0,count:0}, allTime:{amount:0,count:0}, smsTopupRevenue:0},
      finSubTab: 'payments', payFilterType: '', payFilterMethod: '', payFilterStatus: '',
      paymentModal: false, selectedPayment: null,
      smsLogs: [], emailLogs: [],
      smsBalance: null, smsBalanceLoading: false,
      // User modal
      userModal: false, selectedUser: {},
      userListings: [], userListingsLoading: false,
      userReviews: [], userReviewsLoading: false,
      userRatingStats: null, userVerifications: [], userOrders: [],

      // Insurance admin
      insSubTab: 'policies',
      insurancePolicies: [], insuranceClaims: [], insuranceProducts: [], insuranceProviders: [],
      insuranceStats: {}, claimStats: {},
      loadingInsurance: false,
      polFilter: { status:'', paymentStatus:'', providerId:'', search:'' },
      claimFilter: { status:'', claimType:'', search:'' },

      // Activate / cancel policy modals
      activateModal: false, activatePolicy: null, activateForm: { externalPolicyNumber:'', adminNote:'' }, activateBusy: false, activateError: '',
      cancelPolicyModal: false, cancelPolicyTarget: null, cancelPolicyReason: '', cancelPolicyBusy: false, cancelPolicyError: '',

      // Claim review modal (single modal, action driven by claimAction)
      claimDetailModal: false, claimDetailTarget: null, claimAction: '', claimActionForm: { reviewNotes:'', approvedAmount:null, rejectionReason:'', payoutAmount:null, payoutMethod:'mpesa', payoutReference:'' }, claimActionBusy: false, claimActionError: '',

      // Product / provider modals
      productModal: false, productForm: { id:'', providerId:'', name:'', description:'', insuranceType:'livestock', coverageDetailsRaw:'{"covers":[],"excludes":[],"max_payout_pct":100}', eligibleSpeciesRaw:'[]', minAnimalValue:null, maxAnimalValue:null, premiumRatePct:null, flatPremium:null, durationMonths:12, waitingPeriodDays:0, isActive:1 }, productBusy: false, productError: '',
      providerModal: false,
      providerForm: { id:'', name:'', shortName:'', logoUrl:'', contactEmail:'', contactPhone:'', websiteUrl:'', insuranceTypesRaw:'["livestock"]', isActive:1, usePlatformMpesa:1, mpesaShortcode:'', mpesaShortcodeType:'paybill', mpesaConsumerKey:'', mpesaConsumerSecret:'', mpesaPasskey:'', mpesaEnvironment:'production', accountRefTemplate:'{policyNumber}' },
      providerSecretsRevealed: false,
      providerBusy: false, providerError: '',
      providerTestPhone: '', providerTestBusy: false, providerTestResult: null,
    };
  },
  computed: {
    mdComputedTotals(){
      var agg = {};
      (this.mdForm.movements || []).forEach(function(m){
        var sp = String(m.species || '').trim(); if (!sp) return;
        sp = sp.charAt(0).toUpperCase() + sp.slice(1).toLowerCase();
        if (!agg[sp]) agg[sp] = { species: sp, slaughter: 0, breeding: 0, total: 0 };
        agg[sp].slaughter += Number(m.slaughter) || 0;
        agg[sp].breeding  += Number(m.breeding) || 0;
        agg[sp].total = agg[sp].slaughter + agg[sp].breeding;
      });
      return Object.values(agg);
    },
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},
    smsBalanceBannerStyle(){
      var st = this.smsBalance ? this.smsBalance.state : '';
      if (st === 'critical') return 'background:rgba(255,107,107,.12);border:1px solid rgba(255,107,107,.35);color:#B71C1C;';
      if (st === 'low')      return 'background:rgba(255,193,7,.12);border:1px solid rgba(255,193,7,.35);color:#8B6914;';
      if (st === 'ok')       return 'background:rgba(80,200,120,.10);border:1px solid rgba(80,200,120,.30);color:#1F6B1F;';
      return 'background:rgba(150,150,150,.10);border:1px solid rgba(150,150,150,.25);color:#555;';
    },

    myAdminModules(){
      // Fresh-pulled from sessionStorage so role-grant in the user modal updates
      // the visible tabs without needing a page reload.
      try { return JSON.parse(sessionStorage.getItem('adminModules') || '[]'); } catch(e) { return []; }
    },
    iAmSuperAdmin(){
      try {
        var roles = JSON.parse(sessionStorage.getItem('userRoles') || '[]');
        return roles.indexOf('super_admin') > -1;
      } catch(e) { return false; }
    },
    canModule(){
      // Returns a function used in templates: canModule('insurance')
      var mods = this.myAdminModules;
      return function(key){ return mods.indexOf(key) > -1; };
    },
    tabs() {
      var allTabs = [
        { key:'overview', module:'overview', label:'Overview', icon:'mdi mdi-view-dashboard' },
        { key:'analytics', module:'overview', label:'Analytics', icon:'mdi mdi-chart-areaspline' },
        { key:'approvals', module:'overview', label:'Approvals', icon:'mdi mdi-check-decagram', badge:this.mcPendingCount || null },
        { key:'orders', module:'orders', label:'Orders', icon:'mdi mdi-receipt', badge:this.allOrders.length },
        { key:'transports', module:'transport', label:'Transport', icon:'mdi mdi-truck-fast', badge:this.transportStats.active || null },
        { key:'insurance', module:'insurance', label:'Insurance', icon:'mdi mdi-shield-check', badge:(this.claimStats.submitted||0)+(this.claimStats.under_review||0) || null },
        { key:'users', module:'users', label:'Users', icon:'mdi mdi-account-group', badge:this.allUsers.length },
        { key:'finance', module:'finance', label:'Finance', icon:'mdi mdi-cash-multiple' },
        { key:'credit', module:'finance', label:'Credit Passport', icon:'mdi mdi-shield-star', badge:(this.creditDist.length || null) },
        { key:'disputes', module:'disputes', label:'Disputes', icon:'mdi mdi-gavel', badge:this.disputes.filter(function(d){return d.status==='open';}).length || null },
        { key:'listings', module:'listings', label:'Listings', icon:'mdi mdi-storefront', badge:this.allListings.length },
        { key:'agroshops', module:'listings', label:'Agro Shops', icon:'mdi mdi-storefront-outline', badge:this.agroShops.filter(function(s){return s.kyc_status==='pending';}).length || null },
        { key:'insproviders', module:'insurance', label:'Insurance Cos', icon:'mdi mdi-shield-account', badge:this.insProviders.filter(function(s){return s.kyc_status==='pending';}).length || null },
        { key:'finproviders', module:'finance', label:'Finance Cos', icon:'mdi mdi-bank-outline', badge:this.finProviders.filter(function(s){return s.kyc_status==='pending';}).length || null },
        { key:'corporates', module:'users', label:'Corporate Buyers', icon:'mdi mdi-domain', badge:this.corpAccounts.filter(function(s){return s.kyc_status==='pending';}).length || null },
        { key:'verifications', module:'verifications', label:'KYC', icon:'mdi mdi-shield-check', badge:this.pendingVerifications.length || null },
        { key:'market', module:'market', label:'Market', icon:'mdi mdi-chart-line', badge:this.marketReports.filter(function(r){return r.status==='draft';}).length || null },
        { key:'community', module:'community', label:'Community', icon:'mdi mdi-forum', badge:this.communityArticles.filter(function(a){return a.status==='draft';}).length || null },
        { key:'support', module:'orders', label:'Support Inbox', icon:'mdi mdi-lifebuoy', badge:this.supportConvos.reduce(function(n,c){return n + (Number(c.user_unread)||0)}, 0) || null },
        { key:'pricing', module:'pricing', label:'Pricing', icon:'mdi mdi-tag-multiple', badge:this.adminPlans.length || null },
        { key:'team', module:'roles', label:'Team', icon:'mdi mdi-account-tie-hat', badge:this.team.length || null },
        { key:'compliance', module:'settings', label:'Compliance', icon:'mdi mdi-file-document-check' },
        { key:'watchlist', module:'users', label:'Watchlist', icon:'mdi mdi-account-alert', badge: (this.watchlist.needsPhone||[]).length + (this.watchlist.heavyUsers||[]).length + (this.watchlist.multipleRejects||[]).length || null },
        { key:'settings', module:'settings', label:'Settings', icon:'mdi mdi-cog' },
      ];
      var mods = this.myAdminModules;
      return allTabs.filter(function(t){ return mods.indexOf(t.module) > -1; });
    },
    filteredPayments() {
      var list = this.flockrPayments.payments || [];
      var t = this.payFilterType; var m = this.payFilterMethod; var s = this.payFilterStatus;
      if (t) list = list.filter(function(p){ return p.payment_type === t; });
      if (m) list = list.filter(function(p){ return p.payment_method === m; });
      if (s) list = list.filter(function(p){ return p.status === s; });
      return list;
    },
    overviewStats() {
      var self = this; var s = this.stats;
      return [
        { label:'Total Orders', value:s.totalOrders||0, icon:'mdi mdi-receipt', color:'#BFA164', bg:'rgba(191,161,100,0.1)', action:function(){ self.tab='orders'; self.orderFilter=''; } },
        { label:'Completed', value:s.completedOrders||0, icon:'mdi mdi-check-circle', color:'#50C878', bg:'rgba(80,200,120,0.1)', action:function(){ self.tab='orders'; self.orderFilter='completed'; } },
        { label:'Active Listings', value:s.activeListings||0, icon:'mdi mdi-storefront', color:'#4FC3F7', bg:'rgba(79,195,247,0.1)', action:function(){ self.tab='listings'; self.listingFilter='active'; } },
        { label:'Total Users', value:s.totalUsers||0, icon:'mdi mdi-account-group', color:'#CE93D8', bg:'rgba(206,147,216,0.1)', action:function(){ self.tab='users'; self.userFilter=''; } },
        { label:'Sellers', value:s.totalSellers||0, icon:'mdi mdi-store', color:'#FFB74D', bg:'rgba(255,183,77,0.1)', action:function(){ self.tab='users'; self.userFilter='seller'; } },
        { label:'GMV', value:'KES '+this.fN(s.gmv||0), icon:'mdi mdi-chart-line', color:'#50C878', bg:'rgba(80,200,120,0.1)', action:function(){ self.tab='finance'; self.finSubTab='payments'; } },
        { label:'Open Disputes', value:s.openDisputes||0, icon:'mdi mdi-alert-circle', color:'#FF6B6B', bg:'rgba(255,107,107,0.1)', action:function(){ self.tab='disputes'; self.disputeFilter='open'; } },
        { label:'Pending Withdrawals', value:s.pendingWithdrawals||0, icon:'mdi mdi-cash-clock', color:'#FFAB00', bg:'rgba(255,171,0,0.1)', action:function(){ self.tab='finance'; self.finSubTab='withdrawals'; } },
      ];
    },
    filteredOrders() {
      var self = this; var list = this.allOrders;
      if (this.orderFilter) list = list.filter(function(o) { return o.status === self.orderFilter; });
      if (this.orderTransportFilter === 'flockr_arranges') list = list.filter(function(o) { return self.needsArrange(o); });
      if (this.orderSearch) { var q = this.orderSearch.toLowerCase(); list = list.filter(function(o) { return (o.order_number||'').toLowerCase().indexOf(q)>-1||(o.buyer_name||'').toLowerCase().indexOf(q)>-1||(o.seller_name||'').toLowerCase().indexOf(q)>-1; }); }
      return list;
    },
    needsTransportCount() {
      var self = this;
      return (this.allOrders || []).filter(function(o){ return self.needsArrange(o); }).length;
    },
    // Admin-portal #20 — the picked buyer's active orders that still need transport,
    // offered as one-click prefills in the arrange modal.
    arrangeBuyerOrders() {
      var self = this; var uid = this.arrange.onBehalfOfUserId;
      if (!uid) return [];
      return (this.allOrders || []).filter(function(o){ return o.buyer_id === uid && self.needsArrange(o); });
    },
    filteredOrdersGMV() {
      return (this.filteredOrders || []).reduce(function(sum, o){ return sum + Number(o.total_amount || 0); }, 0);
    },
    grantTrialEndsAt() {
      var d = Number(this.grantTrialForm.days || 0);
      if (!d || d <= 0) return '—';
      var end = new Date(Date.now() + d * 24 * 60 * 60 * 1000);
      return end.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    usersByCountry() {
      var users = this.allUsers || [];
      if (!users.length) return [];
      // Phone-prefix → country metadata. Longer prefixes are checked first to avoid 27 swallowing 254 etc.
      var META = {
        '254':{country:'Kenya', flag:'🇰🇪', prefix:'+254', color:'#50C878'},
        '255':{country:'Tanzania', flag:'🇹🇿', prefix:'+255', color:'#4FC3F7'},
        '256':{country:'Uganda', flag:'🇺🇬', prefix:'+256', color:'#FFB74D'},
        '250':{country:'Rwanda', flag:'🇷🇼', prefix:'+250', color:'#CE93D8'},
        '257':{country:'Burundi', flag:'🇧🇮', prefix:'+257', color:'#BFA164'},
        '251':{country:'Ethiopia', flag:'🇪🇹', prefix:'+251', color:'#FF6B6B'},
        '252':{country:'Somalia', flag:'🇸🇴', prefix:'+252', color:'#FFAB00'},
        '253':{country:'Djibouti', flag:'🇩🇯', prefix:'+253', color:'#7986CB'},
        '211':{country:'South Sudan', flag:'🇸🇸', prefix:'+211', color:'#4DD0E1'},
        '234':{country:'Nigeria', flag:'🇳🇬', prefix:'+234', color:'#81C784'},
        '233':{country:'Ghana', flag:'🇬🇭', prefix:'+233', color:'#FFD54F'},
        '260':{country:'Zambia', flag:'🇿🇲', prefix:'+260', color:'#9575CD'},
        '263':{country:'Zimbabwe', flag:'🇿🇼', prefix:'+263', color:'#F06292'},
        '265':{country:'Malawi', flag:'🇲🇼', prefix:'+265', color:'#AED581'},
        '267':{country:'Botswana', flag:'🇧🇼', prefix:'+267', color:'#4DB6AC'},
        '258':{country:'Mozambique', flag:'🇲🇿', prefix:'+258', color:'#FF8A65'},
        '225':{country:"Cote d'Ivoire", flag:'🇨🇮', prefix:'+225', color:'#A1887F'},
        '27': {country:'South Africa', flag:'🇿🇦', prefix:'+27', color:'#FFB300'},
        '20': {country:'Egypt', flag:'🇪🇬', prefix:'+20', color:'#E0CDA9'}
      };
      var ORDERED = ['254','255','256','250','257','251','252','253','211','234','233','260','263','265','267','258','225','27','20'];
      function detect(phone){
        if (!phone) return 'unknown';
        var p = String(phone).replace(/^\+/, '').replace(/\s|-/g, '');
        for (var i=0;i<ORDERED.length;i++) { if (p.indexOf(ORDERED[i])===0) return ORDERED[i]; }
        // Kenyan numbers stored without prefix/leading 0 (07XXXXXXXX, 01XXXXXXXX, 7XXXXXXXX, 1XXXXXXXX)
        if (/^0?[71]\d{8}$/.test(p)) return '254';
        return 'unknown';
      }
      var counts = {};
      users.forEach(function(u){ var c = detect(u.phone); counts[c] = (counts[c]||0) + 1; });
      var total = users.length;
      var rows = Object.keys(counts).map(function(k){
        var meta = META[k] || {country:'No phone / unknown', flag:'❓', prefix:'', color:'#6B6356'};
        if (k !== 'unknown' && !META[k]) meta = {country:'Other (+'+k+')', flag:'🌍', prefix:'+'+k, color:'#A09888'};
        return {
          key: k,
          country: meta.country,
          flag: meta.flag,
          prefix: meta.prefix,
          color: meta.color,
          users: counts[k],
          share: Math.round((counts[k]/total)*1000)/10
        };
      });
      rows.sort(function(a,b){ return b.users - a.users; });
      return rows;
    },
    filteredUsers() {
      var self = this; var list = this.allUsers;
      if (this.userTypeTab) list = list.filter(function(u){ return self.userMatchesType(u, self.userTypeTab); });
      else if (this.userFilter) list = list.filter(function(u) { return self.userMatchesType(u, self.userFilter); });
      if (this.userSearch) { var q = this.userSearch.toLowerCase(); list = list.filter(function(u) { return (u.display_name||'').toLowerCase().indexOf(q)>-1||(u.phone||'').indexOf(q)>-1; }); }
      return list;
    },
    // Dev-Requests #8 — counts for the tab-strip badges.
    userTypeCounts() {
      var self = this;
      var counts = { total: this.allUsers.length, farmer:0, buyer:0, seller:0, aggregator:0, vet:0, transporter:0, product_seller:0, corporate:0, agent:0, admin:0 };
      var keys = Object.keys(counts);
      this.allUsers.forEach(function(u){
        keys.forEach(function(t){ if (t !== 'total' && self.userMatchesType(u, t)) counts[t]++; });
      });
      return counts;
    },
    // Legend + color palette for the analytics line chart (Alex 2026-07-21).
    chartLegend() {
      return [
        { key: 'active_users',     label: 'Active users',     color: '#50C878' },
        { key: 'new_signups',      label: 'New signups',      color: '#CE93D8' },
        { key: 'listings_created', label: 'Listings created', color: '#FFAB00' },
        { key: 'orders_placed',    label: 'Orders placed',    color: '#4FC3F7' },
        { key: 'listing_views',    label: 'Listing views',    color: '#FF6B6B' },
        { key: 'gmv_kes',          label: 'GMV (KES)',        color: '#BFA164' }
      ];
    },
    // Only the toggled-on series — used in the SVG loop so we don't mix
    // v-for with v-if (ESLint vue/no-use-v-if-with-v-for).
    chartActiveSeries() {
      var on = this.chartSeriesOn;
      return this.chartLegend.filter(function(s){ return on[s.key]; });
    },
    // Admin-portal #7 — one shared y-scale across toggled-on series so the axis
    // labels mean something. Max is rounded up to a 4-divisible "nice" number
    // so the quarter ticks land on clean values.
    chartMax() {
      var series = this.analytics.series || [];
      var max = 0;
      this.chartActiveSeries.forEach(function(s){
        series.forEach(function(d){ max = Math.max(max, Number(d[s.key] || 0)); });
      });
      if (max <= 4) return 4;
      var base = Math.ceil(max / 4);
      var p = Math.pow(10, Math.floor(Math.log10(base)));
      var f = base / p;
      var nf = f <= 1 ? 1 : (f <= 2 ? 2 : (f <= 5 ? 5 : 10));
      return 4 * nf * p;
    },
    chartYTicks() {
      var m = this.chartMax;
      return [0, m/4, m/2, (3*m)/4, m];
    },
    // ---- Field Agents (referral-driven onboarding) ----
    filteredAgents() {
      var list = this.agents || [];
      if (this.agentSearch) {
        var q = this.agentSearch.toLowerCase();
        list = list.filter(function(a){
          return (a.display_name||'').toLowerCase().indexOf(q)>-1
              || (a.phone||'').indexOf(q)>-1
              || (a.referral_code||'').toLowerCase().indexOf(q)>-1;
        });
      }
      return list;
    },
    agentsTotalReferred() {
      return (this.agents||[]).reduce(function(s,a){ return s + Number(a.referred_count||0); }, 0);
    },
    topAgentName() {
      var top = (this.agents||[])[0];
      if (!top || !Number(top.referred_count)) return '—';
      return (top.first_name || top.display_name || '—') + ' · ' + top.referred_count;
    },
    agentsActiveLast30() {
      var thirty = Date.now() - 30*24*60*60*1000;
      return (this.agents||[]).filter(function(a){
        if (!a.last_active_at) return false;
        return new Date(a.last_active_at).getTime() >= thirty;
      }).length;
    },
    filteredListings() {
      var self = this; var list = this.allListings;
      if (this.listingFilter) list = list.filter(function(l) { return l.status === self.listingFilter; });
      if (this.listingSearch) { var q = this.listingSearch.toLowerCase(); list = list.filter(function(l) { return (l.title||'').toLowerCase().indexOf(q)>-1||(l.seller_name||'').toLowerCase().indexOf(q)>-1; }); }
      return list;
    },
    allClusters() {
      return [].concat(this.imageClusters.exactClusters || [], this.imageClusters.perceptualClusters || []);
    },
    aiReport() {
      if (!this.verifDetail || !this.verifDetail.ai_report) return null;
      try { return typeof this.verifDetail.ai_report === 'string' ? JSON.parse(this.verifDetail.ai_report) : this.verifDetail.ai_report; }
      catch (e) { return null; }
    },
    filteredDisputes() {
      var self = this; var list = this.disputes;
      if (this.disputeFilter) list = list.filter(function(d) { return d.status === self.disputeFilter; });
      return list;
    },
    filteredVerifications() {
      var self = this; var list = this.allVerifications;
      if (this.verifFilter) list = list.filter(function(v) { return v.verification_status === self.verifFilter; });
      return list;
    },
    groupedVerifications() {
      var groups = {};
      this.allVerifications.forEach(function(v) {
        if (!groups[v.user_id]) {
          groups[v.user_id] = { user_id: v.user_id, display_name: v.display_name, phone: v.phone, items: [], pendingCount: 0, verifiedCount: 0, rejectedCount: 0 };
        }
        groups[v.user_id].items.push(v);
        if (v.verification_status === 'pending') groups[v.user_id].pendingCount++;
        else if (v.verification_status === 'verified') groups[v.user_id].verifiedCount++;
        else if (v.verification_status === 'rejected') groups[v.user_id].rejectedCount++;
      });
      // Sort: users with pending first
      return Object.values(groups).sort(function(a, b) { return b.pendingCount - a.pendingCount || b.items.length - a.items.length; });
    },
    filteredGroupedVerifications() {
      var f = this.verifFilter;
      if (!f) return this.groupedVerifications;
      return this.groupedVerifications.filter(function(g) {
        if (f === 'pending') return g.pendingCount > 0;
        if (f === 'verified') return g.pendingCount === 0 && g.rejectedCount === 0;
        if (f === 'rejected') return g.rejectedCount > 0;
        return true;
      });
    }
  },
  mounted() {
    // Apply global theme
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);

    document.title = 'FLOCKR | Admin';
    // Honor ?tab=X query param so notifications can deep-link to a specific
    // admin tab (e.g. ?tab=transports&group=<order_group_id> from the
    // "Per-Seller Checkout — Transport Coordination Needed" notification).
    if (this.$route && this.$route.query) {
      var q = this.$route.query;
      var requested = q.tab ? String(q.tab) : '';
      var validTabs = (this.tabs || []).map(function(x){ return x.key; });
      // Pre-fill the transport filter search with the order/group id so the
      // admin lands on the exact row to action, not just the right tab.
      if (requested === 'transports' && (q.group || q.order)) {
        this.transportFilter.search = String(q.group || q.order);
      }
      if (requested && validTabs.indexOf(requested) >= 0) {
        this.switchTab(requested);
      }
    }
    this.loadAll();
    this.loadPendingActions(); // maker-checker badge count
  },
  methods: {
    fN(v) { return v ? Number(v).toLocaleString() : '0'; },
    fD(d) { return d ? new Date(d).toLocaleDateString('en-KE', { day:'numeric', month:'short', year:'numeric' }) : '-'; },
    formatPhone(p) { if(!p) return '—'; if(p.length > 20) return p.substring(0,8)+'…'; return p; },
    payTypeBadge(t) {
      var map = {sms_topup:{background:'rgba(206,147,216,0.15)',color:'#CE93D8'}, marketplace_order:{background:'rgba(191,161,100,0.15)',color:'#BFA164'}, farm_c2b:{background:'rgba(80,200,120,0.15)',color:'#50C878'}, farm_stk:{background:'rgba(80,200,120,0.15)',color:'#50C878'}, withdrawal:{background:'rgba(255,107,107,0.15)',color:'#FF6B6B'}, admin_test:{background:'rgba(79,195,247,0.15)',color:'#4FC3F7'}, pos_sale:{background:'rgba(255,171,0,0.15)',color:'#FFAB00'}};
      return map[t] || {background:'rgba(255,255,255,0.05)',color:'#A09888'};
    },
    photoUrl(p) { if(!p) return ''; if(p.startsWith && p.startsWith('http')) return p; return uploads + String(p).replace('uploads/',''); },
    switchTab(t) {
      this.tab = t;
      if(t==='finance') this.loadFlockrPayments();
      if(t==='transports') this.loadTransports();
      if(t==='insurance') this.loadInsurancePolicies();
      if(t==='team') this.loadTeam();
      if(t==='market') this.loadMarketAdmin();
      if(t==='community') this.loadCommunityAdmin();
      if(t==='compliance') this.loadCompliance();
      if(t==='watchlist') this.loadWatchlist();
      if(t==='support') this.loadSupportConvos();
      if(t==='pricing') this.loadAdminPlans();
      if(t==='agroshops') this.loadAgroShops();
      if(t==='insproviders') this.loadInsProviders();
      if(t==='finproviders') this.loadFinProviders();
      if(t==='corporates') this.loadCorporates();
      if(t==='analytics') this.loadAnalytics();
      if(t==='credit') this.loadCredit();
      if(t==='approvals') this.loadPendingActions();
    },

    // ===== Maker-checker approvals (Issue 4) =====
    mcLabel(fn){
      if (!fn) return '';
      // 'adminUpdateOrderStatus' → 'Update Order Status'
      return fn.replace(/^admin/, '').replace(/([a-z])([A-Z])/g, '$1 $2');
    },
    mcPrettyPayload(a){
      try {
        var p = JSON.parse(a.payload_json || '{}');
        delete p.adminUserId;
        return JSON.stringify(p, null, 2);
      } catch(e){ return a.payload_json || ''; }
    },
    async loadPendingActions(){
      this.mcLoading = true; this.mcExpandedId = null;
      try {
        var r = await this.apiPost({ function:'adminListPendingActions', status: this.mcFilter });
        if (r && r.success && r.data) {
          this.mcActions = r.data.actions || [];
          this.mcPendingCount = Number(r.data.pendingCount || 0);
        }
      } catch(e){ console.error('loadPendingActions', e); }
      this.mcLoading = false;
    },
    openMcModal(a, mode){ this.mcModal = { show:true, mode:mode, action:a, notes:'', busy:false }; },
    closeMcModal(){ if (this.mcModal.busy) return; this.mcModal.show = false; },
    async submitMcModal(){
      var m = this.mcModal;
      if (!m.action) return;
      if (m.mode === 'reject' && !m.notes.trim()) { alert('Please give a reason for the rejection.'); return; }
      m.busy = true;
      var r = await this.apiPost({
        function: m.mode === 'approve' ? 'adminApproveAction' : 'adminRejectAction',
        actionId: m.action.id,
        notes: m.notes.trim()
      });
      m.busy = false;
      if (r && r.success) {
        alert(m.mode === 'approve' ? ('Approved and executed: ' + (r.message || 'done')) : 'Action rejected — the maker has been notified.');
        this.mcModal.show = false;
        await this.loadPendingActions();
        if (typeof this.loadAll === 'function') this.loadAll();
      } else {
        alert((r && r.message) || 'Failed — try refreshing the list.');
      }
    },
    async loadCredit(){
      try {
        var r = await this.apiPost({function:'adminListCreditScores', limit:100});
        if(r && r.data && r.data.success && r.data.data){
          this.creditUsers = r.data.data.users || [];
          this.creditDist = r.data.data.distribution || [];
        }
      } catch(e){ console.error('loadCredit', e); }
    },
    tierBadgeColour(tier){
      var map = { Diamond:'#7c3aed', Platinum:'#0ea5e9', Gold:'#f59e0b', Silver:'#94a3b8', Bronze:'#a16207' };
      return map[tier] || '#6B6356';
    },

    async apiPost(payload){
      // Auto-inject adminUserId for every admin* call so individual loaders don't have to.
      // Server's assertAdmin() requires it on every handler — this is the single point of truth.
      try {
        if (payload && typeof payload.function === 'string' && payload.function.indexOf('admin') === 0 && !payload.adminUserId) {
          payload.adminUserId = sessionStorage.getItem('userId') || '';
        }
        var r = await executeJson(payload, flocker);
        var body = r && r.data ? r.data : null;
        // Maker-checker gate (Issue 4): the action was parked for a second
        // admin's approval, not executed. Return success:false so callers skip
        // their optimistic local updates — their error path shows this message.
        if (body && body.success && body.data && body.data.pending === true) {
          this.mcPendingCount++;
          return { success:false, pending:true, message: body.message || 'Submitted for approval — a second admin must approve before this takes effect', data: body.data };
        }
        return body;
      } catch(e){ return { success:false, message:(e.response && e.response.data && e.response.data.message) || 'Network error' }; }
    },

    // ===== User-management =====
    hasRole(roleKey){
      return (this.userRoles || []).some(function(r){ return r.role === roleKey; });
    },
    async loadUserRoles(userId){
      this.userRolesLoading = true;
      try{
        var r = await this.apiPost({ function:'adminGetUserRoles', userId: userId });
        if(r && r.success){ this.userRoles = (r.data && r.data.roles) || []; }
      }catch(e){}
      this.userRolesLoading = false;
    },
    // Admin trial-grant module
    async loadUserSubscription(userId){
      this.userSubLoading = true;
      try{
        var r = await this.apiPost({ function:'adminGetUserSubscription', userId: userId });
        if(r && r.success){ this.userSub = (r.data && r.data.subscription) || null; }
      }catch(e){ console.error(e); }
      this.userSubLoading = false;
    },
    async openGrantTrial(){
      // Lazy-load the plan catalogue on first open (excludes Free — you don't "grant" Free)
      if(!this.grantTrialPlans.length){
        try{ var r = await this.apiPost({ function:'adminGetSubscriptionPlans' }); if(r && r.success){ this.grantTrialPlans = ((r.data && r.data.plans) || []).filter(function(p){ return p.slug !== 'free' && p.is_active; }); } }catch(e){}
      }
      this.grantTrialForm = { planId: '', days: 30, reason: '' };
      this.showGrantTrialModal = true;
    },
    async submitGrantTrial(){
      if(!this.selectedUser || !this.grantTrialForm.planId || !this.grantTrialForm.days || !this.grantTrialForm.reason.trim()) return;
      this.userSubBusy = true;
      try{
        var r = await this.apiPost({
          function: 'adminGrantTrialSubscription',
          userId: this.selectedUser.id,
          planId: this.grantTrialForm.planId,
          days:   this.grantTrialForm.days,
          reason: this.grantTrialForm.reason.trim()
        });
        if(r && r.success){
          alert(r.message || 'Trial granted');
          this.showGrantTrialModal = false;
          await this.loadUserSubscription(this.selectedUser.id);
        } else {
          alert((r && r.message) || 'Grant failed');
        }
      }catch(e){ alert('Network error — try again'); }
      this.userSubBusy = false;
    },
    async endUserSub(){
      if(!this.userSub || this.userSub.status !== 'active') return;
      var reason = window.prompt("End this subscription early? Enter a reason for the audit log:", "");
      if(reason === null) return;
      reason = reason.trim();
      if(!reason){ alert('A reason is required'); return; }
      this.userSubBusy = true;
      try{
        var r = await this.apiPost({ function:'adminEndUserSubscription', subscriptionId: this.userSub.id, reason: reason });
        if(r && r.success){ alert(r.message || 'Subscription ended'); await this.loadUserSubscription(this.selectedUser.id); }
        else { alert((r && r.message) || 'Failed'); }
      }catch(e){ alert('Network error — try again'); }
      this.userSubBusy = false;
    },
    async grantRole(roleKey){
      var label = (this.roleCatalog.find(function(r){ return r.v === roleKey; }) || {}).l || roleKey;
      if(!confirm("Grant the '" + label + "' role to " + this.selectedUser.display_name + "?")) return;
      this.userActionBusy = true;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function:'adminGrantUserRole', adminUserId: adminUserId, userId: this.selectedUser.id, role: roleKey });
      if(r && r.success){ await this.loadUserRoles(this.selectedUser.id); }
      else alert((r && r.message) || 'Failed to grant role');
      this.userActionBusy = false;
    },
    async revokeRole(roleKey){
      var label = (this.roleCatalog.find(function(r){ return r.v === roleKey; }) || {}).l || roleKey;
      if(!confirm("Revoke the '" + label + "' role from " + this.selectedUser.display_name + "?")) return;
      this.userActionBusy = true;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function:'adminRevokeUserRole', adminUserId: adminUserId, userId: this.selectedUser.id, role: roleKey });
      if(r && r.success){ await this.loadUserRoles(this.selectedUser.id); }
      else alert((r && r.message) || 'Failed to revoke role');
      this.userActionBusy = false;
    },
    // ===== Admin module permissions =====
    hasModule(moduleKey){ return this.userModules.indexOf(moduleKey) > -1; },
    async loadUserPermissions(userId){
      this.userPermissionsLoading = true;
      try {
        var r = await this.apiPost({ function:'adminGetUserPermissions', userId: userId });
        if(r && r.success){
          this.userModules = (r.data && r.data.modules) || [];
          this.moduleCatalogue = (r.data && r.data.catalogue) || [];
        }
      } catch(e){}
      this.userPermissionsLoading = false;
    },
    async grantModulePermission(moduleKey){
      if(!this.iAmSuperAdmin) return;
      this.userActionBusy = true;
      var r = await this.apiPost({
        function:'adminGrantModulePermission',
        adminUserId: sessionStorage.getItem('userId') || '',
        userId: this.selectedUser.id,
        module: moduleKey
      });
      if(r && r.success){
        await this.loadUserPermissions(this.selectedUser.id);
        // If the super-admin is editing themselves, refresh their own session-storage modules
        if (this.selectedUser.id === sessionStorage.getItem('userId')) await this.refreshMyAdminModules();
      } else {
        alert((r && r.message) || 'Failed to grant module access');
      }
      this.userActionBusy = false;
    },
    async revokeModulePermission(moduleKey){
      if(!this.iAmSuperAdmin) return;
      var label = (this.moduleCatalogue.find(function(m){ return m.key === moduleKey; }) || {}).label || moduleKey;
      if(!confirm("Remove '" + label + "' module access from " + (this.selectedUser.display_name || 'this admin') + "?")) return;
      this.userActionBusy = true;
      var r = await this.apiPost({
        function:'adminRevokeModulePermission',
        adminUserId: sessionStorage.getItem('userId') || '',
        userId: this.selectedUser.id,
        module: moduleKey
      });
      if(r && r.success){
        await this.loadUserPermissions(this.selectedUser.id);
        if (this.selectedUser.id === sessionStorage.getItem('userId')) await this.refreshMyAdminModules();
      } else {
        alert((r && r.message) || 'Failed to revoke module access');
      }
      this.userActionBusy = false;
    },
    // ===== Admin Team (bird's-eye RBAC view) =====
    teamHasModule(t, key){ return (t.modules || []).indexOf(key) > -1; },
    async loadTeam(){
      this.teamLoading = true;
      var r = await this.apiPost({ function:'adminGetAdminTeam', search: this.teamSearch });
      if (r && r.success){
        this.team = (r.data && r.data.team) || [];
        this.teamCatalogue = (r.data && r.data.catalogue) || [];
      }
      this.teamLoading = false;
    },
    async toggleTeamModule(t, moduleKey){
      if (!this.iAmSuperAdmin || t.is_super || moduleKey === 'overview') return;
      var key = t.id + ':' + moduleKey;
      this.teamTogglingKey = key;
      var has = this.teamHasModule(t, moduleKey);
      var fn  = has ? 'adminRevokeModulePermission' : 'adminGrantModulePermission';
      var r = await this.apiPost({
        function: fn,
        adminUserId: sessionStorage.getItem('userId') || '',
        userId: t.id,
        module: moduleKey
      });
      if (r && r.success){
        // Optimistic local update so the chip flips without reloading the whole grid
        if (has) {
          t.modules = (t.modules || []).filter(function(m){ return m !== moduleKey; });
        } else {
          if (!t.modules) t.modules = [];
          if (t.modules.indexOf(moduleKey) === -1) t.modules.push(moduleKey);
        }
      } else {
        alert((r && r.message) || 'Failed to update module access');
      }
      this.teamTogglingKey = '';
    },

// ===== MARKET ADMIN =====
    async loadMarketAdmin(){
      this.marketLoading = true;
      try {
        // Reference data — load once
        if (!this.marketSpecies.length) {
          var sp = await this.apiPost({ function:'getAnimalSpecies' });
          if (sp && sp.success) this.marketSpecies = (sp.data && sp.data.species) || sp.data || [];
        }
        if (!this.marketCounties.length) {
          var co = await this.apiPost({ function:'getCounties' });
          if (co && co.success) this.marketCounties = (co.data && co.data.counties) || co.data || [];
        }
        // Prices
        var pp = { function:'adminGetMarketPrices' };
        if (this.marketPriceFilter.speciesId) pp.speciesId = this.marketPriceFilter.speciesId;
        if (this.marketPriceFilter.countyId)  pp.countyId  = this.marketPriceFilter.countyId;
        var pr = await this.apiPost(pp);
        if (pr && pr.success) this.marketPrices = (pr.data && pr.data.prices) || [];
        // Reports
        var rr = await this.apiPost({ function:'adminGetMarketReports' });
        if (rr && rr.success) this.marketReports = (rr.data && rr.data.reports) || [];
        // Market day reports (physical-market gate figures)
        var dd = await this.apiPost({ function:'adminGetMarketDayReports' });
        if (dd && dd.success) this.marketDays = (dd.data && dd.data.reports) || [];
      } catch(e){ console.error('loadMarketAdmin', e); }
      this.marketLoading = false;
    },

    // ---- Market Days (weekly gate figures) ----
    openMarketDayModal(r){
      if (r) {
        var computed = {};
        (r.movements || []).forEach(function(m){
          var sp = m.species;
          if (!computed[sp]) computed[sp] = { slaughter:0, breeding:0 };
          computed[sp].slaughter += Number(m.slaughter) || 0;
          computed[sp].breeding  += Number(m.breeding) || 0;
        });
        var override = (r.totals || []).some(function(t){
          var c = computed[t.species] || { slaughter:0, breeding:0 };
          return Number(t.slaughter) !== c.slaughter || Number(t.breeding) !== c.breeding;
        });
        this.mdForm = {
          id: r.id,
          marketName: r.market_name,
          countyId: r.county_id || '',
          reportDate: r.report_date,
          notes: r.notes || '',
          status: r.status || 'published',
          totalsOverride: override,
          movements: (r.movements || []).map(function(m){ return { scope:m.scope, destination:m.destination, species:m.species, slaughter:Number(m.slaughter)||0, breeding:Number(m.breeding)||0 }; }),
          totals: (r.totals || []).map(function(t){ return { species:t.species, slaughter:Number(t.slaughter)||0, breeding:Number(t.breeding)||0, total:Number(t.total)||0 }; }),
          prices: (r.prices || []).map(function(p){ return { species:p.species, sex:p.sex, ageGroup:p.age_group, price:Number(p.price)||0 }; })
        };
      } else {
        this.mdForm = { id:'', marketName:'', countyId:'', reportDate: new Date().toISOString().slice(0,10), notes:'', status:'published', totalsOverride:false, movements:[], totals:[], prices:[] };
      }
      this.marketDayError = ''; this.mdExcelInfo = '';
      this.marketDayModal = true;
    },
    mdAddMovement(){
      var last = this.mdForm.movements[this.mdForm.movements.length - 1];
      this.mdForm.movements.push({ scope: last ? last.scope : 'outside_county', destination:'', species: last ? last.species : '', slaughter:0, breeding:0 });
    },
    mdAddPrice(){
      var last = this.mdForm.prices[this.mdForm.prices.length - 1];
      this.mdForm.prices.push({ species: last ? last.species : '', sex:'male', ageGroup:'matured', price:null });
    },
    mdOnTotalsOverrideToggle(){
      if (this.mdForm.totalsOverride && !this.mdForm.totals.length) {
        this.mdForm.totals = this.mdComputedTotals.map(function(t){ return { species:t.species, slaughter:t.slaughter, breeding:t.breeding, total:t.total }; });
      }
    },
    async submitMarketDay(){
      this.marketDayBusy = true; this.marketDayError = '';
      var f = this.mdForm;
      var movements = f.movements.filter(function(m){ return m.destination && m.species && ((Number(m.slaughter)||0) + (Number(m.breeding)||0)) > 0; });
      var prices = f.prices.filter(function(p){ return p.species && Number(p.price) > 0; });
      if (!movements.length && !prices.length) {
        this.marketDayError = 'Add at least one movement or price row'; this.marketDayBusy = false; return;
      }
      var r = await this.apiPost({
        function: 'adminSaveMarketDayReport',
        id: f.id || '',
        marketName: f.marketName, countyId: f.countyId || '', reportDate: f.reportDate,
        notes: f.notes, status: f.status,
        movements: JSON.stringify(movements),
        totals: JSON.stringify(f.totalsOverride ? f.totals.filter(function(t){ return t.species; }) : []),
        prices: JSON.stringify(prices)
      });
      if (r && r.success) { this.marketDayModal = false; await this.loadMarketAdmin(); }
      else this.marketDayError = (r && r.message) || 'Failed to save market day report';
      this.marketDayBusy = false;
    },
    async doDeleteMarketDay(){
      if (!this.marketDayDeleteTarget) return;
      this.marketDayBusy = true;
      var r = await this.apiPost({ function:'adminDeleteMarketDayReport', id: this.marketDayDeleteTarget.id });
      this.marketDayBusy = false;
      this.marketDayDeleteTarget = null;
      if (r && r.success) await this.loadMarketAdmin();
    },
    async mdDownloadTemplate(){
      var XLSX = await import('xlsx');
      var wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['Market Name', 'Rumuruti'],
        ['County', 'Laikipia'],
        ['Report Date (YYYY-MM-DD)', new Date().toISOString().slice(0,10)],
        ['Notes', 'Lambs and kids not presented to the market']
      ]), 'Report');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['Scope (outside_county / within_county)', 'Destination', 'Species', 'Slaughter', 'Breeding'],
        ['outside_county', 'Nakuru', 'Cattle', 5, 0],
        ['outside_county', 'Nakuru', 'Sheep', 403, 12],
        ['within_county', 'Nanyuki', 'Cattle', 42, 0]
      ]), 'Movements');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['Species', 'Sex (male/female)', 'Age (calf/weaner/matured)', 'Price (KES)'],
        ['Cattle', 'male', 'calf', 29000],
        ['Cattle', 'male', 'matured', 80000],
        ['Sheep', 'female', 'weaner', 7800]
      ]), 'Prices');
      XLSX.writeFile(wb, 'flockr_market_day_template.xlsx');
    },
    async mdOnExcelFile(e){
      var file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      this.marketDayError = ''; this.mdExcelInfo = '';
      try {
        var XLSX = await import('xlsx');
        var buf = await file.arrayBuffer();
        var wb = XLSX.read(buf, { type:'array', cellDates:true });
        var self = this;
        var norm = function(v){ return String(v == null ? '' : v).trim(); };

        // Report sheet — key/value pairs
        var repWs = wb.Sheets['Report'];
        if (repWs) {
          XLSX.utils.sheet_to_json(repWs, { header:1 }).forEach(function(row){
            var k = norm(row[0]).toLowerCase(); var v = row[1];
            if (!k) return;
            if (k.indexOf('market') === 0) self.mdForm.marketName = norm(v);
            else if (k.indexOf('county') === 0) {
              var cName = norm(v).toLowerCase();
              var match = self.marketCounties.find(function(c){ return String(c.name).toLowerCase() === cName; });
              if (match) self.mdForm.countyId = match.id;
            }
            else if (k.indexOf('report date') === 0 || k.indexOf('date') === 0) {
              if (v instanceof Date) self.mdForm.reportDate = v.toISOString().slice(0,10);
              else if (/^\d{4}-\d{2}-\d{2}/.test(norm(v))) self.mdForm.reportDate = norm(v).slice(0,10);
            }
            else if (k.indexOf('note') === 0) self.mdForm.notes = norm(v);
          });
        }

        // Movements sheet
        var movements = [];
        var mvWs = wb.Sheets['Movements'];
        if (mvWs) {
          XLSX.utils.sheet_to_json(mvWs, { header:1 }).forEach(function(row){
            var scope = norm(row[0]).toLowerCase();
            var dest = norm(row[1]); var sp = norm(row[2]);
            if (!dest || !sp || scope.indexOf('scope') === 0 || dest.toLowerCase() === 'destination') return;
            movements.push({
              scope: scope.indexOf('within') > -1 ? 'within_county' : 'outside_county',
              destination: dest, species: sp,
              slaughter: Number(row[3]) || 0, breeding: Number(row[4]) || 0
            });
          });
        }

        // Prices sheet
        var prices = [];
        var prWs = wb.Sheets['Prices'];
        if (prWs) {
          XLSX.utils.sheet_to_json(prWs, { header:1 }).forEach(function(row){
            var sp = norm(row[0]); var sex = norm(row[1]).toLowerCase(); var age = norm(row[2]).toLowerCase();
            var price = Number(row[3]) || 0;
            if (!sp || sp.toLowerCase() === 'species' || price <= 0) return;
            if (['calf','weaner','matured','kid','lamb'].indexOf(age) === -1) age = 'matured';
            prices.push({ species: sp, sex: sex.indexOf('f') === 0 ? 'female' : 'male', ageGroup: age, price: price });
          });
        }

        if (!movements.length && !prices.length) {
          this.marketDayError = 'No movement or price rows found — make sure you are using the Flockr template (sheets: Report, Movements, Prices)';
          return;
        }
        if (movements.length) this.mdForm.movements = movements;
        if (prices.length) this.mdForm.prices = prices;
        this.mdForm.totalsOverride = false; this.mdForm.totals = [];
        this.mdExcelInfo = 'Loaded ' + file.name + ': ' + movements.length + ' movement rows, ' + prices.length + ' price rows. Review below, then save.';
      } catch(err){
        console.error('mdOnExcelFile', err);
        this.marketDayError = 'Could not read that file — is it a valid Excel/CSV? (' + (err && err.message) + ')';
      }
    },
    openPriceModal(p){
      if (p) {
        this.priceForm = {
          id: p.id,
          speciesId: p.species_id || '',
          breedId: p.breed_id || '',
          countyId: p.county_id || '',
          avgPrice: Number(p.avg_price),
          minPrice: p.min_price !== null ? Number(p.min_price) : null,
          maxPrice: p.max_price !== null ? Number(p.max_price) : null,
          priceUnit: p.price_unit || 'per_animal',
          sampleSize: Number(p.sample_size || 0),
          totalVolume: p.total_volume !== null ? Number(p.total_volume) : null,
          source: p.source || 'platform_transactions',
          recordDate: p.record_date,
          periodType: p.period_type || 'weekly'
        };
      } else {
        this.priceForm = { id:'', speciesId:'', breedId:'', countyId:'', avgPrice:null, minPrice:null, maxPrice:null, priceUnit:'per_animal', sampleSize:0, totalVolume:null, source:'platform_transactions', recordDate: new Date().toISOString().slice(0,10), periodType:'weekly' };
      }
      this.priceError = '';
      this.priceModal = true;
    },
    async submitPrice(){
      this.priceBusy = true; this.priceError = '';
      var f = this.priceForm;
      var r = await this.apiPost({
        function: 'adminUpsertMarketPrice',
        id: f.id,
        speciesId: f.speciesId, breedId: f.breedId || '', countyId: f.countyId,
        avgPrice: f.avgPrice, minPrice: f.minPrice, maxPrice: f.maxPrice,
        priceUnit: f.priceUnit, sampleSize: f.sampleSize, totalVolume: f.totalVolume,
        source: f.source, recordDate: f.recordDate, periodType: f.periodType
      });
      if (r && r.success) { this.priceModal = false; await this.loadMarketAdmin(); }
      else this.priceError = (r && r.message) || 'Failed to save price';
      this.priceBusy = false;
    },
    async deletePrice(p){
      if (!confirm("Delete this price entry? (" + p.species_name + " in " + p.county_name + ", " + p.record_date + ")")) return;
      var r = await this.apiPost({ function:'adminDeleteMarketPrice', id: p.id });
      if (r && r.success) await this.loadMarketAdmin();
      else alert((r && r.message) || 'Failed to delete');
    },
    openBulkPriceModal(){
      this.bulkPriceText = ''; this.bulkPriceResult = null; this.bulkPriceModal = true;
    },
    async submitBulkPrices(){
      // Parse the textarea: tab-separated rows. Map columns by position.
      var lines = this.bulkPriceText.split(/\r?\n/).map(function(l){return l.trim();}).filter(Boolean);
      var rows = lines.map(function(line){
        var c = line.split('\t');
        return {
          speciesId: c[0], countyId: c[1], avgPrice: c[2], breedId: c[3] || '',
          minPrice: c[4] || '', maxPrice: c[5] || '', priceUnit: c[6] || '',
          sampleSize: c[7] || '', totalVolume: c[8] || '',
          source: c[9] || '', periodType: c[10] || '', recordDate: c[11] || ''
        };
      });
      if (!rows.length) { this.bulkPriceResult = { success:false, message:'No rows to import' }; return; }
      this.bulkPriceBusy = true;
      var r = await this.apiPost({ function:'adminBulkImportMarketPrices', rows: JSON.stringify(rows) });
      this.bulkPriceResult = { success: !!(r && r.success), message: (r && r.message) || 'No response' };
      if (r && r.success) await this.loadMarketAdmin();
      this.bulkPriceBusy = false;
    },
    openReportModal(rep){
      if (rep) {
        this.reportForm = {
          id: rep.id, title: rep.title, slug: rep.slug || '',
          reportType: rep.report_type || 'weekly',
          content: rep.content || '',
          coverImageUrl: rep.cover_image_url || '',
          reportDate: rep.report_date,
          countyId: rep.county_id || '', speciesId: rep.species_id || '',
          pdfUrl: rep.pdf_url || '', status: rep.status || 'draft'
        };
      } else {
        this.reportForm = { id:'', title:'', slug:'', reportType:'weekly', content:'', coverImageUrl:'', reportDate: new Date().toISOString().slice(0,10), countyId:'', speciesId:'', pdfUrl:'', status:'draft' };
      }
      this.reportError = '';
      this.reportModal = true;
    },
    async submitReport(){
      this.reportBusy = true; this.reportError = '';
      var f = this.reportForm;
      var r = await this.apiPost({
        function: 'adminUpsertMarketReport',
        id: f.id, title: f.title, slug: f.slug,
        reportType: f.reportType, content: f.content,
        coverImageUrl: f.coverImageUrl, reportDate: f.reportDate,
        countyId: f.countyId, speciesId: f.speciesId, pdfUrl: f.pdfUrl, status: f.status
      });
      if (r && r.success) { this.reportModal = false; await this.loadMarketAdmin(); }
      else this.reportError = (r && r.message) || 'Failed to save report';
      this.reportBusy = false;
    },
    async deleteReport(rep){
      if (!confirm("Delete report '" + rep.title + "'? This is permanent.")) return;
      var r = await this.apiPost({ function:'adminDeleteMarketReport', id: rep.id });
      if (r && r.success) await this.loadMarketAdmin();
      else alert((r && r.message) || 'Failed to delete');
    },

    // Dev-Requests #3 — expert insights CRUD
    async loadExpertInsights(){
      this.loadingExpertInsights = true;
      try {
        var r = await this.apiPost({ function:'getExpertInsights', limit: 100 });
        if (r && r.success) this.expertInsights = (r.data && r.data.insights) || [];
        // Lazy-load species + counties on first open for the modal dropdowns.
        if (!this.speciesList.length) {
          var sp = await this.apiPost({ function:'getAnimalSpecies' });
          if (sp && sp.success) this.speciesList = (sp.data && (sp.data.species || sp.data)) || [];
        }
        if (!this.countiesList.length) {
          var co = await this.apiPost({ function:'getCounties' });
          if (co && co.success) this.countiesList = (co.data && (co.data.counties || co.data)) || [];
        }
      } catch(e){ console.error(e); }
      this.loadingExpertInsights = false;
    },
    openInsightModal(ins){
      if (ins) {
        this.insightModal = {
          open: true, editing: true,
          id: ins.id,
          title: ins.title || '',
          body: ins.body || '',
          speciesId: ins.species_id || '',
          countyId: ins.county_id || '',
          authorName: ins.author_name || '',
          authorRole: ins.author_role || '',
          isPinned: !!Number(ins.is_pinned),
          isPublished: !!Number(ins.is_published)
        };
      } else {
        this.insightModal = { open:true, editing:false, id:'', title:'', body:'', speciesId:'', countyId:'', authorName:'', authorRole:'', isPinned:false, isPublished:true };
      }
    },
    async saveInsight(){
      var m = this.insightModal;
      var r = await this.apiPost({
        function: 'adminUpsertExpertInsight',
        id: m.id || '',
        title: m.title,
        body: m.body,
        speciesId: m.speciesId || '',
        countyId: m.countyId || '',
        authorId: sessionStorage.getItem('userId') || '',
        authorName: m.authorName || '',
        authorRole: m.authorRole || '',
        isPinned: m.isPinned ? 1 : 0,
        isPublished: m.isPublished ? 1 : 0
      });
      if (r && r.success) { this.insightModal.open = false; await this.loadExpertInsights(); }
      else alert((r && r.message) || 'Failed to save');
    },
    async deleteInsight(ins){
      if (!confirm("Archive insight '" + ins.title + "'?")) return;
      var r = await this.apiPost({ function:'adminDeleteExpertInsight', id: ins.id });
      if (r && r.success) await this.loadExpertInsights();
      else alert((r && r.message) || 'Failed to archive');
    },

// ===== COMMUNITY ADMIN =====
    async loadCommunityAdmin(){
      this.communityLoading = true;
      try {
        var ar = await this.apiPost({ function:'adminGetArticles' });
        if (ar && ar.success) this.communityArticles = (ar.data && ar.data.articles) || [];
        var ft = await this.apiPost({ function:'adminGetForumTopicsAll' });
        if (ft && ft.success) this.communityForumTopics = (ft.data && ft.data.topics) || [];
        var cc = await this.apiPost({ function:'adminGetArticleCategories' });
        if (cc && cc.success) this.communityCategories = (cc.data && cc.data.categories) || [];
      } catch(e){ console.error('loadCommunityAdmin', e); }
      this.communityLoading = false;
    },
    openArticleModal(a){
      if (a) {
        this.articleForm = {
          id: a.id, categoryId: a.category_id || '',
          title: a.title || '', slug: a.slug || '',
          excerpt: a.excerpt || '', content: a.content || '',
          coverImageUrl: a.cover_image_url || '',
          language: a.language || 'en',
          targetAudience: a.target_audience || 'all',
          status: a.status || 'draft',
          isFeatured: Number(a.is_featured || 0),
          tags: typeof a.tags === 'string' ? a.tags : JSON.stringify(a.tags || []),
          targetSpecies: typeof a.target_species === 'string' ? a.target_species : JSON.stringify(a.target_species || [])
        };
      } else {
        this.articleForm = { id:'', categoryId:'', title:'', slug:'', excerpt:'', content:'', coverImageUrl:'', language:'en', targetAudience:'all', status:'draft', isFeatured:0, tags:'[]', targetSpecies:'[]' };
      }
      this.articleError = '';
      this.articleModal = true;
    },
    async submitArticle(){
      try { JSON.parse(this.articleForm.tags || '[]'); } catch(e){ this.articleError = 'Tags must be a valid JSON array.'; return; }
      try { JSON.parse(this.articleForm.targetSpecies || '[]'); } catch(e){ this.articleError = 'Target species must be a valid JSON array.'; return; }
      this.articleBusy = true; this.articleError = '';
      var f = this.articleForm;
      var r = await this.apiPost({
        function:'adminUpsertArticle',
        id: f.id, categoryId: f.categoryId, title: f.title, slug: f.slug,
        excerpt: f.excerpt, content: f.content, coverImageUrl: f.coverImageUrl,
        language: f.language, targetAudience: f.targetAudience, status: f.status,
        isFeatured: f.isFeatured, tags: f.tags, targetSpecies: f.targetSpecies
      });
      if (r && r.success) { this.articleModal = false; await this.loadCommunityAdmin(); }
      else this.articleError = (r && r.message) || 'Failed to save article';
      this.articleBusy = false;
    },
    async deleteArticle(a){
      if (!confirm("Delete article '" + a.title + "'? This is permanent.")) return;
      var r = await this.apiPost({ function:'adminDeleteArticle', id: a.id });
      if (r && r.success) await this.loadCommunityAdmin();
      else alert((r && r.message) || 'Failed to delete');
    },
    openCategoryModal(c){
      if (c) {
        this.categoryForm = { id: c.id, name: c.name || '', slug: c.slug || '', description: c.description || '', icon: c.icon || '', sortOrder: Number(c.sort_order || 0), isActive: Number(c.is_active || 0) };
      } else {
        this.categoryForm = { id:'', name:'', slug:'', description:'', icon:'', sortOrder:0, isActive:1 };
      }
      this.categoryError = '';
      this.categoryModal = true;
    },
    async submitCategory(){
      this.categoryBusy = true; this.categoryError = '';
      var f = this.categoryForm;
      var r = await this.apiPost({ function:'adminUpsertArticleCategory', id: f.id, name: f.name, slug: f.slug, description: f.description, icon: f.icon, sortOrder: f.sortOrder, isActive: f.isActive });
      if (r && r.success) { this.categoryModal = false; await this.loadCommunityAdmin(); }
      else this.categoryError = (r && r.message) || 'Failed to save category';
      this.categoryBusy = false;
    },
    async pinTopic(t){
      var r = await this.apiPost({ function:'adminPinForumTopic', topicId: t.id, pinned: t.is_pinned == 1 ? 0 : 1 });
      if (r && r.success) await this.loadCommunityAdmin();
      else alert((r && r.message) || 'Failed to pin');
    },
    async lockTopic(t){
      var r = await this.apiPost({ function:'adminLockForumTopic', topicId: t.id, locked: t.status === 'closed' ? 0 : 1 });
      if (r && r.success) await this.loadCommunityAdmin();
      else alert((r && r.message) || 'Failed to lock');
    },
    async hideTopic(t){
      var reason = prompt("Reason for hiding this topic? (sent to author)") || '';
      if (!reason) return;
      var r = await this.apiPost({ function:'adminHideForumTopic', topicId: t.id, reason: reason });
      if (r && r.success) await this.loadCommunityAdmin();
      else alert((r && r.message) || 'Failed to hide');
    },

    async refreshMyAdminModules(){
      try {
        var r = await this.apiPost({ function:'getMyRoles', userId: sessionStorage.getItem('userId') || '' });
        if (r && r.success) {
          var d = r.data || {};
          sessionStorage.setItem('userRoles', JSON.stringify(d.roles || []));
          sessionStorage.setItem('adminModules', JSON.stringify(d.adminModules || []));
        }
      } catch(e){}
    },
    async resetUserPassword(){
      if(!confirm("Reset password for " + this.selectedUser.display_name + "? A temporary password will be sent to them by SMS and email.")) return;
      this.userActionBusy = true;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function:'adminResetUserPassword', adminUserId: adminUserId, userId: this.selectedUser.id });
      if(r && r.success){
        var d = (r.data || {}).delivered || {};
        var channels = [];
        if(d.sms) channels.push('SMS');
        if(d.email) channels.push('email');
        alert('Password reset. Temporary password sent via ' + (channels.length ? channels.join(' + ') : 'in-app notification only — user has no SMS/email on file') + '.');
      } else alert((r && r.message) || 'Failed to reset password');
      this.userActionBusy = false;
    },
    openSendMessage(){ this.messageDraft = ''; this.showMessageModal = true; },
    openSendEmail(){ this.emailDraft = { subject:'', body:'' }; this.showEmailModal = true; },
    async openChatThread(){
      // Find or create the conversation between admin and this user, then jump straight to it.
      this.userActionBusy = true;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function:'getOrCreateConversation', userA: adminUserId, userB: this.selectedUser.id, contextType:'admin_outreach' });
      this.userActionBusy = false;
      if(r && r.success && r.data && r.data.conversationId){
        this.userModal = false;
        this.$router.push('/messages/' + r.data.conversationId);
      } else {
        alert((r && r.message) || 'Could not open chat thread');
      }
    },
    async sendUserMessage(){
      if(!this.messageDraft.trim()) return;
      this.userActionBusy = true;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function:'sendMessage', senderId: adminUserId, receiverId: this.selectedUser.id, content: this.messageDraft.trim(), contextType:'admin_outreach' });
      this.userActionBusy = false;
      if(r && r.success){
        this.showMessageModal = false;
        // Jump straight into the chat thread so the admin can keep an eye on replies.
        var conv = await this.apiPost({ function:'getOrCreateConversation', userA: adminUserId, userB: this.selectedUser.id, contextType:'admin_outreach' });
        if(conv && conv.success && conv.data && conv.data.conversationId){
          this.userModal = false;
          this.$router.push('/messages/' + conv.data.conversationId);
        } else {
          alert('Message sent. Open Messages to keep chatting.');
        }
      } else alert((r && r.message) || 'Failed to send message');
    },
    async sendUserEmail(){
      if(!this.emailDraft.subject.trim() || !this.emailDraft.body.trim()) return;
      this.userActionBusy = true;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function:'adminSendUserEmail', adminUserId: adminUserId, userId: this.selectedUser.id, subject: this.emailDraft.subject, body: this.emailDraft.body });
      if(r && r.success){
        this.showEmailModal = false;
        alert('Email sent.');
      } else alert((r && r.message) || 'Failed to send email');
      this.userActionBusy = false;
    },

    aiBarColor(score) {
      score = Number(score) || 0;
      if (score >= 80) return '#50C878';
      if (score >= 50) return '#FFAB00';
      return '#FF6B6B';
    },
    // Dev-Requests #14 — badge background/foreground for the AI score chip.
    aiScoreBg(score) {
      score = Number(score) || 0;
      if (score >= 85) return 'rgba(80,200,120,.18)';
      if (score >= 50) return 'rgba(255,171,0,.18)';
      return 'rgba(255,107,107,.18)';
    },
    aiScoreFg(score) {
      score = Number(score) || 0;
      if (score >= 85) return '#50C878';
      if (score >= 50) return '#FFAB00';
      return '#FF6B6B';
    },
    // ===== IMAGE DUPLICATES (fraud guard) =====
    switchToDuplicates() {
      this.listingsSub = 'duplicates';
      if (!this.imageClusters.totalClusters && !this.imageClustersLoading) this.loadImageClusters();
    },
    async loadImageClusters() {
      this.imageClustersLoading = true;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function: 'adminGetImageDuplicates', adminUserId: adminUserId });
      this.imageClustersLoading = false;
      if (r && r.success && r.data) {
        this.imageClusters = r.data;
      } else {
        alert((r && r.message) || 'Failed to load duplicates');
      }
    },
    // Pick the oldest photo in a cluster as the canonical "original".
    originalOf(cluster) {
      return cluster.photos.slice().sort(function(a, b){ return new Date(a.created_at) - new Date(b.created_at); })[0];
    },
    async unlistDuplicate(photo, original) {
      if (!confirm('Unlist "' + photo.title + '" and notify ' + photo.seller_name + '?\n\nReason: photo is a duplicate of "' + original.title + '" (uploaded ' + original.seller_name + ').')) return;
      this.unlistingPhotoId = photo.photo_id;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({
        function: 'adminUnlistDuplicateListing',
        adminUserId: adminUserId,
        listingId: photo.listing_id,
        canonicalListingId: original.listing_id,
        canonicalTitle: original.title,
        canonicalSeller: original.seller_name
      });
      this.unlistingPhotoId = null;
      if (r && r.success) { this.loadImageClusters(); } else { alert((r && r.message) || 'Failed'); }
    },
    async forceAllowPhoto(photo) {
      var reason = window.prompt('Why is this duplicate OK to keep? (e.g. same seller running parallel listings)') || '';
      if (!reason) return;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function: 'adminForceAllowImage', adminUserId: adminUserId, photoId: photo.photo_id, reason: reason });
      if (r && r.success) { this.loadImageClusters(); } else { alert((r && r.message) || 'Failed'); }
    },
    // ===== UNVERIFIED SELLER CLEANUP =====
    switchToUnverified() {
      this.listingsSub = 'unverified';
      if (!this.unverifiedSellers.count && !this.unverifiedLoading) this.loadUnverifiedSellers();
    },
    async loadUnverifiedSellers() {
      this.unverifiedLoading = true;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function: 'adminGetUnverifiedSellers', adminUserId: adminUserId });
      this.unverifiedLoading = false;
      if (r && r.success && r.data) { this.unverifiedSellers = r.data; }
      else { alert((r && r.message) || 'Failed to load unverified sellers'); }
    },
    async bulkUnlistUnverified() {
      var n = this.unverifiedSellers.count;
      var listings = this.unverifiedSellers.totalListingsToUnlist;
      if (!confirm('Unlist all ' + listings + ' active listings across ' + n + ' unverified sellers and notify each one by SMS + email? This cannot be undone in one click.')) return;
      this.unverifiedLoading = true;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function: 'adminBulkUnlistUnverified', adminUserId: adminUserId });
      this.unverifiedLoading = false;
      if (r && r.success) {
        alert('Unlisted ' + r.data.listingsUnlisted + ' listings across ' + r.data.sellersHit + ' sellers. SMS sent: ' + r.data.smsSent + ', emails sent: ' + r.data.emailSent + '.');
        this.loadUnverifiedSellers();
      } else {
        alert((r && r.message) || 'Failed');
      }
    },

    async bulkUnlistDuplicates() {
      var total = this.imageClusters.totalClusters;
      if (!confirm('Bulk unlist EVERY non-original duplicate (' + total + ' clusters) and notify each affected seller? This cannot be undone in one click — sellers will get an SMS, email, and in-app notification.')) return;
      this.imageClustersLoading = true;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function: 'adminBulkUnlistDuplicates', adminUserId: adminUserId });
      this.imageClustersLoading = false;
      if (r && r.success) {
        alert('Unlisted ' + (r.data.unlisted || 0) + ' listings and notified ' + (r.data.notified || 0) + ' sellers.');
        this.loadImageClusters();
      } else {
        alert((r && r.message) || 'Failed');
      }
    },

    async moderateListing(l, newStatus){
      var prompts = {
        paused:  'Why are you pausing this listing? (shown to seller)',
        removed: 'Why are you removing this listing from the marketplace? (shown to seller)',
        active:  ''
      };
      var reason = '';
      if (prompts[newStatus]) {
        reason = window.prompt(prompts[newStatus] + '\n\nLeave blank to skip the reason — but providing one is recommended.', '') || '';
      }
      var confirmText = {
        paused:  'Pause "' + l.title + '"? It will be hidden from the marketplace until reactivated.',
        removed: 'REMOVE "' + l.title + '" from the marketplace? Seller will be notified by email.',
        active:  'Reactivate "' + l.title + '"? It will become visible on the marketplace again.'
      };
      if (!confirm(confirmText[newStatus])) return;
      this.moderatingId = l.id;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function:'adminUpdateListingStatus', adminUserId: adminUserId, listingId: l.id, status: newStatus, reason: reason });
      this.moderatingId = null;
      if (r && r.success) {
        l.status = newStatus; // optimistic local update
      } else {
        alert((r && r.message) || 'Failed to update listing status');
      }
    },

    async loadAllTransporters(){
      this.loadingTransporters = true;
      try{
        var r = await this.apiPost({ function:'adminGetAllTransporters', search: this.transporterSearch, status: this.transporterStatusFilter });
        if(r && r.success){ this.allTransporters = (r.data && r.data.transporters) || []; }
      }catch(e){ console.error(e); }
      this.loadingTransporters = false;
    },
    async messageTransporter(t){
      // Open a chat thread with this transporter so the admin can negotiate before booking
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function:'getOrCreateConversation', userA: adminUserId, userB: t.user_id, contextType:'admin_outreach' });
      if(r && r.success && r.data && r.data.conversationId){
        this.$router.push('/messages/' + r.data.conversationId);
      } else {
        alert((r && r.message) || 'Could not open chat with transporter');
      }
    },
    arrangeWith(t){
      // Pre-fill the existing arrange modal with this transporter, then drop the admin into the
      // booking form to fill in the buyer + cargo + route.
      this.openArrangeTransport();
      this.arrange.transporterUserId = t.user_id;
      this.arrange.transporterSearch = t.company_name || t.transporter_name;
      this.arrange.transporterMatches = [];
    },
    async loadAdminPlans(){
      this.plansLoading = true;
      try {
        var r = await this.apiPost({ function: 'adminGetSubscriptionPlans', adminUserId: sessionStorage.getItem('userId') || '' });
        if (r && r.success) this.adminPlans = (r.data && r.data.plans) || [];
      } catch(e) {}
      this.plansLoading = false;
    },
    openPlanEditor(plan){
      if (plan) {
        this.planEditor.form = {
          id: plan.id,
          name: plan.name || '',
          slug: plan.slug || '',
          description: plan.description || '',
          targetUserType: plan.target_user_type || 'any',
          priceMonthly: Number(plan.priceMonthly || 0),
          priceAnnually: plan.priceAnnually !== null && plan.priceAnnually !== undefined ? plan.priceAnnually : '',
          currency: plan.currency || 'KES',
          maxListings: plan.max_listings !== null ? plan.max_listings : '',
          maxPhotosPerListing: plan.max_photos_per_listing !== null ? plan.max_photos_per_listing : 5,
          commissionRatePct: plan.commission_rate_pct !== null ? plan.commission_rate_pct : '',
          isActive: !!plan.isActive,
          isPopular: !!plan.isPopular,
          sortOrder: plan.sort_order || 99,
          featuresJson: plan.features || '{}',
        };
      } else {
        this.planEditor.form = { id:null, name:'', slug:'', description:'', targetUserType:'any', priceMonthly:0, priceAnnually:'', currency:'KES', maxListings:'', maxPhotosPerListing:5, commissionRatePct:'', isActive:true, isPopular:false, sortOrder: (this.adminPlans.length + 1), featuresJson:'{"max_listings":10,"analytics":true,"support":"email"}' };
      }
      this.planEditor.open = true;
    },
    closePlanEditor(){ if (this.planEditor.saving) return; this.planEditor.open = false; },
    async savePlan(){
      this.planEditor.saving = true;
      var f = this.planEditor.form;
      var payload = {
        function: 'adminUpsertSubscriptionPlan',
        adminUserId: sessionStorage.getItem('userId') || '',
        name: f.name, slug: f.slug, description: f.description || '',
        targetUserType: f.targetUserType, priceMonthly: f.priceMonthly,
        priceAnnually: f.priceAnnually === '' ? '' : f.priceAnnually,
        currency: f.currency || 'KES',
        maxListings: f.maxListings === '' ? '' : f.maxListings,
        maxPhotosPerListing: f.maxPhotosPerListing === '' ? '' : f.maxPhotosPerListing,
        commissionRatePct: f.commissionRatePct === '' ? '' : f.commissionRatePct,
        isActive: f.isActive ? 1 : 0, isPopular: f.isPopular ? 1 : 0,
        sortOrder: f.sortOrder, featuresJson: f.featuresJson || '{}',
      };
      if (f.id) payload.id = f.id;
      var r = await this.apiPost(payload);
      this.planEditor.saving = false;
      if (r && r.success) { alert(r.message || 'Plan saved'); this.planEditor.open = false; await this.loadAdminPlans(); }
      else { alert((r && r.message) || 'Save failed'); }
    },
    async togglePlan(plan, field, newValue){
      var r = await this.apiPost({ function: 'adminToggleSubscriptionPlan', adminUserId: sessionStorage.getItem('userId') || '', id: plan.id, field: field, value: newValue ? 1 : 0 });
      if (r && r.success) { await this.loadAdminPlans(); }
      else { alert((r && r.message) || 'Toggle failed'); await this.loadAdminPlans(); }
    },
    canAdminOverride(status){
      // Only show override for orders that are still moving through fulfilment — done/cancelled don't need help
      return ['paid','confirmed','preparing','dispatched','in_transit','delivered','inspection'].indexOf(status) !== -1;
    },
    paymentIssue(o){
      var unpaidish = ['pending_payment','payment_initiated','stk_sent'].indexOf(o.status) !== -1
        || (['cancelled','expired'].indexOf(o.status) !== -1 && this.escrowDisplay(o) === 'Not Funded');
      if (!unpaidish) return '';
      var s = o.payment_status_last;
      if (s === 'cancelled' || s === 'timeout' || s === 'failed') return o.payment_result_desc || s;
      return '';
    },
    escrowDisplay(o){
      var unpaid = ['pending_payment','payment_initiated','stk_sent','cancelled','expired'].indexOf(o.status) !== -1;
      var e = o.escrow_status;
      if ((!e || e === 'pending') && unpaid) return 'Not Funded';
      return e || '-';
    },
    // Admin-portal #21 — no Arrange on unpaid/dead orders: transport is only
    // arranged once the buyer's money is in escrow.
    needsArrange(o){
      if (o.transport_option !== 'flockr_arranges') return false;
      // #19 — a cancelled booking frees the order up for re-arranging
      if (o.transport_booking_id && o.transport_booking_status !== 'cancelled') return false;
      return ['pending','pending_payment','payment_initiated','stk_sent','cancelled','expired','refunded'].indexOf(o.status) === -1;
    },
    // Admin-portal #27 — vet/transporter/seller/aggregator/agro-input identities
    // live in user_roles (users.user_type enum can never hold them), so those
    // tabs match on the roles list; the base types keep the user_type match.
    userMatchesType(u, t){
      if (['seller','aggregator','vet','transporter','product_seller'].indexOf(t) > -1) {
        return (u.roles || '').split(',').indexOf(t) > -1;
      }
      return (u.user_type || u.role_type || 'buyer') === t;
    },
    openAdminOverride(o){ this.overrideOrder = o; this.overrideNewStatus = ''; this.overrideNotes = ''; },
    closeAdminOverride(){ if (this.overriding) return; this.overrideOrder = null; },
    async submitAdminOverride(){
      if (!this.overrideOrder || !this.overrideNewStatus) return;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var critical = (this.overrideNewStatus === 'completed' || this.overrideNewStatus === 'cancelled');
      var msg = critical
        ? "Apply '" + this.overrideNewStatus + "' to order " + this.overrideOrder.order_number + "?\n\n" + (this.overrideNewStatus === 'completed' ? "This will release KES " + this.fN(this.overrideOrder.total_amount) + " from escrow to the seller's wallet." : "This will refund the buyer.")
        : "Move order " + this.overrideOrder.order_number + " to '" + this.overrideNewStatus + "'?";
      if (!confirm(msg)) return;
      this.overriding = true;
      var r = await this.apiPost({
        function: 'adminUpdateOrderStatus',
        adminUserId: adminUserId,
        orderId: this.overrideOrder.id,
        status: this.overrideNewStatus,
        adminNotes: this.overrideNotes || ''
      });
      this.overriding = false;
      if (r && r.success) {
        alert((r.message || 'Order updated'));
        this.overrideOrder = null;
        if (typeof this.loadOrders === 'function') await this.loadOrders();
      } else {
        alert((r && r.message) || 'Override failed');
      }
    },
    arrangeForOrder(o){
      // Open the arrange modal pre-filled with the order's buyer, delivery location, animal count, and order ref.
      // Pickup is left blank — admin needs to confirm with seller (and may use the seller's profile location later).
      this.openArrangeTransport();
      this.arrange.onBehalfOfUserId = o.buyer_id;
      this.arrange.buyerSearch = (o.buyer_name || '') + (o.buyer_phone ? ' · ' + o.buyer_phone : '');
      this.arrange.buyerMatches = [];
      this.prefillArrangeFromOrder(o);
    },
    prefillArrangeFromOrder(o){
      this.arrange.orderId = o.id;
      this.arrange.animalCount = Number(o.quantity || 1);
      this.arrange.animalTypeDescription = o.listing_title || '';
      // Delivery comes from the order
      if (o.delivery_location) {
        this.arrange.deliveryAddress = o.delivery_location;
      }
      if (o.delivery_latitude && o.delivery_longitude) {
        this.arrange.deliveryLatitude = Number(o.delivery_latitude);
        this.arrange.deliveryLongitude = Number(o.delivery_longitude);
      }
      // Admin-portal #15: pickup defaults to where the listing was set up (still editable)
      var pickupNote;
      if (o.listing_latitude && o.listing_longitude) {
        this.arrange.pickupAddress = o.listing_location || 'Listing location';
        this.arrange.pickupLatitude = Number(o.listing_latitude);
        this.arrange.pickupLongitude = Number(o.listing_longitude);
        pickupNote = 'Pickup prefilled from the listing location — confirm with seller (' + (o.seller_name || '') + (o.seller_phone ? ', ' + o.seller_phone : '') + ').';
      } else {
        pickupNote = 'Pickup location: please confirm with seller (' + (o.seller_name || '') + (o.seller_phone ? ', ' + o.seller_phone : '') + ').';
      }
      // Helpful default note so the transporter sees the order context
      this.arrange.specialInstructions = 'Linked to Order #' + (o.order_number || o.id) + '. ' + pickupNote;
    },
    async verifyTransporter(t, makeVerified){
      var verb = makeVerified ? 'verify' : 'revoke verification for';
      var note = window.prompt('Optional note (will be stored in the audit log and shown to the transporter):', '') || '';
      if (!confirm('Are you sure you want to ' + verb + ' "' + (t.company_name || t.transporter_name) + '"?')) return;
      this.verifyingId = t.id;
      var adminUserId = sessionStorage.getItem('userId') || '';
      var r = await this.apiPost({ function:'adminVerifyTransporter', adminUserId: adminUserId, userId: t.user_id, verified: makeVerified ? '1' : '0', note: note });
      this.verifyingId = null;
      if (r && r.success) {
        t.ntsa_verified = makeVerified ? '1' : '0'; // optimistic local update
      } else {
        alert((r && r.message) || 'Failed to update verification');
      }
    },

    async loadTransports(){
      this.transportsLoading = true;
      try{
        var params = { function:'adminGetTransportBookings' };
        if(this.transportFilter.status) params.status = this.transportFilter.status;
        if(this.transportFilter.paymentStatus) params.paymentStatus = this.transportFilter.paymentStatus;
        if(this.transportFilter.dateFrom) params.dateFrom = this.transportFilter.dateFrom;
        if(this.transportFilter.dateTo) params.dateTo = this.transportFilter.dateTo;
        if(this.transportFilter.search) params.search = this.transportFilter.search;
        var r = await this.apiPost(params);
        if(r && r.success){
          this.transports = (r.data && r.data.bookings) || [];
          this.transportStats = (r.data && r.data.stats) || {};
        }
      }catch(e){ console.error(e); }
      this.transportsLoading = false;
    },

    // ===== INSURANCE ADMIN =====
    humanizeTypes(raw){
      if(!raw) return '—';
      try{
        var arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if(Array.isArray(arr)) return arr.map(function(s){return String(s).replace('_',' ');}).join(', ');
      }catch(e){}
      return String(raw);
    },
    async loadInsurancePolicies(){
      this.loadingInsurance = true;
      try{
        // Eager-load providers for the filter dropdown / activate context — cheap and useful
        if(!this.insuranceProviders.length) await this.loadInsuranceProviders(true);
        var params = { function:'adminGetAllInsurancePolicies' };
        if(this.polFilter.status) params.status = this.polFilter.status;
        if(this.polFilter.paymentStatus) params.paymentStatus = this.polFilter.paymentStatus;
        if(this.polFilter.providerId) params.providerId = this.polFilter.providerId;
        if(this.polFilter.search) params.search = this.polFilter.search;
        var r = await this.apiPost(params);
        if(r && r.success){
          this.insurancePolicies = (r.data && r.data.policies) || [];
          this.insuranceStats    = (r.data && r.data.stats) || {};
        }
      }catch(e){ console.error('loadInsurancePolicies',e); }
      this.loadingInsurance = false;
    },
    async loadInsuranceClaims(){
      this.loadingInsurance = true;
      try{
        var params = { function:'adminGetAllInsuranceClaims' };
        if(this.claimFilter.status) params.status = this.claimFilter.status;
        if(this.claimFilter.claimType) params.claimType = this.claimFilter.claimType;
        if(this.claimFilter.search) params.search = this.claimFilter.search;
        var r = await this.apiPost(params);
        if(r && r.success){
          this.insuranceClaims = (r.data && r.data.claims) || [];
          this.claimStats      = (r.data && r.data.stats) || {};
        }
      }catch(e){ console.error('loadInsuranceClaims',e); }
      this.loadingInsurance = false;
    },
    async loadInsuranceProducts(){
      this.loadingInsurance = true;
      try{
        if(!this.insuranceProviders.length) await this.loadInsuranceProviders(true);
        var r = await this.apiPost({ function:'adminGetInsuranceProductsAll' });
        if(r && r.success) this.insuranceProducts = (r.data && r.data.products) || [];
      }catch(e){ console.error('loadInsuranceProducts',e); }
      this.loadingInsurance = false;
    },
    async loadInsuranceProviders(silent){
      if(!silent) this.loadingInsurance = true;
      try{
        var r = await this.apiPost({ function:'adminGetInsuranceProviders' });
        if(r && r.success) this.insuranceProviders = (r.data && r.data.providers) || [];
      }catch(e){ console.error('loadInsuranceProviders',e); }
      if(!silent) this.loadingInsurance = false;
    },

    // -- Activate policy
    openActivatePolicy(p){
      this.activatePolicy = p;
      this.activateForm = { externalPolicyNumber:'', adminNote:'' };
      this.activateError = '';
      this.activateModal = true;
    },
    async submitActivatePolicy(){
      this.activateBusy = true; this.activateError = '';
      var r = await this.apiPost({
        function:'adminActivatePolicy',
        adminUserId: sessionStorage.getItem('userId') || '',
        policyId: this.activatePolicy.id,
        externalPolicyNumber: this.activateForm.externalPolicyNumber,
        adminNote: this.activateForm.adminNote
      });
      if(r && r.success){
        this.activateModal = false;
        await this.loadInsurancePolicies();
      } else {
        this.activateError = (r && r.message) || 'Failed to activate policy.';
      }
      this.activateBusy = false;
    },

    // -- Cancel policy
    openCancelPolicy(p){
      this.cancelPolicyTarget = p;
      this.cancelPolicyReason = '';
      this.cancelPolicyError = '';
      this.cancelPolicyModal = true;
    },
    async submitCancelPolicy(){
      if(!this.cancelPolicyReason.trim()) { this.cancelPolicyError = 'Reason is required.'; return; }
      this.cancelPolicyBusy = true; this.cancelPolicyError = '';
      var r = await this.apiPost({
        function:'adminCancelPolicy',
        adminUserId: sessionStorage.getItem('userId') || '',
        policyId: this.cancelPolicyTarget.id,
        reason: this.cancelPolicyReason
      });
      if(r && r.success){
        this.cancelPolicyModal = false;
        await this.loadInsurancePolicies();
      } else {
        this.cancelPolicyError = (r && r.message) || 'Failed to cancel policy.';
      }
      this.cancelPolicyBusy = false;
    },

    // -- Claim review
    openClaimDetail(c){
      this.claimDetailTarget = c;
      this.claimAction = '';
      this.claimActionForm = { reviewNotes:'', approvedAmount:null, rejectionReason:'', payoutAmount:null, payoutMethod:'mpesa', payoutReference:'' };
      this.claimActionError = '';
      this.claimDetailModal = true;
    },
    closeClaimDetail(){ this.claimDetailModal = false; this.claimDetailTarget = null; this.claimAction = ''; },
    async submitClaimAction(){
      if(!this.claimAction) return;
      this.claimActionBusy = true; this.claimActionError = '';
      var params = {
        function:'adminUpdateClaimStatus',
        adminUserId: sessionStorage.getItem('userId') || '',
        claimId: this.claimDetailTarget.id,
        status: this.claimAction
      };
      if(this.claimAction === 'under_review' || this.claimAction === 'approved') params.reviewNotes = this.claimActionForm.reviewNotes;
      if(this.claimAction === 'approved') params.approvedAmount = this.claimActionForm.approvedAmount;
      if(this.claimAction === 'rejected') params.rejectionReason = this.claimActionForm.rejectionReason;
      if(this.claimAction === 'paid'){
        params.payoutAmount    = this.claimActionForm.payoutAmount;
        params.payoutMethod    = this.claimActionForm.payoutMethod;
        params.payoutReference = this.claimActionForm.payoutReference;
      }
      var r = await this.apiPost(params);
      if(r && r.success){
        this.closeClaimDetail();
        await this.loadInsuranceClaims();
      } else {
        this.claimActionError = (r && r.message) || 'Failed to update claim.';
      }
      this.claimActionBusy = false;
    },

    // -- Product CRUD
    openProductModal(p){
      if(!this.insuranceProviders.length) this.loadInsuranceProviders(true);
      if(p){
        this.productForm = {
          id: p.id,
          providerId: p.provider_id,
          name: p.name || '',
          description: p.description || '',
          insuranceType: p.insurance_type || 'livestock',
          coverageDetailsRaw: typeof p.coverage_details === 'string' ? p.coverage_details : JSON.stringify(p.coverage_details||{}),
          eligibleSpeciesRaw: typeof p.eligible_species === 'string' ? p.eligible_species : JSON.stringify(p.eligible_species||[]),
          minAnimalValue: p.min_animal_value !== null && p.min_animal_value !== undefined ? Number(p.min_animal_value) : null,
          maxAnimalValue: p.max_animal_value !== null && p.max_animal_value !== undefined ? Number(p.max_animal_value) : null,
          premiumRatePct: p.premium_rate_pct !== null && p.premium_rate_pct !== undefined ? Number(p.premium_rate_pct) : null,
          flatPremium: p.flat_premium !== null && p.flat_premium !== undefined ? Number(p.flat_premium) : null,
          durationMonths: Number(p.duration_months || 12),
          waitingPeriodDays: Number(p.waiting_period_days || 0),
          isActive: Number(p.is_active || 0)
        };
      } else {
        this.productForm = { id:'', providerId:'', name:'', description:'', insuranceType:'livestock', coverageDetailsRaw:'{"covers":[],"excludes":[],"max_payout_pct":100}', eligibleSpeciesRaw:'[]', minAnimalValue:null, maxAnimalValue:null, premiumRatePct:null, flatPremium:null, durationMonths:12, waitingPeriodDays:0, isActive:1 };
      }
      this.productError = '';
      this.productModal = true;
    },
    async submitProduct(){
      // Validate JSON inputs client-side so we surface errors before they reach the DB
      try { JSON.parse(this.productForm.coverageDetailsRaw || '{}'); } catch(e){ this.productError = 'Coverage details must be valid JSON.'; return; }
      try { JSON.parse(this.productForm.eligibleSpeciesRaw || '[]'); } catch(e){ this.productError = 'Eligible species must be a valid JSON array.'; return; }
      this.productBusy = true; this.productError = '';
      var r = await this.apiPost({
        function:'adminUpsertInsuranceProduct',
        adminUserId: sessionStorage.getItem('userId') || '',
        productId: this.productForm.id,
        providerId: this.productForm.providerId,
        name: this.productForm.name,
        description: this.productForm.description,
        insuranceType: this.productForm.insuranceType,
        coverageDetails: this.productForm.coverageDetailsRaw,
        eligibleSpecies: this.productForm.eligibleSpeciesRaw,
        minAnimalValue: this.productForm.minAnimalValue,
        maxAnimalValue: this.productForm.maxAnimalValue,
        premiumRatePct: this.productForm.premiumRatePct,
        flatPremium:    this.productForm.flatPremium,
        durationMonths: this.productForm.durationMonths,
        waitingPeriodDays: this.productForm.waitingPeriodDays,
        isActive: this.productForm.isActive
      });
      if(r && r.success){
        this.productModal = false;
        await this.loadInsuranceProducts();
      } else {
        this.productError = (r && r.message) || 'Failed to save product.';
      }
      this.productBusy = false;
    },

    // -- Provider CRUD
    openProviderModal(p){
      if(p){
        this.providerForm = {
          id: p.id,
          name: p.name || '',
          shortName: p.short_name || '',
          logoUrl: p.logo_url || '',
          contactEmail: p.contact_email || '',
          contactPhone: p.contact_phone || '',
          websiteUrl: p.website_url || '',
          insuranceTypesRaw: typeof p.insurance_types === 'string' ? p.insurance_types : JSON.stringify(p.insurance_types||[]),
          isActive: Number(p.is_active || 0),
          usePlatformMpesa: Number(p.use_platform_mpesa != null ? p.use_platform_mpesa : 1),
          mpesaShortcode: p.mpesa_shortcode || '',
          mpesaShortcodeType: p.mpesa_shortcode_type || 'paybill',
          // Secrets are NEVER pre-filled — server preserves on blank, admin re-enters to rotate
          mpesaConsumerKey: '',
          mpesaConsumerSecret: '',
          mpesaPasskey: '',
          mpesaEnvironment: p.mpesa_environment || 'production',
          accountRefTemplate: p.account_ref_template || '{policyNumber}'
        };
      } else {
        this.providerForm = { id:'', name:'', shortName:'', logoUrl:'', contactEmail:'', contactPhone:'', websiteUrl:'', insuranceTypesRaw:'["livestock"]', isActive:1, usePlatformMpesa:1, mpesaShortcode:'', mpesaShortcodeType:'paybill', mpesaConsumerKey:'', mpesaConsumerSecret:'', mpesaPasskey:'', mpesaEnvironment:'production', accountRefTemplate:'{policyNumber}' };
      }
      this.providerError = '';
      this.providerSecretsRevealed = false;
      this.providerTestPhone = '';
      this.providerTestResult = null;
      this.providerModal = true;
    },
    async submitProvider(){
      try { JSON.parse(this.providerForm.insuranceTypesRaw || '[]'); } catch(e){ this.providerError = 'Insurance types must be a valid JSON array.'; return; }
      if(this.providerForm.usePlatformMpesa===0 && !this.providerForm.mpesaShortcode){
        this.providerError = 'Shortcode is required when using the provider’s own M-Pesa.'; return;
      }
      this.providerBusy = true; this.providerError = '';
      var r = await this.apiPost({
        function:'adminUpsertInsuranceProvider',
        adminUserId: sessionStorage.getItem('userId') || '',
        providerId: this.providerForm.id,
        name: this.providerForm.name,
        shortName: this.providerForm.shortName,
        logoUrl: this.providerForm.logoUrl,
        contactEmail: this.providerForm.contactEmail,
        contactPhone: this.providerForm.contactPhone,
        websiteUrl: this.providerForm.websiteUrl,
        insuranceTypes: this.providerForm.insuranceTypesRaw,
        isActive: this.providerForm.isActive,
        usePlatformMpesa: this.providerForm.usePlatformMpesa,
        mpesaShortcode: this.providerForm.mpesaShortcode,
        mpesaShortcodeType: this.providerForm.mpesaShortcodeType,
        mpesaConsumerKey: this.providerForm.mpesaConsumerKey,
        mpesaConsumerSecret: this.providerForm.mpesaConsumerSecret,
        mpesaPasskey: this.providerForm.mpesaPasskey,
        mpesaEnvironment: this.providerForm.mpesaEnvironment,
        accountRefTemplate: this.providerForm.accountRefTemplate
      });
      if(r && r.success){
        this.providerModal = false;
        await this.loadInsuranceProviders();
      } else {
        this.providerError = (r && r.message) || 'Failed to save provider.';
      }
      this.providerBusy = false;
    },
    async testProviderMpesa(){
      if(!this.providerForm.id || !this.providerTestPhone) return;
      this.providerTestBusy = true; this.providerTestResult = null;
      var r = await this.apiPost({
        function:'adminTestProviderMpesa',
        adminUserId: sessionStorage.getItem('userId') || '',
        providerId: this.providerForm.id,
        phone: this.providerTestPhone,
        amount: 1
      });
      this.providerTestResult = {
        success: !!(r && r.success),
        message: r && r.message ? r.message : 'No response'
      };
      this.providerTestBusy = false;
    },

    transportStatusGroup(s){
      if(['requested','quoted'].indexOf(s)>-1) return 'pending';
      if(s==='completed') return 'completed';
      if(s==='cancelled') return 'cancelled';
      return 'active';
    },

    openArrangeTransport(){
      this.arrange = { onBehalfOfUserId:'', buyerSearch:'', buyerMatches:[], transporterUserId:'', transporterSearch:'', transporterMatches:[], orderId:'', animalCount:1, animalTypeDescription:'', estimatedTotalWeightKg:null, pickupAddress:'', pickupLatitude:null, pickupLongitude:null, pickupMatches:[], pickupLandmark:'', pickupContactName:'', pickupContactPhone:'', deliveryAddress:'', deliveryLatitude:null, deliveryLongitude:null, deliveryMatches:[], deliveryContactName:'', deliveryContactPhone:'', scheduledPickupDate:'', scheduledPickupTime:'', quotedPrice:null, specialInstructions:'', adminNotes:'' };
      this.arrangeError = '';
      this.showArrangeModal = true;
    },

    searchBuyer(){
      var q = (this.arrange.buyerSearch || '').toLowerCase().trim();
      if(q.length < 2){ this.arrange.buyerMatches = []; return; }
      this.arrange.buyerMatches = (this.allUsers || []).filter(function(u){
        return (u.display_name || '').toLowerCase().indexOf(q) > -1
            || (u.phone || '').toLowerCase().indexOf(q) > -1
            || (u.email || '').toLowerCase().indexOf(q) > -1;
      }).slice(0, 8);
    },

    pickBuyer(u){
      this.arrange.onBehalfOfUserId = u.id;
      this.arrange.buyerSearch = (u.display_name || '') + (u.phone ? ' · ' + u.phone : '');
      this.arrange.buyerMatches = [];
    },

    async searchArrangeTransporter(){
      var q = (this.arrange.transporterSearch || '').toLowerCase().trim();
      if(q.length < 2){ this.arrange.transporterMatches = []; return; }
      try{
        var r = await this.apiPost({ function:'searchTransporters', animalType:'' });
        if(r && r.success){
          var list = (r.data && r.data.transporters) || [];
          this.arrange.transporterMatches = list.filter(function(t){
            return (t.company_name || '').toLowerCase().indexOf(q) > -1
                || (t.transporter_name || '').toLowerCase().indexOf(q) > -1;
          }).slice(0, 8);
        }
      }catch(e){ this.arrange.transporterMatches = []; }
    },

    pickArrangeTransporter(t){
      this.arrange.transporterUserId = t.user_id;
      this.arrange.transporterSearch = t.company_name || t.transporter_name;
      this.arrange.transporterMatches = [];
    },

    async searchArrangeLocation(which){
      var q = (which === 'pickup' ? this.arrange.pickupAddress : this.arrange.deliveryAddress) || '';
      q = String(q).trim();
      if(q.length < 3){
        if(which === 'pickup') this.arrange.pickupMatches = []; else this.arrange.deliveryMatches = [];
        return;
      }
      try{
        var url = 'https://nominatim.openstreetmap.org/search?format=json&countrycodes=ke&limit=6&q=' + encodeURIComponent(q);
        var res = await fetch(url); var data = await res.json();
        var mapped = data.map(function(d){ var parts = (d.display_name || '').split(','); return { display_name: parts.slice(0,3).join(', ').trim(), lat: d.lat, lon: d.lon }; });
        if(which === 'pickup') this.arrange.pickupMatches = mapped; else this.arrange.deliveryMatches = mapped;
      }catch(e){}
    },

    pickArrangeLocation(which, loc){
      if(which === 'pickup'){
        this.arrange.pickupAddress = loc.display_name;
        this.arrange.pickupLatitude = Number(loc.lat); this.arrange.pickupLongitude = Number(loc.lon);
        this.arrange.pickupMatches = [];
      } else {
        this.arrange.deliveryAddress = loc.display_name;
        this.arrange.deliveryLatitude = Number(loc.lat); this.arrange.deliveryLongitude = Number(loc.lon);
        this.arrange.deliveryMatches = [];
      }
    },

    async submitArrangeTransport(){
      this.arrangeError = '';
      if(!this.arrange.onBehalfOfUserId){ this.arrangeError = 'Pick the buyer this transport is for'; return; }
      if(!this.arrange.pickupAddress || !this.arrange.pickupLatitude){ this.arrangeError = 'Pickup address is required (pick one from the search list)'; return; }
      if(!this.arrange.deliveryAddress || !this.arrange.deliveryLatitude){ this.arrangeError = 'Delivery address is required (pick one from the search list)'; return; }
      if(!this.arrange.scheduledPickupDate){ this.arrangeError = 'Pickup date is required'; return; }

      this.arranging = true;
      try{
        var payload = {
          function:'adminCreateTransportBooking',
          adminUserId: sessionStorage.getItem('userId') || '',
          onBehalfOfUserId: this.arrange.onBehalfOfUserId,
          transporterUserId: this.arrange.transporterUserId || '',
          orderId: this.arrange.orderId || '',
          animalCount: this.arrange.animalCount,
          animalTypeDescription: this.arrange.animalTypeDescription || '',
          estimatedTotalWeightKg: this.arrange.estimatedTotalWeightKg || '',
          pickupAddress: this.arrange.pickupAddress + (this.arrange.pickupLandmark ? ' · Landmark: ' + this.arrange.pickupLandmark.trim() : ''),
          pickupLatitude: this.arrange.pickupLatitude,
          pickupLongitude: this.arrange.pickupLongitude,
          pickupContactName: this.arrange.pickupContactName || '',
          pickupContactPhone: this.arrange.pickupContactPhone || '',
          deliveryAddress: this.arrange.deliveryAddress,
          deliveryLatitude: this.arrange.deliveryLatitude,
          deliveryLongitude: this.arrange.deliveryLongitude,
          deliveryContactName: this.arrange.deliveryContactName || '',
          deliveryContactPhone: this.arrange.deliveryContactPhone || '',
          scheduledPickupDate: this.arrange.scheduledPickupDate,
          scheduledPickupTime: this.arrange.scheduledPickupTime || '',
          quotedPrice: this.arrange.quotedPrice || '',
          specialInstructions: this.arrange.specialInstructions || '',
          adminNotes: this.arrange.adminNotes || ''
        };
        var r = await this.apiPost(payload);
        if(r && r.success){
          this.showArrangeModal = false;
          await this.loadTransports();
        } else {
          this.arrangeError = (r && r.message) || 'Failed to create booking';
        }
      }catch(e){
        this.arrangeError = (e.response && e.response.data && e.response.data.message) || 'Network error';
      }
      this.arranging = false;
    },
    viewOrder(id) { this.$router.push('/orders/' + id); },
    getInitials(name) { if(!name) return '?'; return name.split(' ').map(function(w){return w[0]}).join('').substring(0,2).toUpperCase(); },

    async viewUserDetail(u) {
      this.selectedUser = u;
      this.userListings = []; this.userReviews = []; this.userRatingStats = null; this.userVerifications = []; this.userOrders = [];
      this.userRoles = []; this.userModules = []; this.moduleCatalogue = []; this.userActionBusy = false;
      this.userSub = null;
      this.userModal = true;
      this.loadUserRoles(u.id);
      this.loadUserPermissions(u.id);
      this.loadUserSubscription(u.id);

      // Load user profile with rating stats
      try {
        var f = new FormData(); f.append('function', 'getUserProfile'); f.append('userId', u.id); f.append('viewerId', sessionStorage.getItem('userId')||'');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.user) {
          var full = r.data.data.user;
          this.selectedUser = Object.assign({}, u, full);
          var stats = full.rating_stats || [];
          this.ratingStats = stats.find(function(s) { return s.role_context === 'seller'; }) || stats[0] || null;
          this.userRatingStats = this.ratingStats;
          this.userVerifications = full.verifications || [];
        }
      } catch (e) {}

      // Load listings if seller
      if (u.role_type === 'seller') {
        this.userListingsLoading = true;
        try {
          var f2 = new FormData(); f2.append('function', 'getMyListings'); f2.append('sellerId', u.id);
          var r2 = await execute(f2, flocker);
          if (r2.data && r2.data.success && r2.data.data) this.userListings = r2.data.data.listings || [];
        } catch (e) {}
        this.userListingsLoading = false;

        // Load reviews
        this.userReviewsLoading = true;
        try {
          var f3 = new FormData(); f3.append('function', 'getUserReviews'); f3.append('userId', u.id);
          var r3 = await execute(f3, flocker);
          if (r3.data && r3.data.success && r3.data.data) this.userReviews = r3.data.data.reviews || [];
        } catch (e) {}
        this.userReviewsLoading = false;
      }

      // Load recent orders
      try {
        var f4 = new FormData(); f4.append('function', 'adminGetUserOrders'); f4.append('adminUserId', sessionStorage.getItem('userId')||''); f4.append('userId', u.id);
        var r4 = await execute(f4, flocker);
        if (r4.data && r4.data.success && r4.data.data) this.userOrders = r4.data.data.orders || [];
      } catch (e) {}
    },

    async loadAll() {
      this.loading = true;
      await Promise.all([this.loadStats(), this.loadOrders(), this.loadUsers(), this.loadFinance(), this.loadFlockrPayments(), this.loadDisputes(), this.loadListings(), this.loadVerifications(), this.loadSettings()]);
      this.loading = false;
    },

    async loadStats() {
      try {
        var f = new FormData(); f.append('function', 'adminGetStats'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.stats = r.data.data.stats || {};
          this.revenue = r.data.data.revenue || this.revenue;
          this.newUsers = r.data.data.newUsers || [];
        }
      } catch (e) { console.error(e); }
    },
    async loadOrders() {
      try {
        var f = new FormData(); f.append('function', 'adminGetOrders'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.allOrders = r.data.data.orders || [];
          this.recentOrders = this.allOrders.slice(0, 10);
        }
      } catch (e) {}
    },
    async loadUsers() {
      try {
        var f = new FormData(); f.append('function', 'adminGetUsers'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) { this.allUsers = r.data.data.users || []; }
      } catch (e) {}
    },
    // ---- Field Agents ----
    async loadAgents() {
      this.agentsLoading = true;
      try {
        var f = new FormData(); f.append('function', 'adminGetAgents'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) { this.agents = r.data.data.agents || []; }
      } catch (e) { console.error('loadAgents', e); }
      this.agentsLoading = false;
    },

    // Dev-Requests #9/#10/#11 — referral admin dashboard loaders
    // ===== Analytics tab (Alex 2026-07-21) =====
    async loadAnalytics() {
      this.analyticsBusy = true;
      try {
        var r = await this.apiPost({ function:'adminGetAnalyticsOverview', days: this.analyticsDays });
        if (r && r.success && r.data) {
          this.analytics = {
            kpis: Object.assign({}, this.analytics.kpis, r.data.kpis || {}),
            series: r.data.series || []
          };
        }
        this.loadFunnel();
        this.loadTopListings();
        this.loadTopActions();
      } catch(e){ console.error('loadAnalytics', e); }
      this.analyticsBusy = false;
    },
    // Admin-portal #5 — click a KPI card to see the records behind the number.
    async openAnalyticsDrill(metric) {
      var defs = {
        active_today:     { title: 'Active users · last 24h', cols: [
          {key:'display_name',label:'Name'},{key:'phone',label:'Phone',fmt:'mono'},{key:'user_type',label:'Type',fmt:'cap'},{key:'login_count',label:'Total logins',fmt:'num'},{key:'last_active_at',label:'Last active',fmt:'date'}]},
        new_signups:      { title: 'New signups · last ' + this.analyticsDays + ' days', cols: [
          {key:'display_name',label:'Name'},{key:'phone',label:'Phone',fmt:'mono'},{key:'user_type',label:'Type',fmt:'cap'},{key:'created_at',label:'Joined',fmt:'date'}]},
        listings_created: { title: 'New listings · last ' + this.analyticsDays + ' days', cols: [
          {key:'title',label:'Listing'},{key:'seller_name',label:'Seller'},{key:'price',label:'Price',fmt:'money'},{key:'status',label:'Status',fmt:'cap'},{key:'created_at',label:'Created',fmt:'date'}]},
        orders_placed:    { title: 'Orders placed · last ' + this.analyticsDays + ' days', cols: [
          {key:'order_number',label:'Order #',fmt:'mono'},{key:'buyer_name',label:'Buyer'},{key:'seller_name',label:'Seller'},{key:'total_amount',label:'Amount',fmt:'money'},{key:'status',label:'Status',fmt:'cap'},{key:'created_at',label:'Date',fmt:'date'}]},
        gmv:              { title: 'GMV — paid orders · last ' + this.analyticsDays + ' days', cols: [
          {key:'order_number',label:'Order #',fmt:'mono'},{key:'buyer_name',label:'Buyer'},{key:'seller_name',label:'Seller'},{key:'total_amount',label:'Amount',fmt:'money'},{key:'status',label:'Status',fmt:'cap'},{key:'created_at',label:'Date',fmt:'date'}]},
        listing_views:    { title: 'Listing views · last ' + this.analyticsDays + ' days', cols: [
          {key:'title',label:'Listing'},{key:'seller_name',label:'Seller'},{key:'price',label:'Price',fmt:'money'},{key:'views',label:'Views',fmt:'num'}]}
      };
      var d = defs[metric]; if (!d) return;
      this.analyticsDrill = { open:true, metric:metric, title:d.title, cols:d.cols, rows:[], loading:true };
      try {
        var r = await this.apiPost({ function:'adminAnalyticsDrill', metric:metric, days:this.analyticsDays });
        if (r && r.success && r.data) this.analyticsDrill.rows = r.data.rows || [];
      } catch(e){ console.error('analyticsDrill', e); }
      this.analyticsDrill.loading = false;
    },
    async loadFunnel() {
      try {
        var r = await this.apiPost({ function:'adminGetAnalyticsFunnel', days: this.analyticsDays });
        if (r && r.success && r.data) this.funnel = r.data;
      } catch(e){}
    },
    async loadTopListings() {
      try {
        var r = await this.apiPost({ function:'adminGetAnalyticsTopListings', days: this.analyticsDays });
        if (r && r.success && r.data) this.topListings = r.data.listings || [];
      } catch(e){}
    },
    async loadTopActions() {
      try {
        var r = await this.apiPost({ function:'adminGetAnalyticsTopActions', days: 7 });
        if (r && r.success && r.data) this.topActions = r.data.actions || [];
      } catch(e){}
    },
    async backfillAnalytics() {
      if (!confirm('Backfill 90 days of analytics from raw event tables? Takes a few seconds.')) return;
      this.analyticsBusy = true;
      try {
        var r = await this.apiPost({ function:'adminBackfillAnalytics', days: 90 });
        if (r && r.success) { this.loadAnalytics(); }
        else alert((r && r.message) || 'Backfill failed');
      } catch(e) { alert('Network error'); }
      this.analyticsBusy = false;
    },
    toggleSeries(k) { this.chartSeriesOn[k] = !this.chartSeriesOn[k]; this.chartSeriesOn = Object.assign({}, this.chartSeriesOn); },
    // Admin-portal #25 — export any admin table to CSV, client-side, respecting
    // the currently applied filters. BOM prefix so Excel opens UTF-8 correctly.
    exportCsv(filename, cols, rows) {
      var esc = function(v){ v = (v === null || v === undefined) ? '' : String(v); return '"' + v.replace(/"/g, '""') + '"'; };
      var lines = [cols.map(function(c){ return esc(c.label); }).join(',')];
      (rows || []).forEach(function(r){
        lines.push(cols.map(function(c){ return esc(r[c.key]); }).join(','));
      });
      var blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename + '_' + new Date().toISOString().slice(0,10) + '.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    },
    actionBarPct(count) {
      var max = this.topActions.reduce(function(m,a){ return Math.max(m, Number(a.c||0)); }, 1);
      return Math.round((count / max) * 100);
    },
    shortDate(d) {
      if (!d) return '';
      try {
        var dt = new Date(String(d).replace(' ','T'));
        return dt.toLocaleDateString('en-KE', { day:'numeric', month:'short' });
      } catch(e){ return String(d).slice(5, 10); }
    },
    xForIndex(i) {
      var n = this.analytics.series.length;
      if (n < 2) return (this.chartPadL + this.chartW - this.chartPadR) / 2;
      return this.chartPadL + (i / (n - 1)) * (this.chartW - this.chartPadL - this.chartPadR);
    },
    yForVal(v) {
      var max = this.chartMax || 1;
      return (this.chartH - this.chartPadB) - ((Number(v) || 0) / max) * (this.chartH - this.chartPadB - this.chartPadT);
    },
    polylinePoints(key) {
      var series = this.analytics.series;
      if (!series.length) return '';
      var self = this;
      return series.map(function(d, i){
        return self.xForIndex(i).toFixed(1) + ',' + self.yForVal(d[key]).toFixed(1);
      }).join(' ');
    },
    chartMove(evt) {
      var n = this.analytics.series.length;
      if (!n) return;
      var svg = evt.currentTarget;
      var rect = svg.getBoundingClientRect();
      var vx = ((evt.clientX - rect.left) / rect.width) * this.chartW;
      var span = this.chartW - this.chartPadL - this.chartPadR;
      var i = n < 2 ? 0 : Math.round(((vx - this.chartPadL) / span) * (n - 1));
      i = Math.max(0, Math.min(n - 1, i));
      var pxPerUnit = rect.width / this.chartW;
      var left = this.xForIndex(i) * pxPerUnit + 14;
      if (left > rect.width - 190) left = this.xForIndex(i) * pxPerUnit - 190;
      this.chartHover = { show:true, i:i, left:Math.max(0, left) };
    },

    async loadReferralMetrics() {
      this.referralLoading = true;
      try {
        var m = await this.apiPost({ function:'adminGetReferralMetrics' });
        if (m && m.success && m.data) {
          this.referralMetrics = {
            totalReferrals: m.data.totalReferrals || 0,
            activeUsers: m.data.activeUsers || 0,
            usersWhoListed: m.data.usersWhoListed || 0,
            usersWhoTransacted: m.data.usersWhoTransacted || 0
          };
        }
        this.loadReferralCodes();
        this.loadReferralSummary();
      } catch(e){ console.error('loadReferralMetrics', e); }
      this.referralLoading = false;
    },
    async loadReferralCodes() {
      try {
        var params = { function:'adminGetReferralCodes' };
        if (this.referralFilter.dateFrom) params.dateFrom = this.referralFilter.dateFrom;
        if (this.referralFilter.dateTo)   params.dateTo   = this.referralFilter.dateTo;
        var r = await this.apiPost(params);
        if (r && r.success) this.referralCodes = (r.data && r.data.codes) || [];
      } catch(e){ console.error('loadReferralCodes', e); }
    },
    async loadReferralSummary() {
      try {
        var r = await this.apiPost({ function:'adminGetReferralSummary', limit: 200 });
        if (r && r.success) this.referralSummary = (r.data && r.data.referrers) || [];
      } catch(e){ console.error('loadReferralSummary', e); }
    },
    async openReferralDrill(bucket) {
      this.referralDrill = { open:true, bucket:bucket, users:[], loading:true };
      try {
        var r = await this.apiPost({ function:'adminGetReferralDrilldown', bucket:bucket });
        if (r && r.success) this.referralDrill.users = (r.data && r.data.users) || [];
      } catch(e){ console.error('openReferralDrill', e); }
      this.referralDrill.loading = false;
    },
    openCreateAgentModal() {
      this.newAgent = { firstName:'', lastName:'', phone:'', email:'', region:'' };
      this.showCreateAgent = true;
    },
    async submitCreateAgent() {
      if (!this.newAgent.firstName || !this.newAgent.lastName || !this.newAgent.phone) return;
      this.creatingAgent = true;
      try {
        var f = new FormData();
        f.append('function', 'adminCreateAgent');
        f.append('adminUserId', sessionStorage.getItem('userId')||'');
        f.append('adminId',     sessionStorage.getItem('userId')||'');
        f.append('firstName', this.newAgent.firstName);
        f.append('lastName',  this.newAgent.lastName);
        f.append('phone',     this.newAgent.phone);
        f.append('email',     this.newAgent.email || '');
        f.append('region',    this.newAgent.region || '');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.agentCreated = r.data.data;
          this.showCreateAgent = false;
          await this.loadAgents();
        } else {
          alert((r.data && r.data.message) || 'Failed to create agent');
        }
      } catch (e) {
        alert('Failed to create agent: ' + (e.message || e));
      }
      this.creatingAgent = false;
    },
    copyAgentCode(a) {
      this.copyToClipboard(a.referral_code);
    },
    copyToClipboard(text) {
      if (!text) return;
      try {
        navigator.clipboard.writeText(text);
        // Lightweight visual feedback — many components in the app use console toasts; keep this terse.
        if (window && window.$toast) window.$toast.success('Copied: ' + text);
      } catch (e) {
        var ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); } catch (_) {}
        document.body.removeChild(ta);
      }
    },
    async resendAgentWelcome(a) {
      if (!confirm('Resend welcome SMS + email to ' + a.display_name + '?\n\nThis will rotate their temporary password — their existing password will stop working.')) return;
      try {
        var f = new FormData();
        f.append('function', 'adminResendAgentWelcome');
        f.append('adminUserId', sessionStorage.getItem('userId')||'');
        f.append('agentId', a.id);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          alert('Sent. SMS: ' + (r.data.data.smsSent ? 'ok' : 'failed') + ' · Email: ' + (r.data.data.emailSent ? 'ok' : (a.email ? 'failed' : '(no email)')));
        } else {
          alert((r.data && r.data.message) || 'Failed');
        }
      } catch (e) { alert('Failed: ' + (e.message||e)); }
    },
    viewAgentReferrals(a) {
      // Re-uses the existing user-detail viewer (already shows referral code).
      // For now, route to the seller profile page which lists their listings + acts as a 'public' view.
      this.$router.push('/marketplace/seller/' + a.id);
    },
    async loadFinance() {
      try {
        var f = new FormData(); f.append('function', 'adminGetFinance'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.allWithdrawals = r.data.data.withdrawals || [];
          this.pendingWithdrawals = this.allWithdrawals.filter(function(w) { return w.status === 'pending'; });
          this.escrowTxns = r.data.data.escrow || [];
        }
      } catch (e) {}
    },
    async loadDisputes() {
      try {
        var f = new FormData(); f.append('function', 'adminGetDisputes'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) { this.disputes = r.data.data.disputes || []; }
      } catch (e) {}
    },
    async loadListings() {
      try {
        var f = new FormData(); f.append('function', 'adminGetListings'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) { this.allListings = r.data.data.listings || []; }
      } catch (e) {}
    },
    async loadVerifications() {
      try {
        var f = new FormData(); f.append('function', 'adminGetVerifications'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.allVerifications = r.data.data.verifications || [];
          this.pendingVerifications = this.allVerifications.filter(function(v) { return v.verification_status === 'pending'; });
        }
      } catch (e) {}
    },
    async loadSmsBalance() {
      this.smsBalanceLoading = true;
      try {
        var f = new FormData();
        f.append('function', 'adminGetSmsProviderBalance');
        f.append('adminUserId', sessionStorage.getItem('userId') || '');
        var r = await execute(f, flocker);
        if (r.data && r.data.data) this.smsBalance = r.data.data;
      } catch (e) {}
      this.smsBalanceLoading = false;
    },
    async loadSettings() {
      // Lazy-load usage limits alongside the rest of settings
      this.loadUsageLimits();
      this.loadSupportContacts();
      try {
        var f = new FormData(); f.append('function', 'adminGetSettings'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          var arr = r.data.data.settings || [];
          var map = {};
          arr.forEach(function(s) { map[s.setting_key] = s.setting_value || ''; });
          this.settings = map;
          this.trustStripEnabled = (map.landing_trust_strip_enabled === '1');
          this.anitracSectionEnabled = (map.landing_anitrac_section_enabled === '1');
        }
      } catch (e) {}
      // Load logs
      try {
        var f2 = new FormData(); f2.append('function', 'adminGetSMSLogs'); f2.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r2 = await execute(f2, flocker);
        if (r2.data && r2.data.success && r2.data.data) this.smsLogs = r2.data.data.logs || [];
      } catch (e) {}
      // Fetch live provider balance so the admin sees InsufficientBalance issues at a glance.
      this.loadSmsBalance();
      try {
        var f3 = new FormData(); f3.append('function', 'adminGetEmailLogs'); f3.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r3 = await execute(f3, flocker);
        if (r3.data && r3.data.success && r3.data.data) this.emailLogs = r3.data.data.logs || [];
      } catch (e) {}
      // Load SMS pricing config
      try {
        var f4 = new FormData(); f4.append('function', 'getSmsConfig');
        var r4 = await execute(f4, flocker);
        if (r4.data && r4.data.success && r4.data.data) {
          if (r4.data.data.smsConfig) this.smsPricing = r4.data.data.smsConfig;
          if (r4.data.data.platformStats) this.smsPlatformStats = Object.assign(this.smsPlatformStats, r4.data.data.platformStats);
          if (r4.data.data.balanceStats) this.smsPlatformStats = Object.assign(this.smsPlatformStats, r4.data.data.balanceStats);
        }
      } catch (e) {}
      // Load Flockr M-Pesa config
      try {
        var f5 = new FormData(); f5.append('function', 'getFlockrMpesaFull');
        var r5 = await execute(f5, flocker);
        if (r5.data && r5.data.success && r5.data.data && r5.data.data.config) {
          this.flockrMpesa = r5.data.data.config;
        }
      } catch (e) {}
    },
    async loadSupportContacts() {
      try {
        var f = new FormData(); f.append('function','adminListSupportContacts'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.supportContactsAdmin = (r.data.data.contacts || []).map(function(c){
            c.is_active = Number(c.is_active);
            c.display_order = Number(c.display_order) || 0;
            c._saving = false;
            return c;
          });
        }
      } catch (e) { console.error('loadSupportContacts failed', e); }
    },
    addSupportContactRow() {
      this.supportContactsAdmin.push({ id: 0, name: '', role: '', phone: '', email: '', display_order: (this.supportContactsAdmin.length + 1), is_active: 1, _saving: false });
    },
    async saveSupportContact(c, idx) {
      if (!c.name || (!c.phone && !c.email)) { alert('Name and at least one of phone/email are required'); return; }
      c._saving = true;
      try {
        var f = new FormData();
        f.append('function','adminSaveSupportContact');
        f.append('adminUserId', sessionStorage.getItem('userId')||'');
        if (c.id) f.append('id', c.id);
        f.append('name', c.name);
        f.append('role', c.role || '');
        f.append('phone', c.phone || '');
        f.append('email', c.email || '');
        f.append('displayOrder', c.display_order || 0);
        f.append('isActive', c.is_active ? 1 : 0);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          await this.loadSupportContacts();
        } else {
          alert((r.data && r.data.message) || 'Save failed');
        }
      } catch (e) { alert('Save failed: ' + e.message); }
      c._saving = false;
    },
    async deleteSupportContact(c, idx) {
      if (!c.id) { this.supportContactsAdmin.splice(idx,1); return; }
      if (!confirm('Delete ' + (c.name || 'this contact') + '?')) return;
      try {
        var f = new FormData();
        f.append('function','adminDeleteSupportContact');
        f.append('adminUserId', sessionStorage.getItem('userId')||'');
        f.append('id', c.id);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) await this.loadSupportContacts();
        else alert((r.data && r.data.message) || 'Delete failed');
      } catch (e) { alert('Delete failed: ' + e.message); }
    },
    async loadUsageLimits() {
      this.usageLoading = true;
      try {
        var f = new FormData(); f.append('function','adminGetUsageLimits'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.usageCaps = (r.data.data.caps || []).map(function(c){ c._saving = false; return c; });
          this.usageStats = r.data.data.usage || {};
          this.topConsumers = r.data.data.topConsumers || [];
        }
      } catch(e){}
      this.usageLoading = false;
    },
    async loadCompliance() {
      this.compliance.loading = true;
      try {
        var f = new FormData(); f.append('function','adminGetTermsAcceptances'); f.append('adminUserId', sessionStorage.getItem('userId')||''); f.append('limit','100');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.compliance.currentVersion = r.data.data.currentVersion || '';
          this.compliance.totalUsers = r.data.data.totalUsers || 0;
          this.compliance.acceptedCurrent = r.data.data.acceptedCurrent || 0;
          this.compliance.pendingCurrent = r.data.data.pendingCurrent || 0;
          this.compliance.rows = r.data.data.rows || [];
          this.compliance.byVersion = r.data.data.byVersion || [];
        }
      } catch(e){}
      this.compliance.loading = false;
    },
    async loadWatchlist() {
      this.watchlist.loading = true;
      try {
        var f = new FormData(); f.append('function','adminGetWatchlist'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.watchlist.needsPhone = r.data.data.needsPhone || [];
          this.watchlist.heavyUsers = r.data.data.heavyUsers || [];
          this.watchlist.multipleRejects = r.data.data.multipleRejects || [];
          this.watchlist.pendingVerifs = r.data.data.pendingVerifs || [];
        }
      } catch(e){}
      this.watchlist.loading = false;
    },
    async loadSupportConvos() {
      this.supportLoading = true;
      try {
        var r = await this.apiPost({ function:'adminGetSupportConversations' });
        if (r && r.success && r.data && Array.isArray(r.data.conversations)) this.supportConvos = r.data.conversations;
      } catch(e) { console.error(e); }
      this.supportLoading = false;
    },
    openSupportConvo(c) {
      // Opens the thread using the current admin's session, so their replies are attributed
      // to them (not the anonymous "Flockr Support" user). Users see the reply as coming
      // from Flockr Support via the message renderer's alias mapping.
      this.$router.push({ path:'/messages/'+c.id, query:{ name: c.user_name || 'User', ref: c.context_type || 'support', refId: c.context_id || '' } });
    },
    async loadAgroShops() {
      this.agroShopLoading = true;
      try {
        var r = await this.apiPost({ function:'adminListAgroShops', kycStatus: this.agroShopFilter || '' });
        if (r && r.success) this.agroShops = (r.data && r.data.shops) || [];
      } catch(e){}
      this.agroShopLoading = false;
    },
    async verifyAgroShop(shop, status) {
      var verb = status === 'verified' ? 'verify' : (status === 'rejected' ? 'reject' : 'reset');
      if (!confirm('Are you sure you want to ' + verb + ' "' + shop.shop_name + '"?')) return;
      this.agroShopActing = shop.id;
      try {
        var r = await this.apiPost({ function:'adminVerifyAgroShop', shopId: shop.id, kycStatus: status });
        if (r && r.success) { shop.kyc_status = status; }
        else alert((r && r.message) || 'Failed to update shop');
      } catch(e){ alert('Network error'); }
      this.agroShopActing = null;
    },
    async loadInsProviders() {
      this.insProviderLoading = true;
      try {
        var r = await this.apiPost({ function:'adminListInsuranceProviders', kycStatus: this.insProviderFilter || '' });
        if (r && r.success) this.insProviders = (r.data && r.data.providers) || [];
      } catch(e){}
      this.insProviderLoading = false;
    },
    async verifyInsProvider(p, status) {
      var verb = status === 'verified' ? 'verify' : (status === 'rejected' ? 'reject' : 'reset');
      if (!confirm('Are you sure you want to ' + verb + ' "' + p.name + '"?')) return;
      this.insProviderActing = p.id;
      try {
        var r = await this.apiPost({ function:'adminVerifyInsuranceProvider', providerId: p.id, kycStatus: status });
        if (r && r.success) { p.kyc_status = status; }
        else alert((r && r.message) || 'Failed to update provider');
      } catch(e){ alert('Network error'); }
      this.insProviderActing = null;
    },
    finInstLabel(t){ var m = { bank:'Bank', mfi:'MFI', sacco:'SACCO', fintech:'Fintech', other:'Other' }; return m[t] || (t||''); },
    finProductTypeLabel(t){ var m = { livestock_asset:'Livestock Asset', working_capital:'Working Capital', lease_to_own:'Lease-to-Own', savings:'Savings' }; return m[t] || t; },
    parseFinTypes(v){ if (Array.isArray(v)) return v; try { var a = JSON.parse(v||'[]'); return Array.isArray(a) ? a : []; } catch(e){ return []; } },
    async loadFinProviders() {
      this.finProviderLoading = true;
      try {
        var r = await this.apiPost({ function:'adminListFinanceProviders', kycStatus: this.finProviderFilter || '' });
        if (r && r.success) this.finProviders = (r.data && r.data.providers) || [];
      } catch(e){}
      this.finProviderLoading = false;
    },
    async verifyFinProvider(p, status) {
      var verb = status === 'verified' ? 'verify' : (status === 'rejected' ? 'reject' : 'reset');
      var notes = '';
      if (status === 'rejected') {
        var n = prompt('Reason for rejection (shown to the institution):');
        if (n === null) return;
        notes = n;
      } else {
        if (!confirm('Are you sure you want to ' + verb + ' "' + p.name + '"?')) return;
      }
      this.finProviderActing = p.id;
      try {
        var r = await this.apiPost({ function:'adminVerifyFinanceProvider', providerId: p.id, kycStatus: status, kycNotes: notes });
        if (r && r.success) { p.kyc_status = status; if (notes) p.kyc_notes = notes; }
        else alert((r && r.message) || 'Failed to update institution');
      } catch(e){ alert('Network error'); }
      this.finProviderActing = null;
    },
    async loadCorporates() {
      this.corpLoading = true;
      try {
        var r = await this.apiPost({ function:'adminListCorporateAccounts', kycStatus: this.corpFilter || '' });
        if (r && r.success) this.corpAccounts = (r.data && r.data.accounts) || [];
      } catch(e){}
      this.corpLoading = false;
    },
    async verifyCorp(c, status) {
      var verb = status === 'verified' ? 'verify' : (status === 'rejected' ? 'reject' : 'reset');
      var notes = '';
      if (status === 'rejected') {
        var n = prompt('Reason for rejection (shown to the company):');
        if (n === null) return;
        notes = n;
      } else {
        if (!confirm('Are you sure you want to ' + verb + ' "' + c.company_name + '"?')) return;
      }
      this.corpActing = c.id;
      try {
        var r = await this.apiPost({ function:'adminVerifyCorporateAccount', accountId: c.id, kycStatus: status, kycNotes: notes });
        if (r && r.success) { c.kyc_status = status; if (notes) c.kyc_notes = notes; }
        else alert((r && r.message) || 'Failed to update corporate account');
      } catch(e){ alert('Network error'); }
      this.corpActing = null;
    },
    async saveUsageLimit(cap) {
      cap._saving = true;
      try {
        var f = new FormData(); f.append('function','adminSaveUsageLimit'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        f.append('action', cap.action); f.append('value', cap.value);
        var r = await execute(f, flocker);
        if (!(r.data && r.data.success)) {
          alert((r.data && r.data.message) || 'Could not save limit');
        }
      } catch(e){ alert('Network error'); }
      cap._saving = false;
    },
    async saveSettings() {
      this.savingSettings = true; this.settingsSaved = false;
      try {
        var f = new FormData(); f.append('function', 'adminSaveSettings'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        f.append('settings', JSON.stringify(this.settings));
        var r = await execute(f, flocker);
        if (r.data && r.data.success) this.settingsSaved = true;
        else alert('❌ ' + ((r.data && r.data.message) || 'Failed'));
      } catch (e) { alert('❌ Network error'); }
      this.savingSettings = false;
    },
    async loadFlockrPayments() {
      try {
        var f = new FormData(); f.append('function', 'getFlockrPayments');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          var d = r.data.data;
          // Normalize field names from PHP response
          var smsRev = 0;
          var allTotal = 0; var allCount = 0;
          if (d.byType) d.byType.forEach(function(t){ allTotal += parseFloat(t.amount||0); allCount += parseInt(t.count||0); if(t.payment_type==='sms_topup') smsRev = parseFloat(t.amount||0); });
          this.flockrPayments = {
            payments: d.payments || [],
            today: {amount: parseFloat(d.today?d.today.a:0), count: parseInt(d.today?d.today.c:0)},
            thisMonth: {amount: parseFloat(d.thisMonth?d.thisMonth.a:0), count: parseInt(d.thisMonth?d.thisMonth.c:0)},
            allTime: {amount: allTotal, count: allCount},
            smsTopupRevenue: smsRev,
            byType: d.byType || [],
            byMethod: d.byMethod || [],
            byShortcode: d.byShortcode || []
          };
        }
      } catch (e) {}
    },
    async saveSmsPricing() {
      this.savingSmsPricing = true; this.smsPricingSaved = false;
      try {
        var f = new FormData();
        f.append('function', 'updateSmsPricing');
        f.append('ratePerUnit', this.smsPricing.rate_per_unit);
        f.append('charsPerUnit', this.smsPricing.chars_per_unit);
        f.append('minTopup', this.smsPricing.min_topup);
        f.append('maxTopup', this.smsPricing.max_topup);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          this.smsPricingSaved = true;
          var self = this; setTimeout(function(){ self.smsPricingSaved = false; }, 3000);
        } else { alert((r.data && r.data.message) || 'Failed'); }
      } catch (e) { alert('Error saving SMS pricing'); }
      this.savingSmsPricing = false;
    },
    async saveFlockrMpesa() {
      this.savingFlockrMpesa = true; this.flockrMpesaSaved = false;
      try {
        var f = new FormData();
        f.append('function', 'saveFlockrMpesaConfig');
        f.append('shortcode', this.flockrMpesa.shortcode);
        f.append('shortcodeType', this.flockrMpesa.shortcode_type);
        f.append('consumerKey', this.flockrMpesa.consumer_key);
        f.append('consumerSecret', this.flockrMpesa.consumer_secret);
        f.append('passkey', this.flockrMpesa.passkey);
        f.append('environment', this.flockrMpesa.environment);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          this.flockrMpesaSaved = true;
          var self = this; setTimeout(function(){ self.flockrMpesaSaved = false; }, 3000);
        } else { alert((r.data && r.data.message) || 'Failed'); }
      } catch (e) { alert('Error'); }
      this.savingFlockrMpesa = false;
    },
    async testFlockrMpesa() {
      this.testingFlockrMpesa = true; this.flockrMpesaTestResult = null;
      try {
        var f = new FormData(); f.append('function', 'testFlockrMpesaConnection');
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          this.flockrMpesaTestResult = { ok: true, msg: r.data.message + ' — ' + (r.data.data ? r.data.data.environment : '') };
        } else { this.flockrMpesaTestResult = { ok: false, msg: (r.data && r.data.message) || 'Connection failed' }; }
      } catch (e) { this.flockrMpesaTestResult = { ok: false, msg: 'Error' }; }
      this.testingFlockrMpesa = false;
    },
    async activateFlockrMpesa() {
      if (this.flockrMpesa.callback_registered) {
        if (!confirm('Callbacks are already registered with Safaricom. Re-register?')) return;
      } else {
        if (!confirm('Register C2B Confirmation + Validation URLs with Safaricom for paybill ' + this.flockrMpesa.shortcode + '?')) return;
      }
      this.activatingFlockrMpesa = true; this.flockrMpesaActivateResult = null;
      try {
        var f = new FormData(); f.append('function', 'registerFlockrMpesaCallbacks');
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          this.flockrMpesaActivateResult = { ok: true, msg: r.data.message };
          this.flockrMpesa.callback_registered = 1;
          this.flockrMpesa.callback_registered_at = new Date().toISOString().slice(0, 19).replace('T', ' ');
          if (r.data.data) {
            this.flockrMpesa.validation_url = r.data.data.validationUrl;
            this.flockrMpesa.confirmation_url = r.data.data.confirmationUrl;
          }
        } else {
          this.flockrMpesaActivateResult = { ok: false, msg: (r.data && r.data.message) || 'Registration failed' };
        }
      } catch (e) { this.flockrMpesaActivateResult = { ok: false, msg: 'Network error' }; }
      this.activatingFlockrMpesa = false;
    },
    async testFlockrStkPush() {
      this.testingFlockrStk = true; this.adminStkStatus = '';
      try {
        var f = new FormData();
        f.append('function', 'testFlockrStkPush');
        f.append('phone', this.flockrStkTestPhone);
        f.append('amount', this.flockrStkTestAmount);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          this.adminStkSent = true;
          this.adminStkStatus = 'pending';
          this.adminStkRequestId = r.data.data.stkRequestId;
          this.startAdminStkPolling();
        } else { alert((r.data && r.data.message) || 'Failed'); }
      } catch (e) { alert('Error'); }
      this.testingFlockrStk = false;
    },
    startAdminStkPolling() {
      this.stopAdminStkPolling();
      this.adminStkPolling = true;
      var self = this;
      this.adminStkPollTimer = setInterval(function() { self.pollAdminStkStatus(); }, 3000);
    },
    stopAdminStkPolling() {
      if (this.adminStkPollTimer) { clearInterval(this.adminStkPollTimer); this.adminStkPollTimer = null; }
      this.adminStkPolling = false;
    },
    async pollAdminStkStatus() {
      if (!this.adminStkRequestId) return;
      this.adminStkChecking = true;
      try {
        var f = new FormData();
        f.append('function', 'checkStkStatus');
        f.append('stkRequestId', this.adminStkRequestId);
        var r = await execute(f, flocker);
        if (r.data && r.data.data) {
          var d = r.data.data;
          this.adminStkStatus = d.status || 'pending';
          this.adminStkMessage = d.message || '';
          if (d.status === 'success') {
            this.adminStkReceipt = d.mpesaReceipt || '';
            this.stopAdminStkPolling();
          } else if (d.status === 'cancelled' || d.status === 'failed' || d.status === 'timeout') {
            this.stopAdminStkPolling();
          }
        }
      } catch (e) {}
      this.adminStkChecking = false;
    },
    resetAdminStk() {
      this.stopAdminStkPolling();
      this.adminStkSent = false; this.adminStkRequestId = null; this.adminStkStatus = '';
      this.adminStkMessage = ''; this.adminStkReceipt = '';
    },
    async testSMS() {
      this.testingSMS = true; this.testResult = null;
      var phone = prompt('Enter phone number to test SMS (e.g. 0712345678):');
      if (!phone) { this.testingSMS = false; return; }
      try {
        var f = new FormData(); f.append('function', 'adminTestSMS'); f.append('adminUserId', sessionStorage.getItem('userId')||''); f.append('phone', phone);
        var r = await execute(f, flocker);
        this.testResult = { ok: r.data && r.data.success, msg: (r.data && r.data.message) || 'Failed' };
        this.loadSettings();
      } catch (e) { this.testResult = { ok: false, msg: 'Network error' }; }
      this.testingSMS = false;
    },
    async testEmail() {
      this.testingEmail = true; this.testResult = null;
      var email = prompt('Enter email address to test:');
      if (!email) { this.testingEmail = false; return; }
      try {
        var f = new FormData(); f.append('function', 'adminTestEmail'); f.append('adminUserId', sessionStorage.getItem('userId')||''); f.append('email', email);
        var r = await execute(f, flocker);
        this.testResult = { ok: r.data && r.data.success, msg: (r.data && r.data.message) || 'Failed' };
        this.loadSettings();
      } catch (e) { this.testResult = { ok: false, msg: 'Network error' }; }
      this.testingEmail = false;
    },

    async toggleUserStatus(u, newStatus) {
      if (!confirm('Set ' + u.display_name + ' to ' + newStatus + '?')) return;
      try {
        var f = new FormData(); f.append('function', 'adminUpdateUser'); f.append('adminUserId', sessionStorage.getItem('userId')||''); f.append('userId', u.id); f.append('accountStatus', newStatus);
        f.append('adminId', sessionStorage.getItem('userId')); f.append('adminName', sessionStorage.getItem('userName') || 'Admin');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.pending) { alert(r.data.message); this.mcPendingCount++; }
        else if (r.data && r.data.success) { u.status = newStatus; }
      } catch (e) {}
    },
    async processWithdrawal(id, action, manual) {
      // Approve (manual) — admin will send the M-Pesa by hand and click "Mark
      // Disbursed" afterwards. Skips Daraja B2C entirely. Daraja B2C path
      // (manual !== true) is kept for when B2C is fully configured.
      var msg = action === 'approved'
        ? (manual
            ? 'Approve this withdrawal? You will need to send the M-Pesa manually, then click "Mark Disbursed" to record the receipt.'
            : 'Approve this withdrawal? Funds will be sent via M-Pesa B2C.')
        : 'Reject this withdrawal?';
      if (!confirm(msg)) return;
      try {
        var f = new FormData(); f.append('function', 'adminProcessWithdrawal'); f.append('adminUserId', sessionStorage.getItem('userId')||''); f.append('withdrawalId', id); f.append('action', action);
        f.append('adminId', sessionStorage.getItem('userId'));
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.pending) { alert(r.data.message); this.mcPendingCount++; }
        else if (r.data && r.data.success) this.loadFinance();
      } catch (e) {}
    },
    async rejectWithdrawal(w) {
      var reason = prompt('Reason for rejecting this withdrawal?', '');
      if (reason === null) return;
      try {
        var f = new FormData(); f.append('function', 'adminProcessWithdrawal'); f.append('adminUserId', sessionStorage.getItem('userId')||''); f.append('withdrawalId', w.id); f.append('action', 'rejected');
        f.append('adminId', sessionStorage.getItem('userId')); f.append('reason', reason || 'Rejected by admin');
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.pending) { alert(r.data.message); this.mcPendingCount++; }
        else if (r.data && r.data.success) this.loadFinance();
      } catch (e) {}
    },
    openDisburse(w) {
      this.disburseWd = w;
      this.disburseForm = { mpesaReceipt: '', amountSent: Number(w.amount), notes: '' };
      this.disburseError = '';
      this.disburseModal = true;
    },
    async submitDisburse() {
      if (!this.disburseWd) return;
      this.disburseError = '';
      var code = String(this.disburseForm.mpesaReceipt || '').trim().toUpperCase();
      if (!code) { this.disburseError = 'M-Pesa transaction code is required'; return; }
      var amt = Number(this.disburseForm.amountSent || 0);
      if (!(amt > 0)) { this.disburseError = 'Disbursement amount must be greater than 0'; return; }
      this.disbursing = true;
      try {
        var f = new FormData();
        f.append('function', 'markWithdrawalDisbursed');
        f.append('adminUserId', sessionStorage.getItem('userId') || '');
        f.append('adminId', sessionStorage.getItem('userId') || '');
        f.append('withdrawalId', this.disburseWd.id);
        f.append('mpesaReceipt', code);
        f.append('amountSent', String(amt));
        if (this.disburseForm.notes) f.append('notes', this.disburseForm.notes);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          this.disburseModal = false;
          this.loadFinance();
        } else {
          this.disburseError = (r.data && r.data.message) ? r.data.message : 'Failed to record disbursement';
        }
      } catch (e) {
        this.disburseError = 'Network error — try again';
      }
      this.disbursing = false;
    },
    showResolveDispute(d) { this.openDisputeDetail(d); },

    async openDisputeDetail(d) {
      this.disputeDetail = null; this.resolveType = 'refund'; this.resolveAmount = ''; this.resolveNotes = ''; this.adminNoteText = '';
      try {
        var f = new FormData(); f.append('function', 'getDispute'); f.append('disputeId', d.id);
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          var detail = r.data.data.dispute;
          // Separate admin notes from evidence
          detail.adminNotes = (detail.evidence || []).filter(function(e) { return e.is_admin_only == 1 || e.is_admin_only === '1'; });
          detail.evidence = (detail.evidence || []).filter(function(e) { return e.is_admin_only != 1 && e.is_admin_only !== '1'; });
          this.disputeDetail = detail;
        }
      } catch (e) { console.error(e); }
    },

    async addAdminNote(sendToParties) {
      if (!this.adminNoteText.trim() || !this.disputeDetail) return;
      this.addingNote = true;
      try {
        var f = new FormData();
        f.append('function', 'addDisputeEvidence');
        f.append('disputeId', this.disputeDetail.id);
        f.append('submittedBy', sessionStorage.getItem('userId'));
        f.append('evidenceType', 'text');
        f.append('content', this.adminNoteText);
        f.append('isAdminOnly', sendToParties ? '0' : '1');
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          this.adminNoteText = '';
          this.openDisputeDetail(this.disputeDetail);
        }
      } catch (e) {}
      this.addingNote = false;
    },

    async updateDisputeStatus(newStatus) {
      if (!this.disputeDetail) return;
      try {
        var f = new FormData();
        f.append('function', 'adminUpdateDisputeStatus'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        f.append('disputeId', this.disputeDetail.id);
        f.append('status', newStatus);
        f.append('adminId', sessionStorage.getItem('userId'));
        var r = await execute(f, flocker);
        if (r.data && r.data.success) { this.disputeDetail.status = newStatus; this.loadDisputes(); }
        else { alert((r.data && r.data.message) || 'Failed'); }
      } catch (e) {}
    },

    fDT(d) { return d ? new Date(d).toLocaleString('en-KE', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '-'; },
    isImage(url) { if (!url) return false; var ext = url.split('.').pop().toLowerCase(); return ['jpg','jpeg','png','gif','webp'].indexOf(ext) > -1; },
    isPdf(url) { if (!url) return false; return url.split('.').pop().toLowerCase() === 'pdf'; },
    getFileName(url) { if (!url) return ''; return url.split('/').pop(); },
    previewImage(url) { this.previewImg = this.photoUrl(url); },
    openDoc(url) { window.open(this.photoUrl(url), '_blank'); },
    verifIcon(s) { if (s==='verified') return 'mdi-check-circle'; if (s==='pending') return 'mdi-clock'; return 'mdi-close-circle'; },
    verifColor(s) { if (s==='verified') return '#50C878'; if (s==='pending') return '#FFAB00'; return '#FF6B6B'; },
    verifLabel(t) {
      var map = { national_id:'National ID', kra_pin:'KRA PIN', business_registration:'Business Registration', kvb_license:'Vet License', phone_otp:'Phone', email:'Email', location_gps:'Location', ntsa_compliance:'NTSA Compliance' };
      return map[t] || (t||'').replace(/_/g,' ');
    },
    verifTypeIcon(t) {
      var map = { national_id:'mdi-card-account-details', kra_pin:'mdi-file-certificate', business_registration:'mdi-domain', kvb_license:'mdi-stethoscope', phone_otp:'mdi-phone-check', email:'mdi-email-check', location_gps:'mdi-crosshairs-gps', ntsa_compliance:'mdi-truck-check' };
      return map[t] || 'mdi-file-document';
    },
    async processVerif(action) {
      if (!this.verifDetail) return;
      if (action === 'approve' && !confirm('Approve this verification?')) return;
      this.verifProcessing = true;
      try {
        var f = new FormData();
        f.append('function', 'approveVerification');
        f.append('verificationId', this.verifDetail.id);
        f.append('action', action);
        f.append('verifiedBy', sessionStorage.getItem('userId'));
        if (action === 'reject') f.append('rejectionReason', this.rejectReason);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          // Auto-send notification to seller
          var nf = new FormData();
          nf.append('function', 'adminSendNotification'); nf.append('adminUserId', sessionStorage.getItem('userId')||'');
          nf.append('userId', this.verifDetail.user_id);
          nf.append('category', 'system');
          nf.append('type', 'verification_update');
          nf.append('actionUrl', '/verification');
          nf.append('referenceType', 'verification');
          nf.append('referenceId', this.verifDetail.id);
          nf.append('adminId', sessionStorage.getItem('userId'));
          if (action === 'approve') {
            nf.append('title', 'Verification Approved!');
            nf.append('body', 'Your ' + this.verifLabel(this.verifDetail.verification_type) + ' has been verified. You now have a verified badge on your profile.');
            nf.append('icon', 'mdi-shield-check');
          } else {
            nf.append('title', 'Verification Rejected');
            nf.append('body', 'Your ' + this.verifLabel(this.verifDetail.verification_type) + ' was rejected: ' + this.rejectReason + '. Please resubmit with the correct documents.');
            nf.append('icon', 'mdi-shield-alert');
          }
          try { await execute(nf, flocker); } catch (ne) {}

          alert('✅ Verification ' + (action === 'approve' ? 'approved' : 'rejected'));
          this.verifDetail = null;
          this.loadVerifications();
        } else { alert('❌ ' + ((r.data && r.data.message) || 'Failed')); }
      } catch (e) { alert('❌ Network error'); }
      this.verifProcessing = false;
    },
    setVerifNotif(msg) {
      this.verifNotifBody = msg;
      this.verifNotifTitle = 'Verification Update';
    },
    async sendVerifNotification() {
      if (!this.verifDetail || !this.verifNotifBody.trim()) return;
      this.sendingNotif = true;
      try {
        var f = new FormData();
        f.append('function', 'adminSendNotification'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        f.append('userId', this.verifDetail.user_id);
        f.append('title', this.verifNotifTitle || 'Verification Update');
        f.append('body', this.verifNotifBody);
        f.append('category', 'system');
        f.append('type', 'verification_update');
        f.append('actionUrl', '/verification');
        f.append('icon', 'mdi-shield-alert');
        f.append('referenceType', 'verification');
        f.append('referenceId', this.verifDetail.id);
        f.append('adminId', sessionStorage.getItem('userId'));
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          alert('✅ Notification sent to ' + this.verifDetail.display_name);
          this.verifNotifBody = '';
        } else { alert('❌ ' + ((r.data && r.data.message) || 'Failed')); }
      } catch (e) { alert('❌ Network error'); }
      this.sendingNotif = false;
    },
    async submitResolve() {
      if (!this.disputeDetail) return;
      this.resolving = true;
      try {
        var f = new FormData(); f.append('function', 'adminResolveDispute'); f.append('adminUserId', sessionStorage.getItem('userId')||'');
        f.append('disputeId', this.disputeDetail.id); f.append('resolutionType', this.resolveType);
        f.append('resolutionAmount', this.resolveAmount); f.append('resolutionNotes', this.resolveNotes);
        f.append('adminId', sessionStorage.getItem('userId'));
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          var msg = r.data.message || 'Dispute resolved';
          if (msg.indexOf('NOT processed') > -1) { alert('⚠️ ' + msg); } else { alert('✅ ' + msg); }
          this.loadDisputes();
          this.openDisputeDetail(this.disputeDetail);
        } else { alert('❌ ' + ((r.data && r.data.message) || 'Failed')); }
      } catch (e) { alert('❌ Network error'); }
      this.resolving = false;
    }
  }
};
</script>


<style>
/* Force full light-shell on admin */
html[data-theme="light"] body,
html[data-theme="light"] #app,
html[data-theme="light"] #layout-wrapper,
html[data-theme="light"] .adm-root,
html[data-theme="light"] .adm-root *{
  --fm-page:#F7F5F2 !important;
  --fm-bg1:#FFFFFF !important;
  --fm-bg2:#F7F5F2 !important;
  --fm-bg3:#F0EBE4 !important;
  --fm-br:#E8E2DA !important;
  --fm-h:#1A1A1A !important;
  --fm-s:#777777 !important;
  --fm-m:#555555 !important;
}

html[data-theme="light"] body,
html[data-theme="light"] #app,
html[data-theme="light"] #layout-wrapper,
html[data-theme="light"] .adm-root{
  background:#F7F5F2 !important;
  color:#1A1A1A !important;
}

html[data-theme="light"] .container-fluid,
html[data-theme="light"] .adm-root > div,
html[data-theme="light"] .adm-root > nav{
  background:transparent !important;
}

html[data-theme="light"] .adm-nav{
  background:#FFFFFF !important;
  border-bottom:1px solid #E8E2DA !important;
}

html[data-theme="light"] .adm-stats-grid,
html[data-theme="light"] .row,
html[data-theme="light"] .col-lg-4,
html[data-theme="light"] .col-lg-5,
html[data-theme="light"] .col-lg-7,
html[data-theme="light"] .col-md-4,
html[data-theme="light"] .col-md-6,
html[data-theme="light"] .col-6,
html[data-theme="light"] .col-12{
  background:transparent !important;
}

html[data-theme="light"] .adm-card,
html[data-theme="light"] .adm-stat,
html[data-theme="light"] .adm-modal,
html[data-theme="light"] .adm-table-wrap,
html[data-theme="light"] .adm-alert-item,
html[data-theme="light"] .adm-overlay > .adm-modal{
  background:#FFFFFF !important;
  color:#1A1A1A !important;
  border-color:#E8E2DA !important;
  box-shadow:none !important;
}

html[data-theme="light"] .adm-card *,
html[data-theme="light"] .adm-stat *,
html[data-theme="light"] .adm-modal *{
  color:inherit;
}

html[data-theme="light"] .adm-brand,
html[data-theme="light"] .adm-user,
html[data-theme="light"] .adm-card-title,
html[data-theme="light"] .adm-stat-val,
html[data-theme="light"] .adm-table td strong,
html[data-theme="light"] .adm-table td b,
html[data-theme="light"] .adm-table td,
html[data-theme="light"] .adm-table th,
html[data-theme="light"] .adm-modal h1,
html[data-theme="light"] .adm-modal h2,
html[data-theme="light"] .adm-modal h3,
html[data-theme="light"] .adm-modal h4,
html[data-theme="light"] .adm-modal h5,
html[data-theme="light"] .adm-modal h6{
  color:#1A1A1A !important;
}

html[data-theme="light"] .adm-stat-lbl,
html[data-theme="light"] .adm-mono,
html[data-theme="light"] .adm-muted,
html[data-theme="light"] .adm-empty,
html[data-theme="light"] .adm-rev-row,
html[data-theme="light"] .adm-rev-row span,
html[data-theme="light"] small,
html[data-theme="light"] p{
  color:#555555 !important;
}

html[data-theme="light"] .adm-tab{
  background:#FFFFFF !important;
  color:#555555 !important;
  border:1px solid #E8E2DA !important;
}

html[data-theme="light"] .adm-tab.active{
  background:rgba(191,161,100,.12) !important;
  color:#BFA164 !important;
  border-color:#BFA164 !important;
}

html[data-theme="light"] .adm-table,
html[data-theme="light"] .adm-table thead,
html[data-theme="light"] .adm-table tbody,
html[data-theme="light"] .adm-table tr,
html[data-theme="light"] .adm-table th,
html[data-theme="light"] .adm-table td{
  background:#FFFFFF !important;
  border-color:#E8E2DA !important;
}

html[data-theme="light"] .adm-rev-row{
  border-bottom-color:#E8E2DA !important;
}

html[data-theme="light"] input,
html[data-theme="light"] select,
html[data-theme="light"] textarea,
html[data-theme="light"] .adm-input,
html[data-theme="light"] .adm-select{
  background:#FFFFFF !important;
  color:#1A1A1A !important;
  border-color:#E0D8D0 !important;
}

html[data-theme="light"] input::placeholder,
html[data-theme="light"] textarea::placeholder{
  color:#777777 !important;
}

html[data-theme="light"] .adm-overlay{
  background:rgba(247,245,242,.7) !important;
}
</style>

<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
</style>
<style scoped>
.adm { min-height:100vh; background:#0A0A0A; color:var(--fm-m,#A09888); padding-bottom:60px; }
.adm-nav { background:rgba(10,10,10,0.95); border-bottom:1px solid var(--fm-br,#1E1E1E); position:fixed; top:0; left:0; right:0; z-index:100; backdrop-filter:blur(10px); }
.adm-brand { color:#BFA164; font-weight:700; font-size:14px; letter-spacing:1px; text-transform:uppercase; }
.adm-user { color:var(--fm-s,#6B6356); font-size:12px; }

.adm-tabs { display:flex; gap:4px; overflow-x:auto; padding-bottom:16px; border-bottom:1px solid var(--fm-br,#1E1E1E); margin-bottom:20px; }
.adm-tab { background:none; border:1px solid transparent; border-radius:8px; padding:8px 16px; color:var(--fm-s,#6B6356); font-size:13px; font-weight:500; cursor:pointer; white-space:nowrap; transition:all .2s; display:flex; align-items:center; gap:4px; }
.adm-tab:hover { color:var(--fm-m,#A09888); background:var(--fm-bg1,#141414); }
.adm-tab.active { color:#BFA164; background:rgba(191,161,100,0.08); border-color:rgba(191,161,100,0.2); }
.adm-tab-badge { background:#BFA164; color:#0A0A0A; font-size:10px; font-weight:700; padding:1px 6px; border-radius:10px; margin-left:4px; }

.adm-stats-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:12px; }
.adm-stat { display:flex; align-items:center; gap:14px; background:var(--fm-bg1,#141414); border:1px solid var(--fm-br,#1E1E1E); border-radius:10px; padding:16px; transition:all .2s; }
.adm-stat:hover { border-color:#2A2520; background:#181818; transform:translateY(-1px); }
.adm-stat-icon { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
.adm-stat-val { color:var(--fm-h,#F5F0E8); font-size:18px; font-weight:700; }
.adm-stat-lbl { color:var(--fm-s,#6B6356); font-size:11px; margin-top:2px; }

.adm-card { background:var(--fm-bg1,#141414); border:1px solid var(--fm-br,#1E1E1E); border-radius:12px; padding:20px; }
.adm-card-title { color:#BFA164; font-size:14px; font-weight:700; margin-bottom:14px; }

/* Usage & Limits */
.adm-cap-row { border:1px solid var(--fm-br,#1E1E1E); background:var(--fm-bg2,#0F0F0F); border-radius:10px; padding:12px; margin-bottom:10px; }
.adm-cap-label { color:var(--fm-h,#F5F0E8); font-weight:600; font-size:13px; }
.adm-cap-meta { color:var(--fm-s,#6B6356); font-size:10px; margin-top:2px; }
.adm-cap-code { background:rgba(191,161,100,.12); color:#BFA164; padding:1px 5px; border-radius:3px; font-size:10px; }
.adm-cap-input { width:90px; font-size:12px; background:var(--fm-bg1,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); color:var(--fm-h,#F5F0E8); }
.adm-cap-unit { color:var(--fm-s,#6B6356); font-size:11px; }
.adm-cap-stats { display:flex; flex-wrap:wrap; gap:14px; font-size:11px; color:var(--fm-m,#A09888); margin-top:6px; }
.adm-cap-stats strong { color:var(--fm-h,#F5F0E8); }
.adm-top-consumers { border:1px solid var(--fm-br,#1E1E1E); border-radius:8px; overflow:hidden; }
.adm-top-consumers-row { display:flex; align-items:center; justify-content:space-between; padding:6px 10px; border-bottom:1px solid var(--fm-br,#1A1A1A); font-size:11px; }
.adm-top-consumers-row:last-child { border-bottom:none; }
.adm-tc-name { color:var(--fm-h,#F5F0E8); font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.adm-tc-meta { color:var(--fm-s,#6B6356); font-size:9px; }

/* Light theme overrides for Usage & Limits */
html[data-theme="light"] .adm-cap-row { background:#FFFFFF !important; border-color:#E8E2DA !important; }
html[data-theme="light"] .adm-cap-label { color:#1A1A1A !important; }
html[data-theme="light"] .adm-cap-meta,
html[data-theme="light"] .adm-cap-unit,
html[data-theme="light"] .adm-tc-meta { color:#888 !important; }
html[data-theme="light"] .adm-cap-input { background:#F7F5F2 !important; border-color:#E0D8D0 !important; color:#1A1A1A !important; }
html[data-theme="light"] .adm-cap-stats { color:#555 !important; }
html[data-theme="light"] .adm-cap-stats strong,
html[data-theme="light"] .adm-tc-name { color:#1A1A1A !important; }
html[data-theme="light"] .adm-top-consumers,
html[data-theme="light"] .adm-top-consumers-row { border-color:#E8E2DA !important; }

.adm-table-wrap { overflow-x:auto; }
.adm-table { width:100%; border-collapse:collapse; font-size:12px; }
.adm-table th { color:var(--fm-s,#6B6356); font-size:10px; text-transform:uppercase; letter-spacing:0.5px; padding:8px 10px; border-bottom:1px solid var(--fm-br,#1E1E1E); text-align:left; white-space:nowrap; }
.adm-table td { padding:10px; border-bottom:1px solid #0F0F0F; color:var(--fm-m,#A09888); white-space:nowrap; }
.adm-table tr:hover td { background:rgba(191,161,100,0.03); }
.adm-mono { font-family:monospace; font-size:11px; color:var(--fm-s,#6B6356); }
.adm-muted { color:#4A4540; font-size:11px; }

.adm-badge { display:inline-block; padding:2px 8px; border-radius:4px; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.3px; }
.adm-b-completed,.adm-b-released,.adm-b-active,.adm-b-approved,.adm-b-verified { background:rgba(80,200,120,0.1); color:#50C878; }
.adm-b-paid,.adm-b-confirmed,.adm-b-funded { background:rgba(191,161,100,0.1); color:#BFA164; }
.adm-b-pending,.adm-b-pending_payment,.adm-b-payment_initiated,.adm-b-stk_sent,.adm-b-open { background:rgba(255,171,0,0.1); color:#FFAB00; }
.adm-b-in_transit,.adm-b-delivered,.adm-b-inspection { background:rgba(79,195,247,0.1); color:#4FC3F7; }
.adm-b-cancelled,.adm-b-refunded,.adm-b-rejected,.adm-b-suspended,.adm-b-notfunded { background:rgba(255,107,107,0.1); color:#FF6B6B; }
.adm-b-info,.adm-b-none,.adm-b-draft,.adm-b-sold { background:rgba(107,99,86,0.15); color:var(--fm-s,#6B6356); }

.adm-empty { text-align:center; color:var(--fm-s,#3A3530); padding:30px; font-size:13px; }
.adm-alert-item { padding:10px 0; border-bottom:1px solid var(--fm-br,#1E1E1E); cursor:pointer; }
.adm-alert-item:hover { background:rgba(191,161,100,0.03); margin:0 -20px; padding:10px 20px; }

.adm-rev-rows { display:flex; flex-direction:column; gap:8px; }
.adm-rev-row { display:flex; justify-content:space-between; align-items:center; font-size:13px; }

.adm-select { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#2A2520); border-radius:6px; padding:6px 10px; color:var(--fm-m,#A09888); font-size:12px; }
.adm-input { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#2A2520); border-radius:6px; padding:6px 10px; color:var(--fm-h,#F5F0E8); font-size:12px; }
.adm-input:focus,.adm-select:focus { border-color:#BFA164; outline:none; }
.adm-input::placeholder { color:var(--fm-s,#3A3530); }
.adm-lbl { display:block; color:var(--fm-s,#6B6356); font-size:11px; margin-bottom:4px; }

.adm-overlay { position:fixed; top:0; left:0; right:0; bottom:0; background:rgba(0,0,0,0.7); z-index:2000; display:flex; align-items:center; justify-content:center; padding:20px; backdrop-filter:blur(4px); }
.adm-modal { background:var(--fm-bg1,#141414); border:1px solid var(--fm-br,#2A2520); border-radius:14px; padding:28px; max-width:420px; width:100%; }
.adm-modal-head { padding:0 0 12px 0; border-bottom:1px solid var(--fm-br,#2A2520); margin-bottom:14px; }
.adm-modal-body { padding:0; }
.adm-modal-foot { padding:14px 0 0 0; border-top:1px solid var(--fm-br,#2A2520); margin-top:14px; }
.btn-close-x { background:none; border:none; color:var(--fm-m,#A09888); font-size:22px; line-height:1; cursor:pointer; }
.adm-switch { position:relative; display:inline-block; width:36px; height:20px; cursor:pointer; }
.adm-switch input { opacity:0; width:0; height:0; }
.adm-switch span { position:absolute; cursor:pointer; top:0; left:0; right:0; bottom:0; background:#3a322a; border-radius:20px; transition:.2s; }
.adm-switch span::before { position:absolute; content:""; height:14px; width:14px; left:3px; bottom:3px; background:#A09888; border-radius:50%; transition:.2s; }
.adm-switch input:checked + span { background:#BFA164; }
.adm-switch input:checked + span::before { transform:translateX(16px); background:#fff; }

/* Transport admin helpers */
.adm-sect { color:#BFA164; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; margin-bottom:8px; }
.adm-dropdown { position:absolute; left:0; right:0; top:calc(100% + 4px); background:var(--fm-bg1,#141414); border:1px solid var(--fm-br,#1E1E1E); border-radius:6px; max-height:240px; overflow-y:auto; z-index:100; box-shadow:0 8px 20px rgba(0,0,0,.4); }
.adm-dropdown-item { padding:10px 12px; cursor:pointer; font-size:13px; color:var(--fm-h,#F5F0E8); border-bottom:1px solid var(--fm-br,#1A1A1A); }
.adm-dropdown-item:last-child { border-bottom:none; }
.adm-dropdown-item:hover { background:rgba(191,161,100,.08); color:#BFA164; }
.adm-pill { padding:2px 8px; border-radius:8px; font-weight:600; text-transform:capitalize; display:inline-block; }
.pill-pending { background:rgba(255,152,0,.1); color:#FF9800; }
.pill-active { background:rgba(191,161,100,.15); color:#BFA164; }
.pill-completed { background:rgba(80,200,120,.1); color:#50C878; }
.pill-cancelled { background:rgba(255,107,107,.1); color:#FF6B6B; }

/* ===== Insurance admin styles ===== */
.adm-label { display:block; font-size:11px; color:var(--fm-s,#6B6356); margin:8px 0 4px; font-weight:600; text-transform:uppercase; letter-spacing:.4px; }
.adm-table-wrap { overflow-x:auto; }
.ins-st { padding:2px 8px; border-radius:8px; font-weight:600; font-size:10px; text-transform:capitalize; display:inline-block; background:rgba(160,152,136,.15); color:#A09888; }
.ins-st-pending { background:rgba(241,180,76,.15); color:#f1b44c; }
.ins-st-active,.ins-st-paid,.ins-st-approved { background:rgba(80,200,120,.15); color:#50C878; }
.ins-st-claimed { background:rgba(191,161,100,.15); color:#BFA164; }
.ins-st-expired,.ins-st-lapsed { background:rgba(160,152,136,.15); color:#A09888; }
.ins-st-cancelled,.ins-st-rejected,.ins-st-withdrawn,.ins-st-refunded { background:rgba(255,107,107,.15); color:#FF6B6B; }
.ins-st-submitted,.ins-st-under_review { background:rgba(255,152,0,.15); color:#FF9800; }

.ins-mod-overlay { position:fixed; inset:0; background:rgba(0,0,0,.7); backdrop-filter:blur(4px); z-index:2200; display:flex; align-items:center; justify-content:center; padding:20px; }
.ins-mod-card { background:var(--fm-bg1,#141414); border:1px solid var(--fm-br,#1E1E1E); border-radius:14px; padding:20px; max-width:480px; width:100%; max-height:90vh; overflow-y:auto; }
.ins-mod-card.ins-mod-wide { max-width:640px; }
.ins-mod-summary { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:8px 16px; margin:8px 0 12px; padding:10px 12px; background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); border-radius:8px; font-size:12px; }
.ins-mod-summary > div { display:flex; flex-direction:column; }
.ins-mod-summary span { color:var(--fm-s,#6B6356); font-size:10px; text-transform:uppercase; letter-spacing:.4px; }
.ins-mod-summary strong { color:var(--fm-h,#F5F0E8); font-weight:600; font-size:13px; }
.ins-mod-section { margin:10px 0; padding:8px 12px; background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); border-radius:8px; }
.ins-mod-section strong { color:var(--fm-h,#F5F0E8); font-size:12px; }
.ins-mod-action { margin-top:12px; padding-top:10px; border-top:1px dashed var(--fm-br,#1E1E1E); }

/* ===== Admin Team grid ===== */
.team-row { display:flex; align-items:flex-start; gap:16px; padding:14px 0; border-bottom:1px solid var(--fm-br,#1E1E1E); flex-wrap:wrap; }
.team-row:last-child { border-bottom:none; }
.team-id { display:flex; align-items:center; gap:12px; cursor:pointer; min-width:240px; flex:0 0 auto; }
.team-id:hover .team-name { color:#BFA164; }
.team-avatar { width:40px; height:40px; border-radius:50%; background:var(--fm-bg3,#1E1E1E); display:flex; align-items:center; justify-content:center; font-weight:700; color:#BFA164; overflow:hidden; flex-shrink:0; font-size:14px; }
.team-avatar img { width:100%; height:100%; object-fit:cover; }
.team-name { color:var(--fm-h,#F5F0E8); font-weight:600; font-size:13px; line-height:1.4; transition:color .15s; }
.team-meta { color:var(--fm-m,#A09888); font-size:11px; display:flex; gap:10px; flex-wrap:wrap; margin-top:2px; }
.team-role-pill { font-size:10px; padding:1px 8px; border-radius:8px; font-weight:600; text-transform:uppercase; letter-spacing:.3px; margin-left:6px; vertical-align:middle; }
.team-role-pill.team-role-super { background:rgba(191,161,100,.15); color:#BFA164; }
.team-role-pill.team-role-admin { background:rgba(80,200,120,.15); color:#50C878; }

.team-chips { display:flex; flex-wrap:wrap; gap:6px; flex:1; min-width:300px; align-items:flex-start; }
.team-chip { font-size:11px; padding:4px 10px; border-radius:14px; border:1px solid var(--fm-br,#2A2520); background:var(--fm-bg2,#0F0F0F); color:var(--fm-m,#A09888); cursor:pointer; display:inline-flex; align-items:center; transition:all .15s; }
.team-chip:hover:not(:disabled) { border-color:#BFA164; }
.team-chip.team-chip-on { background:rgba(80,200,120,.1); border-color:rgba(80,200,120,.4); color:#50C878; }
.team-chip.team-chip-on:hover:not(:disabled) { border-color:#50C878; background:rgba(80,200,120,.15); }
.team-chip.team-chip-implicit { background:rgba(191,161,100,.1); border-color:rgba(191,161,100,.4); color:#BFA164; cursor:default; }
.team-chip.team-chip-static { cursor:default; }
.team-chip:disabled { opacity:.7; cursor:not-allowed; }

@media (max-width:768px) {
  .adm-stats-grid { grid-template-columns:1fr 1fr; }
  .adm-tabs { gap:2px; }
  .adm-tab { padding:6px 10px; font-size:11px; }
  .adm-listing-grid { grid-template-columns:1fr; }
}

/* View toggle */
.adm-view-toggle { display:flex; border:1px solid var(--fm-br,#2A2520); border-radius:6px; overflow:hidden; }

/* Dispute detail */
.adm-disp-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.adm-disp-field { font-size:13px; color:var(--fm-m,#A09888); }
.adm-disp-field .adm-lbl { display:block; color:var(--fm-s,#6B6356); font-size:10px; text-transform:uppercase; margin-bottom:2px; }
.adm-b-priority-low { background:rgba(107,99,86,0.15); color:var(--fm-s,#6B6356); }
.adm-b-priority-medium { background:rgba(255,171,0,0.1); color:#FFAB00; }
.adm-b-priority-high { background:rgba(255,107,107,0.1); color:#FF6B6B; }
.adm-b-priority-urgent { background:rgba(255,50,50,0.15); color:#FF3232; }
.adm-evidence-list { display:flex; flex-direction:column; gap:12px; }
.adm-evidence-item { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); border-radius:10px; padding:12px; }
.adm-evidence-header { display:flex; justify-content:space-between; align-items:center; }
.adm-evidence-photo { position:relative; margin-top:8px; cursor:zoom-in; border-radius:8px; overflow:hidden; max-width:300px; }
.adm-evidence-photo img { width:100%; height:auto; display:block; border-radius:8px; }
.adm-evidence-zoom { position:absolute; bottom:8px; right:8px; width:28px; height:28px; background:rgba(0,0,0,0.6); border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff; font-size:16px; }
.adm-evidence-doc { display:flex; align-items:center; gap:10px; padding:10px; background:var(--fm-bg1,#141414); border:1px solid var(--fm-br,#2A2520); border-radius:8px; margin-top:8px; text-decoration:none; color:var(--fm-m,#A09888); font-size:12px; transition:all .15s; }
.adm-evidence-doc:hover { border-color:#BFA164; }
.adm-notes-list { display:flex; flex-direction:column; gap:8px; margin-bottom:12px; }
.adm-note-item { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); border-radius:8px; padding:10px 12px; }
.adm-note-header { display:flex; justify-content:space-between; align-items:center; }
.adm-party-card { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); border-radius:8px; padding:10px 12px; }
.adm-party-label { font-size:10px; color:var(--fm-s,#6B6356); text-transform:uppercase; margin-bottom:2px; }

/* User detail */
.adm-user-avatar-lg { width:72px; height:72px; border-radius:50%; background:rgba(191,161,100,0.15); color:#BFA164; display:flex; align-items:center; justify-content:center; font-size:24px; font-weight:700; margin:0 auto; }
.adm-user-stars { display:flex; justify-content:center; gap:2px; font-size:18px; }
.adm-verif-item { display:flex; align-items:center; gap:10px; padding:8px 0; border-bottom:1px solid var(--fm-br,#1E1E1E); }
.adm-verif-item:last-child { border-bottom:none; }
.adm-user-listing-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(160px, 1fr)); gap:10px; }
.adm-user-lcard { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); border-radius:8px; overflow:hidden; cursor:pointer; transition:all .2s; }
.adm-user-lcard:hover { border-color:#2A2520; }
.adm-user-lcard-img { position:relative; width:100%; height:100px; background:#1A1A1A; overflow:hidden; }
.adm-user-lcard-img img { width:100%; height:100%; object-fit:cover; }
.adm-user-lcard-noimg { width:100%; height:100%; display:flex; align-items:center; justify-content:center; }
.adm-user-lcard-noimg i { font-size:28px; color:#2A2520; }
.adm-user-lcard-body { padding:8px 10px; }
.adm-review-item { padding:12px 0; border-bottom:1px solid var(--fm-br,#1E1E1E); }
.adm-review-item:last-child { border-bottom:none; }
.adm-user-order { display:flex; justify-content:space-between; align-items:center; padding:10px 0; border-bottom:1px solid var(--fm-br,#1E1E1E); cursor:pointer; }
.adm-user-order:hover { background:rgba(191,161,100,0.03); margin:0 -20px; padding:10px 20px; }
.adm-user-order:last-child { border-bottom:none; }

/* KYC docs */
.adm-kyc-docs { display:grid; grid-template-columns:repeat(auto-fill, minmax(180px, 1fr)); gap:12px; }
.adm-kyc-doc { position:relative; border-radius:10px; overflow:hidden; border:1px solid var(--fm-br,#2A2520); cursor:zoom-in; transition:all .2s; }
.adm-kyc-doc:hover { border-color:#BFA164; transform:scale(1.02); }
.adm-kyc-doc img { width:100%; height:160px; object-fit:cover; display:block; }
.adm-kyc-doc-label { position:absolute; bottom:0; left:0; right:0; background:rgba(0,0,0,0.7); color:var(--fm-h,#F5F0E8); font-size:11px; font-weight:600; padding:4px 10px; text-align:center; }
.adm-kyc-pdf { cursor:pointer; }
.adm-kyc-pdf-inner { display:flex; flex-direction:column; align-items:center; justify-content:center; height:160px; background:#1A1A1A; }
.adm-kyc-pdf-inner i { font-size:52px; color:#FF6B6B; }
.adm-kyc-pdf-name { color:var(--fm-m,#A09888); font-size:11px; margin-top:6px; max-width:160px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; text-align:center; }

/* Grouped verifications */
.adm-verif-groups { display:flex; flex-direction:column; gap:10px; }
.adm-verif-group { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); border-radius:10px; overflow:hidden; }
.adm-verif-group-header { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; cursor:pointer; transition:all .15s; }
.adm-verif-group-header:hover { background:var(--fm-bg1,#141414); }
.adm-user-avatar-sm { width:36px; height:36px; border-radius:50%; background:rgba(191,161,100,0.12); color:#BFA164; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; flex-shrink:0; }
.adm-verif-group-body { border-top:1px solid #1E1E1E; }
.adm-verif-item-row { display:flex; align-items:center; justify-content:space-between; padding:12px 18px 12px 58px; border-bottom:1px solid var(--fm-br,#1A1A1A); cursor:pointer; transition:all .15s; }
.adm-verif-item-row:hover { background:var(--fm-bg1,#141414); }
.adm-verif-item-row:last-child { border-bottom:none; }
/* Dev-Requests #13 — inline ID + selfie thumbnails on verifications list */
.adm-verif-thumb-strip { display:flex; gap:6px; margin-left:auto; padding-right:12px; }
.adm-verif-thumb { width:44px; height:44px; border-radius:6px; overflow:hidden; background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); display:flex;align-items:center;justify-content:center; }
.adm-verif-thumb img { width:100%; height:100%; object-fit:cover; }
.adm-verif-thumb i { color:#FF6B6B; font-size:22px; }
.adm-verif-thumb-selfie { border-radius:50%; }

/* Notification chips */
.adm-notif-chip { background:rgba(191,161,100,0.08); border:1px solid rgba(191,161,100,0.2); color:#BFA164; font-size:11px; padding:3px 10px; border-radius:20px; white-space:nowrap; }
.adm-notif-chip:hover { background:rgba(191,161,100,0.15); color:#BFA164; border-color:#BFA164; }
.adm-view-toggle button { background:var(--fm-bg2,#0F0F0F); border:none; color:var(--fm-s,#6B6356); padding:6px 10px; font-size:14px; cursor:pointer; transition:all .2s; }
.adm-view-toggle button.active { background:rgba(191,161,100,0.15); color:#BFA164; }
.adm-view-toggle button:hover:not(.active) { color:var(--fm-m,#A09888); }

/* Listing card grid */
.adm-listing-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(300px, 1fr)); gap:16px; }
.adm-lcard { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); border-radius:12px; overflow:hidden; cursor:pointer; transition:all .2s; }
.adm-lcard:hover { border-color:#2A2520; transform:translateY(-2px); box-shadow:0 8px 24px rgba(0,0,0,0.3); }
.adm-lcard-img { position:relative; width:100%; height:180px; background:#1A1A1A; overflow:hidden; }
.adm-lcard-img img { width:100%; height:100%; object-fit:cover; }
.adm-lcard-noimg { width:100%; height:100%; display:flex; align-items:center; justify-content:center; }
.adm-lcard-noimg i { font-size:48px; color:#2A2520; }
.adm-lcard-status { position:absolute; top:10px; left:10px; padding:3px 8px; border-radius:4px; font-size:10px; font-weight:600; text-transform:uppercase; }
.adm-lcard-batch { position:absolute; top:10px; right:10px; background:rgba(191,161,100,0.9); color:#0A0A0A; padding:2px 8px; border-radius:4px; font-size:10px; font-weight:700; }
.adm-lcard-body { padding:14px; }
.adm-lcard-title { color:var(--fm-h,#F5F0E8); font-size:15px; font-weight:700; margin-bottom:4px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.adm-lcard-meta { color:var(--fm-s,#6B6356); font-size:12px; margin-bottom:6px; }
.adm-lcard-price { color:#BFA164; font-size:18px; font-weight:700; margin-bottom:8px; }
.adm-lcard-ptype { font-size:10px; font-weight:500; color:var(--fm-s,#6B6356); text-transform:uppercase; margin-left:4px; }
.adm-lcard-details { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:8px; }
.adm-lcard-details span { font-size:11px; color:var(--fm-m,#A09888); display:flex; align-items:center; gap:3px; }
.adm-lcard-details i { font-size:13px; color:var(--fm-s,#6B6356); }
.adm-lcard-seller { font-size:12px; color:var(--fm-m,#A09888); display:flex; align-items:center; gap:4px; margin-bottom:4px; }
.adm-lcard-loc { font-size:11px; color:var(--fm-s,#6B6356); display:flex; align-items:center; gap:4px; margin-bottom:6px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.adm-lcard-delivery { font-size:11px; color:#50C878; margin-bottom:8px; display:flex; align-items:center; gap:4px; }
.adm-lcard-footer { display:flex; align-items:center; gap:12px; font-size:11px; color:#4A4540; padding-top:8px; border-top:1px solid #1E1E1E; }
.adm-lcard-footer i { font-size:13px; margin-right:2px; }

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .adm-container { padding: 12px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; }
  .adm-card { padding: 12px !important; border-radius: 10px !important; }
  .adm-sidebar { display: none !important; }   /* admin sidebar is desktop-only */
  .adm-main { width: 100% !important; margin-left: 0 !important; padding-left: 0 !important; padding-right: 0 !important; }
  .adm-stat-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; }
  .adm-stat { padding: 10px !important; }
  .adm-stat-num { font-size: 18px !important; }
  .adm-stat-lbl { font-size: 9px !important; }
  .adm-tabs { gap: 4px; overflow-x: auto; padding-bottom: 4px;
              scrollbar-width: none; -ms-overflow-style: none; }
  .adm-tabs::-webkit-scrollbar { display: none; }
  .adm-tab { font-size: 12px; padding: 7px 10px; flex-shrink: 0; white-space: nowrap; }
  .adm-table { font-size: 11px; }
  .adm-table th, .adm-table td { padding: 6px 8px; }
  .adm-modal-overlay { padding: 12px; align-items: flex-end; }
  .adm-modal { padding: 16px; border-radius: 14px 14px 0 0; max-height: 90vh; overflow-y: auto; }
  .adm-input, .adm-select, .adm-textarea { font-size: 16px !important; padding: 9px 11px !important; }
  .adm-lcard-name { font-size: 13px !important; }
  .adm-lcard-price { font-size: 14px !important; }
  .adm-lcard-details { font-size: 10px !important; }
}

/* Image-duplicate clusters */
.adm-dupe-cluster { border:1px solid var(--fm-br,#1E1E1E); border-radius:10px; padding:14px; margin-bottom:14px; background:rgba(255,255,255,0.02); }
.adm-dupe-cluster-hdr { display:flex; align-items:center; margin-bottom:12px; font-size:12px; }
.adm-dupe-photos { display:flex; flex-wrap:wrap; gap:12px; }
.adm-dupe-photo { width:170px; border:1px solid var(--fm-br,#2A2520); border-radius:8px; overflow:hidden; background:var(--fm-bg2,#0F0F0F); }
.adm-dupe-photo.is-original { border-color:#50C878; box-shadow:0 0 0 2px rgba(80,200,120,0.18); }
.adm-dupe-photo img { width:100%; height:140px; object-fit:cover; display:block; }
.adm-dupe-photo-meta { padding:8px; font-size:11px; color:var(--fm-h,#F5F0E8); }
.adm-dupe-photo-title { font-weight:600; line-height:1.2; margin-bottom:2px; }
.adm-dupe-tag-orig { font-size:9px; font-weight:700; color:#50C878; letter-spacing:0.4px; text-transform:uppercase; margin-bottom:3px; }
.btn-xs { font-size:10px; padding:3px 8px; border-radius:4px; line-height:1.3; }
</style>